// ZAP Activity Planner v1.0
import React, { useState, useEffect, useCallback, useRef } from 'react';

class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(err) { return { error: err }; }
  componentDidCatch(err, info) { console.error('ZAP ErrorBoundary:', err, info); }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: 24, background: '#1a0000', border: '2px solid #EF4444', borderRadius: 12, margin: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#EF4444', marginBottom: 8 }}>⚠️ ZAP Render Error</div>
          <div style={{ fontSize: 12, color: '#fff', fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{this.state.error.toString()}</div>
          <button onClick={() => this.setState({ error: null })} style={{ marginTop: 12, padding: '8px 16px', background: '#EF4444', border: 'none', borderRadius: 8, color: '#fff', fontWeight: 700, cursor: 'pointer' }}>Dismiss</button>
        </div>
      );
    }
    return this.props.children;
  }
}


// ============================================================
// CONSTANTS & THEME
// ============================================================
// S226: Direct VC call — Netlify proxy removed. Cold start on proxy was causing VC
// to fail on first load after idle, dropping cascade to P2 unnecessarily.
// VC_API_KEY read from build-time env var (set in Netlify dashboard).
const VC_API_KEY = process.env.REACT_APP_VC_API_KEY || '';
const VC_BASE_URL = 'https://weather.visualcrossing.com/VisualCrossingWebServices/rest/services/timeline';
const NWS_USER_AGENT = 'ZAP-Activity/1.0 (zap-all-activities.netlify.app)';
const CACHE_TTL = 900000; // 15 min

// Wraps any async fetch in a timeout — returns failure object instead of hanging
const withTimeout = (promise, ms, label) =>
  Promise.race([
    promise,
    new Promise(resolve =>
      setTimeout(() => resolve({ success: false, error: `${label} timed out after ${ms/1000}s` }), ms)
    ),
  ]);
const MB_API_KEY = 'H2nyzakt47srTN5U'; // S273: Swapped to working key — old key tMGERa6DujvH3Tqs exhausted (429 credits exceeded). Verified live 2026-07-11.
const GS_API_KEY = '67b3e95ac8f36707152750727cb3a72b70be8bb0';
const WB_API_KEY = '8fecb123d7ca42989b18388f4c736417';
const WAPI_KEY   = '5a005b16db4e4058b0f34523261603';

// Password gate removed — no longer needed (S96)

// Spond Events
const SPOND_EVENTS_URL = 'https://api.github.com/repos/johnzur-droid/ZAP-ALL-ACTIVITIES/contents/snapshots/spond_events.json?ref=staging';
const SPOND_GH_TOKEN = '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';
const SPOND_INVITE_LINK = 'https://spond.com/invite/ZRAJZ';

// WU comparison logging (admin only — silent, localStorage)
const WU_LOG_KEY = 'zap_wu_compare_log';
const WU_LOG_MAX = 500; // max entries before truncation

function logWUComparison(courseName, vcData, wuData) {
  try {
    const entry = {
      ts: new Date().toISOString(),
      course: courseName,
      vc: {
        temp: vcData?.temp,
        windSpeed: vcData?.windSpeed,
        windGust: vcData?.windGust,
        humidity: vcData?.humidity,
        precipChance: vcData?.precipChance,
        conditions: vcData?.conditions,
        source: vcData?.source,
      },
      wu: wuData || null, // null until WU API is wired — slot reserved
    };
    const raw = localStorage.getItem(WU_LOG_KEY);
    const log = raw ? JSON.parse(raw) : [];
    log.push(entry);
    if (log.length > WU_LOG_MAX) log.splice(0, log.length - WU_LOG_MAX);
    localStorage.setItem(WU_LOG_KEY, JSON.stringify(log));
  } catch (e) {
    console.warn('WU log write failed:', e);
  }
}

function getWULog() {
  try {
    const raw = localStorage.getItem(WU_LOG_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (e) { return []; }
}

function clearWULog() {
  try { localStorage.removeItem(WU_LOG_KEY); } catch (e) {}
}

// Colors — S254 D9 palette (black/warm, matches HomeScreen mockup_D9)
const COLOR_BG = '#111';            // App background
// iOS PWA scroll fix — window.scrollTo alone is unreliable in standalone mode
const scrollToTop = () => {
  try { document.getElementById('root').scrollTop = 0; } catch {}
};
const COLOR_HEADER = '#1c1c1c';     // Header/nav bars
const COLOR_CARD = '#f7f2ee';       // Card/container backgrounds (warm cream)
// S69 LOCKED — Tailwind status colors
// S85 UPDATE — bright bg + dark text (Set 4 approved by JZ)
const COLOR_GREEN = '#4ADE80';      // GO accent (kept for non-status uses)
const COLOR_GREEN_DARK = '#22C55E'; // GO bg (bright green)
const COLOR_GOLD = '#FACC15';       // CAUTION accent
const COLOR_ORANGE = '#FB923C';     // STRONG CAUTION accent
const COLOR_WHITE = '#ffffff';
const COLOR_OFF_WHITE = '#f7f2ee';  // Primary text on dark backgrounds
const COLOR_MUTED = '#c4a882';      // Secondary/muted text (warm tan — readable on #111 and #f7f2ee)
const COLOR_RED = '#F87171';        // NO-GO accent
const COLOR_RED_BG = '#EF4444';     // NO-GO bg (bright red — S85)
const COLOR_GOLD_BG = '#EAB308';    // CAUTION bg (bright yellow — S85)
const COLOR_GREEN_BG = '#22C55E';   // GO bg (bright green — S85)
const COLOR_ORANGE_BG = '#F97316';  // STRONG CAUTION bg (bright orange — S85)
const COLOR_DARK_GREEN = '#bf9d6f'; // Accent/border color (warm gold-brown)
const COLOR_DARKEST = '#0a0a0a';    // Dark inset backgrounds
const COLOR_INPUT_BG = '#1a1a1a';   // Form input backgrounds (dark, white text)

// Diff color helper — consistent golf scoring colors everywhere
// negative (under par) = red #F1373D, even = green #22C55E, positive (over par) = blue #3B82F6
const getDiffColor = (diff) => {
  if (diff == null) return '#9CA3AF';
  if (diff < 0) return '#F1373D';
  if (diff > 0) return '#3B82F6';
  return '#22C55E';
};
const formatDiff = (diff) => {
  if (diff == null) return '—';
  if (diff < 0) return String(diff);
  if (diff > 0) return '+' + diff;
  return 'E';
};

// ============================================================
// S45 LOCKED GO/NO-GO THRESHOLDS
// ============================================================
// ── GO/NO-GO THRESHOLDS — S98 LOCKED ──────────────────────
const PRECIP_CAUTION  = 30;   // 0-29=GO, 30-49=CAU
const PRECIP_STR_CAU  = 50;   // 50-69=STR
const PRECIP_NOGO     = 70;   // 70+=NOGO
const WIND_CAUTION    = 15;   // 0-14=GO, 15-19=CAU
const WIND_STR_CAU    = 20;   // 20-29=STR
const WIND_NOGO       = 30;   // 30+=NOGO
// S85 LOCKED — Bright bg + dark text (Set 4 — approved by JZ)
const STATUS_COLORS = {
  GO:               { color: '#22C55E', bg: '#000000', border: '#22C55E', text: '#ffffff' },
  CAUTION:          { color: '#FDE047', bg: '#000000', border: '#FDE047', text: '#ffffff' },
  'STRONG CAUTION': { color: '#F97316', bg: '#000000', border: '#F97316', text: '#ffffff' },
  'NO-GO':          { color: '#EF4444', bg: '#000000', border: '#EF4444', text: '#ffffff' },
  UNKNOWN:          { color: '#9CA3AF', bg: '#000000', border: '#9CA3AF', text: '#ffffff' },
};

// Base button style
const BTN_BASE = {
  border: 'none',
  borderRadius: 10,
  cursor: 'pointer',
  fontFamily: "'Georgia', serif",
  outline: 'none',
  WebkitTapHighlightColor: 'transparent',
};

const BTN_LARGE = {
  ...BTN_BASE,
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  padding: '16px 20px',
  borderRadius: 14,
  border: 'none',
  minHeight: 60,
  gap: 10,
};

const CARD_STYLE = {
  background: COLOR_CARD,
  borderRadius: 14,
  border: `2px solid ${COLOR_DARK_GREEN}`,
};

const BTN_SMALL = {
  ...BTN_BASE,
  padding: '10px 16px',
  borderRadius: 10,
  fontSize: '14px',
  fontWeight: 600,
  minHeight: 44,
  minWidth: 44,
};

// ============================================================
// COURSE LORE
// ============================================================
const COURSE_LORE = {
  'spooky-brook': {
    emoji: '👻',
    headline: 'Named after a real brook. Mostly.',
    body: `The course takes its name from Spooky Brook — a small tributary that winds across several fairways. The brook is mostly harmless (the hazard "really doesn't pose much of a threat," per every golf reviewer ever), but hole 17 makes you carry a pond to reach the green, which is the course's one genuine moment of drama. Designed by Ed Ault and opened in 1970, Spooky Brook was built with ordinary golfers in mind — wide fairways, generous rough, trees that are spaced far enough apart that you can usually find your ball. It's the most senior- and beginner-friendly course in the Somerset County system, and it knows it. Part of Colonial Park, owned by Somerset County. No restaurant — just vending machines. That's not a bug, it's a feature.`,
  },
  'quail-brook': {
    emoji: '🌲',
    headline: 'The woods get serious on the back nine.',
    body: `Opened in 1981 as Somerset County's 25th anniversary gift to itself, Quail Brook was designed by the Ed Ault family — Ed, his son Brian, and Tom Clark, the same trio that later designed TPC Avenel in Maryland. The front nine is rolling and fun. The back nine gets tight and hilly in a way that surprises first-timers. At 6,617 yards it's the longest of the Somerset County flat-ish courses. Shares grill management with Spooky Brook — same kitchen, different address.`,
  },
  'warrenbrook': {
    emoji: '🌳',
    headline: 'Heavy woods. Uphill lies. No mercy.',
    body: `Warrenbrook opened in 1982 as Somerset County's third golf course — coming after Green Knoll (1959) and Quail Brook (1981). Designed by Hal C. Purdy, the heavily wooded layout has bunkers on every hole and constant elevation changes that produce uphill, downhill, and sidehill lies. At 6,372 yards from the tips it's not long, but the tight tree-lined fairways punish anything off line. The signature 17th — a 376-yard dogleg right par-4 over a pond — and the distinctive arched stone bridge across the water hazard give the course a character all its own. The Tudor-style clubhouse adds to the period feel. If you haven't played it before, give yourself a stroke.`,
  },
  'green-knoll': {
    emoji: '🏌️',
    headline: 'The original. Somerset County\'s first public course.',
    body: `Green Knoll opened in April 1959 as Somerset County's first public golf course and the first major project completed by the Park Commission. Designed by William F. Gordon — who also designed Oak Hill in Hunterdon — the layout was carved from land purchased from George V. N. Davis at a construction cost of $178,760, and was officially dedicated on July 1, 1960. The first golf pro was John L. Grace. The course plays 6,443 yards with rolling terrain and water in play on holes 5, 6, 8, and 15. The signature 8th is a dogleg-right par-4 where two creeks dictate strategy — the first winds across both the right rough and the landing zone, the second crosses 20 yards short of the green. There is no driving range; the adjacent 9-hole pitch-and-putt (par 27) doubles as a warmup range on weekends and holidays and remains one of the park system's most popular attractions. A classic old-school layout that predates the design philosophy that came after it.`,
  },
  'neshanic-valley': {
    emoji: '🏆',
    headline: 'The flagship. Built on old farmland with mountain views.',
    body: `Neshanic Valley is the crown jewel of the Somerset County system and the newest of the five, opening Labor Day weekend 2004 with the Meadow and Lake nines; the Ridge nine followed in 2005. Designed by Michael Hurdzan and Dana Fry — the same team behind Erin Hills, site of the 2017 US Open — it sprawls across 420 acres of former rolling farmland overlooking the historic Neshanic Valley and the Sourland Mountains. The 27-hole championship layout plays in three combinations of two nines, with five tee stations stretching the round from 5,050 up to 7,108 yards. Bentgrass throughout; deep bunkers, fescue rough, and wetland hazards punish anything off line. Add a 9-hole par-32 Academy Course, the Callaway Golf Performance Center, and a 12,000-square-foot clubhouse, and it's the most complete public golf facility in New Jersey. The Grille Room serves real food — sandwiches and cocktails to enjoy on the second-floor deck with its 270° view. Ranked by Golfweek as a Top 50 Municipal Course in the US, it has hosted the 2007 Met Public Links, 2009 NJSGA Women's Amateur, 2012 USGA Women's Public Links Championship, and 2014 NJSGA Men's Public Links. A Rutgers weather sensor is on-site.`,
  },
  'fox-hollow': {
    emoji: '🦊',
    headline: 'Six par 3s. Originally designed by a Trent Jones protégé.',
    body: `Fox Hollow opened in 1962, designed by Hal C. Purdy — who got his start as a construction supervisor under Robert Trent Jones Sr. The course was completely redesigned in 2000, which is why it plays younger than its age. The layout has an unusual feature: six par 3s, including water carries on the 9th and 18th — an uncommon way to close each nine. At par 70 it plays shorter than the distance suggests, but the tree-covered elevation changes on the back nine make up for it. Voted one of the top Northeast courses by Garden State Golf readers. Still one of Somerset County's best-kept secrets.`,
  },
  'architects': {
    emoji: '🏛️',
    headline: 'Every hole is a tribute to a different legend.',
    body: `The Architects Golf Club opened in 2001, the most original course concept in New Jersey golf. Owners Dennis and Larry Turco — co-owners of Turco Golf Construction — partnered with golf architect Stephen Kay and Golf Digest architectural editor Ron Whitten to build "New Jersey's only legacy golf course." Each of the 18 holes is designed in the style of a different golden-age architect spanning 1885–1955. Hole 1 channels Old Tom Morris with pot bunkers either side of a straightaway par 5. Hole 7 channels A.W. Tillinghast — the dolomite mounds are pure Bethpage. Hole 13 is the showstopper: Alister MacKenzie's diagonal green fronted by a stream and stone bridge, with flashy bunkers behind — Augusta's 12th in spirit. Holes 9 and 15 honor Donald Ross. Hole 18 is pure Robert Trent Jones Sr. — an 80-yard runway tee, fairway pinched to 25 yards by Trent Jones bunkers left and right, recently restored. Plaques at each tee tell the architect's story. The 16,000 sq ft clubhouse opened 2007; Architects Bar & Grill (public year-round) opened 2019. Sits in the rolling hills of Warren County. Managed by Troon since 2020. GolfPass ranked it #1 NJ public course in 2022.`,
  },
  'high-bridge-hills': {
    emoji: '⚡',
    headline: 'Links-style. Ridgeline. 100-foot drop on hole 8.',
    body: `High Bridge Hills opened in 1999, designed by Mark A. Mungeam (ASGCA) — the most authentically links-style course on the Jersey Circuit. Owned by the Borough of High Bridge and managed by Troon Indigo Sports, the course unfolds along a ridgeline overlooking Spruce Run Reservoir in Hunterdon County. Plays 6,500 yards from the tips to a par 70 (course rating 72.3, slope 128). The defining hole is the par-3 8th: a tee-to-green drop of nearly 100 feet over a natural gorge. The exposed ridgeline means wind is always a factor, and the open elevation makes lightning risk higher here than anywhere else on the circuit. When ZAP says caution, this is the course where you listen. Bent grass greens and fairways throughout. The red-barn clubhouse is the on-site landmark — visible from holes 1, 9, 10, and 18.`,
  },
  'pebble-creek': {
    emoji: '🪨',
    headline: 'Two courses in one. Dense forest out, water-heavy in.',
    body: `Pebble Creek opened September 1, 1996 — one of the final designs by Hal C. Purdy, the prolific New Jersey architect who also designed Fox Hollow. The course has a distinct split personality: the front nine is carved from mature hardwood forest with narrow, tree-lined fairways. The back nine opens up — but water comes into play on seven of the nine holes, including the signature par-5 14th, which wraps around a 2.5-acre lake. Plays 6,276 yards from the blue tees to a par 71. Finishes on a par 3 on hole 18, which is unusual and divides opinion. A World Trade Center Memorial sits between the pro shop and the 1st tee — a distinctive sobering touch. No driving range. The clubhouse has a covered patio overlooking 18, plus a snack bar. For a proper meal, Huddy's Inn is a few doors up Route 537 at 206 — listed on the scorecard as "Our 19th Hole."`,
  },
  'hominy-hill': {
    emoji: '👑',
    headline: 'Robert Trent Jones built it for one man. Now it\'s yours.',
    body: `Hominy Hill has the best origin story on the Jersey Circuit. Henry Dickson Mercer Sr. — president of States Marine Corporation, Augusta National member — bought 411 acres of Colts Neck farmland in 1941 to raise prize-winning Guernsey cattle. He named the property "Hominy Hill Farm" after the Manhomoney Hills, the historic name for Colts Neck's small hills. Mercer's home club, Rumson Country Club, balked when he wanted to entertain his Japanese and Greek shipping contacts. So in 1963 he commissioned Robert Trent Jones Sr. to convert 180 acres into a championship course of his own — opened in 1965, with a blank check, 138 bunkers, tributaries of Mine Brook as water hazards, and a converted dairy barn as the clubhouse (designed by architect Derrick Kipp with informal and formal restaurants on two floors). Almost nobody played it — invitation-only, plus occasional charity events. In 1975 the family sold to Monmouth County, resisting higher developer offers. Plays 7,049 yards from the blue tees to a par 72 (rating 74.2, slope 131); championship tees stretch to 7,120. Hosted the 1983 U.S. Amateur Public Links and the 1995 U.S. Women's Amateur Public Links. Voted #1 NJ public course by New Jersey Monthly readers. Critical lightning note (per the official scorecard): no on-site lightning detection, no lightning shelters — golfers play at their own risk. ZAP exists for courses like this.`,
  },
  'oak-hill': {
    emoji: '🌳',
    headline: 'Private. Ridge-line. 50-mile views.',
    body: `Oak Hill Golf Club is the only private course on the Jersey Circuit — a 240-acre member-owned gem two miles north of Milford in the hills of Holland Township. Founded as the "Ridge Golf Club" in 1963, the course opened in 1964 with 9 holes designed by William F. Gordon (whose work also designed Green Knoll). A second 9 by Gordon followed in 1965, chosen over a competing bid from George Fazio for design continuity. Members hand-picked stones from the rough during construction — they're still called the "Rock Pickers." Plays 6,669 yards from the tips to a par 72 (rating 72.0, slope 136). Sits high enough to take in 50-mile views, with prevailing winds and exposed lightning risk on the upper holes — the nearest NJWxNet station is NJ53, the same one serving High Bridge Hills, and conditions here can differ sharply from the valley courses on the same day. Hole 5 is one of the most picturesque on the circuit: a short downhill par 3 over a stream and pond. The Grill Room and expansive deck overlook the course. Member-only — Patrick has that covered.`,
  },
};

// COURSE REGISTRY
// ============================================================
// ============================================================
// LOCATION SYSTEM -- Activity Planner
// ============================================================
// No static course registry. Locations are created dynamically
// from GPS one-tap or address autocomplete.
// A synthetic location object is produced that matches the
// shape the weather pipeline expects (same as a "course" object).
//
// Synthetic location shape:
// {
//   id: 'location_<timestamp>',
//   name: <human-readable label>,
//   lat: <number>,
//   lng: <number>,
//   elevation: null,          // unknown for arbitrary locations
//   terrain: 'moderate',      // default -- no topo data
//   soilType: 'standard',     // default
//   waterDensity: 'moderate', // will be overridden by OSM fetch
//   par: null,                // not applicable
//   town: '',
//   state: '',
//   zip: '',
//   microClimate: '',
// }

// ── NJWxNet station coordinates (WGS84) ──
// Used for distance-based station selection (replaces NJWXNET_COURSE_MAP).
// ECN-004: NJ53 and NJ76 have WMO-standard 10m sensors.
// NJ09 wind excluded (3m sensor, field-truth confirmed unreliable).
const NJWXNET_STATIONS = [
  { stid: 'NJ84', name: 'Franklin Twp-Somerset', lat: 40.53917, lng: -74.56611, windReliable: true },
  { stid: 'NJ76', name: 'Duke Farms-Hillsborough', lat: 40.54162, lng: -74.62594, windReliable: true },
  { stid: 'NJ53', name: 'Snyder Research Farm-Pittstown', lat: 40.56120, lng: -74.96100, windReliable: true },
  { stid: 'NJ09', name: 'Holmdel Park', lat: 40.37760, lng: -74.18090, windReliable: false },
];
const NJWXNET_STIDS = 'NJ84,NJ76,NJ53,NJ09';
const NJWXNET_DEFAULT_RADIUS_MI = 5; // default to NJWxNet if nearest station ≤ this distance

// Haversine distance in miles between two lat/lng points
function haversineMi(lat1, lng1, lat2, lng2) {
  const R = 3958.8; // Earth radius miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat/2)**2 +
            Math.cos(lat1 * Math.PI/180) * Math.cos(lat2 * Math.PI/180) * Math.sin(dLng/2)**2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
}

// Get nearest NJWxNet station + distance for a lat/lng
function getNearestNJWxNetStation(lat, lng) {
  let nearest = null, minDist = Infinity;
  for (const st of NJWXNET_STATIONS) {
    const d = haversineMi(lat, lng, st.lat, st.lng);
    if (d < minDist) { minDist = d; nearest = st; }
  }
  return { station: nearest, distanceMi: Math.round(minDist * 10) / 10 };
}

// Legacy NJWXNET_COURSE_MAP kept as empty object -- guards against any residual references
const NJWXNET_COURSE_MAP = {};
const _nwsGridCache = {};
async function fetchNWSGridForLocation(lat, lng) {
  const key = `${lat.toFixed(4)},${lng.toFixed(4)}`;
  if (_nwsGridCache[key]) return _nwsGridCache[key];
  try {
    const resp = await fetch(`https://api.weather.gov/points/${lat},${lng}`, {
      headers: { 'User-Agent': NWS_USER_AGENT, 'Accept': 'application/geo+json' }
    });
    if (!resp.ok) return null;
    const data = await resp.json();
    const props = data?.properties;
    if (!props?.gridId || props?.gridX == null || props?.gridY == null) return null;
    const result = { gridId: props.gridId, gridX: props.gridX, gridY: props.gridY };
    _nwsGridCache[key] = result;
    return result;
  } catch (e) {
    return null;
  }
}

// Legacy NWS_GRID_MAP stub -- guards against residual references
const NWS_GRID_MAP = {};

// ── Synthetic location factory ──
function makeLocation(lat, lng, name, extraFields = {}) {
  return {
    id: `loc_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
    name: name || `${lat.toFixed(4)}, ${lng.toFixed(4)}`,
    lat,
    lng,
    elevation: null,
    terrain: 'moderate',
    soilType: 'standard',
    waterDensity: 'moderate',
    par: null,
    town: extraFields.town || '',
    state: extraFields.state || '',
    zip: extraFields.zip || '',
    microClimate: '',
    entryMode: extraFields.entryMode || 'unknown',
    ...extraFields,
  };
}

const TOWNS = []; // Activity Planner: no static course list

// ============================================================
// PLAYER ROSTER & SCORECARD STORAGE
// ============================================================
// ---- Cloudinary config ----
const CLOUDINARY_CLOUD_NAME = 'dovzgljte';
const CLOUDINARY_UPLOAD_PRESET = 'zap_photos';
const CDN = 'https://res.cloudinary.com/dovzgljte/image/upload/';
const COURSE_ASSETS = {
  'oak-hill':         { scorecard: CDN+'Oak-Hill-Scorecard_d4feax.webp',           map: null, photos: [CDN+'oak_hill_fog_aerial.jpg', CDN+'oak_hill_5th_par3.jpg'] },
  'spooky-brook':     { scorecard: CDN+'SpookyBrook_fozfxf.png',                   map: CDN+'sbgc_map_zzlhda.jpg', photos: [CDN+'spooky_brook_entrance.jpg', CDN+'spooky_brook_17th_green.jpg', CDN+'spooky_brook_clubhouse.jpg'] },
  'green-knoll':      { scorecard: CDN+'GreenKnoll_1_cu6wok.png',                  map: CDN+'scorecard_1_qxkiyl.jpg', photos: [CDN+'green_knoll_5th_fairway.jpg', CDN+'green_knoll_6th_green.jpg', CDN+'green_knoll_17th_green.jpg'] },
  'warrenbrook':      { scorecard: CDN+'Warrenbrook_tq8dkf.png',                   map: CDN+'wbgc_map_xpwobm.jpg', photos: [CDN+'warrenbrook_clubhouse.jpg', CDN+'warrenbrook_17th_pond.jpg', CDN+'warrenbrook_stone_bridge.jpg'] },
  'quail-brook':      { scorecard: CDN+'correct-score-card-2_bslytg.jpg',          map: CDN+'course-layout_eh5nst.webp', photos: [] },
  'pebble-creek':     { scorecard: CDN+'pebble_creek_scorecard.png',              map: CDN+'pebble_creek_course_layout.png', photos: [CDN+'pebble_creek_cover.png', CDN+'pebble_creek_front9_maps.png', CDN+'pebble_creek_back9_maps.png'] },
  'hominy-hill':      { scorecard: CDN+'hominy_hill_scorecard.jpg',                map: null, photos: [CDN+'hominy_hill_course.jpg', CDN+'hominy_hill_clubhouse.jpg'] },
  'fox-hollow':       { scorecard: CDN+'fox_hollow_scorecard.jpg',                 map: null, photos: [CDN+'fox-hollow-gc-nj-usa-7_b5wf4n.jpg'] },
  'high-bridge-hills':{ scorecard: CDN+'high_bridge_scorecard.jpg',                map: CDN+'Screenshot_2026-03-25_042936_uizvjg.jpg', photos: [CDN+'high_bridge_aerial.jpg', CDN+'high_bridge_hole8_gorge.jpg', CDN+'high-bridge-hills-gc-nj-usa-1_qtk7tu.jpg'] },
  'architects':       { scorecard: CDN+'architects_scorecard.jpg',                 map: null, photos: [CDN+'architects_misty_morning.jpg', CDN+'architects_13th_mackenzie.jpg', CDN+'the-architects-gc-nj-usa-8_zwvnsw.jpg'] },
  'neshanic-valley':  { scorecard: CDN+'neshanic_meadow_scorecard.png', map: CDN+'neshanic_meadow_layout.png', photos: [CDN+'neshanic-meadow-lake-3_pdcycf.jpg', CDN+'neshanic-meadow-lake-5_bagvqw.jpg', CDN+'neshanic-meadow-lake-10_2_jxisdo.jpg', CDN+'hole-5_mkrptc.webp'], scorecards: [{label:'Meadow',src:CDN+'neshanic_meadow_scorecard.png'},{label:'Lake',src:CDN+'neshanic_lake_scorecard.png'},{label:'Ridge',src:CDN+'neshanic_ridge_scorecard.png'}], maps: [{label:'Meadow',src:CDN+'neshanic_meadow_layout.png'},{label:'Lake',src:CDN+'neshanic_lake_layout.png'},{label:'Ridge',src:CDN+'neshanic_ridge_layout.png'}] },
};

const PLAYERS = [
  { id: 'john',    name: 'John',    emoji: '🔭' },
  { id: 'tony',    name: 'Tony',    emoji: '😏' },
  { id: 'patrick', name: 'Patrick', emoji: '☘️' },
  { id: 'matt',    name: 'Matt',    emoji: '⚖️' },
  { id: 'gary',    name: 'Gary',    emoji: '🎙️' },
  { id: 'tommy',   name: 'Tommy',   emoji: '🦉' },
  { id: 'jeff',    name: 'Jeff',    emoji: '🏆' },
  { id: 'toms',    name: 'Tom S.',  emoji: '🌡️' },
];
// John manages this list — add/remove players here

const SCORECARD_URL = 'https://script.google.com/macros/s/AKfycbxX95MQ8tYmQ_bVqnTeUHX7pbxEqryR5VwgZ8dFPbpxARbSCQ2uSFXLNJ9OdANlW146cw/exec';
// Unified system health logging - fire and forget
// ============================================================
// API-3: SYSTEM HEALTH LOGGING
// Logs failures + special events only — never successes.
// Events: failure, p2_activated, p1_recovered, rate_limit,
//         cascade_fail, storm_detected, sensor_anomaly,
//         nj09_excluded, webhook_failed, app_open
// Call counts: per source per device per day in localStorage.
// ============================================================

// Call counter — per source per day, resets at midnight
const ZAP_CALLS_PREFIX = 'zap_calls_';
const ZAP_FAVORITES_KEY = 'zap_favorites';   // Location Memory: favorites list
const ZAP_RECENTS_KEY   = 'zap_recents';     // Location Memory: recents list
const ZAP_PIN_KEY       = 'zap_pin';         // Location Memory: user PIN (Phase 2 recovery)
const _today = () => new Date().toISOString().slice(0, 10); // YYYY-MM-DD

const incrementCallCount = (source, crs, stationId = null) => {
  try {
    const key = `${ZAP_CALLS_PREFIX}${source.toLowerCase().replace(/\s+/g, '_')}_${_today()}`;
    const n = parseInt(localStorage.getItem(key) || '0', 10);
    localStorage.setItem(key, String(n + 1));
  } catch (e) {}
  // DATA-HEALTH-1: fire-and-forget POST to Apps Script for aggregate tracking
  try {
    fetch(SCORECARD_URL, {
      method: 'POST', mode: 'no-cors',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        type: 'api_count',
        source,
        courseId: crs?.id || '',
        courseName: crs?.name || '',
        deviceDate: _today(),
        stationId: stationId || '',
      }),
    }).catch(() => {});
  } catch (e) {}
};

const getCallCount = (source) => {
  try {
    const key = `${ZAP_CALLS_PREFIX}${source.toLowerCase().replace(/\s+/g, '_')}_${_today()}`;
    return parseInt(localStorage.getItem(key) || '0', 10);
  } catch (e) { return 0; }
};

// Rolling avg response time — last 5 calls per source in localStorage
const ZAP_RT_PREFIX = 'zap_rt_';
const recordResponseTime = (source, ms) => {
  try {
    const key = `${ZAP_RT_PREFIX}${source.toLowerCase().replace(/\s+/g, '_')}`;
    const arr = JSON.parse(localStorage.getItem(key) || '[]');
    arr.push(ms);
    if (arr.length > 5) arr.shift();
    localStorage.setItem(key, JSON.stringify(arr));
  } catch (e) {}
};

const getAvgResponseTime = (source) => {
  try {
    const key = `${ZAP_RT_PREFIX}${source.toLowerCase().replace(/\s+/g, '_')}`;
    const arr = JSON.parse(localStorage.getItem(key) || '[]');
    if (!arr.length) return null;
    return Math.round(arr.reduce((s, v) => s + v, 0) / arr.length);
  } catch (e) { return null; }
};

// P2 active state — track which sources are currently on P2
const _p2Active = {};

// S226: Silent P1 retry tracker — set when any forecast variable falls to P2.
// fetchAll checks this after setWeatherData and schedules a background reload
// to attempt P1 recovery without disrupting the displayed data.
const _forecastP2 = {};

// DATA-HEALTH-1: Aggregate API counts cache — fetched from Apps Script
let _apiCountsCache = {};
const API_LIMITS = { weatherapi: 1000000, pirateweather: 20000 };

const fetchApiCounts = async () => {
  try {
    const r = await fetch(`${SCORECARD_URL}?action=getApiCounts&date=${_today()}`);
    if (!r.ok) return;
    const d = await r.json();
    if (d && typeof d === 'object') _apiCountsCache = d;
  } catch (e) {}
};

const logSystemHealth = (eventType, course, details = {}) => {
  try {
    fetch(SCORECARD_URL, {
      method: 'POST', mode: 'no-cors',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        type: 'system_health',
        eventType,
        timestamp: new Date().toISOString(),
        course: course?.name || course || 'Unknown',
        courseId: course?.id || '',
        ...details,
      }),
    }).catch(() => {});
  } catch (e) {}
};

// Response time logger — sends to dedicated Response Times sheet tab
const logResponseTime = (source, callType, course, responseMs, trigger = 'unknown', cached = false, extras = {}) => {
  try {
    fetch(SCORECARD_URL, {
      method: 'POST', mode: 'no-cors',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        type: 'response_time',
        source,
        callType,
        course: course?.name || course || '',
        courseId: course?.id || course || '',
        responseMs,
        trigger,
        cached,
        ...extras,
      }),
    }).catch(() => {});
  } catch (e) {}
};

// Timed wrapper — returns { result, ms } for response time tracking
const withTiming = async (promise) => {
  const t0 = Date.now();
  const result = await promise;
  return { result, ms: Date.now() - t0 };
};


function getLocalRounds() {
  try { return JSON.parse(localStorage.getItem('zap_rounds') || '[]'); } catch { return []; }
}
function getRounds(sharedRounds) {
  // If shared rounds loaded from Sheet, use those. Fall back to localStorage.
  if (sharedRounds && sharedRounds.length > 0) return sharedRounds;
  return getLocalRounds();
}

// Sort key for "most recent" — handles ISO date + 12h/24h teeTime.
// sharedRounds from Sheet arrives in arbitrary row order, so naive [length-1]
// returns whichever row Sheet put last — NOT the most recent round.
function _roundSortKey(r) {
  const d = String(r?.date || '').slice(0, 10);
  const t = String(r?.teeTime || r?.startTime || '').trim();
  let tt = t;
  const m = t.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (m) {
    let h = parseInt(m[1]); const mm = m[2]; const pm = m[3].toUpperCase() === 'PM';
    if (pm && h !== 12) h += 12;
    if (!pm && h === 12) h = 0;
    tt = String(h).padStart(2, '0') + ':' + mm;
  }
  return `${d}T${tt || '00:00'}`;
}
// Returns rounds sorted newest-first. Does NOT mutate input.
function sortRoundsNewestFirst(rounds) {
  return [...(rounds || [])].sort((a, b) => _roundSortKey(b).localeCompare(_roundSortKey(a)));
}
// Returns the most recent round, optionally excluding a given id.
function mostRecentRound(rounds, excludeId) {
  const list = (rounds || []).filter(r => !excludeId || r.id !== excludeId);
  if (list.length === 0) return null;
  return sortRoundsNewestFirst(list)[0];
}

function saveRound(round) {
  try {
    const rounds = getLocalRounds();
    if (rounds.some(r => r.id === round.id)) return; // dedup — never save same round twice
    rounds.push(round);
    localStorage.setItem('zap_rounds', JSON.stringify(rounds));
  } catch {}
}

function updateRoundSyncStatus(roundId, syncStatus) {
  try {
    const rounds = getLocalRounds();
    const idx = rounds.findIndex(r => r.id === roundId);
    if (idx >= 0) {
      rounds[idx] = { ...rounds[idx], syncStatus };
      localStorage.setItem('zap_rounds', JSON.stringify(rounds));
    }
  } catch {}
}



function getRoundDiff(r) {
  // Returns total - par differential. Lower = better.
  const parsArray = Array.isArray(r.pars) ? r.pars
    : (typeof r.pars === 'string' ? r.pars.split(',').map(Number) : []);
  const par = parsArray.length > 0 ? parsArray.reduce((s, p) => s + (Number(p) || 0), 0) : null;
  return par != null ? (Number(r.total) || 0) - par : null;
}
function getCourseRecord(courseId, sharedRounds) {
  const rounds = getRounds(sharedRounds).filter(r => r.courseId === courseId && r.total != null);
  if (!rounds.length) return null;
  // Per-course: same par throughout so raw total sort is fine (spec line 6959/6966)
  return rounds.reduce((best, r) => r.total < best.total ? r : best);
}
function getAllTimeRecord(sharedRounds) {
  const rounds = getRounds(sharedRounds).filter(r => r.total != null);
  if (!rounds.length) return null;
  return rounds.reduce((best, r) => getRoundDiff(r) < getRoundDiff(best) ? r : best);
}

function getWorstRecord(sharedRounds) {
  const rounds = getRounds(sharedRounds).filter(r => r.total != null);
  if (!rounds.length) return null;
  return rounds.reduce((worst, r) => getRoundDiff(r) > getRoundDiff(worst) ? r : worst);
}

// ============================================================
// UTILITY FUNCTIONS
// ============================================================
function degreesToCardinal(deg) {
  if (deg == null) return '';
  const dirs = ['N','NNE','NE','ENE','E','ESE','SE','SSE','S','SSW','SW','WSW','W','WNW','NW','NNW'];
  return dirs[Math.round(deg / 22.5) % 16];
}

function formatTime12h(timeStr) {
  if (!timeStr) return 'N/A';
  try {
    // Handle Unix epoch integer (VC currentConditions returns epoch)
    if (typeof timeStr === 'number' || (typeof timeStr === 'string' && /^\d{9,10}$/.test(timeStr))) {
      const d = new Date(parseInt(timeStr) * 1000);
      return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    }
    // Handle ISO datetime string e.g. "2026-04-12T20:14" or "2026-04-12T20:14:33"
    if (typeof timeStr === 'string' && timeStr.includes('T')) {
      const d = new Date(timeStr);
      if (!isNaN(d.getTime())) {
        return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
      }
    }
    // Handle HH:MM:SS or HH:MM string
    const parts = timeStr.split(':');
    const hour = parseInt(parts[0]);
    const m = parts[1] || '00';
    const ampm = hour >= 12 ? 'PM' : 'AM';
    return `${hour === 0 ? 12 : hour > 12 ? hour - 12 : hour}:${m} ${ampm}`;
  } catch {
    return String(timeStr);
  }
}

// Short time format for compact display — "7:48" no AM/PM
function formatTimeShort(timeStr) {
  if (!timeStr) return '';
  try {
    if (typeof timeStr === 'number' || (typeof timeStr === 'string' && /^\d{9,10}$/.test(timeStr))) {
      const d = new Date(parseInt(timeStr) * 1000);
      return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: false }).replace(/^0/, '');
    }
    if (typeof timeStr === 'string' && timeStr.includes('T')) {
      const d = new Date(timeStr);
      if (!isNaN(d.getTime())) {
        const h = d.getHours();
        const m = String(d.getMinutes()).padStart(2, '0');
        const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
        return `${h12}:${m}`;
      }
    }
    const parts = timeStr.split(':');
    const hour = parseInt(parts[0]);
    const m = parts[1] || '00';
    const h12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${h12}:${m}`;
  } catch {
    return '';
  }
}

function calcDryingTrend(rain, temp, humidity) {
  if (rain > 1) return 'POOR';
  if (rain > 0.5) return 'FAIR';
  if (temp > 85 && humidity < 40) return 'FAST (Solar baking active)';
  if (rain > 0.1) return 'FAIR';
  return 'GOOD';
}

function celsiusToF(c) { return (9 * c) / 5 + 32; }

// ============================================================
// VISUAL CROSSING WEATHER API (Priority 1)
// ============================================================
const vcCache = {};

async function fetchVisualCrossing(course) {
  const cacheKey = `vc_${course.lat}_${course.lng}`;
  const cached = vcCache[cacheKey];
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) return cached.data;

  try {
    // S226: Direct VC call — proxy removed. Same params as proxy was passing.
    // Key kept out of git via REACT_APP_VC_API_KEY build-time env var (Netlify dashboard).
    if (!VC_API_KEY) throw new Error('VC_API_KEY not configured');
    const vcUrl = `https://weather.visualcrossing.com/VisualCrossingWebServices/rest/services/timeline/${course.lat},${course.lng}?unitGroup=us&include=current,hours,days,stations,alerts&key=${VC_API_KEY}&contentType=json`;
    const resp = await fetch(vcUrl);
    if (!resp.ok) throw new Error(`Visual Crossing API error: ${resp.status} ${resp.statusText}`);

    const data = await resp.json();
    if (data.error) throw new Error(`Visual Crossing error: ${data.error}`);

    // Parse station metadata
    const stationMap = {};
    if (data.stations) {
      Object.entries(data.stations).forEach(([id, s]) => {
        stationMap[id] = {
          id,
          name: s.name || id,
          lat: s.latitude,
          lng: s.longitude,
          distanceMi: s.distance ? Math.round((s.distance / 1000) * 0.621371 * 10) / 10 : null,
          quality: s.quality,
          contribution: s.contribution,
          useCount: s.useCount,
        };
      });
    }

    const current = data.currentConditions || {};
    const activeStationIds = current.stations || [];
    const allMapped = activeStationIds.map((id) => stationMap[id]).filter(Boolean);
    const qualityStations = allMapped.filter(s => s.quality == null || s.quality > 0);
    const activeStations = (qualityStations.length > 0 ? qualityStations : allMapped)
      .sort((a, b) => (a.distanceMi || 99) - (b.distanceMi || 99));
    const closestStation = activeStations[0];

    const days = data.days || [];
    const today = days[0] || {};
    const tomorrow = days[1] || {};
    // Store all daily summaries for calendar fallback (days 4-15 have no hourly)
    const vcDailyAll = days.map(d => ({
      date: d.datetime,
      tempMax: d.tempmax != null ? Math.round(d.tempmax) : null, tempMin: d.tempmin != null ? Math.round(d.tempmin) : null,
      windSpeed: Math.round(d.windspeed || 0),
      precipChance: d.precipprob || 0,
      precip: d.precip || 0,
      conditions: d.conditions || '',
      sunset: d.sunset ? formatTime12h(d.sunset) : null,
    }));

    // Build hourly array
    const hourly = [];
    days.forEach((day) => {
      (day.hours || []).forEach((h) => {
        hourly.push({
          time: new Date(`${day.datetime}T${h.datetime}`),
          temp: h.temp,
          feelsLike: h.feelslike != null ? Math.round(h.feelslike) : null,
          humidity: h.humidity != null ? Math.round(h.humidity) : null,
          windSpeed: h.windspeed,
          windSpeedNum: Math.round(h.windspeed || 0),
          windDir: h.winddir,
          windGust: h.windgust != null ? Math.round(h.windgust) : null,
          precipChance: h.precipprob || 0,
          precip: h.precip || 0,
          snow: h.snow || 0,
          uvIndex: h.uvindex || 0,
          cloudCover: h.cloudcover,
          visibility: h.visibility,
          conditions: h.conditions,
          isDaytime: h.datetime >= (today.sunrise || '06:00:00') && h.datetime < (today.sunset || '19:00:00'),
          source: h.source,
          stations: h.stations || [],
        });
      });
    });

    // Rolling 24h rain — sum hourly precip for last 24 hours (not calendar-day total)
    const now24 = new Date();
    const ago24 = new Date(now24.getTime() - 24 * 60 * 60 * 1000);
    const past24Rain = Math.round(100 * hourly.filter(h => h.time >= ago24 && h.time <= now24).reduce((sum, h) => sum + (h.precip || 0), 0)) / 100;

    const result = {
      success: true,
      stale: !current.temp && !current.humidity,
      vcDailyAll,
      source: 'Visual Crossing',
      sourceDetail: (() => {
        if (!closestStation) return 'Visual Crossing composite';
        // Clean up station name: strip FW/CW prefix IDs from personal weather stations
        let sName = closestStation.name || closestStation.id;
        sName = sName.replace(/^[A-Z]{1,2}\d{3,5}\s+/, '').replace(/\s+(US|USA)$/i, '').trim();
        if (!sName || sName === closestStation.id) sName = closestStation.id;
        return `Visual Crossing composite · nearest ${sName} ${closestStation.distanceMi} mi`;
      })(),
      dataType: current.source === 'obs' ? 'Physical Sensor' : current.source === 'fcst' ? 'Forecast Model' : current.source || 'Unknown',
      stations: stationMap,
      activeStations,
      closestStation,
      current: {
        temp: current.temp,
        feelsLike: current.feelslike,
        humidity: current.humidity,
        dewPoint: current.dew,
        windSpeed: `${Math.round(current.windspeed || 0)} mph`,
        windSpeedNum: Math.round(current.windspeed || 0),
        windDir: degreesToCardinal(current.winddir),
        windDirDeg: current.winddir,
        windGust: current.windgust,
        precip: current.precip || 0,
        precipChance: current.precipprob || 0,
        snow: current.snow || 0,
        snowDepth: current.snowdepth || 0,
        uvIndex: current.uvindex || 0,
        visibility: current.visibility,
        cloudCover: current.cloudcover,
        pressure: current.pressure,
        conditions: current.conditions,
        shortForecast: current.conditions,
        sunrise: current.sunrise,
        sunset: current.sunset,

        source: current.source,
      },
      today: {
        high: today.tempmax != null ? Math.round(today.tempmax) : null,
        low: today.tempmin != null ? Math.round(today.tempmin) : null,
        maxWind: Math.round(today.windspeed || 0),
        avgHumidity: Math.round(today.humidity || 0),
        precip: today.precip || 0,
        precipChance: today.precipprob || 0,
        snow: today.snow || 0,
        uvIndex: today.uvindex || 0,
        sunrise: today.sunrise,
        sunset: today.sunset,
        conditions: today.conditions,
        description: today.description,
        dayForecast: { shortForecast: today.conditions, detailedForecast: today.description },
      },
      tomorrow: {
        high: tomorrow.tempmax != null ? Math.round(tomorrow.tempmax) : null,
        low: tomorrow.tempmin != null ? Math.round(tomorrow.tempmin) : null,
        maxWind: Math.round(tomorrow.windspeed || 0),
        avgHumidity: Math.round(tomorrow.humidity || 0),
        precip: tomorrow.precip || 0,
        precipChance: tomorrow.precipprob || 0,
        snow: tomorrow.snow || 0,
        uvIndex: tomorrow.uvindex || 0,
        sunrise: tomorrow.sunrise,
        sunset: tomorrow.sunset,
        conditions: tomorrow.conditions,
        description: tomorrow.description,
        dayForecast: { shortForecast: tomorrow.conditions, detailedForecast: tomorrow.description },
      },
      ground: {
        past24Rain,
        snowpack: current.snowdepth || 0,
        dryingTrend: calcDryingTrend(past24Rain, current.temp, current.humidity),
      },
      sunsetToday: formatTime12h(today.sunset || current.sunset),
      sunsetTomorrow: formatTime12h(tomorrow.sunset),
      sunriseToday: formatTime12h(today.sunrise || current.sunrise),
      hourly,
      alerts: data.alerts || [],
      grid: { id: 'VC', x: course.lat, y: course.lng },
      updateTime: current.datetimeEpoch ? new Date(current.datetimeEpoch * 1000).toISOString() : new Date().toISOString(),
    };

    vcCache[cacheKey] = { data: result, timestamp: Date.now() };
    incrementCallCount('visual_crossing', course);
    return result;
  } catch (err) {
    console.error('Visual Crossing fetch failed:', err);
    return { success: false, error: err.message, source: 'Visual Crossing', stale: true };
  }
}

// ============================================================
// AWC METAR API (Priority 2 — Aviation Weather Center)
// Endpoint: aviationweather.gov/api/data/metar?ids={ICAO}&format=json
// Free, no API key. Covers NJ region with KSMQ, KFWN, KTTN, KCDW, KBLM.
// Used when Visual Crossing fails or returns stale data.
// Station selection: find nearest ICAO to course GPS from a curated NJ list.
// ============================================================
const awcCache = {};
const AWC_METAR_TTL = 900000; // 15 min

// NJ-area ICAO stations with GPS for distance lookup
const AWC_NJ_STATIONS = [
  { id: 'KSMQ', name: 'Somerset Airport', lat: 40.6258, lng: -74.6699 },
  { id: 'KTTN', name: 'Trenton-Mercer', lat: 40.2767, lng: -74.8135 },
  { id: 'KBLM', name: 'Belmar/Monmouth', lat: 40.1869, lng: -74.1249 },
  { id: 'KCDW', name: 'Caldwell/Essex', lat: 40.8752, lng: -74.2814 },
  { id: 'KFWN', name: 'Sussex Airport', lat: 41.2003, lng: -74.6234 },
  { id: 'KMMU', name: 'Morristown Muni', lat: 40.7994, lng: -74.4149 },
  { id: 'KNEL', name: 'Lakehurst/McGuire', lat: 40.0328, lng: -74.3534 },
  { id: 'K12N', name: 'Aeroflex/Andover', lat: 41.0996, lng: -74.7372 },
];

function haversineMilesLocal(lat1, lng1, lat2, lng2) {
  const R = 3958.8;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat/2)**2 + Math.cos(lat1 * Math.PI/180) * Math.cos(lat2 * Math.PI/180) * Math.sin(dLng/2)**2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
}

function findNearestAWCStation(lat, lng) {
  let best = null, bestDist = Infinity;
  AWC_NJ_STATIONS.forEach(s => {
    const d = haversineMilesLocal(lat, lng, s.lat, s.lng);
    if (d < bestDist) { bestDist = d; best = { ...s, distanceMi: Math.round(d * 10) / 10 }; }
  });
  return best;
}

// Parse METAR wind string like "24012KT" or "VRB05KT"
function parseMetarWind(windStr) {
  if (!windStr) return { dir: null, speed: 0 };
  const m = windStr.match(/^(\d{3}|VRB)(\d{2,3})(?:G\d{2,3})?KT$/);
  if (!m) return { dir: null, speed: 0 };
  return { dir: m[1] === 'VRB' ? null : parseInt(m[1]), speed: parseInt(m[2]) };
}

async function fetchAWCMETAR(course) {
  const station = findNearestAWCStation(course.lat, course.lng);
  if (!station) return { success: false, error: 'No AWC station found', source: 'AWC METAR' };

  const cacheKey = `awc_${station.id}`;
  const cached = awcCache[cacheKey];
  if (cached && Date.now() - cached.ts < AWC_METAR_TTL) return cached.data;

  try {
    // AWC API — CORS is open, no key required
    const url = `https://aviationweather.gov/api/data/metar?ids=${station.id}&format=json&hours=2`;
    const resp = await fetch(url);
    if (!resp.ok) throw new Error(`AWC API ${resp.status}`);
    const json = await resp.json();

    // json is an array; take the most recent observation
    const obs = Array.isArray(json) ? json[0] : null;
    if (!obs) throw new Error('No AWC METAR observation returned');

    // Detect frozen/malfunctioning sensor (maintenance symbol "$" in raw)
    const raw = obs.rawOb || '';
    if (raw.includes('$')) {
      console.warn(`AWC METAR ${station.id}: maintenance flag — cascading to NWS HRRR`);
      logSystemHealth('sensor_maintenance', course, { station: station.id, flag: '$' });
      return { success: false, error: 'AWC sensor maintenance flag', source: 'AWC METAR' };
    }

    const wind = parseMetarWind(obs.wdir !== undefined ? `${String(obs.wdir).padStart(3,'0')}${String(obs.wspd).padStart(2,'0')}KT` : null);
    const tempC = obs.temp !== undefined ? obs.temp : null;
    const tempF = tempC !== null ? Math.round(tempC * 9/5 + 32) : null;
    const dewC = obs.dewp !== undefined ? obs.dewp : null;
    const dewF = dewC !== null ? Math.round(dewC * 9/5 + 32) : null;

    // Estimate humidity from temp/dew (Magnus formula)
    let humidity = null;
    if (tempF !== null && dewF !== null) {
      const es = 6.112 * Math.exp(17.67 * dewC / (dewC + 243.5));
      const e = 6.112 * Math.exp(17.67 * tempC / (tempC + 243.5));
      humidity = Math.round(Math.min(100, (e > 0 ? (es / e) * 100 : 50)));
    }

    const visibMi = obs.visib !== undefined ? parseFloat(obs.visib) : null;
    const altimHpa = obs.altim !== undefined ? Math.round(obs.altim * 33.8639) / 1000 : null;

    // Sky/cloud — worstCoverage
    const skyCover = obs.clouds ? obs.clouds.map(c => c.cover).join(',') : '';
    const hasBrokenOrOvercast = /BKN|OVC/.test(skyCover);
    const conditions = obs.wxString || (hasBrokenOrOvercast ? 'Overcast' : 'Clear');
    const precipChance = /RA|SN|DZ|SH|TS/.test(conditions) ? 90 : hasBrokenOrOvercast ? 30 : 5;

    // Build minimal hourly array (METAR gives single obs — replicate current for next 2h)
    const now = new Date();
    const hourly = [0, 1, 2].map(offset => ({
      time: new Date(now.getTime() + offset * 3600000),
      temp: tempF,
      humidity,
      windSpeed: `${Math.round((obs.wspd || 0) * 1.15078)} mph`,
      windSpeedNum: Math.round((obs.wspd || 0) * 1.15078),
      windDir: wind.dir !== null ? degreesToCardinal(wind.dir) : 'Variable',
      windGust: obs.wgst ? Math.round(obs.wgst * 1.15078) : null,
      precipChance,
      conditions,
      isDaytime: now.getHours() >= 6 && now.getHours() < 20,
    }));

    const result = {
      success: true,
      stale: false,
      source: 'AWC METAR',
      sourceDetail: `${station.name} · ${station.distanceMi} mi`,
      dataType: 'Physical Sensor',
      current: {
        temp: tempF,
        feelsLike: tempF !== null ? calcFeelsLike(tempF, humidity || 50, obs.wspd || 0) : null,
        humidity,
        dewPoint: dewF,
        windSpeed: `${Math.round((obs.wspd || 0) * 1.15078)} mph`,
        windSpeedNum: Math.round((obs.wspd || 0) * 1.15078),
        windDir: wind.dir !== null ? degreesToCardinal(wind.dir) : 'Variable',
        windDirDeg: wind.dir,
        windGust: obs.wgst ? Math.round(obs.wgst * 1.15078) : null,
        precipChance,
        snow: 0,
        snowDepth: obs.snowDepth || 0,
        uvIndex: 0, // METAR doesn't provide UV
        visibility: visibMi,
        pressure: altimHpa,
        conditions,
        shortForecast: conditions,
      },
      today: { high: tempF, low: tempF, maxWind: obs.wspd || 0, avgHumidity: humidity || 0, precipChance, dayForecast: { shortForecast: conditions } },
      tomorrow: { high: null, low: null, maxWind: null, avgHumidity: null, precipChance: null, dayForecast: { shortForecast: 'Check back later — airport sensor shows current conditions only' } },
      ground: { past24Rain: 0, snowpack: 0, dryingTrend: 'N/A — airport sensor, no recent rain history' },
      sunsetToday: 'N/A',
      sunsetTomorrow: 'N/A',
      hourly,
      grid: { id: station.id, x: station.lat, y: station.lng },
      updateTime: obs.reportTime ? new Date(obs.reportTime).toISOString() : new Date().toISOString(),
    };

    awcCache[cacheKey] = { data: result, ts: Date.now() };
    incrementCallCount('awc_metar', course);
    return result;
  } catch (err) {
    console.error('AWC METAR fetch failed:', err);
    return { success: false, error: err.message, source: 'AWC METAR' };
  }
}

// ============================================================
// OPEN-METEO ET (Evapotranspiration — Solar Baking Calibration)
// Endpoint: open-meteo.com/v1/forecast?daily=et0_fao_evapotranspiration
// Free, no API key. Used to enhance solar baking offset accuracy.
// ET > 5 mm/day = significant evaporative drying. Replaces temp+humidity heuristic.
// ============================================================
const etCache = {};
const ET_CACHE_TTL = 3600000; // 1 hour — ET is daily, doesn't need frequent refresh

async function fetchET(lat, lng) {
  const key = `et_${lat}_${lng}`;
  const cached = etCache[key];
  if (cached && Date.now() - cached.ts < ET_CACHE_TTL) return cached.data;

  try {
    const url = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lng}` +
      `&daily=et0_fao_evapotranspiration&forecast_days=3&timezone=America%2FNew_York`;
    const resp = await fetch(url);
    if (!resp.ok) throw new Error(`Open-Meteo ET ${resp.status}`);
    const json = await resp.json();

    const times = json.daily?.time || [];
    const etVals = json.daily?.et0_fao_evapotranspiration || [];

    const today = new Date().toLocaleDateString('en-CA');
    const todayIdx = times.indexOf(today);
    const todayET = todayIdx >= 0 ? etVals[todayIdx] : null;
    const tomorrowET = todayIdx >= 0 && todayIdx + 1 < etVals.length ? etVals[todayIdx + 1] : null;

    // ET thresholds:
    // < 2 mm/day = minimal drying (winter/cloudy)
    // 2–5 mm/day = moderate drying
    // > 5 mm/day = strong drying (solar baking active)
    // > 8 mm/day = extreme (hot summer day)
    const result = {
      success: true,
      todayET,       // mm/day
      tomorrowET,
      solarBakingActive: todayET !== null && todayET > 5,
      solarBakingStrong: todayET !== null && todayET > 8,
      label: todayET === null ? 'N/A' : todayET > 8 ? 'Extreme drying' : todayET > 5 ? 'Strong drying (solar baking)' : todayET > 2 ? 'Moderate drying' : 'Minimal drying',
    };

    etCache[key] = { data: result, ts: Date.now() };
    return result;
  } catch (err) {
    console.warn('Open-Meteo ET fetch failed:', err);
    return { success: false, todayET: null, solarBakingActive: false };
  }
}

// ============================================================
// OPEN-METEO FORECAST API (Priority 4 fallback — replaces NWS Grid)
// Free, no API key. WMO-standard. Validated 3/16/26: humidity accurate.
// NWS Grid was disqualified: returned 41% humidity when actual was 90%.
// Endpoint: api.open-meteo.com/v1/forecast
// ============================================================
const omForecastCache = {};

// WMO weather code → plain-English description (Open-Meteo subset)
// Used by fetchOpenMeteo and fetchNJWxNetTimeseries (historical archive conditions)
function wmoDescription(code) {
  switch(code) {
    case 0:  return 'Clear sky';
    case 1:  return 'Mainly clear';
    case 2:  return 'Partly cloudy';
    case 3:  return 'Overcast';
    case 45: return 'Fog';
    case 48: return 'Freezing fog';
    case 51: return 'Light drizzle';
    case 53: return 'Drizzle';
    case 55: return 'Heavy drizzle';
    case 56: return 'Light freezing drizzle';
    case 57: return 'Freezing drizzle';
    case 61: return 'Light rain';
    case 63: return 'Rain';
    case 65: return 'Heavy rain';
    case 66: return 'Light freezing rain';
    case 67: return 'Freezing rain';
    case 71: return 'Light snow';
    case 73: return 'Snow';
    case 75: return 'Heavy snow';
    case 77: return 'Snow grains';
    case 80: return 'Light rain shower';
    case 81: return 'Rain shower';
    case 82: return 'Heavy rain shower';
    case 85: return 'Snow shower';
    case 86: return 'Heavy snow shower';
    case 95: return 'Thunderstorm';
    case 96: return 'Thunderstorm with hail';
    case 99: return 'Thunderstorm with heavy hail';
    default:
      logSystemHealth('unknown_wmo_code', null, { code });
      return code <= 3 ? 'Clear sky' : code <= 48 ? 'Fog' : code <= 67 ? 'Rain' : code <= 77 ? 'Snow' : code <= 82 ? 'Rain shower' : code <= 86 ? 'Snow shower' : 'Thunderstorm';
  }
}

async function fetchOpenMeteo(course) {
  const cacheKey = `om_${course.lat}_${course.lng}`;
  const cached = omForecastCache[cacheKey];
  if (cached && Date.now() - cached.ts < CACHE_TTL) return cached.data;

  try {
    const { lat, lng } = course;
    const url =
      `https://api.open-meteo.com/v1/forecast` +
      `?latitude=${lat}&longitude=${lng}` +
      `&current=temperature_2m,relative_humidity_2m,apparent_temperature,precipitation,rain,showers,snowfall,weather_code,cloud_cover,wind_speed_10m,wind_direction_10m,wind_gusts_10m,is_day` +
      `&hourly=temperature_2m,relative_humidity_2m,apparent_temperature,precipitation_probability,precipitation,rain,snowfall,snow_depth,weather_code,wind_speed_10m,wind_direction_10m,wind_gusts_10m,uv_index,is_day,soil_moisture_0_to_1cm` +
      `&daily=temperature_2m_max,temperature_2m_min,apparent_temperature_max,apparent_temperature_min,sunrise,sunset,precipitation_sum,rain_sum,snowfall_sum,precipitation_probability_max,wind_speed_10m_max,wind_gusts_10m_max,uv_index_max` +
      `&temperature_unit=fahrenheit&wind_speed_unit=mph&precipitation_unit=inch` +
      `&forecast_days=7&timezone=America%2FNew_York`;

    const resp = await fetch(url);
    if (!resp.ok) throw new Error(`Open-Meteo forecast ${resp.status}`);
    const json = await resp.json();

    const cur = json.current || {};
    const daily = json.daily || {};
    const hourly = json.hourly || {};

    // wmoDescription is at module scope — shared with fetchNJWxNetTimeseries

    // Build hourly array — full 7 days (S50: removed 48h cap, needed for calendar days 3-7)
    const nowMs = Date.now();
    const omHourly = [];
    const times = hourly.time || [];
    for (let i = 0; i < times.length; i++) {
      const t = new Date(times[i]);
      const diffMs = t - nowMs;
      if (diffMs < -3600000) continue;           // skip past hours (allow 1hr back)
      // no 48h cap — full 7 days needed for calendar
      omHourly.push({
        time: t,
        temp: Math.round(hourly.temperature_2m?.[i] ?? 0),
        feelsLike: calcFeelsLike(Math.round(hourly.temperature_2m?.[i] ?? 0), Math.round(hourly.relative_humidity_2m?.[i] ?? 50), Math.round(hourly.wind_speed_10m?.[i] ?? 0)),
        humidity: Math.round(hourly.relative_humidity_2m?.[i] ?? 0),
        windSpeedNum: Math.round(hourly.wind_speed_10m?.[i] ?? 0),
        windSpeed: `${Math.round(hourly.wind_speed_10m?.[i] ?? 0)} mph`,
        windDir: degreesToCardinal(hourly.wind_direction_10m?.[i] ?? 0),
        windGust: Math.round(hourly.wind_gusts_10m?.[i] ?? 0),
        precipChance: hourly.precipitation_probability?.[i] ?? 0,
        precip: hourly.precipitation?.[i] ?? 0,
        snowfall: hourly.snowfall?.[i] ?? 0,
        snowDepth: hourly.snow_depth?.[i] ?? 0,
        uvIndex: hourly.uv_index?.[i] ?? 0,
        isDaytime: (hourly.is_day?.[i] ?? 1) === 1,
        wmoCode: hourly.weather_code?.[i] ?? 0,
        shortForecast: wmoDescription(hourly.weather_code?.[i] ?? 0),
        soilMoisture: hourly.soil_moisture_0_to_1cm?.[i] ?? null,
      });
    }

    // Current conditions
    const currentHour = omHourly[0] || {};
    const current = {
      temp: Math.round(cur.temperature_2m ?? currentHour.temp ?? 0),
      feelsLike: Math.round(cur.apparent_temperature ?? currentHour.feelsLike ?? 0),
      humidity: Math.round(cur.relative_humidity_2m ?? currentHour.humidity ?? 0),
      windSpeedNum: Math.round(cur.wind_speed_10m ?? currentHour.windSpeedNum ?? 0),
      windSpeed: `${Math.round(cur.wind_speed_10m ?? 0)} mph`,
      windDir: degreesToCardinal(cur.wind_direction_10m ?? 0),
      windGust: Math.round(cur.wind_gusts_10m ?? 0),
      precip: cur.precipitation ?? 0,
      precipChance: currentHour.precipChance ?? 0,
      snowfall: cur.snowfall ?? 0,
      isDaytime: (cur.is_day ?? 1) === 1,
      wmoCode: cur.weather_code ?? 0,
      shortForecast: wmoDescription(cur.weather_code ?? 0),
      conditions: wmoDescription(cur.weather_code ?? 0),
    };

    // Daily helpers
    const getDay = (idx) => ({
      high: Math.round(daily.temperature_2m_max?.[idx] ?? 0),
      low: Math.round(daily.temperature_2m_min?.[idx] ?? 0),
      feelsHigh: Math.round(daily.apparent_temperature_max?.[idx] ?? 0),
      feelsLow: Math.round(daily.apparent_temperature_min?.[idx] ?? 0),
      maxWind: Math.round(daily.wind_speed_10m_max?.[idx] ?? 0),
      maxGust: Math.round(daily.wind_gusts_10m_max?.[idx] ?? 0),
      avgHumidity: null,   // OM daily doesn't give avg humidity — hourly is better
      precip: daily.precipitation_sum?.[idx] ?? 0,
      precipChance: daily.precipitation_probability_max?.[idx] ?? 0,
      snow: daily.snowfall_sum?.[idx] ?? 0,
      uvIndex: daily.uv_index_max?.[idx] ?? 0,
      sunrise: daily.sunrise?.[idx] ?? null,
      sunset: daily.sunset?.[idx] ?? null,
      conditions: wmoDescription(0),   // OM daily has no single weather_code
      dayForecast: { shortForecast: `High ${Math.round(daily.temperature_2m_max?.[idx] ?? 0)}°`, detailedForecast: '' },
    });

    const todayDay = getDay(0);
    const tomorrowDay = getDay(1);

    // avg humidity from today's hourly
    const todayStr = new Date().toLocaleDateString('en-CA');
    const todayHours = omHourly.filter(h => new Date(h.time).toLocaleDateString('en-CA') === todayStr);
    if (todayHours.length) {
      todayDay.avgHumidity = Math.round(todayHours.reduce((s, h) => s + h.humidity, 0) / todayHours.length);
    }
    const tomorrowStr = (() => { const d = new Date(); d.setDate(d.getDate() + 1); return d.toLocaleDateString('en-CA'); })();
    const tomorrowHours = omHourly.filter(h => new Date(h.time).toLocaleDateString('en-CA') === tomorrowStr);
    if (tomorrowHours.length) {
      tomorrowDay.avgHumidity = Math.round(tomorrowHours.reduce((s, h) => s + h.humidity, 0) / tomorrowHours.length);
    }

    // Past 24hr precip — use hourly precipitation sum
    const past24Rain = Math.round(
      omHourly.filter(h => nowMs - h.time > 0 && nowMs - h.time <= 86400000)
              .reduce((s, h) => s + h.precip, 0) * 100
    ) / 100;

    const snowpack = current.snowfall > 0 ? current.snowfall : 0;
    const dryingTrend = calcDryingTrend(past24Rain, current.temp, current.humidity);

    // Current soil moisture — most recent non-null value from hourly (0-1cm depth, m³/m³ → %)
    const omSoilMoistureRaw = omHourly[0]?.soilMoisture ?? null; // S267 fix: use current hour, not last forecast hour
    const omSoilMoisturePct = omSoilMoistureRaw != null ? Math.round(omSoilMoistureRaw * 100 * 10) / 10 : null;

    const result = {
      success: true,
      stale: false,
      source: 'Open-Meteo',
      sourceDetail: 'Open-Meteo WMO Forecast',
      dataType: 'Forecast Model',
      updateTime: new Date().toISOString(),
      current,
      today: todayDay,
      tomorrow: tomorrowDay,
      ground: { past24Rain, snowpack, dryingTrend },
      soilMoisturePct: omSoilMoisturePct,  // VWC % at 0-1cm depth — feeds cart status
      sunsetToday: formatTime12h(todayDay.sunset),
      sunsetTomorrow: formatTime12h(tomorrowDay.sunset),
      sunriseToday: formatTime12h(todayDay.sunrise),
      hourly: omHourly,
      vcDailyAll: (daily.time || []).map((dateStr, idx) => ({
        date: dateStr,
        tempMax: Math.round(daily.temperature_2m_max?.[idx] ?? 0),
        tempMin: Math.round(daily.temperature_2m_min?.[idx] ?? 0),
        windSpeed: Math.round(daily.wind_speed_10m_max?.[idx] ?? 0),
        precipChance: daily.precipitation_probability_max?.[idx] ?? 0,
        precip: daily.precipitation_sum?.[idx] ?? 0,
        conditions: wmoDescription(daily.weather_code?.[idx] ?? 0),
        sunset: daily.sunset?.[idx] ? formatTime12h(daily.sunset[idx]) : null,
      })),
      alerts: [],
      grid: { id: 'OM', x: lat, y: lng },
    };

    omForecastCache[cacheKey] = { data: result, ts: Date.now() };
    incrementCallCount('open_meteo', course);
    return result;
  } catch (err) {
    console.error('Open-Meteo forecast fetch failed:', err);
    return { success: false, error: err.message, source: 'Open-Meteo', stale: true };
  }
}


// ============================================================
// NWS HOURLY FORECAST (S174 — new fetcher built for cascade rewrite)
// Endpoint: api.weather.gov/gridpoints/{office}/{x},{y}/forecast/hourly
// Returns: ~156 hours (6.5 days) of clean hourly forecast data.
// Free, unlimited, no API key. User-Agent header required.
// PROVIDES: temp, wind, conditions, precipChance — humidity comes from VC.
// DOES NOT PROVIDE: UV (no UV anywhere in NWS API), gust.
// NOTE: Reference §1.4 — NWS humidity is buggy (~50% off). Reads humidity field
//       but downstream cascade should NOT use NWS humidity. We pass it through
//       only to populate the row; cascade-merge picks humidity from VC.
// ============================================================
const nwsHourlyCache = {};

async function fetchNWSHourlyForecast(course) {
  // Activity Planner: dynamic grid lookup (replaces hardcoded NWS_GRID_MAP)
  const grid = await fetchNWSGridForLocation(course.lat, course.lng);
  if (!grid) {
    logSystemHealth('forecast_source_fail', course, { source: 'nws', error: 'NWS points lookup failed', position: 'P1' });
    return { success: false, error: 'NWS grid lookup failed for this location', source: 'NWS' };
  }
  try {
    const url = `https://api.weather.gov/gridpoints/${grid.gridId}/${grid.gridX},${grid.gridY}/forecast/hourly`;
    const t0 = Date.now();
    const resp = await fetch(url, { headers: { 'User-Agent': NWS_USER_AGENT, 'Accept': 'application/geo+json' } });
    const _nwsMs = Date.now() - t0;
    recordResponseTime('nws', _nwsMs);
    if (!resp.ok) throw new Error(`NWS ${resp.status}`);
    const data = await resp.json();
    const props = data?.properties;
    if (!props?.periods) throw new Error('NWS: no periods');
    const hourly = props.periods.slice(0, 156).map(p => {
      const t = new Date(p.startTime);
      const windMatch = (p.windSpeed || '').match(/(\d+)/);
      const windSpeedNum = windMatch ? parseInt(windMatch[1]) : 0;
      return {
        time: t.toISOString(),
        temp: p.temperature,
        feelsLike: p.temperature,
        humidity: p.relativeHumidity?.value ?? 50,
        windSpeedNum,
        windSpeed: p.windSpeed || '0 mph',
        windDir: p.windDirection || '',
        windGust: 0,
        precipChance: p.probabilityOfPrecipitation?.value ?? 0,
        precip: 0,
        uvIndex: 0,
        conditions: p.shortForecast || '',
        shortForecast: p.shortForecast || '',
      };
    });
    const today = hourly[0] || {};
    return {
      success: true, source: 'NWS', sourceDetail: `NWS Hourly · ${grid.gridId} ${grid.gridX},${grid.gridY}`,
      current: { ...today },
      hourly,
      today: { high: Math.max(...hourly.slice(0,24).map(h=>h.temp||0)), low: Math.min(...hourly.slice(0,24).map(h=>h.temp||999)),
        maxWind: Math.max(...hourly.slice(0,24).map(h=>h.windSpeedNum||0)), avgHumidity: 50, precipChance: today.precipChance || 0,
        dayForecast: { shortForecast: today.shortForecast || '' } },
      tomorrow: { high: Math.max(...hourly.slice(24,48).map(h=>h.temp||0)), low: Math.min(...hourly.slice(24,48).map(h=>h.temp||999)),
        maxWind: Math.max(...hourly.slice(24,48).map(h=>h.windSpeedNum||0)), avgHumidity: 50, precipChance: 0,
        dayForecast: { shortForecast: '' } },
      ground: { past24Rain: 0, snowpack: 0 },
    };
  } catch (err) {
    return { success: false, error: err.message, source: 'NWS' };
  }
}

// ============================================================
// WEATHERAPI FORECAST (S174 — new fetcher built for cascade rewrite)
// Endpoint: api.weatherapi.com/v1/forecast.json?days=3
// Free tier returns 3 days hourly. Used for WIND P2, GUST P3, UV P2,
// PRECIP 48h+ P2, CONDITIONS P3 fallback paths. NOT precip0_12 (binary spikes — replaced S195).
// 1M calls/month — effectively unlimited at alpha scale.
// ============================================================
const wapiForecastCache = {};
const mbCache = {}; // Meteoblue forecast cache — restored S206

async function fetchWeatherAPIForecast(course) {
  const cacheKey = `wapif_${course.lat}_${course.lng}`;
  const cached = wapiForecastCache[cacheKey];
  if (cached && Date.now() - cached.ts < CACHE_TTL) return cached.data;

  try {
    const url = `https://api.weatherapi.com/v1/forecast.json` +
      `?key=${WAPI_KEY}&q=${course.lat},${course.lng}&days=3&aqi=no&alerts=no`;
    const resp = await fetch(url);
    if (!resp.ok) throw new Error(`WeatherAPI forecast HTTP ${resp.status}`);
    const data = await resp.json();
    const days = data.forecast?.forecastday || [];
    if (!days.length) throw new Error('WeatherAPI returned no forecast days');

    const hourly = [];
    days.forEach(day => {
      (day.hour || []).forEach(h => {
        const dt = new Date(h.time_epoch * 1000);
        hourly.push({
          time: dt,
          temp: h.temp_f != null ? Math.round(h.temp_f) : null,
          feelsLike: h.feelslike_f != null ? Math.round(h.feelslike_f) : null,
          humidity: h.humidity != null ? Math.round(h.humidity) : null,
          windSpeedNum: h.wind_mph != null ? Math.round(h.wind_mph) : 0,
          windSpeed: `${h.wind_mph != null ? Math.round(h.wind_mph) : 0} mph`,
          windDir: h.wind_dir || '',
          windGust: h.gust_mph != null ? Math.round(h.gust_mph) : 0,
          precipChance: h.chance_of_rain || 0,
          precip: h.precip_in || 0,
          uvIndex: h.uv || 0,
          isDaytime: (h.is_day ?? 1) === 1,
          conditions: h.condition?.text || '',
          shortForecast: h.condition?.text || '',
          source: 'WeatherAPI',
        });
      });
    });

    if (!hourly.length) throw new Error('WeatherAPI returned no usable hours');

    const nowMs = Date.now();
    const current = hourly.reduce((best, h) => Math.abs(h.time - nowMs) < Math.abs(best.time - nowMs) ? h : best, hourly[0]) || {};
    const todayHours = hourly.filter(h => h.time.toDateString() === new Date().toDateString());
    const tmrDate = new Date(); tmrDate.setDate(tmrDate.getDate() + 1);
    const tmrHours = hourly.filter(h => h.time.toDateString() === tmrDate.toDateString());

    const dayStats = (hrs) => ({
      high: hrs.length ? Math.max(...hrs.map(h => h.temp)) : null,
      low:  hrs.length ? Math.min(...hrs.map(h => h.temp)) : null,
      maxWind: hrs.length ? Math.max(...hrs.map(h => h.windSpeedNum)) : null,
      maxGust: hrs.length ? Math.max(...hrs.map(h => h.windGust)) : null,
      precipChance: hrs.length ? Math.max(...hrs.map(h => h.precipChance || 0)) : null,
      precip: hrs.reduce((s,h) => s + (h.precip||0), 0),
      conditions: hrs[Math.floor(hrs.length/2)]?.conditions || '',
      dayForecast: { shortForecast: hrs[Math.floor(hrs.length/2)]?.shortForecast || '' },
    });

    const result = {
      success: true, stale: false,
      source: 'WeatherAPI', sourceDetail: 'WeatherAPI 3-day forecast',
      dataType: 'Forecast Model',
      current: { ...current },
      today: dayStats(todayHours),
      tomorrow: dayStats(tmrHours),
      hourly,
      ground: { past24Rain: 0, snowpack: 0, dryingTrend: 'Unknown' },
      vcDailyAll: [],
      sunsetToday: '', sunsetTomorrow: '', sunriseToday: '',
      updateTime: new Date().toISOString(),
    };

    wapiForecastCache[cacheKey] = { data: result, ts: Date.now() };
    incrementCallCount('weatherapi', course);
    return result;
  } catch (err) {
    console.error('WeatherAPI forecast fetch failed:', err);
    return { success: false, error: err.message, source: 'WeatherAPI' };
  }
}
// ============================================================
// FORECAST CASCADE (S174 — sequential per-variable rewrite)
//
// Replaces S167's all-or-nothing fetchWeatherCascade.
//
// Per S172 cascade table (status doc § Forecast Cascade):
//   TEMP:           NWS  → VC      → PirateWeather
//   WIND:           VC   → WAPIfcst → NWS
//   GUST:           VC   → PirateWeather → WAPIfcst
//   FEELS:          calcFeelsLike(local) → VC → PirateWeather
//   HUMIDITY:       VC   → PirateWeather  (NWS humidity is broken — Reference §1.4)
//   PRECIP 0-12h:   Meteoblue → VC → NWS  (MB restored S206 — ECMWF true hourly; PirateWeather was P1 S195–S205)
//   PRECIP 12-48h:  Meteoblue → VC → Open-Meteo  (MB restored S206 — ECMWF true hourly; PirateWeather was P1 S195–S205)
//   PRECIP 48h+:    Open-Meteo → WAPIfcst → (no P3)
//   CONDITIONS:     NWS → Open-Meteo → WAPIfcst
//   UV:             Open-Meteo → WAPIfcst → (S174 override: NWS has no UV)
//
// SEQUENTIAL: each source fetched ONLY when needed. Sources reused across
// variables (e.g. VC is P1 for wind/gust/precip12-48h — single VC call serves all).
// On the happy path (everything works), 4 forecast calls fire:
//   NWS, VC, WeatherAPI forecast, Open-Meteo.
// PirateWeather only fires when something else failed.
// ============================================================

// S274: Sanitize rain labels when precip < 30% — prevents VC returning "Rain"
// at low probability. Only downgrades pure rain-intensity labels.
// Never touches: thunder, lightning, freezing, snow, sleet, ice, fog, mist, haze.
const sanitizeCondition = (cond, precipChance) => {
  if ((precipChance ?? 0) >= 30) return cond;
  const c = (cond || '').toLowerCase();
  if (/thunder|lightning|tstorm|freezing|snow|blizzard|sleet|ice|fog|mist|haze/.test(c)) return cond;
  if (/rain|drizzle|shower/.test(c)) return 'Chance of Showers';
  return cond;
};

async function fetchForecastCascade(crs) {
  // Lazy fetch cache — each source called at most once per cascade
  const sources = {};
  const fetchSource = async (name, fn, timeoutMs, label) => {
    if (sources[name] !== undefined) return sources[name];
    const { result, ms } = await withTiming(withTimeout(fn(crs), timeoutMs, label));
    sources[name] = result;
    recordResponseTime(name, ms);
    logResponseTime(name, 'forecast', crs, ms, 'page_load', false, { success: !!result?.success }); // S216 F7
    return result;
  };

  // P1 fetch helpers — only called when a P1 variable needs them
  const getNWS = () => fetchSource('nws', fetchNWSHourlyForecast, 8000, 'NWS Hourly');
  const getVC  = () => fetchSource('visual_crossing', fetchVisualCrossing, 10000, 'Visual Crossing');
  const getWAPI = () => fetchSource('weatherapi', fetchWeatherAPIForecast, 8000, 'WeatherAPI Forecast');
  const getOM  = () => fetchSource('open_meteo', fetchOpenMeteo, 10000, 'Open-Meteo');
  const getPW  = () => fetchSource('pirateweather', fetchPirateWeather, 8000, 'PirateWeather');
  const getMB  = () => fetchSource('meteoblue', fetchMeteoblue, 10000, 'Meteoblue');

  // Per-variable cascade resolution. Each entry returns the source object
  // that won (and the source name) so the merger can pull the right field.
  // If P1 succeeds → P2/P3 never fetched.
  const resolveVar = async (varName, cascade) => {
    let p1Error = null;
    for (const step of cascade) {
      const src = await step.fetch();
      if (src?.success) {
        if (step.name !== cascade[0].name) {
          logSystemHealth('p2_activated', crs, { variable: varName, p1: cascade[0].name, p2: step.name, p1Error });
          _forecastP2[crs.id] = true; // S226: flag for silent retry
        }
        return { source: src, name: step.name };
      }
      // S216 F7: log each individual source failure
      logSystemHealth('forecast_source_fail', crs, {
        variable: varName,
        source: step.name,
        error: src?.error || 'unknown',
        position: step.name === cascade[0].name ? 'P1' : 'P2/P3',
      });
      if (step.name === cascade[0].name) {
        p1Error = src?.error || 'unknown';
      }
    }
    // S216 F7: log when entire cascade fails for a variable
    logSystemHealth('forecast_cascade_fail', crs, { variable: varName });
    return null;
  };

  // ── Cascade definitions per S172 lock + S174 UV correction + S206 MB precip restore ──
  const cascades = {
    temp:           [{ name: 'VC', fetch: getVC }, { name: 'PirateWeather', fetch: getPW }, { name: 'NWS', fetch: getNWS }],  // S237: NWS demoted to P3 — temp consistent across all sources, cold-start cost not justified
    wind:           [{ name: 'VC', fetch: getVC }, { name: 'WeatherAPI', fetch: getWAPI }, { name: 'NWS', fetch: getNWS }],
    gust:           [{ name: 'VC', fetch: getVC }, { name: 'PirateWeather', fetch: getPW }, { name: 'WeatherAPI', fetch: getWAPI }],
    humidity:       [{ name: 'VC', fetch: getVC }, { name: 'PirateWeather', fetch: getPW }],
    precip0_12:     [{ name: 'Meteoblue', fetch: getMB }, { name: 'VC', fetch: getVC }, { name: 'NWS', fetch: getNWS }],  // S206: MB P1 (ECMWF true hourly), VC P2, NWS P3
    precip12_48:    [{ name: 'Meteoblue', fetch: getMB }, { name: 'VC', fetch: getVC }, { name: 'OpenMeteo', fetch: getOM }],  // S206: MB P1 (ECMWF true hourly), VC P2, OM P3
    precip48plus:   [{ name: 'OpenMeteo', fetch: getOM }, { name: 'WeatherAPI', fetch: getWAPI }],
    conditions:     [{ name: 'VC', fetch: getVC }, { name: 'OpenMeteo', fetch: getOM }, { name: 'WeatherAPI', fetch: getWAPI }],
    uv:             [{ name: 'OpenMeteo', fetch: getOM }, { name: 'WeatherAPI', fetch: getWAPI }],
  };

  // Pre-fire all P1 sources in parallel before resolving variables.
  // Without this, the for-await loop below fires each source serially — up to 30s+
  // on a cold load. WeatherAPI not pre-fired — P2 wind/gust only, not a P1 source.
  await Promise.allSettled([getVC(), getOM(), getMB()]); // S237: NWS removed from pre-fire — demoted to P3 temp fallback only, fires lazily if VC+PW both fail

  // Resolve all variables. Order matters only for logging clarity — actual fetches
  // are deduplicated by fetchSource (cache hit after parallel pre-fire above).
  const resolved = {};
  for (const [varName, cascade] of Object.entries(cascades)) {
    resolved[varName] = await resolveVar(varName, cascade);
  }

  // ── Merge the resolved sources into a single forecast payload ──
  // Backbone (skeleton with current + today + tomorrow + hourly + ground + sunrise/sunset)
  // comes from VC if available, else NWS, else OM, else WAPI.
  // NOTE: PirateWeather is NEVER used as backbone — it is fallback-only per design.
  // S200 FIX: VC restored as P1 backbone. VC provides full 48h hourly array including
  // past hours of today. NWS as P1 only returns hours from current period forward,
  // causing drilldown to show only remaining hours of the day.
  const backbone =
    (sources.visual_crossing?.success && sources.visual_crossing) ||
    (sources.nws?.success && sources.nws) ||
    (sources.open_meteo?.success && sources.open_meteo) ||
    (sources.weatherapi?.success && sources.weatherapi) ||
    null;

  if (!backbone) {
    logSystemHealth('cascade_fail', crs, { source: 'ALL', error: 'All forecast sources failed', severity: 'CRITICAL' });
    return { success: false, error: 'All forecast sources failed', source: 'cascade' };
  }

  // Per-hour merge: walk backbone.hourly. For each hour, replace fields
  // with values from the cascade-winning source for that variable.
  // Match by timestamp (ms epoch) — sources may have different array lengths.
  const indexHourly = (src) => {
    if (!src?.hourly) return new Map();
    const m = new Map();
    src.hourly.forEach(h => m.set(new Date(h.time).getTime(), h));
    return m;
  };

  const winnerFor = (varName) => resolved[varName]?.source || null;
  const winnerName = (varName) => resolved[varName]?.name || null;

  const tempIdx     = indexHourly(winnerFor('temp'));
  const windIdx     = indexHourly(winnerFor('wind'));
  const gustIdx     = indexHourly(winnerFor('gust'));
  const humidityIdx = indexHourly(winnerFor('humidity'));
  const condIdx     = indexHourly(winnerFor('conditions'));
  const uvIdx       = indexHourly(winnerFor('uv'));

  // Precip — three time windows. Cutoffs are relative to "now" of this call.
  const nowMs       = Date.now();
  const cutoff12h   = nowMs + 12 * 3600 * 1000;
  const cutoff48h   = nowMs + 48 * 3600 * 1000;
  const precip0_12  = indexHourly(winnerFor('precip0_12'));
  const precip12_48 = indexHourly(winnerFor('precip12_48'));
  const precip48p   = indexHourly(winnerFor('precip48plus'));

  const mergedHourly = backbone.hourly.map(bh => {
    const t = new Date(bh.time).getTime();
    const tempH     = tempIdx.get(t);
    const windH     = windIdx.get(t);
    const gustH     = gustIdx.get(t);
    const humH      = humidityIdx.get(t);
    const condH     = condIdx.get(t);
    const uvH       = uvIdx.get(t);
    // Pick precip source by horizon
    let precipH = null;
    if (t <= cutoff12h)        precipH = precip0_12.get(t);
    else if (t <= cutoff48h)   precipH = precip12_48.get(t);
    else                       precipH = precip48p.get(t);

    const tempVal     = tempH?.temp     ?? bh.temp;
    const humVal      = humH?.humidity  ?? bh.humidity ?? 50;
    const windVal     = windH?.windSpeedNum ?? bh.windSpeedNum ?? 0;
    const gustVal     = gustH?.windGust ?? bh.windGust ?? 0;
    const feelsVal    = (tempVal != null) ? calcFeelsLike(tempVal, humVal, windVal) : bh.feelsLike;
    // S206 FIX: conditions follow precip winner ONLY when precip >= 30% — prevents Meteoblue sky label
    // (often wrong on clear days) from overriding VC conditions when MB wins precip at 0% prob.
    // Below 30%: conditions cascade winner (VC) owns sky label.
    // At/above 30%: precip winner owns it — matters what kind of precip is coming.
    // S274: sanitizeCondition prevents rain labels (VC) showing below 30% precip threshold.
    const rawCondVal  = (precipH && (precipH.precipChance ?? 0) >= 30)
                          ? (precipH.conditions ?? condH?.conditions ?? bh.conditions ?? '')
                          : (condH?.conditions  ?? bh.conditions   ?? '');
    const condVal     = sanitizeCondition(rawCondVal, precipH?.precipChance ?? bh.precipChance ?? 0);
    const shortVal    = precipH?.shortForecast ?? condH?.shortForecast ?? bh.shortForecast ?? condVal;
    const uvVal       = uvH?.uvIndex ?? bh.uvIndex ?? 0;
    const pChanceVal  = precipH?.precipChance ?? bh.precipChance ?? 0;
    const pAmtVal     = precipH?.precip ?? bh.precip ?? 0;

    return {
      ...bh,
      temp: tempVal,
      feelsLike: feelsVal,
      humidity: humVal,
      windSpeedNum: windVal,
      windSpeed: `${windVal} mph`,
      windDir: windH?.windDir ?? bh.windDir,
      windGust: gustVal,
      precipChance: pChanceVal,
      precip: pAmtVal,
      uvIndex: uvVal,
      conditions: condVal,
      shortForecast: shortVal,
    };
  });

  // Current conditions — apply same per-variable cascade to the "now" point.
  // Use the closest hour from each winning source's hourly array.
  const closestFromIdx = (idx) => {
    let best = null, bestDist = Infinity;
    for (const [t, h] of idx) {
      const d = Math.abs(t - nowMs);
      if (d < bestDist) { bestDist = d; best = h; }
    }
    return best;
  };
  const curTempH = closestFromIdx(tempIdx);
  const curWindH = closestFromIdx(windIdx);
  const curGustH = closestFromIdx(gustIdx);
  const curHumH  = closestFromIdx(humidityIdx);
  const curCondH = closestFromIdx(condIdx);
  const curUvH   = closestFromIdx(uvIdx);
  const curPrecH = closestFromIdx(precip0_12);  // current = 0-12h cascade

  const baseCurrent = backbone.current || {};
  const curTemp  = curTempH?.temp   ?? baseCurrent.temp;
  const curHum   = curHumH?.humidity ?? baseCurrent.humidity ?? 50;
  const curWind  = curWindH?.windSpeedNum ?? baseCurrent.windSpeedNum ?? 0;
  const curGust  = curGustH?.windGust ?? baseCurrent.windGust ?? 0;
  const curFeels = (curTemp != null) ? calcFeelsLike(curTemp, curHum, curWind) : baseCurrent.feelsLike;

  const mergedCurrent = {
    ...baseCurrent,
    temp: curTemp,
    feelsLike: curFeels,
    humidity: curHum,
    windSpeedNum: curWind,
    windSpeed: `${curWind} mph`,
    windDir: curWindH?.windDir ?? baseCurrent.windDir,
    windGust: curGust,
    precipChance: curPrecH?.precipChance ?? baseCurrent.precipChance ?? 0,
    precip: curPrecH?.precip ?? baseCurrent.precip ?? 0,
    uvIndex: curUvH?.uvIndex ?? baseCurrent.uvIndex ?? 0,
    conditions: curCondH?.conditions ?? baseCurrent.conditions ?? '',
    shortForecast: curCondH?.shortForecast ?? baseCurrent.shortForecast ?? '',
  };

  // Build the merged result. Keep VC's daily/sunrise/sunset/ground/vcDailyAll
  // when VC was the backbone, else fall through to whatever the backbone provides.
  const merged = {
    ...backbone,
    success: true,
    source: 'S174 Cascade',
    sourceDetail: `Cascade: T=${winnerName('temp')}, W=${winnerName('wind')}, P=${winnerName('precip0_12')}/${winnerName('precip12_48')}/${winnerName('precip48plus')}, C=${winnerName('conditions')}, UV=${winnerName('uv')}`,
    current: mergedCurrent,
    hourly: mergedHourly,
    _sources: {
      temp:         winnerName('temp'),
      wind:         winnerName('wind'),
      gust:         winnerName('gust'),
      humidity:     winnerName('humidity'),
      precip0_12:   winnerName('precip0_12'),
      precip12_48:  winnerName('precip12_48'),
      precip48plus: winnerName('precip48plus'),
      conditions:   winnerName('conditions'),
      uv:           winnerName('uv'),
      backbone:     backbone === sources.visual_crossing ? 'VC' :
                    backbone === sources.nws ? 'NWS' :
                    backbone === sources.open_meteo ? 'OpenMeteo' : 'WeatherAPI',
    },
    // Per-source success flags for modal dot rendering
    _sourceStatus: {
      nws:           sources.nws?.success === true,
      visualCrossing: sources.visual_crossing?.success === true,
      weatherapi:    sources.weatherapi?.success === true,
      pirateweather: sources.pirateweather?.success === true,
      openMeteo:     sources.open_meteo?.success === true,
      meteoblue:     sources.meteoblue?.success === true,
    },
    // Pass through full Open-Meteo result so fetchAll can extend forecast to 7 days
    // (OM is unique in returning 168h — others cap at 48-72h). Avoids double-fetching OM.
    _omFull: sources.open_meteo?.success ? sources.open_meteo : null,
  };

  return merged;
}

// ============================================================
// METEOBLUE — P1 for precip 0-12h and 12-48h (restored S206)
// Benchmark winner: #1 combined precip 0-12h and 12-48h (Apr 2026).
// Uses basic-1h package only (8,000 credits/call — NOT wind package).
// Wind stays on VC cascade. New key H2nyzakt47srTN5U expires 2027-04-30.
// decodeMb: precipProb drives conditions ≥30%; pictocode drives sky <30%.
// ============================================================
async function fetchMeteoblue(course) {
  const cacheKey = `mb_${course.lat}_${course.lng}`;
  const cached = mbCache[cacheKey];
  if (cached && Date.now() - cached.ts < CACHE_TTL) return cached.data;
  try {
    const url = `https://my.meteoblue.com/packages/basic-1h_basic-day` +
      `?lat=${course.lat}&lon=${course.lng}&apikey=${MB_API_KEY}` +
      `&temperature=F&windspeed=mph&precipitationamount=inch&format=json`;
    const resp = await fetch(url);
    if (!resp.ok) throw new Error(`Meteoblue HTTP ${resp.status}`);
    const data = await resp.json();
    if (data.error) throw new Error(`Meteoblue: ${data.error_message || 'API error'}`);

    const h1 = data.data_1h || {};
    const times = h1.time || [];

    const decodeMb = (picto, precipProb, temp) => {
      const pp = precipProb ?? 0;
      // Rain/Snow derived from precipProb — never from pictocode alone
      if (pp >= 70) return 'Rain';
      if (pp >= 50) return 'Rain Likely';
      if (pp >= 30) return 'Chance of Rain';
      // Below 30% — sky state from hourly pictocode (1-35). Source: meteoblue.com/en/weather/docs/pictogramoverview
      if (!picto) return 'Clear';
      // Thunderstorm codes
      if (picto >= 10 && picto <= 12) return 'Thunderstorm';
      if (picto >= 27 && picto <= 30) return 'Thunderstorm';
      // Snow codes — temp-gated: above 35F treat as Rain (melts before ground)
      const isAboveFreezing = temp != null && temp > 35;
      if (picto === 24) return isAboveFreezing ? 'Rain' : 'Snow';
      if (picto === 26) return isAboveFreezing ? 'Rain' : 'Snow';
      if (picto === 32) return isAboveFreezing ? 'Rain' : 'Snow';
      if (picto === 34) return isAboveFreezing ? 'Rain' : 'Snow';
      if (picto === 35) return isAboveFreezing ? 'Rain' : 'Snow';
      // Rain codes
      if (picto === 23 || picto === 25) return 'Rain';
      if (picto === 31) return 'Rain';
      if (picto === 33) return 'Rain';
      // Fog
      if (picto >= 16 && picto <= 18) return 'Fog';
      // Clear (1-6) / Hazy (13-15) / Partly cloudy (7-9) / Overcast (19-22)
      if (picto >= 1  && picto <= 6)  return 'Clear';
      if (picto >= 13 && picto <= 15) return 'Cloudy';
      if (picto >= 7  && picto <= 9)  return 'Partly Cloudy';
      if (picto >= 19 && picto <= 22) return 'Overcast';
      return 'Cloudy';
    };

    const hourly = times.map((t, i) => {
      const dt = new Date(t);
      const picto = h1.pictocode?.[i];
      const temp = h1.temperature?.[i];
      const wind = h1.windspeed?.[i];
      const precip = h1.precipitation?.[i] ?? 0;
      const precipProb = h1.precipitation_probability?.[i] ?? null;
      const cond = decodeMb(picto, precipProb, temp);
      return {
        time: dt,
        temp: temp != null ? Math.round(temp) : null,
        feelsLike: temp != null ? calcFeelsLike(Math.round(temp), h1.relativehumidity?.[i] != null ? Math.round(h1.relativehumidity[i]) : 50, wind != null ? Math.round(wind) : 0) : null,
        humidity: h1.relativehumidity?.[i] != null ? Math.round(h1.relativehumidity[i]) : null,
        windSpeedNum: wind != null ? Math.round(wind) : 0,
        windSpeed: `${wind != null ? Math.round(wind) : 0} mph`,
        windDir: degreesToCardinal(h1.winddirection?.[i] || 0),
        windGust: 0, // basic-1h has no gust — gust stays on VC cascade
        precipChance: precipProb,
        precip: Math.round((precip || 0) * 10000) / 10000,
        uvIndex: h1.uvindex?.[i] || 0,
        isDaytime: dt.getHours() >= 6 && dt.getHours() < 20,
        conditions: cond,
        shortForecast: cond,
        source: 'Meteoblue',
      };
    }).filter(h => h.temp != null);

    if (!hourly.length) throw new Error('Meteoblue returned no usable hours');

    const nowMs = Date.now();
    const current = hourly.reduce((best, h) => Math.abs(h.time - nowMs) < Math.abs(best.time - nowMs) ? h : best, hourly[0]) || {};
    const todayHours = hourly.filter(h => h.time.toDateString() === new Date().toDateString());
    const tmrDate = new Date(); tmrDate.setDate(tmrDate.getDate() + 1);
    const tmrHours = hourly.filter(h => h.time.toDateString() === tmrDate.toDateString());

    const dayStats = (hrs) => ({
      high: hrs.length ? Math.max(...hrs.map(h => h.temp)) : null,
      low:  hrs.length ? Math.min(...hrs.map(h => h.temp)) : null,
      maxWind: hrs.length ? Math.max(...hrs.map(h => h.windSpeedNum)) : null,
      precipChance: hrs.length ? Math.max(...hrs.map(h => h.precipChance || 0)) : null,
      precip: hrs.reduce((s, h) => s + (h.precip || 0), 0),
      conditions: hrs[Math.floor(hrs.length / 2)]?.conditions || '',
      dayForecast: { shortForecast: hrs[Math.floor(hrs.length / 2)]?.shortForecast || '' },
    });

    const result = {
      success: true, stale: false,
      source: 'Meteoblue', sourceDetail: 'Meteoblue ECMWF 1h forecast',
      dataType: 'Forecast Model',
      current: { ...current, windSpeed: current.windSpeed || '0 mph', conditions: current.conditions || '' },
      today: dayStats(todayHours),
      tomorrow: dayStats(tmrHours),
      hourly,
      ground: { past24Rain: 0, snowpack: 0, dryingTrend: 'Unknown' },
      vcDailyAll: [],
      sunsetToday: '', sunsetTomorrow: '', sunriseToday: '',
      updateTime: new Date().toISOString(),
    };
    mbCache[cacheKey] = { data: result, ts: Date.now() };
    incrementCallCount('meteoblue', course);
    return result;
  } catch (err) {
    console.error('Meteoblue fetch failed:', err);
    return { success: false, error: err.message, source: 'Meteoblue' };
  }
}

// ============================================================
// PIRATEWEATHER — backup source (not in main cascade after S74)
// Was P2 for wind/gust, now GribStream is P2 per S60 benchmark
// Kept for potential future use or fallback scenarios
// Dark Sky compatible API. Returns 168 hours (7 days).
// ============================================================
const PIRATE_KEY = 'QjanbYhlQ7UY4uG8ckaDSKyqvgjDCOEB';
const pirateCache = {};

async function fetchPirateWeather(course) {
  const cacheKey = `pirate_${course.lat}_${course.lng}`;
  const cached = pirateCache[cacheKey];
  if (cached && Date.now() - cached.ts < CACHE_TTL) return cached.data;
  try {
    const url = `https://api.pirateweather.net/forecast/${PIRATE_KEY}/${course.lat},${course.lng}?units=us&extend=hourly`;
    const resp = await fetch(url);
    if (!resp.ok) throw new Error(`PirateWeather HTTP ${resp.status}`);
    const data = await resp.json();

    if (!data.hourly?.data?.length) throw new Error('PirateWeather returned no hourly data');

    const hourly = data.hourly.data.map(h => {
      const dt = new Date(h.time * 1000);
      return {
        time: dt,
        temp: h.temperature != null ? Math.round(h.temperature) : null,
        feelsLike: h.apparentTemperature != null ? Math.round(h.apparentTemperature) : null,
        humidity: h.humidity != null ? Math.round(h.humidity * 100) : null,
        windSpeedNum: h.windSpeed != null ? Math.round(h.windSpeed) : 0,
        windSpeed: `${h.windSpeed != null ? Math.round(h.windSpeed) : 0} mph`,
        windGust: h.windGust != null ? Math.round(h.windGust) : 0,
        windDir: degreesToCardinal(h.windBearing || 0),
        precipChance: h.precipProbability != null ? Math.round(h.precipProbability * 100) : 0,
        precip: h.precipIntensity || 0,
        uvIndex: h.uvIndex || 0,
        isDaytime: dt.getHours() >= 6 && dt.getHours() < 20,
        conditions: h.summary || 'Clear',
        shortForecast: h.summary || 'Clear',
        source: 'PirateWeather',
      };
    }).filter(h => h.temp != null);

    const now_pw = Date.now();
    const current = hourly.reduce((best, h) => Math.abs(h.time - now_pw) < Math.abs(best.time - now_pw) ? h : best, hourly[0]) || {};
    const todayHours = hourly.filter(h => h.time.toDateString() === new Date().toDateString());
    const tmrDate = new Date(); tmrDate.setDate(tmrDate.getDate() + 1);
    const tmrHours = hourly.filter(h => h.time.toDateString() === tmrDate.toDateString());

    const dayStats = (hrs) => ({
      high: hrs.length ? Math.max(...hrs.map(h => h.temp)) : null,
      low:  hrs.length ? Math.min(...hrs.map(h => h.temp)) : null,
      maxWind: hrs.length ? Math.max(...hrs.map(h => h.windSpeedNum)) : null,
      maxGust: hrs.length ? Math.max(...hrs.map(h => h.windGust)) : null,
      precipChance: hrs.length ? Math.max(...hrs.map(h => h.precipChance || 0)) : null,
      precip: hrs.reduce((s,h) => s + (h.precip||0), 0),
      conditions: hrs[Math.floor(hrs.length/2)]?.conditions || '',
      dayForecast: { shortForecast: hrs[Math.floor(hrs.length/2)]?.shortForecast || '' },
    });

    const result = {
      success: true, stale: false,
      source: 'PirateWeather', sourceDetail: 'PirateWeather hourly',
      dataType: 'Forecast Model',
      current: { ...current },
      today: dayStats(todayHours),
      tomorrow: dayStats(tmrHours),
      hourly,
      ground: { past24Rain: 0, snowpack: 0, dryingTrend: 'Unknown' },
      updateTime: new Date().toISOString(),
    };
    pirateCache[cacheKey] = { data: result, ts: Date.now() };
    return result;
  } catch (err) {
    console.error('PirateWeather fetch failed:', err);
    return { success: false, error: err.message, source: 'PirateWeather' };
  }
}

// ============================================================
// WEATHERBIT — P1 forecast precip probability, P2 realtime temp/feels
// GET /v2.0/forecast/hourly — 48h hourly, imperial units
// ============================================================
const wbCache = {};
async function fetchWeatherbit(course) {
  const cacheKey = `wb_${course.lat}_${course.lng}`;
  const cached = wbCache[cacheKey];
  if (cached && Date.now() - cached.ts < CACHE_TTL) return cached.data;
  try {
    const url = `https://api.weatherbit.io/v2.0/forecast/hourly` +
      `?lat=${course.lat}&lon=${course.lng}&key=${WB_API_KEY}&units=I&hours=48`;
    const resp = await fetch(url);
    if (!resp.ok) throw new Error(`Weatherbit HTTP ${resp.status}`);
    const data = await resp.json();
    if (!data.data) throw new Error('Weatherbit no data');

    const hourly = data.data.map(h => {
      const dt = new Date(h.timestamp_local);
      const weather = h.weather || {};
      const wid = weather.code || 0;
      const thunder = wid >= 200 && wid <= 233;
      const cond = weather.description || '';
      return {
        time: dt,
        temp: h.temp != null ? Math.round(h.temp) : null,
        feelsLike: h.app_temp != null ? Math.round(h.app_temp) : null,
        humidity: h.rh != null ? Math.round(h.rh) : null,
        windSpeedNum: h.wind_spd != null ? Math.round(h.wind_spd) : 0,
        windSpeed: `${h.wind_spd != null ? Math.round(h.wind_spd) : 0} mph`,
        windDir: degreesToCardinal(h.wind_dir || 0),
        windGust: h.wind_gust_spd != null ? Math.round(h.wind_gust_spd) : 0,
        precipChance: h.pop || 0,
        precip: h.precip || 0,
        uvIndex: h.uv || 0,
        isDaytime: dt.getHours() >= 6 && dt.getHours() < 20,
        conditions: cond, shortForecast: cond,
        thunder,
        source: 'Weatherbit',
      };
    }).filter(h => h.temp != null);

    if (!hourly.length) throw new Error('Weatherbit returned no usable hours');

    const now_wb = Date.now();
    const current = hourly.reduce((best, h) => Math.abs(h.time - now_wb) < Math.abs(best.time - now_wb) ? h : best, hourly[0]) || {};
    const todayHours = hourly.filter(h => h.time.toDateString() === new Date().toDateString());
    const tmrDate = new Date(); tmrDate.setDate(tmrDate.getDate() + 1);
    const tmrHours = hourly.filter(h => h.time.toDateString() === tmrDate.toDateString());

    const dayStats = (hrs) => ({
      high: hrs.length ? Math.max(...hrs.map(h => h.temp)) : null,
      low:  hrs.length ? Math.min(...hrs.map(h => h.temp)) : null,
      maxWind: hrs.length ? Math.max(...hrs.map(h => h.windSpeedNum)) : null,
      maxGust: hrs.length ? Math.max(...hrs.map(h => h.windGust)) : null,
      precipChance: hrs.length ? Math.max(...hrs.map(h => h.precipChance || 0)) : null,
      precip: hrs.reduce((s,h) => s + (h.precip||0), 0),
      conditions: hrs[Math.floor(hrs.length/2)]?.conditions || '',
      dayForecast: { shortForecast: hrs[Math.floor(hrs.length/2)]?.shortForecast || '' },
    });

    const result = {
      success: true, stale: false,
      source: 'Weatherbit', sourceDetail: 'Weatherbit 48h hourly forecast',
      dataType: 'Forecast Model',
      current: { ...current, windSpeed: current.windSpeed || '0 mph', conditions: current.conditions || '' },
      today: dayStats(todayHours),
      tomorrow: dayStats(tmrHours),
      hourly,
      ground: { past24Rain: 0, snowpack: 0, dryingTrend: 'Unknown' },
      vcDailyAll: [],
      sunsetToday: '', sunsetTomorrow: '', sunriseToday: '',
      updateTime: new Date().toISOString(),
    };
    wbCache[cacheKey] = { data: result, ts: Date.now() };
    return result;
  } catch (err) {
    console.error('Weatherbit fetch failed:', err);
    return { success: false, error: err.message, source: 'Weatherbit' };
  }
}

// ============================================================
// WEATHERAPI CURRENT — P2 realtime temp/feels when NJWxNet fails
// Also P1 direct for Architects (no NJWxNet station)
// GET /v1/current.json — actual current observations, imperial units
// ============================================================
const wapiCurrentCache = {};
async function fetchWeatherAPICurrentObs(course) {
  const cacheKey = `wapic_${course.lat}_${course.lng}`;
  const cached = wapiCurrentCache[cacheKey];
  if (cached && Date.now() - cached.ts < CACHE_TTL) return cached.data;
  try {
    const url = `https://api.weatherapi.com/v1/current.json` +
      `?key=${WAPI_KEY}&q=${course.lat},${course.lng}&aqi=no`;
    const resp = await fetch(url);
    if (!resp.ok) throw new Error(`WeatherAPI Current HTTP ${resp.status}`);
    const data = await resp.json();
    if (!data.current) throw new Error('WeatherAPI Current no data');
    const obs = data.current;
    const result = {
      success: true,
      source: 'WeatherAPI',
      sourceDetail: `WeatherAPI obs · ${data.location?.name || 'nearby'}`,
      current: {
        temp: obs.temp_f != null ? Math.round(obs.temp_f) : null,
        feelsLike: obs.feelslike_f != null ? Math.round(obs.feelslike_f) : null,
        humidity: obs.humidity != null ? Math.round(obs.humidity) : null,
        windSpeedNum: obs.wind_mph != null ? Math.round(obs.wind_mph) : null,
        windSpeed: obs.wind_mph != null ? `${Math.round(obs.wind_mph)} mph` : null,
        windGust: obs.gust_mph != null ? Math.round(obs.gust_mph) : null,
        windDir: obs.wind_dir || '',
        conditions: obs.condition?.text || '',
        precip1hr: obs.precip_in != null ? parseFloat(obs.precip_in.toFixed(2)) : null,
      },
    };
    wapiCurrentCache[cacheKey] = { data: result, ts: Date.now() };
    return result;
  } catch (err) {
    console.error('WeatherAPI Current fetch failed:', err);
    return { success: false, error: err.message, source: 'WeatherAPI Current' };
  }
}

// ============================================================
// NWS HRRR API (kept for reference — replaced by Open-Meteo as P4)
// ============================================================
const nwsCache = {};

async function nwsFetchHourly(gridId, gridX, gridY) {
  const key = `hourly_${gridId}_${gridX}_${gridY}`;
  const cached = nwsCache[key];
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) return cached.data;

  const resp = await fetch(
    `https://api.weather.gov/gridpoints/${gridId}/${gridX},${gridY}/forecast/hourly`,
    { headers: { 'User-Agent': NWS_USER_AGENT } }
  );
  if (!resp.ok) throw new Error(`NWS hourly forecast error: ${resp.status}`);

  const data = await resp.json();
  const periods = data.properties.periods.map((p) => ({
    time: new Date(p.startTime),
    temp: p.temperature,
    tempUnit: p.temperatureUnit,
    humidity: p.relativeHumidity?.value || null,
    dewpoint: p.dewpoint?.value ? Math.round(celsiusToF(p.dewpoint.value)) : null,
    windSpeed: p.windSpeed,
    windSpeedNum: parseInt(p.windSpeed) || 0,
    windDir: p.windDirection,
    precipChance: p.probabilityOfPrecipitation?.value || 0,
    shortForecast: p.shortForecast,
    isDaytime: p.isDaytime,
    icon: p.icon,
  }));

  nwsCache[key] = { data: periods, timestamp: Date.now() };
  return periods;
}

async function nwsFetchGridData(gridId, gridX, gridY) {
  const key = `grid_data_${gridId}_${gridX}_${gridY}`;
  const cached = nwsCache[key];
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) return cached.data;

  const resp = await fetch(
    `https://api.weather.gov/gridpoints/${gridId}/${gridX},${gridY}`,
    { headers: { 'User-Agent': NWS_USER_AGENT } }
  );
  if (!resp.ok) throw new Error(`NWS grid data error: ${resp.status}`);

  const props = (await resp.json()).properties;
  const parseVals = (field, convert) =>
    field?.values
      ? field.values.map((v) => ({
          time: v.validTime.split('/')[0],
          duration: v.validTime.split('/')[1],
          value: convert ? convert(v.value) : v.value,
        }))
      : [];

  const result = {
    updateTime: props.updateTime,
    quantPrecip: parseVals(props.quantitativePrecipitation, (v) => Math.round(v * 0.0393701 * 100) / 100),
    snowfall: parseVals(props.snowfallAmount, (v) => Math.round(v * 0.0393701 * 100) / 100),
    temperature: parseVals(props.temperature, (v) => Math.round(celsiusToF(v))),
    apparentTemp: parseVals(props.apparentTemperature, (v) => Math.round(celsiusToF(v))),
    relativeHumidity: parseVals(props.relativeHumidity),
    windSpeed: parseVals(props.windSpeed, (v) => Math.round(v * 0.621371)),
    windDirection: parseVals(props.windDirection),
    windGust: parseVals(props.windGust, (v) => Math.round(v * 0.621371)),
    skyCover: parseVals(props.skyCover),
    probabilityOfPrecipitation: parseVals(props.probabilityOfPrecipitation),
  };

  nwsCache[key] = { data: result, timestamp: Date.now() };
  return result;
}

async function nwsFetchDayForecast(gridId, gridX, gridY) {
  const key = `day_forecast_${gridId}_${gridX}_${gridY}`;
  const cached = nwsCache[key];
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) return cached.data;

  const resp = await fetch(
    `https://api.weather.gov/gridpoints/${gridId}/${gridX},${gridY}/forecast`,
    { headers: { 'User-Agent': NWS_USER_AGENT } }
  );
  if (!resp.ok) throw new Error(`NWS day forecast error: ${resp.status}`);

  const periods = (await resp.json()).properties.periods.slice(0, 4).map((p) => ({
    name: p.name,
    temp: p.temperature,
    tempUnit: p.temperatureUnit,
    windSpeed: p.windSpeed,
    windDir: p.windDirection,
    shortForecast: p.shortForecast,
    detailedForecast: p.detailedForecast,
    isDaytime: p.isDaytime,
    precipChance: p.probabilityOfPrecipitation?.value || 0,
  }));

  nwsCache[key] = { data: periods, timestamp: Date.now() };
  return periods;
}


// ============================================================
// NJWxNet REAL-TIME LAYER (Synoptic Data API — NJWxNet network 109)
// Role: "Right now at the course" — wind truth + precip confirmation
// NOT a forecast source. Observations only.
// One call fetches all 4 stations simultaneously.
// Station → course mapping:
//   NJ84 (Franklin Twp-Somerset) → spooky-brook, quail-brook, warrenbrook, dr-z-test
//   NJ76 (Hillsborough-Duke)     → green-knoll, neshanic-valley, fox-hollow
//   NJ53 (Pittstown)             → high-bridge-hills, oak-hill
//   NJ09 (Holmdel)               → pebble-creek, hominy-hill
//   architects                   → null (no station in range — OM/TIO fallback)
// Precip: sum all non-null precip_accum_five_minute values over 2-hour rolling window (inches)
// ============================================================
const NJWXNET_TOKEN = '8ef5230a1abc4b3181b786988690ceae';
// Module-level cache: one shared cache for all 4 stations (single API call)
const njwxCache = { data: null, timestamp: 0 };
const NJWX_CACHE_TTL = 300000; // 5 min — stations update every 5 min

// S222: Canonical cache clear — used by all four refresh triggers
const clearAllCaches = () => {
  [vcCache, omForecastCache, nwsHourlyCache, wapiForecastCache,
   pirateCache, mbCache, wapiCurrentCache, awcCache, wbCache,
   nwsCache, lightningCache, osmCache]
    .forEach(cache => { try { Object.keys(cache).forEach(k => delete cache[k]); } catch {} });
  njwxCache.data = null; njwxCache.timestamp = 0;
};

// Return last non-null value from an array
function lastNonNull(arr) {
  if (!arr || !arr.length) return null;
  for (let i = arr.length - 1; i >= 0; i--) {
    if (arr[i] !== null && arr[i] !== undefined) return arr[i];
  }
  return null;
}

async function fetchNJWxNetAll(stidOverride) {
  // B5 fix S189: fetch only the station needed for the active course
  // stidOverride = single station ID (e.g. "NJ84") — avoids fetching all 4 every call
  const stidToFetch = stidOverride || NJWXNET_STIDS;
  // Return cached if fresh — cache key includes stid so per-station cache works
  const cacheKey = stidToFetch;
  if (njwxCache.data && njwxCache.stid === cacheKey && Date.now() - njwxCache.timestamp < NJWX_CACHE_TTL) {
    logResponseTime('njwxnet', 'realtime', stidOverride || 'unknown', 0, 'cache_hit', true); // S222
    return njwxCache.data;
  }
  try {
    const url = `https://api.synopticdata.com/v2/stations/timeseries` +
      `?stid=${stidToFetch}&recent=120` +
      `&vars=air_temp,relative_humidity,wind_speed,wind_gust,precip_accum_five_minute,soil_moisture` +
      `&units=english,speed|mph&token=${NJWXNET_TOKEN}`;
    const _njwxT0 = Date.now();
    const resp = await fetch(url);
    const _njwxMs = Date.now() - _njwxT0;
    recordResponseTime('njwxnet_realtime', _njwxMs);
    if (!resp.ok) throw new Error(`Synoptic API error: ${resp.status}`);
    const data = await resp.json();
    if (data.SUMMARY?.RESPONSE_CODE !== 1) {
      throw new Error(`Synoptic error: ${data.SUMMARY?.RESPONSE_MESSAGE}`);
    }
    // Build stid → parsed obs map
    const stationMap = {};
    for (const s of (data.STATION || [])) {
      const obs = s.OBSERVATIONS || {};
      // Sum all non-null 5-min precip values — last 12 readings = 1hr, all 24 = 2hr
      const precipVals = (obs.precip_accum_five_minute_set_1 || []).filter(v => v !== null && v !== undefined);
      // NJWxNet precip_accum_five_minute — Synoptic fetch uses units=english, values already in inches
      const precip2hr = precipVals.length > 0
        ? Math.round(precipVals.reduce((sum, v) => sum + v, 0) * 10000) / 10000
        : 0;
      const last12 = precipVals.slice(-12);
      const precip1hr = last12.length > 0
        ? Math.round(last12.reduce((sum, v) => sum + v, 0) * 10000) / 10000
        : 0;
      stationMap[s.STID] = {
        stid: s.STID,
        name: s.NAME,
        lat: parseFloat(s.LATITUDE),
        lng: parseFloat(s.LONGITUDE),
        temp: lastNonNull(obs.air_temp_set_1),
        humidity: lastNonNull(obs.relative_humidity_set_1),
        windSpeedRaw: lastNonNull(obs.wind_speed_set_1),   // mph (english units)
        windGustRaw:  lastNonNull(obs.wind_gust_set_1),    // mph
        precip1hr,
        precip2hr,
        // soil_moisture_set_1: NJ76 (Duke Farms) only — other stations return null
        soilMoisture: lastNonNull(obs.soil_moisture_set_1) ?? null,
        obsTime: lastNonNull(obs.date_time),
      };
    }
    njwxCache.data = stationMap;
    njwxCache.stid = cacheKey; // B5 fix: track which station(s) are cached
    njwxCache.timestamp = Date.now();
    return stationMap;
  } catch (err) {
    console.warn('ZAP NJWxNet fetch failed:', err.message);
    return null;
  }
}

// NJWxNet historical timeseries — used for weather snapshot at scorecard submit
// Queries actual obs for a specific station over a tee-to-finish window.
// start/end are JS Date objects in local ET — converted to UTC for Synoptic.
// Returns { success, avgTemp, avgWind, avgGust, avgHumidity, precipAccum, obsCount } or { success:false }
async function fetchNJWxNetTimeseries(stid, start, end, course) {
  try {
    const fmtSynoptic = d => d.toISOString().slice(0, 16).replace(/[-:T]/g, ''); // YYYYmmddHHMM UTC
    const url = `https://api.synopticdata.com/v2/stations/timeseries` +
      `?stid=${stid}` +
      `&start=${fmtSynoptic(start)}&end=${fmtSynoptic(end)}` +
      `&vars=air_temp,relative_humidity,wind_speed,wind_gust,precip_accum_five_minute` +
      `&units=english,speed|mph&token=${NJWXNET_TOKEN}`;
    const _t0 = Date.now();
    const resp = await fetch(url);
    const _ms = Date.now() - _t0;
    recordResponseTime('njwxnet_timeseries', _ms);
    logResponseTime('njwxnet', 'timeseries', course, _ms, 'scorecard_submit', false, { stid, start: fmtSynoptic(start), end: fmtSynoptic(end) });
    incrementCallCount('njwxnet_snapshot', course);
    if (!resp.ok) throw new Error(`Synoptic timeseries error: ${resp.status}`);
    const data = await resp.json();
    if (data.SUMMARY?.RESPONSE_CODE !== 1) throw new Error(data.SUMMARY?.RESPONSE_MESSAGE);
    const s = (data.STATION || [])[0];
    if (!s) throw new Error('No station data returned');
    const obs = s.OBSERVATIONS || {};
    const temps    = (obs.air_temp_set_1                   || []).filter(v => v != null);
    const winds    = (obs.wind_speed_set_1                 || []).filter(v => v != null);
    const gusts    = (obs.wind_gust_set_1                  || []).filter(v => v != null);
    const hums     = (obs.relative_humidity_set_1          || []).filter(v => v != null);
    const precipVals = (obs.precip_accum_five_minute_set_1 || []).filter(v => v != null);
    const avg = arr => arr.length ? Math.round(arr.reduce((s, v) => s + v, 0) / arr.length * 10) / 10 : null;

    // Open-Meteo forecast API — get WMO conditions for the round window
    // Uses past_days to cover same-day and next-day submits. No ERA5, no archive endpoint.
    let conditions = null;
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const roundDay = new Date(start);
      roundDay.setHours(0, 0, 0, 0);
      const pastDays = Math.round((today - roundDay) / 86400000);
      const omUrl = `https://api.open-meteo.com/v1/forecast` +
        `?latitude=${course.lat}&longitude=${course.lng}` +
        `&hourly=weather_code` +
        `&past_days=${pastDays}&forecast_days=1` +
        `&timezone=America%2FNew_York`;
      const omResp = await fetch(omUrl);
      if (omResp.ok) {
        const omData = await omResp.json();
        const omTimes = omData.hourly?.time || [];
        const omCodes = omData.hourly?.weather_code || [];
        // Find hours within the tee-to-finish window
        const startH = start.getHours();
        const endH   = end.getHours();
        const windowCodes = omTimes
          .map((t, i) => ({ h: new Date(t).getHours(), code: omCodes[i] }))
          .filter(e => e.h >= startH && e.h <= endH)
          .map(e => e.code)
          .filter(c => c != null);
        if (windowCodes.length > 0) {
          // Most common WMO code in the window
          const freq = {};
          windowCodes.forEach(c => { freq[c] = (freq[c] || 0) + 1; });
          const dominant = parseInt(Object.entries(freq).sort((a, b) => b[1] - a[1])[0][0]);
          conditions = wmoDescription(dominant);
        }
      }
    } catch { /* conditions stays null — non-fatal */ }

    return {
      success:     true,
      obsCount:    temps.length,
      avgTemp:     avg(temps),
      avgWind:     avg(winds),
      avgGust:     avg(gusts),
      avgHumidity: avg(hums),
      precipAccum: precipVals.length ? Math.round(precipVals.reduce((s, v) => s + v, 0) * 10000) / 10000 : 0, // units=english — already inches
      conditions,
      responseMs:  _ms,
    };
  } catch (err) {
    console.warn('ZAP NJWxNet timeseries failed:', err.message);
    return { success: false, error: err.message };
  }
}

async function fetchNJWxNet(course, forceNJWxNet = null) {
  // Activity Planner: distance-based station selection (replaces NJWXNET_COURSE_MAP lookup)
  const { station, distanceMi } = getNearestNJWxNetStation(course.lat, course.lng);
  const stid = station ? station.stid : null;
  if (!stid) return { success: false, source: 'NJWxNet', error: 'No NJWxNet station found', stid: null };
  // 8-mile threshold — skip unless user forces on (S262: raised from 5mi to match Golf design intent)
  if (forceNJWxNet !== true && distanceMi > 8) return { success: false, source: 'NJWxNet', error: `Nearest station ${station.stid} is ${distanceMi} mi away — exceeds 8 mi threshold`, stid: null, distanceMi, nearestStid: station.stid };
  // User forced off
  if (forceNJWxNet === false) return { success: false, source: 'NJWxNet', error: 'NJWxNet disabled by user', stid: null, distanceMi, nearestStid: station.stid };

  const t0 = Date.now();
  const stationMap = await fetchNJWxNetAll(stid);
  logResponseTime('njwxnet', 'realtime', course, Date.now() - t0, 'page_load', false, { stid });
  incrementCallCount('njwxnet', course, stid);

  if (!stationMap) return { success: false, source: 'NJWxNet', error: 'NJWxNet unavailable', stid };
  const s = stationMap[stid];
  if (!s) return { success: false, source: 'NJWxNet', error: `Station ${stid} not in response`, stid };

  const njSource = distanceMi ? `NJWxNet ${s.name} · ${distanceMi} mi` : `NJWxNet ${s.name}`;
  const windSpeedNum = (station.windReliable && s.windSpeedRaw !== null) ? Math.round(s.windSpeedRaw * 10) / 10 : null;
  const windGust     = (station.windReliable && s.windGustRaw  !== null) ? Math.round(s.windGustRaw  * 10) / 10 : null;
  return {
    success: true,
    source: 'NJWxNet',
    sourceDetail: njSource,
    stid,
    distanceMi,
    windReliable: station.windReliable,
    current: {
      temp:         s.temp !== null ? Math.round(s.temp) : null,
      humidity:     s.humidity !== null ? Math.round(s.humidity) : null,
      windSpeedNum,
      windSpeed:    windSpeedNum !== null ? `${windSpeedNum} mph` : null,
      windGust,
      windSpeedRaw: station.windReliable ? s.windSpeedRaw : null,
      windGustRaw:  station.windReliable ? s.windGustRaw  : null,
      precip1hr: s.precip1hr,
      feelsLike: null,
      _rtSources: {},
    },
  };
}

// ============================================================
// LIGHTNING ENGINE (NWS Alerts API — FREE FOREVER)
// Source: api.weather.gov/alerts/active?point=lat,lng
// Parses TIME...MOT...LOC field from Severe Thunderstorm / Tornado warnings:
//   Format: TIME...MOT...LOC HHMMZ DDDDEGG SSKT LLLL LLLL
//   DDDDEG = storm motion bearing (degrees, direction storm is COMING FROM)
//   SSKT   = storm speed in knots
//   LLLL LLLL = centroid lat/lon (truncated: 4212 9133 = 42.12N, 91.33W)
// No API key. No cost. No expiry. NWS is the data source Xweather/FAA/NWS all use.
// ============================================================
const LIGHTNING_CACHE_TTL = 60000; // 1-min cache — lightning is time-critical
const lightningCache = {};

// Haversine distance in miles between two lat/lng points
function haversineMiles(lat1, lng1, lat2, lng2) {
  const R = 3958.8;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat/2)**2 + Math.cos(lat1 * Math.PI/180) * Math.cos(lat2 * Math.PI/180) * Math.sin(dLng/2)**2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
}

// Bearing in degrees (0=N, 90=E, 180=S, 270=W) from point 1 to point 2
function bearingDeg(lat1, lng1, lat2, lng2) {
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const lat1R = lat1 * Math.PI / 180;
  const lat2R = lat2 * Math.PI / 180;
  const y = Math.sin(dLng) * Math.cos(lat2R);
  const x = Math.cos(lat1R) * Math.sin(lat2R) - Math.sin(lat1R) * Math.cos(lat2R) * Math.cos(dLng);
  return (Math.atan2(y, x) * 180 / Math.PI + 360) % 360;
}

function bearingLabel(deg) {
  const dirs = ['N','NNE','NE','ENE','E','ESE','SE','SSE','S','SSW','SW','WSW','W','WNW','NW','NNW'];
  return dirs[Math.round(deg / 22.5) % 16];
}

// Cluster strikes into cells: group by proximity (within 5 miles of each other)
function clusterStrikes(strikes) {
  const clusters = [];
  const used = new Set();
  for (let i = 0; i < strikes.length; i++) {
    if (used.has(i)) continue;
    const cluster = [strikes[i]];
    used.add(i);
    for (let j = i + 1; j < strikes.length; j++) {
      if (used.has(j)) continue;
      const d = haversineMiles(strikes[i].lat, strikes[i].lng, strikes[j].lat, strikes[j].lng);
      if (d <= 5) { cluster.push(strikes[j]); used.add(j); }
    }
    clusters.push(cluster);
  }
  return clusters;
}

// From a cluster compute centroid, approach vector, and ETA to course
function analyzeCell(cluster, courseLat, courseLng) {
  const centLat = cluster.reduce((s,s2) => s + s2.lat, 0) / cluster.length;
  const centLng = cluster.reduce((s,s2) => s + s2.lng, 0) / cluster.length;
  const distMiles = haversineMiles(courseLat, courseLng, centLat, centLng);
  const bearing = bearingDeg(courseLat, courseLng, centLat, centLng);
  const dirLabel = bearingLabel(bearing);

  // Sort strikes by time to get velocity estimate — need at least 2 strikes spread in time
  const sorted = [...cluster].sort((a, b) => a.timestamp - b.timestamp);
  let approachSpeedMph = null;
  let etaMinutes = null;
  let movingToward = false;

  if (sorted.length >= 3) {
    // Use oldest and newest in cluster to estimate cell movement
    const oldest = sorted[0];
    const newest = sorted[sorted.length - 1];
    const timeDeltaHours = (newest.timestamp - oldest.timestamp) / 3600000;
    if (timeDeltaHours > 0.02) { // at least ~1 min spread
      const cellBearing = bearingDeg(oldest.lat, oldest.lng, newest.lat, newest.lng);
      const cellSpeed = haversineMiles(oldest.lat, oldest.lng, newest.lat, newest.lng) / timeDeltaHours;
      // Is the cell moving toward the course? Check angle delta between cell bearing and bearing-to-course
      const toBearing = bearingDeg(centLat, centLng, courseLat, courseLng);
      const angleDelta = Math.abs(((cellBearing - toBearing) + 360) % 360);
      movingToward = angleDelta < 90 || angleDelta > 270;
      if (movingToward && cellSpeed > 5) {
        approachSpeedMph = Math.round(cellSpeed);
        etaMinutes = Math.round((distMiles / cellSpeed) * 60);
      }
    }
  }

  const inHazardRing = distMiles <= 5;
  const inWarningZone = distMiles <= 10;

  return {
    strikeCount: cluster.length,
    centLat, centLng,
    distMiles: Math.round(distMiles * 10) / 10,
    bearing,
    dirLabel,
    approachSpeedMph,
    etaMinutes,
    movingToward,
    inHazardRing,
    inWarningZone,
    latestStrike: sorted[sorted.length - 1]?.timestamp || null,
  };
}

// Parse TIME...MOT...LOC line from NWS warning description text
// Example: "TIME...MOT...LOC 0025Z 228DEG 42KT 4212 9133"
//   bearingDeg: direction storm is MOVING FROM (228 = moving NE)
//   speedKt: knots
//   centroid: lat/lon truncated integers (4212 = 42.12N, 9133 = 91.33W)
function parseTimMotLoc(text) {
  if (!text) return null;
  const match = text.match(/TIME\.\.\.MOT\.\.\.LOC\s+(\d{4})Z\s+(\d{3,4})DEG\s+(\d+)KT\s+(\d{4})\s+(\d{4,5})/);
  if (!match) return null;
  const bearingFrom = parseInt(match[2], 10); // direction storm is coming FROM
  const speedKt = parseInt(match[3], 10);
  const rawLat = parseInt(match[4], 10);  // e.g. 4212 → 42.12
  const rawLon = parseInt(match[5], 10);  // e.g. 9133 → 91.33 (West, so negative)
  const centLat = rawLat / 100;
  const centLng = -(rawLon / 100); // NWS omits the minus sign for western longitudes
  // Storm is MOVING TO the opposite direction
  const motionBearing = (bearingFrom + 180) % 360;
  const speedMph = speedKt * 1.15078;
  return { centLat, centLng, bearingFrom, motionBearing, speedKt, speedMph };
}

async function fetchLightningData(lat, lng) {
  const cacheKey = `lightning_${Math.round(lat * 100)}_${Math.round(lng * 100)}`;
  const cached = lightningCache[cacheKey];
  if (cached && Date.now() - cached.fetchTime < LIGHTNING_CACHE_TTL) return cached;

  try {
    // NWS active alerts for this exact GPS point
    const url = `https://api.weather.gov/alerts/active?point=${lat},${lng}&status=actual&message_type=alert`;
    const resp = await fetch(url, {
      headers: { 'User-Agent': 'ZAP-Golf-Weather/1.0 (contact: zap-golf@zur.dev)' }
    });
    if (!resp.ok) throw new Error(`NWS alerts API: ${resp.status}`);
    const data = await resp.json();

    const features = data.features || [];

    // Filter to lightning-relevant alert types
    const lightningEvents = ['Tornado Warning', 'Severe Thunderstorm Warning',
      'Tornado Watch', 'Severe Thunderstorm Watch', 'Special Weather Statement'];
    const relevantAlerts = features.filter(f => {
      const evt = f.properties?.event || '';
      return lightningEvents.some(e => evt.includes(e));
    });

    if (relevantAlerts.length === 0) {
      // No active warnings — check if we have a recent all-clear still counting down
      const prev = lightningCache[cacheKey];
      if (prev?.lastStrikeTime) {
        const minutesSince = (Date.now() - prev.lastStrikeTime) / 60000;
        if (minutesSince < 30) {
          const remaining = Math.round(30 - minutesSince);
          const result = {
            ...prev,
            fetchTime: Date.now(),
            strikeCount: 0,
            hazardActive: false,
            warningActive: false,
            allClearMinutesRemaining: remaining,
            statusMsg: `All-clear in ${remaining} min (warning expired)`,
          };
          lightningCache[cacheKey] = result;
          return result;
        }
      }
      // Truly clear
      const result = {
        success: true,
        fetchTime: Date.now(),
        strikeCount: 0,
        cells: [],
        closestCell: null,
        hazardActive: false,
        warningActive: false,
        latestTimestamp: null,
        allClearBy: null,
        allClearMinutesRemaining: 0,
        alerts: [],
        source: 'NWS Alerts API',
      };
      lightningCache[cacheKey] = result;
      return result;
    }

    // Parse each alert for storm cell vectors
    const cells = [];
    let latestTimestamp = null;
    const parsedAlerts = [];

    for (const alert of relevantAlerts) {
      const props = alert.properties;
      const desc = props.description || '';
      const evt = props.event || '';
      const expires = props.expires ? new Date(props.expires).getTime() : null;
      const sent = props.sent ? new Date(props.sent).getTime() : null;

      // Track most recent alert time for 30-min all-clear
      if (sent && (!latestTimestamp || sent > latestTimestamp)) {
        latestTimestamp = sent;
      }

      parsedAlerts.push({ event: evt, expires, sent, headline: props.headline });

      // Try to parse TIME...MOT...LOC vector
      const motloc = parseTimMotLoc(desc);
      if (motloc) {
        const { centLat, centLng, motionBearing, speedMph } = motloc;
        const distMiles = haversineMiles(lat, lng, centLat, centLng);
        const bearingToCell = bearingDeg(lat, lng, centLat, centLng);
        const dirLabel = bearingLabel(bearingToCell);
        const inHazardRing = distMiles <= 5;
        const inWarningZone = distMiles <= 10;

        // Is the cell moving toward the course?
        // angleDelta between storm motion bearing and bearing FROM storm TO course
        const bearingFromCellToCourse = bearingDeg(centLat, centLng, lat, lng);
        const angleDelta = Math.abs(((motionBearing - bearingFromCellToCourse) + 360) % 360);
        const movingToward = angleDelta < 90 || angleDelta > 270;

        let etaMinutes = null;
        if (movingToward && speedMph > 3 && distMiles > 0) {
          etaMinutes = Math.round((distMiles / speedMph) * 60);
        }

        cells.push({
          strikeCount: 1, // NWS doesn't give individual strike count
          centLat, centLng,
          distMiles: Math.round(distMiles * 10) / 10,
          bearing: bearingToCell,
          dirLabel,
          approachSpeedMph: Math.round(speedMph),
          etaMinutes,
          movingToward,
          inHazardRing,
          inWarningZone,
          latestStrike: latestTimestamp,
          event: evt,
        });
      } else {
        // Warning exists but no MOT...LOC parseable — still flag as active threat
        // Use warning polygon centroid if available
        const geo = alert.geometry;
        if (geo?.coordinates) {
          try {
            const coords = geo.type === 'Polygon' ? geo.coordinates[0] : geo.coordinates;
            const centLat = coords.reduce((s, c) => s + c[1], 0) / coords.length;
            const centLng = coords.reduce((s, c) => s + c[0], 0) / coords.length;
            const distMiles = haversineMiles(lat, lng, centLat, centLng);
            cells.push({
              strikeCount: 1,
              centLat, centLng,
              distMiles: Math.round(distMiles * 10) / 10,
              bearing: bearingDeg(lat, lng, centLat, centLng),
              dirLabel: bearingLabel(bearingDeg(lat, lng, centLat, centLng)),
              approachSpeedMph: null,
              etaMinutes: null,
              movingToward: null,
              inHazardRing: distMiles <= 5,
              inWarningZone: distMiles <= 10,
              latestStrike: latestTimestamp,
              event: evt,
            });
          } catch (e) { /* skip bad geometry */ }
        }
      }
    }

    cells.sort((a, b) => a.distMiles - b.distMiles);
    const closestCell = cells[0] || null;
    const hazardActive = cells.some(c => c.inHazardRing) || relevantAlerts.some(f =>
      ['Tornado Warning', 'Severe Thunderstorm Warning'].includes(f.properties?.event));
    const warningActive = cells.some(c => c.inWarningZone) || relevantAlerts.length > 0;

    const allClearMinutesRemaining = latestTimestamp
      ? Math.max(0, Math.round((latestTimestamp + 30 * 60000 - Date.now()) / 60000))
      : 0;

    const result = {
      success: true,
      fetchTime: Date.now(),
      strikeCount: relevantAlerts.length, // active warning count
      cells,
      closestCell,
      hazardActive,
      warningActive,
      latestTimestamp,
      lastStrikeTime: latestTimestamp,
      allClearBy: latestTimestamp ? new Date(latestTimestamp + 30 * 60000) : null,
      allClearMinutesRemaining,
      alerts: parsedAlerts,
      source: 'NWS Alerts API',
    };

    lightningCache[cacheKey] = result;
    return result;

  } catch (err) {
    console.error('Lightning fetch failed:', err);
    return { success: false, error: err.message, source: 'NWS Alerts API' };
  }
}


const osmCache = {};

async function fetchWaterDensity(lat, lng) {
  const key = `water_${lat}_${lng}`;
  // B6 fix S189: check in-memory cache first, then localStorage
  if (osmCache[key]) return osmCache[key];
  try {
    const stored = localStorage.getItem(`zap_osm_${key}`);
    if (stored) {
      const parsed = JSON.parse(stored);
      osmCache[key] = parsed;
      return parsed;
    }
  } catch { /* localStorage unavailable — continue to fetch */ }

  try {
    const query = `
      [out:json][timeout:10];
      (
        way["natural"="water"](around:1000,${lat},${lng});
        relation["natural"="water"](around:1000,${lat},${lng});
        way["landuse"="reservoir"](around:1000,${lat},${lng});
        relation["landuse"="reservoir"](around:1000,${lat},${lng});
        way["waterway"~"stream|river|ditch"](around:1000,${lat},${lng});
        relation["waterway"~"stream|river|ditch"](around:1000,${lat},${lng});
      );
      out body;
      >;
      out skel qt;
    `;

    const resp = await fetch('https://overpass-api.de/api/interpreter', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: 'data=' + encodeURIComponent(query),
    });
    if (!resp.ok) throw new Error(`Overpass API error: ${resp.status}`);

    const data = await resp.json();
    const ways = data.elements.filter(
      (e) => e.type === 'way' && e.tags && (e.tags.natural === 'water' || e.tags.landuse === 'reservoir' || /^(stream|river|ditch)$/.test(e.tags.waterway))
    );
    const relations = data.elements.filter(
      (e) => e.type === 'relation' && e.tags && (e.tags.natural === 'water' || e.tags.landuse === 'reservoir' || /^(stream|river|ditch)$/.test(e.tags.waterway))
    );
    const featureCount = ways.length + relations.length;

    let areaPercentage, multiplier, label;
    if (featureCount === 0) {
      areaPercentage = 0; multiplier = 1; label = 'Baseline (No water nearby)';
    } else if (featureCount <= 2) {
      areaPercentage = 2; multiplier = 1; label = 'Baseline';
    } else if (featureCount <= 5) {
      areaPercentage = 4; multiplier = 1.3; label = 'Moderate water density';
    } else {
      areaPercentage = 8; multiplier = 1.8; label = 'High water density — Swamp is hungry';
    }

    const result = { featureCount, areaPercentage, multiplier, label };
    osmCache[key] = result;
    // B6 fix: persist to localStorage so High Bridge doesn't re-query every load
    try { localStorage.setItem(`zap_osm_${key}`, JSON.stringify(result)); } catch { /* storage full — no-op */ }
    return result;
  } catch (err) {
    console.error('OSM water density query failed:', err);
    logSystemHealth('overpass_fail', null, { error: err.message, lat, lng });
    return { featureCount: 0, areaPercentage: 0, multiplier: 1, label: 'Unknown (OSM query failed)', error: err.message };
  }
}

// ============================================================
// POLLEN DATA — Google Pollen API (lat/lng, date-keyed localStorage cache)
// UPI scale 0–5. Categories: None / Very Low / Low / Medium / High / Very High
// Key restricted by HTTP referrer — safe to call from browser directly (no proxy needed)
// Cache: date-keyed localStorage. New date = new key. No TTL math needed.
// ============================================================
const GOOGLE_POLLEN_KEY = 'AIzaSyD7THXUzFUzKN1qBc2h0ObUf93zCrHfcYM';

function pollenCategoryToResult(category) {
  const map = {
    'NONE':      { label: 'None',     value: 0, color: '#4ADE80' },
    'VERY_LOW':  { label: 'Very Low', value: 1, color: '#4ADE80' },
    'LOW':       { label: 'Low',      value: 2, color: '#4ADE80' },
    'MODERATE':  { label: 'Moderate', value: 3, color: '#FACC15' },
    'HIGH':      { label: 'High',     value: 4, color: '#FB923C' },
    'VERY_HIGH': { label: 'Very High',value: 5, color: '#F87171' },
  };
  return map[category] || { label: 'Unknown', value: 0, color: '#9CA3AF' };
}

async function fetchPollenData(lat, lng, courseId) {
  const today = new Date().toISOString().slice(0, 10);
  const cacheKey = `pollen_v2_${courseId}_${today}`;

  try {
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      const parsed = JSON.parse(cached);
      if (parsed?.success) return parsed;
    }
  } catch {}

  try {
    const url = `https://pollen.googleapis.com/v1/forecast:lookup?key=${GOOGLE_POLLEN_KEY}&location.latitude=${lat}&location.longitude=${lng}&days=1`;
    const resp = await fetch(url);
    if (!resp.ok) throw new Error(`Google Pollen API HTTP ${resp.status}`);
    incrementCallCount('google_pollen', { id: courseId, name: courseId });
    const json = await resp.json();

    const day = json?.dailyInfo?.[0];
    if (!day) throw new Error('No daily pollen data');

    const getType = (code) => day.pollenTypeInfo?.find(t => t.code === code);
    const normCat = (cat) => cat ? cat.toUpperCase().replace(/ /g, '_') : 'NONE';
    const tree  = pollenCategoryToResult(normCat(getType('TREE')?.indexInfo?.category));
    const grass = pollenCategoryToResult(normCat(getType('GRASS')?.indexInfo?.category));
    const weed  = pollenCategoryToResult(normCat(getType('WEED')?.indexInfo?.category));

    const worstValue = Math.max(tree.value, grass.value, weed.value);
    const worstLabels = ['None', 'Very Low', 'Low', 'Medium', 'High', 'Very High'];
    const worstColors = ['#4ADE80', '#4ADE80', '#4ADE80', '#FACC15', '#FB923C', '#F87171'];

    const result = {
      success: true,
      tree,
      grass,
      weed,
      worst: worstLabels[worstValue],
      worstColor: worstColors[worstValue],
      // Legacy compat fields for any display code still using .label / .color
      label: worstLabels[worstValue],
      color: worstColors[worstValue],
      offSeason: worstValue === 0,
    };

    try { localStorage.setItem(cacheKey, JSON.stringify(result)); } catch {}
    return result;
  } catch (err) {
    console.error('Google Pollen fetch failed:', err);
    return { success: false, error: err.message, label: 'Unavailable', color: '#5a4a3a', offSeason: false };
  }
}

// Aliases for legacy references
const fetchVC = fetchVisualCrossing;
const fetchOSMWaterDensity = fetchWaterDensity;
const fetchPollen = fetchPollenData;

// ============================================================
// FEELS-LIKE (NWS standard: Wind Chill + Heat Index)
// ============================================================
function calcFeelsLike(tempF, humidity, windMph) {
  // NWS Wind Chill: applies when temp <= 50°F and wind > 3 mph
  if (tempF <= 50 && windMph > 3) {
    return Math.round(
      35.74 + 0.6215 * tempF - 35.75 * Math.pow(windMph, 0.16) + 0.4275 * tempF * Math.pow(windMph, 0.16)
    );
  }
  // NWS Heat Index: applies when temp >= 80°F
  if (tempF >= 80) {
    const hi = -42.379 + 2.04901523 * tempF + 10.14333127 * humidity
      - 0.22475541 * tempF * humidity - 0.00683783 * tempF * tempF
      - 0.05481717 * humidity * humidity + 0.00122874 * tempF * tempF * humidity
      + 0.00085282 * tempF * humidity * humidity - 0.00000199 * tempF * tempF * humidity * humidity;
    return Math.round(hi);
  }
  // Between 50-80°F with low wind — feels like actual temp
  return Math.round(tempF);
}

// DEAD CODE — never called. Candidate for removal.
// Steadman Australian Apparent Temperature — covers ALL temp ranges
// AT = Ta + 0.33×e − 0.70×ws − 4.00
// Ta=temp °C, e=vapor pressure hPa, ws=wind m/s
function calcApparentTemp(tempF, humidityPct, windMph) {
  const Ta = (tempF - 32) * 5 / 9;
  const ws = windMph * 0.44704;
  const e = (humidityPct / 100) * 6.105 * Math.exp((17.27 * Ta) / (237.7 + Ta));
  const AT_c = Ta + 0.33 * e - 0.70 * ws - 4.00;
  return Math.round(AT_c * 9 / 5 + 32);
}

// ============================================================
// NJWxNet P1 OVERRIDE — patch VC weatherData with NJWxNet current obs
// NJWxNet provides: temp, humidity, windSpeed, windGust, precip1hr
// VC keeps: precipChance, conditions, shortForecast, UV, visibility,
//           cloudCover, sunrise/sunset, hourly forecast, tomorrow, etc.
// The current-hour slot in hourly[] is also patched so buildWindowWeather
// picks up NJWxNet values for the BAD engine window analysis.
// ============================================================
function applyNJWxNetOverride(weatherData, njwx) {
  if (!weatherData?.success || !njwx?.success) return weatherData;
  const nj = njwx.current;
  if (!nj) return weatherData;

  // Build patched current — only override fields where NJWxNet has data
  const patched = { ...weatherData.current };
  const overrideFields = [];

  if (nj.temp != null) {
    patched.temp = Math.round(nj.temp);
    overrideFields.push('temp');
  }
  if (nj.humidity != null) {
    patched.humidity = nj.humidity;
    overrideFields.push('humidity');
  }
  if (nj.windSpeedNum != null) {
    patched.windSpeedNum = nj.windSpeedNum;
    patched.windSpeed = `${nj.windSpeedNum} mph`;
    overrideFields.push('wind');
  }
  if (nj.windGust != null) {
    patched.windGust = nj.windGust;
  }

  // Compute feelsLike from NJWxNet inputs (or best available)
  const flTemp = patched.temp ?? weatherData.current.temp ?? 65;
  const flHumidity = patched.humidity ?? weatherData.current.humidity ?? 50;
  const flWind = patched.windSpeedNum ?? weatherData.current.windSpeedNum ?? 0;
  patched.feelsLike = calcFeelsLike(flTemp, flHumidity, flWind);

  // Tag the source so dashboard knows this is NJWxNet-patched
  patched._njwxOverride = true;
  patched._njwxFields = overrideFields;

  // Also patch ground.past24Rain? No — NJWxNet precip1hr is 1-hour rolling,
  // not 24-hour total. VC past24Rain from hourly accumulation stays.

  // Update source attribution
  const njSource = njwx.sourceDetail || 'NJWxNet';
  const vcSource = weatherData.sourceDetail || weatherData.source || 'Visual Crossing';

  // Build _rtSources reflecting what NJWxNet actually provided
  const rtSources = {};
  if (nj.temp != null)         rtSources.temp     = njSource;
  if (nj.humidity != null)     rtSources.humidity = njSource;
  if (nj.windSpeedNum != null) rtSources.wind     = njSource;
  if (nj.windGust != null)     rtSources.gust     = njSource;
  if (nj.precip1hr != null)    rtSources.precip   = njSource;

  return {
    ...weatherData,
    current: patched,
    hourly: weatherData.hourly,
    source: `NJWxNet + ${weatherData.source}`,
    sourceDetail: `${njSource} (obs) + ${vcSource} (forecast)`,
    _njwxApplied: true,
    _rtSources: rtSources,
  };
}

// ============================================================
// P2 OVERRIDE — patch forecast with P2 realtime when NJWxNet fails
// WeatherAPI current → temp, feelsLike
// VC current → wind, gust
// ============================================================
function applyP2Override(weatherData, wapiCurrent, vcCurrent) {
  if (!weatherData?.success) return weatherData;
  
  const patched = { ...weatherData.current };
  const sources = [];
  
  // WeatherAPI P2: temp + feelsLike + humidity + precip
  if (wapiCurrent?.success && wapiCurrent.current) {
    const wa = wapiCurrent.current;
    if (wa.temp != null) patched.temp = wa.temp;
    if (wa.feelsLike != null) patched.feelsLike = wa.feelsLike;
    if (wa.humidity != null) patched.humidity = wa.humidity;
    if (wa.precip1hr != null) patched.precip1hr = wa.precip1hr;
    sources.push('WeatherAPI');
  }
  
  // VC P2: wind + gust
  if (vcCurrent?.success && vcCurrent.current) {
    const vc = vcCurrent.current;
    if (vc.windSpeedNum != null) {
      patched.windSpeedNum = vc.windSpeedNum;
      patched.windSpeed = `${vc.windSpeedNum} mph`;
    }
    if (vc.windGust != null) patched.windGust = vc.windGust;
    if (vc.windDir) patched.windDir = vc.windDir;
    sources.push('VC');
  }
  
  // Recompute feelsLike if we have the inputs but no direct feelsLike
  if (patched.feelsLike == null && patched.temp != null) {
    const t = patched.temp;
    const h = patched.humidity ?? weatherData.current?.humidity ?? 50;
    const w = patched.windSpeedNum ?? weatherData.current?.windSpeedNum ?? 0;
    patched.feelsLike = calcFeelsLike(t, h, w);
  }
  
  const sourceStr = sources.length > 0 ? sources.join('+') : 'P2 fallback';
  // Build _rtSources reflecting what P2 actually provided — mirrors mergeNJWxNet pattern
  const p2RtSources = {};
  if (wapiCurrent?.success && wapiCurrent.current) {
    p2RtSources.temp     = 'WeatherAPI';
    p2RtSources.humidity = 'WeatherAPI';
    p2RtSources.precip   = 'WeatherAPI';
  }
  if (vcCurrent?.success && vcCurrent.current) {
    p2RtSources.wind = 'VC';
    p2RtSources.gust = 'VC';
  }
  return {
    ...weatherData,
    current: { ...patched, _rtSources: p2RtSources },
    hourly: weatherData.hourly,
    source: `${sourceStr} + ${weatherData.source}`,
    sourceDetail: `${sourceStr} (obs) + ${weatherData.sourceDetail || weatherData.source} (forecast)`,
    _p2Applied: true,
  };
}

// ============================================================
// REALTIME-ONLY REFRESH — called every 5 min on Day of Play
// Fetches NJWxNet P1 + per-variable P2s only. Never touches forecast.
// ============================================================
async function fetchRealtimeOnly(course, forceNJWxNet = null) {
  const { station: _rtStation } = getNearestNJWxNetStation(course.lat, course.lng);
  const isNJ09 = _rtStation?.stid === 'NJ09'; // NJ09 wind excluded -- 3m sensor, unreliable


  // P1: NJWxNet
  const njwx = await fetchNJWxNet(course, forceNJWxNet).catch(() => ({ success: false }));

  const result = {
    temp: null, humidity: null,
    windSpeedNum: null, windSpeed: null, windGust: null,
    feelsLike: null, precip1hr: null,
    _sources: {},
  };

  if (njwx?.success && njwx.current) {
    const nj = njwx.current;
    result.temp        = nj.temp;
    result.humidity    = nj.humidity;
    result.windSpeedNum = isNJ09 ? null : nj.windSpeedNum;
    result.windSpeed   = isNJ09 ? null : (nj.windSpeedNum != null ? `${nj.windSpeedNum} mph` : null);
    result.windGust    = isNJ09 ? null : nj.windGust;
    result.windSpeedRaw = isNJ09 ? null : nj.windSpeedRaw;
    result.windGustRaw  = isNJ09 ? null : nj.windGustRaw;
    result.precip1hr   = nj.precip1hr;
    result._sources.temp     = 'NJWxNet';
    result._sources.humidity = 'NJWxNet';
    if (!isNJ09) {
      result._sources.wind = 'NJWxNet';
      result._sources.gust = 'NJWxNet';
    }
    result._sources.precip = 'NJWxNet';
  } else {
    // NJWxNet failed entirely — P2 temp from WeatherAPI
    const wapi = await fetchWeatherAPICurrentObs(course).catch(() => ({ success: false }));
    incrementCallCount('weatherapi', course);
    if (wapi?.success && wapi.current) {
      result.temp     = wapi.current.temp;
      result.humidity = wapi.current.humidity ?? null;
      result.precip1hr = wapi.current.precip1hr ?? null;
      result._sources.temp     = 'WeatherAPI';
      result._sources.humidity = 'WeatherAPI';
      result._sources.precip   = 'WeatherAPI';
    }
  }

  // Wind/gust P2: PirateWeather — always for NJ09, or when NJWxNet wind is truly zero (use raw to avoid Math.round false positive)
  const rawWind = result.windSpeedRaw ?? result.windSpeedNum ?? null;
  const rawGust = result.windGustRaw  ?? result.windGust    ?? null;
  // B1 fix S189: trigger P2 when NJ09, null wind, OR sensor stuck at zero
  const needsWindP2 = isNJ09 || result.windSpeedNum == null;
  if (needsWindP2) {
    const pw = await fetchPirateWeather(course).catch(() => ({ success: false }));
    if (pw?.success && pw.current) {
      result.windSpeedNum = pw.current.windSpeedNum ?? 0;
      result.windSpeed    = `${result.windSpeedNum} mph`;
      result.windGust     = pw.current.windGust ?? 0;
      result._sources.wind = isNJ09 ? 'PirateWeather (NJ09 excluded)' : 'PirateWeather (P2)';
      result._sources.gust = result._sources.wind;
      const windP2Reason = isNJ09 ? 'nj09_excluded' : 'njwxnet_wind_null';
      logSystemHealth('p2_activated', course, { variable: 'wind_gust', p1: 'NJWxNet', p2: 'PirateWeather', reason: windP2Reason, pwWind: result.windSpeedNum });
    }
  }

  // Feels-like always calculated internally
  const flTemp = result.temp ?? 65;
  const flHum  = result.humidity ?? 50;
  const flWind = result.windSpeedNum ?? 0;
  result.feelsLike = calcFeelsLike(flTemp, flHum, flWind);
  result._sources.feelsLike = 'calculated';

  return result;
}

// ============================================================
// B.A.D. ENGINE
// ============================================================
function calcBugIndex(temp, humidity, windSpeed, waterMultiplier, past24Rain, sunriseStr, sunsetStr) {
  // Biological gate — peer-reviewed thresholds (Yamana-Eltahir 2013, Rowley & Graham 1968)
  // < 50°F: mosquitoes inactive. > 90°F: above upper flight limit (32°C). > 4.5 mph: sustained flight suppressed (2 m/s).
  // < 42% RH: desiccation stress suppresses activity (Yamana-Eltahir humidity threshold).
  if (temp < 50 || temp > 90 || windSpeed > 4.5 || humidity < 42) {
    return {
      index: 0,
      label: 'None',
      advice: temp < 50 ? 'Too cold for bugs' : temp > 90 ? 'Too hot even for bugs' : windSpeed > 4.5 ? 'Too windy for bugs' : 'Too dry for bugs',
      gateKill: true,
      hatchAlert: false,
    };
  }

  // Bell curve peak at 72°F (Rowley & Graham 1968 — Aedes aegypti peak flight activity)
  // Gaussian: e^(-(temp-72)^2 / (2*sigma^2)), sigma=12 gives ~0.5 weight at 60°F and 90°F edges
  const sigma = 12;
  const tempScore = Math.exp(-Math.pow(temp - 72, 2) / (2 * sigma * sigma)); // 0–1

  // Humidity score: scales linearly from 42% (gate) to 100% RH
  const humScore = Math.max(0, (humidity - 42) / 58); // 0–1

  // Combined activity score × water multiplier
  let rawIndex = tempScore * humScore * 10 * waterMultiplier;

  // S267: Temperature lethargic multiplier — 50-65°F range: mosquitoes present but sluggish
  // At 50°F: 0.3×, at 57°F: ~0.63×, at 65°F: 1.0× (Rowley & Graham — reduced flight below 65°F)
  const tempMult = temp < 65 ? (0.3 + ((temp - 50) / 15) * 0.7) : 1.0;
  rawIndex *= tempMult;

  // S267: Time-of-day multiplier — mosquitoes peak at dusk/dawn, lowest activity midnight-to-pre-dawn
  // Parse sunrise/sunset strings e.g. "5:32 AM" / "8:26 PM"
  const parseTimeStr = (str) => {
    if (!str) return null;
    const m = str.match(/(\d+):(\d+)\s*(AM|PM)/i);
    if (!m) return null;
    let h = parseInt(m[1]); const min = parseInt(m[2]); const ap = m[3].toUpperCase();
    if (ap === 'PM' && h !== 12) h += 12;
    if (ap === 'AM' && h === 12) h = 0;
    return h + min / 60;
  };
  const nowHour = new Date().getHours() + new Date().getMinutes() / 60;
  const srHour = parseTimeStr(sunriseStr);
  const ssHour = parseTimeStr(sunsetStr);
  let timeMult = 0.4; // daytime default
  if (srHour !== null && ssHour !== null) {
    const duskStart = ssHour - 2, duskEnd = ssHour + 2;
    const dawnStart = srHour - 1, dawnEnd = srHour + 2;
    const midnight = 0, preDown = srHour - 1;
    if (nowHour >= duskStart && nowHour <= duskEnd) timeMult = 1.0;        // dusk peak
    else if (nowHour >= dawnStart && nowHour <= dawnEnd) timeMult = 0.8;   // dawn peak
    else if (nowHour > duskEnd && nowHour <= 24) timeMult = 0.5;           // post-dusk evening
    else if (nowHour >= midnight && nowHour < preDown) timeMult = 0.2;     // dead of night
    // else: daytime 0.4 (default)
  }
  rawIndex *= timeMult;

  let index = Math.max(1, Math.min(10, Math.round(rawIndex)));
  // Allow true zero when both multipliers kill activity
  if (rawIndex < 0.5) index = 0;
  let hatchAlert = false;

  if (past24Rain > 0.5 && temp > 75) {
    hatchAlert = true;
    index = Math.min(10, index + 2);
  }

  // Return None label when index drops to 0 after multipliers
  if (index === 0) return { index: 0, label: 'None', advice: 'Minimal conditions for bug activity', gateKill: false, hatchAlert: false };

  let label, advice;
  if (index <= 2) { label = 'Low'; advice = 'Minimal bug activity'; }
  else if (index <= 4) { label = 'Moderate'; advice = 'Some bugs possible, spray optional'; }
  else if (index <= 6) { label = 'Moderate-High'; advice = 'Pack the spray'; }
  else if (index <= 8) { label = 'High'; advice = 'Heavy spray recommended'; }
  else { label = 'Extreme'; advice = 'Swarm conditions — consider postponing'; }

  if (hatchAlert) advice = 'HATCH ALERT: Recent rain + warm temps = high bug activity. ' + advice;

  return { index, label, advice, gateKill: false, hatchAlert };
}

function calcGroundSaturation(past24Rain, snowpack, temp, humidity, terrain, elevation, etData, soilMoisturePct, soilType) {
  // Snowpack override
  if (snowpack > 0) {
    return {
      cartStatus: 'COURSE UNPLAYABLE',
      cartDetail: `Snow on ground (${snowpack}")`,
      topoNote: 'Snowpack > 1.0" — location unplayable',
      effectiveRain: past24Rain,
      dryingTrend: 'N/A — Snow',
      sogginess: 'unplayable',
    };
  }

  // ── VWC-BASED CART STATUS (primary path when soil moisture available) ──────
  // Thresholds by soil tier — USDA SSURGO verified (S233).
  // VWC % from NJ76 (Green Knoll/Neshanic) or Open-Meteo soil_moisture_0_to_1cm × 100.
  const VWC_THRESHOLDS = {
    fast:     { allClear: 35, soft: 40, ninetyDeg: 46 }, // sandy/gravelly: Pebble Creek, High Bridge
    standard: { allClear: 28, soft: 34, ninetyDeg: 40 }, // standard loam: Spooky Brook, Green Knoll, Hominy Hill, Dr. Z
    slow:     { allClear: 25, soft: 30, ninetyDeg: 36 }, // slow silt loam: Quail Brook, Warrenbrook, Neshanic, Fox Hollow, Architects
  };
  const tier = soilType && VWC_THRESHOLDS[soilType] ? soilType : 'standard';
  const thresh = VWC_THRESHOLDS[tier];

  if (soilMoisturePct != null) {
    let cartStatus, cartDetail, sogginess;
    if (soilMoisturePct > thresh.ninetyDeg) {
      cartStatus = 'Cart Path Only';
      cartDetail = `Soil VWC ${soilMoisturePct}% — waterlogged (>${thresh.ninetyDeg}% ${tier} threshold)`;
      sogginess = 'soggy';
    } else if (soilMoisturePct > thresh.soft) {
      cartStatus = '90-Degree Rule Likely';
      cartDetail = `Soil VWC ${soilMoisturePct}% — soft conditions`;
      sogginess = 'soft';
    } else if (soilMoisturePct > thresh.allClear) {
      cartStatus = 'Soft — Carts Likely Allowed';
      cartDetail = `Soil VWC ${soilMoisturePct}% — slightly moist`;
      sogginess = 'soft';
    } else {
      cartStatus = 'All Clear';
      cartDetail = `Soil VWC ${soilMoisturePct}% — firm and dry`;
      sogginess = 'dry';
    }
    return { cartStatus, cartDetail, topoNote: null, effectiveRain: past24Rain, dryingTrend: 'Live soil data', sogginess, solarBaking: false };
  }

  // ── RAINFALL FALLBACK (when soil moisture unavailable) ───────────────────
  let effectiveRain = past24Rain;
  let topoNote = null;

  if (terrain === 'flat' && elevation < 100) {
    effectiveRain = past24Rain * 2;
    topoNote = `Flat course (${elevation}ft) — water pools. ${past24Rain}" treated as ${effectiveRain.toFixed(2)}" impact.`;
  } else if (terrain === 'elevated') {
    effectiveRain = Math.max(0, past24Rain - 0.15);
    topoNote = 'Elevated terrain — 12-hour drying credit applied.';
  }

  let solarBaking = false;
  let solarBakingSource = null;

  // ET math: etInches = todayET(mm) / 25.4. netMoisture = past24Rain - etInches.
  if (etData && etData.success && etData.todayET != null) {
    const etInches = etData.todayET / 25.4;
    const netMoisture = Math.max(0, past24Rain - etInches);
    if (etData.solarBakingActive) {
      effectiveRain = netMoisture * 0.5;
      solarBaking = true;
      solarBakingSource = etData.solarBakingStrong ? 'ET ' + etData.todayET.toFixed(1) + 'mm/day (extreme)' : 'ET ' + etData.todayET.toFixed(1) + 'mm/day';
      topoNote = (topoNote || '') + ` Solar baking active (ET=${etData.todayET.toFixed(1)}mm, net=${netMoisture.toFixed(2)}") — soggy duration reduced 50%.`;
    } else {
      effectiveRain = netMoisture;
      topoNote = (topoNote || '') + ` ET ${etData.todayET.toFixed(1)}mm removed — net rain ${netMoisture.toFixed(2)}".`;
    }
  } else if (!etData || !etData.success) {
    // Fallback heuristic if ET fetch failed
    if (temp > 85 && humidity < 40) {
      effectiveRain *= 0.5;
      solarBaking = true;
      solarBakingSource = 'heuristic (hot+dry)';
      topoNote = (topoNote || '') + ' Solar baking active — soggy duration reduced 50%.';
    }
  }

  let dryingTrend = 'GOOD';
  if (effectiveRain > 1) dryingTrend = 'POOR';
  else if (effectiveRain > 0.5) dryingTrend = 'FAIR';
  else if (solarBaking) dryingTrend = 'FAST (Solar baking active)';

  let cartStatus = 'All Clear';
  let cartDetail = 'Carts should be allowed on fairways';
  let sogginess = 'dry';

  if (effectiveRain > 0.8) {
    cartStatus = 'Cart Path Only';
    cartDetail = `Ground is soggy from ${past24Rain}" rain`;
    sogginess = 'soggy';
  } else if (effectiveRain > 0.4) {
    cartStatus = '90-Degree Rule Likely';
    cartDetail = `${past24Rain}" rain — soft conditions`;
    sogginess = 'soft';
  } else if (effectiveRain > 0.1) {
    cartStatus = 'Soft — Carts Likely Allowed';
    cartDetail = `Minimal rain impact (${past24Rain}")`;
    sogginess = 'soft';
  }

  return { cartStatus, cartDetail, topoNote, effectiveRain, dryingTrend, sogginess, solarBaking };
}

function calcFrostWarning(hourly, snowpack) {
  if (snowpack > 0) return { frost: false, reason: 'Snowpack override' };
  for (let i = 0; i < hourly.length - 3; i++) {
    const h = hourly[i];
    if (h.time.getHours() === 3 && h.humidity > 70) {
      for (let j = i + 1; j < Math.min(i + 5, hourly.length); j++) {
        if (hourly[j].time.getHours() === 6 && hourly[j].temp <= 36) {
          return { frost: true, reason: `Frost likely — 3AM humidity ${h.humidity}% + 6AM temp ${hourly[j].temp}°F` };
        }
      }
    }
  }
  return { frost: false, reason: null };
}

// ============================================================
// CURRENT WEATHER BUILDER
// Play window has been removed. The BAD engine now evaluates
// current real-time conditions from weatherData.current directly.
// Morning/Afternoon Go/No-Go evaluation happens in the UI
// components from the hourly array — not here.
// This function is a pass-through kept for call-site compatibility.
// ============================================================
function buildWindowWeather(weatherData, profile) {
  return weatherData;
}

function runBADEngine(weatherData, course, profile, waterDensity, etData, soilMoisturePct) {
  if (!weatherData || !weatherData.success) {
    return { status: 'UNKNOWN', reasons: ['No weather data available'], statusReason: 'No weather data available' };
  }

  const cur = weatherData.current;
  const ground = weatherData.ground;
  const temp = cur.temp || 0;
  const feelsLike = cur.feelsLike != null ? cur.feelsLike : temp; // feelsLike fallback to temp
  const effectiveTemp = feelsLike ?? temp; // S48 LOCKED: use feels-like   // actual temp only — feels-like deferred until source validated by benchmarks
  const effectiveCold = feelsLike ?? temp; // S48 LOCKED: use feels-like   // actual temp only — feels-like deferred until source validated by benchmarks
  const humidity = cur.humidity || 50;
  const windSpeed = cur.windSpeedNum || 0;
  const windGust = cur.windGust || windSpeed;
  const past24Rain = ground.past24Rain || 0;
  const snowpack = ground.snowpack || 0;
  const precipChance = cur.precipChance || 0;
  const conditions = cur.conditions || cur.shortForecast || '';
  const feelsDiff = Math.abs(feelsLike - temp) >= 2;

  const bug = calcBugIndex(temp, humidity, windSpeed, (waterDensity?.multiplier != null ? waterDensity.multiplier : 1), past24Rain, weatherData.sunriseToday, weatherData.sunsetToday);
  const groundResult = calcGroundSaturation(past24Rain, snowpack, temp, humidity, course.terrain, course.elevation, etData, soilMoisturePct ?? null, course.soilType ?? 'standard');
  const frost = calcFrostWarning(weatherData.hourly || [], snowpack);

  // ── STATUS CALCULATION — S45 LOCKED THRESHOLDS ─────────────
  // Base status from canonical function
  const baseStatus = getHourStatus(cur, profile);
  
  // Build reasons arrays
  const noGoReasons = [];
  const strCauReasons = [];
  const cautionReasons = [];
  const condLower = conditions.toLowerCase();
  
  // NO-GO triggers
  if (snowpack > 0) noGoReasons.push(`Snow on ground (${snowpack}") — course unplayable`);
  if (/thunder|lightning/.test(condLower)) noGoReasons.push('Active thunderstorms — lightning danger');
  if (/heavy rain|freezing rain|sleet|ice pellet|\bsnow\b|blizzard/.test(condLower) && !/blowing snow|drifting snow|dust storm/.test(condLower)) noGoReasons.push(`${conditions} — unplayable`);
  if (precipChance > PRECIP_NOGO) noGoReasons.push(`${precipChance}% rain chance — no-go threshold`);
  if (windSpeed >= WIND_NOGO) noGoReasons.push(`Wind ${windSpeed} mph — no-go threshold`);
  if (effectiveCold < profile.minTemp - 5) noGoReasons.push(`Too cold — feels like ${Math.round(effectiveCold)}°F`);
  if (effectiveTemp > profile.maxTemp + 5) noGoReasons.push(`Too hot — feels like ${Math.round(effectiveTemp)}°F`);
  
  // STRONG CAUTION triggers
  if (precipChance > PRECIP_STR_CAU && precipChance <= PRECIP_NOGO) strCauReasons.push(`${precipChance}% rain chance`);
  if (windSpeed > WIND_STR_CAU && windSpeed <= WIND_NOGO) strCauReasons.push(`Wind ${windSpeed} mph — strong`);
  
  // CAUTION triggers — weather
  if (precipChance > PRECIP_CAUTION && precipChance <= PRECIP_STR_CAU) cautionReasons.push(`${precipChance}% rain chance`);
  if (/\bmist\b|\bfog\b|\bhaze\b/.test(condLower)) cautionReasons.push(`${conditions} — reduced visibility`);
  if (windSpeed > WIND_CAUTION && windSpeed <= WIND_STR_CAU) cautionReasons.push(`Wind ${windSpeed} mph — breezy`);
  // Gusts: informational only — color coded but do NOT trigger Go/No-Go (S92 decision per NWS standard)
  if (effectiveTemp > profile.maxTemp && effectiveTemp <= profile.maxTemp + 5) cautionReasons.push(`Feels like ${Math.round(effectiveTemp)}°F — over hot limit`);
  if (effectiveCold < profile.minTemp && effectiveCold >= profile.minTemp - 5) cautionReasons.push(`Feels like ${Math.round(effectiveCold)}°F — under cold limit`);
  
  // CAUTION triggers — extra checks (bugs, frost, ground, visibility)
  if (groundResult.sogginess === 'soggy') cautionReasons.push(`${groundResult.cartStatus} — ${past24Rain}" rain in last 24h`);
  else if (groundResult.sogginess === 'soft') cautionReasons.push(`Soft conditions — ${past24Rain}" recent rain, ${groundResult.cartStatus}`);
  if (bug.index > 6) cautionReasons.push(`High bug activity (${bug.index}/10) — ${bug.advice}`);
  if (frost.frost) cautionReasons.push(frost.reason);
  if (weatherData.current.visibility && weatherData.current.visibility < 3) cautionReasons.push(`Low visibility (${weatherData.current.visibility} mi)`);

  let status, reasons, statusReason;
  if (noGoReasons.length > 0) {
    status = 'NO-GO'; reasons = noGoReasons; statusReason = noGoReasons.join(' · ');
  } else if (strCauReasons.length > 0) {
    status = 'STRONG CAUTION'; reasons = strCauReasons.concat(cautionReasons); statusReason = strCauReasons.concat(cautionReasons).join(' · ');
  } else if (cautionReasons.length > 0) {
    status = 'CAUTION'; reasons = cautionReasons; statusReason = cautionReasons.join(' · ');
  } else {
    status = 'GO';
    const details = [];
    details.push(`${temp}°F`);
    if (windSpeed <= 10) details.push('light wind');
    else details.push(`${windSpeed} mph wind`);
    if (bug.index <= 3) details.push('low bugs');
    if (past24Rain < 0.1) details.push('dry conditions');
    statusReason = `Conditions are favorable — ${details.join(', ')}`;
    reasons = [statusReason];
  }

  return {
    status, reasons, statusReason,
    bug, ground: groundResult, frost,
    waterDensity: waterDensity || { multiplier: 1, label: 'Not queried' },
  };
}

// ============================================================
// HOUR LABEL HELPER
// ============================================================
function hourLabel(h) {
  if (h === 0) return '12 AM';
  if (h < 12) return `${h} AM`;
  if (h === 12) return '12 PM';
  return `${h - 12} PM`;
}

// ============================================================
// 15-MINUTE INTERVAL INTERPOLATION
// Visual Crossing gives hourly. Interpolate linearly between
// hourly points to produce 15-min rows for the drilldown table.
// ============================================================
function interpolateToFifteenMin(hourlyArray) {
  const result = [];
  if (!hourlyArray || hourlyArray.length === 0) return result;

  for (let i = 0; i < hourlyArray.length - 1; i++) {
    const a = hourlyArray[i];
    const b = hourlyArray[i + 1];
    for (let q = 0; q < 4; q++) {
      const frac = q / 4;
      const t = new Date(a.time.getTime() + q * 15 * 60000);
      const lerp = (av, bv) => av != null && bv != null ? Math.round((av + (bv - av) * frac) * 10) / 10 : av;
      result.push({
        time: t,
        temp: lerp(a.temp, b.temp),
        humidity: lerp(a.humidity, b.humidity),
        windSpeedNum: lerp(a.windSpeedNum, b.windSpeedNum),
        windGust: lerp(a.windGust, b.windGust),
        windDir: a.windDir,
        precipChance: lerp(a.precipChance, b.precipChance),
        precip: lerp(a.precip, b.precip),
        conditions: a.conditions,
        uvIndex: lerp(a.uvIndex, b.uvIndex),
        isDaytime: a.isDaytime,
        isInterpolated: q > 0,
      });
    }
  }
  // Add the final hour point
  const last = hourlyArray[hourlyArray.length - 1];
  result.push({ ...last, isInterpolated: false });
  return result;
}

// ============================================================
// CANONICAL GO/NO-GO — S45 LOCKED SPEC
// Single source of truth for all status calculations
// ============================================================
function getHourStatus(hour, profile) {
  const temp = hour.temp || 0;
  const feelsLike = hour.feelsLike != null ? hour.feelsLike : temp;
  const wind = hour.windSpeedNum || 0;
  const gust = hour.windGust || 0;
  const precip = hour.precipChance || 0;
  const cond = (hour.conditions || hour.shortForecast || '').toLowerCase();

  // Collect ALL triggers at ALL severity levels
  const noGoTriggers = [];
  const strCauTriggers = [];
  const cauTriggers = [];
  let isLightning = false;

  // ── NO-GO triggers ──
  if (/thunder|lightning/.test(cond)) { noGoTriggers.push('Thunderstorms'); isLightning = true; }
  if (/freezing rain|freezing drizzle|sleet|ice pellet|\bsnow\b|blizzard/.test(cond) && !/blowing snow|drifting snow|dust storm/.test(cond)) noGoTriggers.push(hour.conditions || 'Severe weather');
  if (gust >= 40) noGoTriggers.push(`Gust ${gust} mph`);
  if (precip >= PRECIP_NOGO) noGoTriggers.push(`Precip ${precip}%`);
  if (wind >= WIND_NOGO) noGoTriggers.push(`Wind ${wind} mph`);
  if (feelsLike < profile.minTemp - 5) noGoTriggers.push(`Feels ${Math.round(feelsLike)}° (limit ${profile.minTemp}°)`);
  if (feelsLike > profile.maxTemp + 5) noGoTriggers.push(`Feels ${Math.round(feelsLike)}° (limit ${profile.maxTemp}°)`);

  // ── STRONG CAUTION triggers ──
  if (precip >= PRECIP_STR_CAU && precip < PRECIP_NOGO) strCauTriggers.push(`Precip ${precip}%`);
  if (wind >= WIND_STR_CAU && wind < WIND_NOGO) strCauTriggers.push(`Wind ${wind} mph`);

  // ── CAUTION triggers ──
  if (precip >= PRECIP_CAUTION && precip < PRECIP_STR_CAU) cauTriggers.push(`Precip ${precip}%`);
  if (wind >= WIND_CAUTION && wind < WIND_STR_CAU) cauTriggers.push(`Wind ${wind} mph`);
  if (feelsLike < profile.minTemp && feelsLike >= profile.minTemp - 5) cauTriggers.push(`Feels ${Math.round(feelsLike)}° (limit ${profile.minTemp}°)`);
  if (feelsLike > profile.maxTemp && feelsLike <= profile.maxTemp + 5) cauTriggers.push(`Feels ${Math.round(feelsLike)}° (limit ${profile.maxTemp}°)`);
  // Rain/shower/drizzle: defers to precip % thresholds (S98 locked). Do NOT trigger CAUTION from condition text.
  if (/\bmist\b|\bfog\b|\bhaze\b/.test(cond)) cauTriggers.push('Fog');

  // ── Determine worst status ──
  let status, color, icon;
  if (noGoTriggers.length > 0) {
    status = 'NO-GO'; color = STATUS_COLORS['NO-GO'].color; icon = isLightning ? 'lightning' : 'nogo';
  } else if (strCauTriggers.length > 0) {
    status = 'STRONG CAUTION'; color = STATUS_COLORS['STRONG CAUTION'].color; icon = 'strong-caution';
  } else if (cauTriggers.length > 0) {
    status = 'CAUTION'; color = STATUS_COLORS['CAUTION'].color; icon = 'caution';
  } else {
    status = 'GO'; color = STATUS_COLORS['GO'].color; icon = 'go';
  }

  // Build triggers object (all levels) for display
  const triggers = {};
  if (noGoTriggers.length) triggers['NO-GO'] = noGoTriggers;
  if (strCauTriggers.length) triggers['STRONG CAUTION'] = strCauTriggers;
  if (cauTriggers.length) triggers['CAUTION'] = cauTriggers;

  // Backward-compatible reason string (worst-level triggers joined)
  const worstTriggers = noGoTriggers.length ? noGoTriggers : strCauTriggers.length ? strCauTriggers : cauTriggers;
  const reason = worstTriggers.length ? worstTriggers[0] : null;

  return { status, color, reason, icon, triggers };
}

// Wrapper for rowPlayability — returns { label, color, reason } for row display
function rowPlayability(row, past24Rain, course, profile) {
  const result = getHourStatus(row, profile);
  const label = result.icon === 'lightning' ? 'NO-GO' : result.status;
  const isLightning = result.icon === 'lightning';
  return { label, color: result.color, reason: result.reason, isLightning };
}

// Get worst status from array of hours — for window evaluation
// S270: on a status tie between hours, compare actual severity instead of
// keeping whichever hour was encountered first. Severity is judged ONLY by
// the category that actually produced that hour's worst-level trigger (per
// getHourStatus's own triggers object) — not by re-scanning all raw fields,
// which would let an irrelevant background value (e.g. a stray 5% precip on
// a wind-driven NO-GO hour) outrank the real cause. Category rank matches
// the documented NO-GO/CAUTION check order (Ch09 §9.4): conditions text
// (severe weather/lightning) > gust > precip > wind > feelsLike deviation.
// Ported verbatim from Golf S270 fix - same bug, identical code, same fix.
function _hourSeverityRank(hour, profile, result) {
  const worstLevel = result.status;
  const triggerTexts = (result.triggers && result.triggers[worstLevel]) || [];
  const gust = hour.windGust || 0;
  const precip = hour.precipChance || 0;
  const wind = hour.windSpeedNum || 0;
  const temp = hour.temp || 0;
  const feelsLike = hour.feelsLike != null ? hour.feelsLike : temp;
  const tempDeviation = feelsLike > profile.maxTemp ? feelsLike - profile.maxTemp
    : feelsLike < profile.minTemp ? profile.minTemp - feelsLike
    : 0;
  // Only consider a category if THIS hour's own triggers at its worst level
  // actually include that category's text — prevents unrelated fields from
  // contaminating the score.
  const hasTrigger = (prefix) => triggerTexts.some(t => t.startsWith(prefix));
  if (triggerTexts.some(t => t === 'Thunderstorms' || (hour.conditions && t === hour.conditions))) return 500;
  if (hasTrigger('Gust'))   return 400 + gust;
  if (hasTrigger('Precip')) return 300 + precip;
  if (hasTrigger('Wind'))   return 200 + wind;
  if (hasTrigger('Feels'))  return 100 + tempDeviation;
  if (hasTrigger('Fog'))    return 50;
  return 0;
}

function getWindowStatus(hours, profile) {
  if (!hours || !hours.length) return { status: 'UNKNOWN', color: STATUS_COLORS['UNKNOWN'].color, reason: 'No data' };
  
  const statusPriority = { 'NO-GO': 4, 'STRONG CAUTION': 3, 'CAUTION': 2, 'GO': 1 };
  let worst = { status: 'GO', color: STATUS_COLORS['GO'].color, reason: null, priority: 1, severity: -1 };
  
  for (const hour of hours) {
    const result = getHourStatus(hour, profile);
    const priority = statusPriority[result.status] || 0;
    const severity = _hourSeverityRank(hour, profile, result);
    if (priority > worst.priority || (priority === worst.priority && severity > worst.severity)) {
      worst = { ...result, priority, severity };
    }
  }
  
  return { status: worst.status, color: worst.color, reason: worst.reason };
}

// Get BEST status from array of hours — for day-card summary semantic.
// A golfer schedules a 4-5hr round inside the day; if ANY hour is playable,
// the day is playable (you tee off then). Worst-hour semantic punishes whole
// days because of dawn/dusk hours nobody plays in. Used for 7-day forecast cards
// where the user is asking "should I plan to play that day?" — not "is every
// minute of 8AM-8PM playable?". RIGHT NOW + calendar tiles still use worst.

// ============================================================
// MAIN APP COMPONENT

// ============================================================
// MAIN APP COMPONENT — v3.0 Three-Mode Architecture
// ============================================================
// ── S85 STATUS ICON — custom SVG circles (replaces all status emoji) ──
const StatusIcon = ({ status, size = 20 }) => {
  const r = size / 2;
  // S98 LOCKED icon designs
  const cfgs = {
    GO: {
      shape: <circle cx={r} cy={r} r={r - 1} fill="#16A34A" />,
      sym: <path d={`M${r*0.52},${r*1.0} L${r*0.82},${r*1.32} L${r*1.48},${r*0.62}`} stroke="#fff" strokeWidth={size * 0.13} fill="none" strokeLinecap="round" strokeLinejoin="round" />,
    },
    CAUTION: {
      shape: <circle cx={r} cy={r} r={r - 1} fill="#FDE047" />,
      sym: <text x={r} y={r + 1} textAnchor="middle" dominantBaseline="central" fill="#000" fontSize={size * 0.62} fontWeight="900" fontFamily="Arial,sans-serif">!</text>,
    },
    'STRONG CAUTION': {
      shape: <circle cx={r} cy={r} r={r - 1} fill="#EA580C" />,
      sym: <text x={r} y={r + 1} textAnchor="middle" dominantBaseline="central" fill="#fff" fontSize={size * 0.48} fontWeight="900" fontFamily="Arial,sans-serif">!!</text>,
    },
    'NO-GO': {
      // Octagon (stop sign shape)
      shape: (() => {
        const s = size; const o = s * 0.29;
        const pts = `${o},0 ${s-o},0 ${s},${o} ${s},${s-o} ${s-o},${s} ${o},${s} 0,${s-o} 0,${o}`;
        return <polygon points={pts} fill="#DC2626" />;
      })(),
      sym: <text x={r} y={r + 1} textAnchor="middle" dominantBaseline="central" fill="#fff" fontSize={size * 0.28} fontWeight="900" fontFamily="Arial,sans-serif" letterSpacing="0">STOP</text>,
    },
  };
  const cfg = cfgs[status] || cfgs.GO;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ flexShrink: 0, verticalAlign: 'middle' }}>
      {cfg.shape}{cfg.sym}
    </svg>
  );
};

  const fmtDateStats = (d) => {
    if (!d) return '';
    try {
      // Apps Script returns e.g. "2026-04-14T03:18:00.000Z" or "2026-04-14"
      return new Date(String(d).slice(0, 10) + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch { return String(d).slice(0, 10); }
  };

  const fmtTimeStats = (t) => {
    if (!t) return '';
    try {
      const s = String(t).trim();
      if (/[AP]M$/i.test(s)) return s;
      const match = s.match(/T(\d{2}):(\d{2})/);
      if (match) {
        let h = parseInt(match[1]), m = parseInt(match[2]);
        const ampm = h >= 12 ? 'PM' : 'AM';
        h = h % 12 || 12;
        return `${h}:${String(m).padStart(2, '0')} ${ampm}`;
      }
      const parts = s.split(':').map(Number);
      if (parts.length >= 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
        let h = parts[0], m = parts[1];
        const ampm = h >= 12 ? 'PM' : 'AM';
        h = h % 12 || 12;
        return `${h}:${String(m).padStart(2, '0')} ${ampm}`;
      }
    } catch {}
    return '';
  };

  // Must be defined before ExpandedRoundCard — forward reference fix (WHITE-SCREEN-REVIEW)
  const getGolfNotationForBadge = (score, par) => {
    if (!score) return { color: COLOR_MUTED, shape: null };
    const diff = score - par;
    if (diff <= -2) return { color: '#F1373D', shape: 'double-circle' };
    if (diff === -1) return { color: '#F1373D', shape: 'circle' };
    if (diff === 0)  return { color: '#FFFFFF', shape: 'none' };
    if (diff === 1)  return { color: '#3B82F6', shape: 'square' };
    if (diff === 2)  return { color: '#3B82F6', shape: 'double-square' };
    if (diff === 3)  return { color: '#FF6B35', shape: 'high' };
    if (diff >= 4 && score < par * 2) return { color: '#DC2626', shape: 'high' };
    if (score >= par * 2) return { color: COLOR_RED, shape: 'snowman' };
    return { color: COLOR_RED, shape: 'high' };
  };

  const StatsNotationBadge = ({ score, par, highlight }) => {
    const n = getGolfNotationForBadge(score, par);
    if (!score) return <span style={{ fontSize: '13px', color: COLOR_MUTED, display: 'block', textAlign: 'center' }}>—</span>;
    const base = {
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      fontSize: '13px', fontWeight: 800, color: n.color, width: 28, height: 28,
      ...(highlight ? { background: '#7f0000', borderRadius: 4, border: `2px solid ${COLOR_RED}` } : {})
    };
    if (n.shape === 'circle') return <span style={{ ...base, border: highlight ? `2px solid ${COLOR_RED}` : `2px solid ${n.color}`, borderRadius: '50%' }}>{score}</span>;
    if (n.shape === 'double-circle') return <span style={{ ...base, border: highlight ? `2px solid ${COLOR_RED}` : `3px double ${n.color}`, borderRadius: '50%' }}>{score}</span>;
    if (n.shape === 'square') return <span style={{ ...base, border: highlight ? `2px solid ${COLOR_RED}` : `2px solid ${n.color}`, borderRadius: 3 }}>{score}</span>;
    if (n.shape === 'double-square') return <span style={{ ...base, border: highlight ? `2px solid ${COLOR_RED}` : `3px double ${n.color}`, borderRadius: 3 }}>{score}</span>;
    if (n.shape === 'snowman') return (
      <span style={{ ...base, fontSize: '11px', display: 'inline-flex', alignItems: 'center', gap: 1 }}>
        <svg width="13" height="18" viewBox="0 0 13 18" fill="none" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
          <circle cx="6.5" cy="4.8" r="3.8" fill="#ffffff"/>
          <circle cx="6.5" cy="13.5" r="4" fill="#ffffff"/>
          <circle cx="5.2" cy="4.1" r="0.7" fill="#111"/>
          <circle cx="7.8" cy="4.1" r="0.7" fill="#111"/>
          <path d="M5.4 5.8 Q6.5 6.8 7.6 5.8" stroke="#111" strokeWidth="0.9" fill="none" strokeLinecap="round"/>
        </svg>{score}
      </span>
    );
    return <span style={{ ...base }}>{score}</span>;
  };

  // Shared weather snapshot renderer — used by all 4 round-card render sites.
  // Handles both snapshot schemas (conditions||condition, precipAccum||precipChance)
  // so old rounds still display correctly. No Go/No-Go — snapshots are actuals, not forecasts.
  const WeatherSnapshotLine = ({ ws, variant }) => {
    if (!ws) return null;
    const conditions = ws.conditions || ws.condition;
    const hasAny = conditions || ws.feelsLike != null || ws.wind != null || ws.gust != null
                   || ws.humidity != null || ws.precipAccum != null || ws.precipChance != null;
    if (!hasAny) return null;
    const wrap = variant === 'compact'
      ? { fontSize: 11, color: COLOR_MUTED, display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', marginBottom: 6, paddingTop: 6, borderTop: `1px solid ${COLOR_DARK_GREEN}` }
      : { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '3px 10px', marginBottom: 6, paddingBottom: 6, borderBottom: `1px solid ${COLOR_DARK_GREEN}` };
    const item = variant === 'compact' ? {} : { fontSize: 12, color: COLOR_MUTED };
    const itemBright = variant === 'compact' ? {} : { fontSize: 12, color: COLOR_OFF_WHITE };
    const sourceLabel = ws.dataSource === 'njwxnet' ? `NJWxNet${ws.stid ? ' ' + ws.stid : ''}`
                      : ws.dataSource === 'njwxnet+forecast' ? `NJWxNet${ws.stid ? ' ' + ws.stid : ''} + forecast wind`
                      : ws.dataSource === 'forecast' ? 'Forecast only'
                      : ws.dataSource === 'forecast_fallback' ? 'NJWxNet failed — forecast'
                      : null;
    return (
      <div style={wrap}>
        {conditions && <span style={itemBright}>{conditions}</span>}
        {ws.feelsLike != null && <span style={item}>Feels {Math.round(ws.feelsLike)}°</span>}
        {ws.wind != null && <span style={item}>Wind {ws.wind}mph</span>}
        {ws.gust != null && <span style={item}>Gust {ws.gust}mph</span>}
        {ws.humidity != null && <span style={item}>Hum {Math.round(ws.humidity)}%</span>}
        {ws.precipAccum != null ? <span style={item}>Rain {ws.precipAccum}"</span>
          : ws.precipChance != null ? <span style={item}>Precip {ws.precipChance}%</span> : null}
        {sourceLabel && <span style={{ fontSize: variant === 'compact' ? 10 : 11, color: COLOR_MUTED, opacity: 0.7, marginLeft: 'auto' }}>· {sourceLabel}</span>}
      </div>
    );
  };

  const ExpandedRoundCard = ({ round, worstHoleIndex }) => {
    const [expandedPhoto, setExpandedPhoto] = React.useState(null);
    const [expandedRoundId, setExpandedRoundId] = React.useState(null);
    const pars = round.pars || Array(18).fill(4);
    const front9 = (round.scores || []).slice(0, 9).reduce((s, v) => s + (v || 0), 0);
    const back9  = (round.scores || []).slice(9, 18).reduce((s, v) => s + (v || 0), 0);
    // S166: removed dead ws / hasWeather / goNoGoColor — orphaned by S161 when
    // WeatherSnapshotLine was lifted out of ExpandedRoundCard. Per S164 rule,
    // dead code from feature removal must be deleted in same commit, not left
    // behind for a Phase 1.1 cleanup.

    return (
      <div style={{ ...CARD_STYLE, padding: '14px', marginTop: -1, borderTopLeftRadius: 0, borderTopRightRadius: 0, borderTop: 'none' }}>
        {/* Front 9 */}
        <div style={{ fontSize: '11px', color: COLOR_GOLD, fontWeight: 700, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 1 }}>Front 9 — {front9 || '—'}</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(9, 1fr)', gap: 4, marginBottom: 14 }}>
          {[0,1,2,3,4,5,6,7,8].map(i => (
            <div key={i} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '10px', color: COLOR_MUTED, marginBottom: 2 }}>{i + 1}</div>
              <div style={{ fontSize: '9px', color: COLOR_MUTED, marginBottom: 3 }}>p{pars[i]}</div>
              <StatsNotationBadge score={round.scores?.[i]} par={pars[i]} highlight={worstHoleIndex === i} />
            </div>
          ))}
        </div>
        {/* Back 9 */}
        <div style={{ fontSize: '11px', color: COLOR_GOLD, fontWeight: 700, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 1 }}>Back 9 — {back9 || '—'}</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(9, 1fr)', gap: 4, marginBottom: 14 }}>
          {[9,10,11,12,13,14,15,16,17].map(i => (
            <div key={i} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '10px', color: COLOR_MUTED, marginBottom: 2 }}>{i + 1}</div>
              <div style={{ fontSize: '9px', color: COLOR_MUTED, marginBottom: 3 }}>p{pars[i]}</div>
              <StatsNotationBadge score={round.scores?.[i]} par={pars[i]} highlight={worstHoleIndex === i} />
            </div>
          ))}
        </div>
        {/* Subtotals */}
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: COLOR_MUTED, padding: '8px 0', borderTop: `1px solid ${COLOR_DARK_GREEN}`, marginBottom: 8 }}>
          <span>Out: <span style={{ color: COLOR_GOLD, fontWeight: 700 }}>{front9}</span></span>
          <span>In: <span style={{ color: COLOR_GOLD, fontWeight: 700 }}>{back9}</span></span>
          <span>Total: <span style={{ color: COLOR_GOLD, fontWeight: 700 }}>{round.total}</span></span>
        </div>
        {/* Tee + Finish times */}
        {(round.teeTime || round.finishTime) && (
          <div style={{ fontSize: '13px', color: COLOR_MUTED, marginBottom: 8, display: 'flex', gap: 16 }}>
            {round.teeTime && <span>Tee: {fmtTimeStats(round.teeTime)}</span>}
            {round.finishTime && <span>Finish: {fmtTimeStats(round.finishTime)}</span>}
          </div>
        )}
        {/* Photo */}
        {round.photoUrl && (
          <div onClick={() => setExpandedPhoto(round.photoUrl)} style={{ cursor: 'pointer', position: 'relative', marginBottom: 10 }}>
            <img src={round.photoUrl} alt="Round group" style={{ width: '100%', height: 100, borderRadius: 8, objectFit: 'cover', display: 'block' }} />
            <div style={{ position: 'absolute', bottom: 6, right: 8, background: 'rgba(0,0,0,0.6)', padding: '3px 7px', borderRadius: 4, fontSize: 11, color: '#fff' }}>Tap to expand</div>
          </div>
        )}
        {/* S161 Report 2 fix: WeatherSnapshotLine REMOVED from ExpandedRoundCard.
            Weather now lives only on the parent card header (RoundSummaryRow / inline card).
            Showing it twice was confusing per JZ device test. */}
        {/* Tier 2 — notes */}
        {round.comment && (
          <div style={{ fontSize: '13px', color: COLOR_OFF_WHITE, padding: '8px 10px', background: COLOR_DARKEST, borderRadius: 6, fontStyle: 'italic', lineHeight: 1.4, marginBottom: 6 }}>
            {round.comment}
          </div>
        )}
        {/* Tier 2 — Truth Chain flags */}
        {round.truthChain && round.truthChain.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
            {round.truthChain.map((flag, i) => (
              <span key={i} style={{ background: '#2a2000', border: `1px solid ${COLOR_GOLD}`, borderRadius: 12, padding: '3px 10px', fontSize: 11, color: COLOR_GOLD, fontWeight: 600 }}>{flag}</span>
            ))}
          </div>
        )}
        {/* Full-screen photo overlay */}
        {expandedPhoto && (
          <div onClick={() => setExpandedPhoto(null)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.95)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <button onClick={() => setExpandedPhoto(null)} style={{ position: 'absolute', top: 16, right: 16, background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '50%', width: 40, height: 40, fontSize: 20, color: '#fff', cursor: 'pointer' }}>✕</button>
            <img src={expandedPhoto} alt="Full size" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 8 }} />
          </div>
        )}
      </div>
    );
  };

const RoundSummaryRow = ({ round, isExpanded, onToggle, borderColor }) => {
  // Compute par from pars array if available, fallback to course par
  const parsArray = Array.isArray(round.pars) ? round.pars
                  : (typeof round.pars === 'string' ? round.pars.split(',').map(Number) : []);
  const roundPar = parsArray.length > 0 ? parsArray.reduce((s, p) => s + (Number(p) || 0), 0) : null;
  const total = Number(round.total) || 0;
  const diff = roundPar != null ? total - roundPar : null;

  // Differential display + color (golf standard)
  let diffText, diffColor;
  if (diff < 0)      { diffText = String(diff); diffColor = '#F1373D'; }
  else if (diff > 0) { diffText = '+' + diff;   diffColor = '#3B82F6'; }
  else               { diffText = 'E';           diffColor = '#22C55E'; }

  // Duration "Xh Ym"
  let durationText = '';
  if (round.teeTime && round.finishTime) {
    // Both times stored as 12-hour with AM/PM (per S143 to12Hour conversion)
    // Need to parse back to compute elapsed minutes
    const parseTime = (t) => {
      const s = String(t).trim();
      const m = s.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)?$/i);
      if (!m) return null;
      let h = parseInt(m[1], 10);
      const min = parseInt(m[2], 10);
      const ampm = (m[3] || '').toUpperCase();
      if (ampm === 'PM' && h !== 12) h += 12;
      else if (ampm === 'AM' && h === 12) h = 0;
      return h * 60 + min;
    };
    const teeMin = parseTime(round.teeTime);
    const finMin = parseTime(round.finishTime);
    if (teeMin != null && finMin != null && finMin >= teeMin) {
      const total = finMin - teeMin;
      const h = Math.floor(total / 60);
      const m = total % 60;
      durationText = `${h}h ${m}m`;
    }
  }

  // Player list (max 4, no wrap, ellipsis handled by CSS)
  const playerList = (round.players || []).filter(Boolean);
  const playersText = Array.isArray(playerList) ? playerList.join(' · ') : String(playerList);

  // Tee → Finish display
  const fmtT = (t) => {
    if (!t) return '';
    const s = String(t).trim();
    if (/[AP]M$/i.test(s)) return s; // already 12-hour
    const parts = s.split(':').map(Number);
    if (parts.length < 2 || isNaN(parts[0])) return s;
    const h = parts[0], m = parts[1];
    return (h > 12 ? h - 12 : h || 12) + ':' + String(m).padStart(2, '0') + ' ' + (h >= 12 ? 'PM' : 'AM');
  };
  const dateLine = `${fmtDateStats(round.date)}${round.teeTime ? ' · ' + fmtT(round.teeTime) : ''}${round.finishTime ? ' — ' + fmtT(round.finishTime) : ''}`;

  // Weather line gating
  const ws = round.weatherSnapshot;
  const hasWeather = ws && (
    ws.feelsLike != null || ws.wind != null || ws.gust != null ||
    ws.humidity != null || ws.precipAccum != null || ws.precipChance != null ||
    ws.conditions || ws.condition
  );

  return (
    <div style={{ width: '100%' }}>
      <button
        onClick={onToggle}
        style={{
          ...CARD_STYLE, width: '100%', textAlign: 'left', cursor: 'pointer',
          padding: '14px 16px',
          border: isExpanded ? `2px solid ${borderColor || COLOR_GOLD}` : `1px solid ${COLOR_DARK_GREEN}`,
          borderBottomLeftRadius: isExpanded ? 0 : 12,
          borderBottomRightRadius: isExpanded ? 0 : 12,
          display: 'block'
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '15px', color: '#0a0a0a', fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{round.courseName}</div>
            <div style={{ fontSize: '13px', color: '#8B6914', fontWeight: 600, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{dateLine}</div>
            {durationText && (
              <div style={{ fontSize: '13px', color: '#0a0a0a', fontWeight: 600, marginTop: 2 }}>{durationText}</div>
            )}
            {playersText && (
              <div style={{ fontSize: '12px', color: COLOR_MUTED, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{playersText}</div>
            )}
          </div>
          <div style={{ textAlign: 'right', flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
            <div style={{ fontSize: '28px', fontWeight: 800, color: diffColor, lineHeight: 1 }}>{diffText}</div>
            <div style={{ fontSize: '12px', color: COLOR_WHITE, fontWeight: 600, marginTop: 4 }}>{round.total} · Par {roundPar}</div>
            <div style={{ fontSize: '11px', color: COLOR_MUTED, marginTop: 4 }}>{isExpanded ? '▲' : '▼'}</div>
          </div>
        </div>
        {hasWeather && (
          <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px solid ${COLOR_DARK_GREEN}` }}>
            <WeatherSnapshotLine ws={ws} />
          </div>
        )}
      </button>
    </div>
  );
};



// ============================================================
// EVENTS SCREEN — Spond integration

// ---- Recovery Screen — triggered by ?admin=recover URL param ----
// JZ sends this URL to a golfer when their round is stuck: https://zap-golf.netlify.app?admin=recover
// Must be opened in the same browser used to submit the round (localStorage is origin-locked).

export default function App() {

  // StatusIcon moved to module level — see above App()

  // ── ICON-1: FULL SVG WEATHER ICON SYSTEM — no emoji ──
  // Day: Clear, MostlyClear, PartlyCloudy, MostlyCloudy, Overcast, Fog, Rain, Thunder, Snow, Sleet
  // Night: Clear→crescent, MostlyClear→crescent, PartlyCloudy→crescent+cloud, MostlyCloudy→cloud, rest same as day

  const skyIcon = (conditions, isDaytime = true) => {
    const c = (conditions || '').toLowerCase();
    if (/thunder|lightning|tstorm/.test(c))          return 'thunder';
    if (/freezing|sleet|ice pellet/.test(c))          return 'sleet';
    if (/snow|blizzard/.test(c))                      return 'snow';
    if (/heavy rain|downpour/.test(c))                return 'rain';
    if (/rain|drizzle|shower/.test(c))                return 'rain';
    if (/fog|mist|haze/.test(c))                      return 'fog';
    if (/overcast/.test(c))                           return 'overcast';
    if (/mostly cloudy/.test(c))                      return isDaytime ? 'mostly-cloudy' : 'overcast';
    if (/partly|cloudy|clouds/.test(c))               return isDaytime ? 'partly-cloudy' : 'partly-cloudy-night';
    if (/mostly clear|few clouds/.test(c))            return isDaytime ? 'mostly-clear' : 'clear-night';
    if (/clear|sunny|fair/.test(c))                   return isDaytime ? 'clear' : 'clear-night';
    return isDaytime ? 'clear' : 'clear-night';
  };

  const renderSkyIcon = (conditions, isDaytime = true, size = 20) => {
    const key = skyIcon(conditions, isDaytime);
    const s = size;
    const Sun = ({ cx = 20, cy = 20, r = 7 }) => <>
      <circle cx={cx} cy={cy} r={r} fill="#FFD54F" />
      {[0,45,90,135,180,225,270,315].map((deg, i) => {
        const rad = Math.PI * deg / 180;
        return <line key={i}
          x1={cx + (r+2)*Math.cos(rad)} y1={cy + (r+2)*Math.sin(rad)}
          x2={cx + (r+5)*Math.cos(rad)} y2={cy + (r+5)*Math.sin(rad)}
          stroke="#FFD54F" strokeWidth="2" strokeLinecap="round" />;
      })}
    </>;
    const Cloud = ({ cx = 22, cy = 24, fill = "#78909c" }) => <>
      <ellipse cx={cx} cy={cy} rx="10" ry="6" fill={fill} />
      <ellipse cx={cx-6} cy={cy-2} rx="7" ry="5" fill={fill} />
      <ellipse cx={cx-12} cy={cy+1} rx="5" ry="4" fill={fill} />
    </>;
    const Crescent = () =>
      <path d="M22 20a9 9 0 1 1-9-9 6.5 6.5 0 0 0 9 9z" fill="white" />;
    const Drops = ({ n = 3, fill = "#4a90d9", heavy = false }) => {
      const xs = n === 2 ? [14, 24] : n === 3 ? [11, 19, 27] : [8, 16, 24, 32];
      return <>{xs.map((x, i) => <line key={i}
        x1={x} y1={23} x2={x-2} y2={heavy ? 33 : 29}
        stroke={fill} strokeWidth={heavy ? 2.5 : 2} strokeLinecap="round" />)}</>;
    };

    const icons = {
      'clear': <Sun cx={20} cy={20} r={8} />,
      'mostly-clear': <><Sun cx={13} cy={15} r={6} /><Cloud cx={26} cy={26} fill="#b0bec5" /></>,
      'partly-cloudy': <><Sun cx={12} cy={13} r={6} /><Cloud cx={24} cy={25} fill="#90a4ae" /></>,
      'mostly-cloudy': <><circle cx={10} cy={12} r={4} fill="#FFD54F" opacity={0.6} /><Cloud cx={22} cy={23} fill="#607d8b" /></>,
      'overcast': <><ellipse cx={22} cy={16} rx="11" ry="7" fill="#607d8b" /><ellipse cx={14} cy={14} rx="9" ry="6" fill="#607d8b" /><ellipse cx={20} cy={23} rx="13" ry="6" fill="#546e7a" /></>,
      'fog': <>{[9,16,23,30].map((y, i) => <rect key={i} x={i%2===0?4:8} y={y} width={i%2===0?32:24} height="3" rx="1.5" fill="#90a4ae" opacity="0.8" />)}</>,
      'rain': <><Cloud cx={22} cy={14} fill="#546e7a" /><Drops n={3} heavy={true} /></>,
      'thunder': <><ellipse cx={22} cy={12} rx="11" ry="6" fill="#37474f" /><ellipse cx={14} cy={10} rx="9" ry="6" fill="#37474f" /><polygon points="21,19 17,19 14,28 19,28 13,38 24,26 19,26 22,19" fill="#FFD54F" /></>,
      'snow': <><Cloud cx={22} cy={13} fill="#78909c" /><circle cx={12} cy={26} r="2.5" fill="#e0e0e0" /><circle cx={20} cy={30} r="2.5" fill="#e0e0e0" /><circle cx={28} cy={26} r="2.5" fill="#e0e0e0" /></>,
      'sleet': <><Cloud cx={22} cy={13} fill="#607d8b" /><line x1={12} y1={23} x2={10} y2={30} stroke="#5b9bd5" strokeWidth="2" strokeLinecap="round" /><circle cx={21} cy={28} r="2.5" fill="#e0e0e0" /><line x1={29} y1={23} x2={27} y2={30} stroke="#5b9bd5" strokeWidth="2" strokeLinecap="round" /></>,
      'clear-night': <Crescent />,
      'partly-cloudy-night': <><Crescent /><Cloud cx={28} cy={28} fill="#78909c" /></>,
    };

    return (
      <svg width={s} height={s} viewBox="0 0 40 40" style={{ flexShrink: 0, display: 'inline-block' }}>
        {icons[key] || icons['clear']}
      </svg>
    );
  };
  // Sky condition as readable text label
  const skyLabel = (conditions) => {
    const c = (conditions || '').toLowerCase();
    if (/thunder|lightning|tstorm/.test(c)) return 'Thunderstorms';
    if (/freezing rain|freezing drizzle/.test(c)) return 'Freezing Rain';
    if (/snow|blizzard/.test(c)) return 'Snow';
    if (/sleet|ice/.test(c)) return 'Sleet/Ice';
    if (/heavy rain|downpour/.test(c)) return 'Heavy Rain';
    if (/rain shower/.test(c)) return 'Rain Showers';
    if (/light rain|drizzle/.test(c)) return 'Light Rain';
    // S275 FIX: preserve MB's probability qualifier instead of collapsing to bare 'Rain'.
    if (/rain likely/.test(c)) return 'Rain Likely';
    if (/chance of rain/.test(c)) return 'Chance of Rain';
    if (/rain/.test(c)) return 'Rain';
    if (/fog/.test(c)) return 'Fog';
    if (/mist/.test(c)) return 'Mist';
    if (/haze/.test(c)) return 'Haze';
    if (/overcast/.test(c)) return 'Overcast';
    if (/mostly cloudy/.test(c)) return 'Mostly Cloudy';
    // S275 FIX: VC uses 'Partially cloudy' (not 'Partly cloudy') — was falling through to bare 'Cloudy'.
    if (/partly cloudy|partially cloudy|partly sunny/.test(c)) return 'Partly Cloudy';
    if (/cloudy|clouds/.test(c)) return 'Cloudy';
    if (/mostly clear|mostly sunny/.test(c)) return 'Mostly Clear';
    if (/clear|sunny|fair/.test(c)) return 'Clear';
    return conditions || 'Clear';
  };

  // ── S95 SHARED HOURLY ROW — matches V6 mockup HRow exactly ──
  // ── S98 TRIGGER POPUP STATE ───────────────────────────────
  const [triggerPopup, setTriggerPopup] = React.useState(null);
  // triggerPopup: { trigger, value, unit, thresholds: [{label,color,min,max}], settingsLink? }

  const TriggerPopupOverlay = () => {
    if (!triggerPopup) return null;
    const { title, value, unit, thresholds, settingsLink } = triggerPopup;
    // Build color bar: find total range
    const minVal = thresholds[0].min;
    const maxVal = thresholds[thresholds.length - 1].max;
    const totalRange = maxVal - minVal || 1;
    // Clamp value position to bar
    const pct = Math.min(100, Math.max(0, ((value - minVal) / totalRange) * 100));
    return (
      <div
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
        onClick={() => setTriggerPopup(null)}
      >
        <div
          style={{ background: '#111', borderRadius: 14, padding: 20, maxWidth: 320, width: '100%', border: '1px solid #333' }}
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#fff' }}>{title}</div>
            <button onClick={() => setTriggerPopup(null)} style={{ background: 'transparent', border: 'none', color: COLOR_OFF_WHITE, fontSize: 22, cursor: 'pointer', padding: 4 }}>×</button>
          </div>
          {/* Current value callout */}
          <div style={{ fontSize: 28, fontWeight: 900, color: (() => {
            for (const t of thresholds) if (value >= t.min && value < t.max) return t.color;
            return thresholds[thresholds.length-1].color;
          })(), marginBottom: 14, textAlign: 'center' }}>
            {Math.round(value)}{unit}
          </div>
          {/* Color bar */}
          <div style={{ position: 'relative', marginBottom: 24 }}>
            <div style={{ display: 'flex', borderRadius: 6, overflow: 'hidden', height: 14 }}>
              {thresholds.map((t, i) => (
                <div key={i} style={{ flex: t.max - t.min, background: t.color, opacity: 0.85 }} />
              ))}
            </div>
            {/* Triangle marker */}
            <div style={{ position: 'absolute', top: -2, left: `calc(${pct}% - 6px)`, fontSize: 14, color: '#fff', lineHeight: 1 }}>▲</div>
            {/* Labels */}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
              {thresholds.map((t, i) => (
                <div key={i} style={{ flex: t.max - t.min, textAlign: 'center', fontSize: 10, color: COLOR_MUTED }}>{t.label}</div>
              ))}
            </div>
          </div>
          {settingsLink && (
            <button
              onClick={() => { setTriggerPopup(null); navigateTo('profile'); }}
              style={{ width: '100%', padding: '10px', background: 'transparent', border: `1px solid ${COLOR_DARK_GREEN}`, borderRadius: 8, color: COLOR_GREEN, fontSize: 13, cursor: 'pointer', fontWeight: 600 }}
            >
              Change your temperature limits in Settings →
            </button>
          )}
          <div style={{ fontSize: 11, color: COLOR_MUTED, textAlign: 'center', marginTop: 12 }}>Tap outside to close</div>
        </div>
      </div>
    );
  };

  // Build popup config for a trigger
  const buildTriggerPopup = (triggerText, h, prof) => {
    const wind = h.windSpeedNum || 0;
    const gust = h.windGust || 0;
    const precip = h.precipChance || 0;
    const feels = h.feelsLike != null ? h.feelsLike : (h.temp || 0);

    if (triggerText.startsWith('Precip') || triggerText.includes('%')) {
      return {
        title: 'Precipitation Chance',
        value: precip, unit: '%',
        thresholds: [
          { label: '0–29%', color: '#22C55E', min: 0, max: 30 },
          { label: '30–49%', color: '#FDE047', min: 30, max: 50 },
          { label: '50–69%', color: '#F97316', min: 50, max: 70 },
          { label: '70%+', color: '#EF4444', min: 70, max: 100 },
        ],
      };
    }
    if (triggerText.startsWith('Wind')) {
      return {
        title: 'Wind Speed',
        value: wind, unit: ' mph',
        thresholds: [
          { label: '0–14 mph', color: '#22C55E', min: 0, max: 15 },
          { label: '15–19', color: '#FDE047', min: 15, max: 20 },
          { label: '20–29', color: '#F97316', min: 20, max: 30 },
          { label: '30+ mph', color: '#EF4444', min: 30, max: 50 },
        ],
      };
    }
    if (triggerText.startsWith('Gust')) {
      return {
        title: 'Wind Gust',
        value: gust, unit: ' mph',
        thresholds: [
          { label: '0–19 mph', color: '#22C55E', min: 0, max: 20 },
          { label: '20–29', color: '#FDE047', min: 20, max: 30 },
          { label: '30–39', color: '#F97316', min: 30, max: 40 },
          { label: '40+ mph', color: '#EF4444', min: 40, max: 60 },
        ],
      };
    }
    if (triggerText.startsWith('Feels')) {
      return {
        title: 'Feels Like Temperature',
        value: feels, unit: '°',
        thresholds: [
          { label: `<${prof.minTemp - 5}°`, color: '#EF4444', min: prof.minTemp - 15, max: prof.minTemp - 5 },
          { label: `${prof.minTemp - 5}–${prof.minTemp}°`, color: '#FDE047', min: prof.minTemp - 5, max: prof.minTemp },
          { label: `${prof.minTemp}–${prof.maxTemp}°`, color: '#22C55E', min: prof.minTemp, max: prof.maxTemp },
          { label: `${prof.maxTemp}–${prof.maxTemp + 5}°`, color: '#FDE047', min: prof.maxTemp, max: prof.maxTemp + 5 },
          { label: `>${prof.maxTemp + 5}°`, color: '#EF4444', min: prof.maxTemp + 5, max: prof.maxTemp + 15 },
        ],
        settingsLink: true,
      };
    }
    // Storm alert triggers — show storm explanation, not precip gauge
    if (triggerText.includes('STORM') || triggerText.includes('GET IN NOW') || triggerText.includes('RAIN APPROACHING')) {
      return null; // No threshold popup for storm alerts — the Storm Alert Zone card is the source of truth
    }
    // Right Now card rain labels — plain English, no forecast % popup
    if (triggerText === 'Heavy Rain' || triggerText === 'Raining' || triggerText === 'Light Rain') {
      return null;
    }
    // Condition triggers (Rain, Fog, Thunder, Snow, Ice, etc.) — no threshold bar, show precip gauge as context
    return {
      title: triggerText,
      value: precip, unit: '%',
      thresholds: [
        { label: '0–29%', color: '#22C55E', min: 0, max: 30 },
        { label: '30–49%', color: '#FDE047', min: 30, max: 50 },
        { label: '50–69%', color: '#F97316', min: 50, max: 70 },
        { label: '70%+', color: '#EF4444', min: 70, max: 100 },
      ],
    };
  };

  // ── S98 HOURLY ROW — new card design system ───────────────
  // Used in Forecast drilldown AND Day of Play for visual uniformity
  const HourlyRow = ({ h, profile: prof, postSunset = false }) => {
    const hTime = new Date(h.time || h.dt * 1000);
    const hHour = hTime.toLocaleTimeString('en-US', { hour: 'numeric', hour12: true });
    const wind = h.windSpeedNum || 0;
    const gust = h.windGust || 0;
    const precip = h.precipChance || 0;
    const temp = h.temp || 0;
    const feels = h.feelsLike != null ? h.feelsLike : temp;
    const hum = h.humidity || 0;
    const uv = Math.round(h.uvIndex || 0);
    const isDaytime = h.isDaytime !== false;
    const hourResult = postSunset ? { status: 'GO', triggers: {} } : getHourStatus(h, prof);
    const statusColor = postSunset ? COLOR_MUTED : (STATUS_COLORS[hourResult.status]?.color || '#9CA3AF');
    const labelShort = hourResult.status === 'STRONG CAUTION' ? 'STR CAU' : hourResult.status;
    const cond = h.conditions || h.shortForecast || 'Clear';
    const showGust = gust > wind + 10;
    // eslint-disable-next-line no-unused-vars
    const gustColor = gust >= 40 ? '#EF4444' : gust >= 30 ? '#F97316' : gust >= 20 ? '#FDE047' : COLOR_MUTED;

    // Trigger logic — matches locked DrilldownHourCard spec (S115):
    // NO-GO: 1 trigger only | STR CAU: max 2 | CAUTION: max 3 (Precip/Wind/Feels)
    // Gust advisory if gust > wind+10 AND gust >= 20
    const triggers = hourResult.triggers || {};
    const allTriggers = [];
    if (!postSunset) {
      if (triggers['NO-GO']?.length) {
        allTriggers.push({ text: triggers['NO-GO'][0], level: 'NO-GO', color: STATUS_COLORS['NO-GO'].color });
      } else if (triggers['STRONG CAUTION']?.length) {
        triggers['STRONG CAUTION'].slice(0, 2).forEach(t => allTriggers.push({ text: t, level: 'STRONG CAUTION', color: STATUS_COLORS['STRONG CAUTION'].color }));
      } else if (triggers['CAUTION']?.length) {
        triggers['CAUTION'].slice(0, 3).forEach(t => allTriggers.push({ text: t, level: 'CAUTION', color: STATUS_COLORS['CAUTION'].color }));
      }
      if (gust > wind + 10 && gust >= 20 && !allTriggers.some(t => t.text.startsWith('Gust'))) {
        const gustLevel = gust >= 40 ? 'NO-GO' : gust >= 30 ? 'STRONG CAUTION' : 'CAUTION';
        allTriggers.push({ text: `Gust ${gust} mph`, level: gustLevel, color: STATUS_COLORS[gustLevel].color });
      }
    }

    return (
      <div style={{
        background: '#000',
        border: postSunset ? `2px solid #333` : `8px solid ${statusColor}`,
        borderRadius: 12,
        padding: '10px 14px',
        boxSizing: 'border-box',
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}>
        {/* R1: Time (left) · Status icon + label (right, not shown post-sunset) */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>{hHour}</span>
          {!postSunset && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <StatusIcon status={hourResult.status} size={18} />
              <span style={{ fontSize: 13, fontWeight: 800, color: statusColor }}>{labelShort}</span>
            </div>
          )}
        </div>
        {/* R2: Sky icon + condition text */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
          {renderSkyIcon(cond, isDaytime, 28)}
          <span style={{ fontSize: 15, fontWeight: 700, color: '#fff' }}>{skyLabel(cond)}</span>
        </div>
        {/* R3: FEELS + actual */}
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 4 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: '#fff', textTransform: 'uppercase' }}>Feels</span>
          <span style={{ fontSize: 22, fontWeight: 800, color: '#fff' }}>{Math.round(feels)}°</span>
          <span style={{ fontSize: 13, color: COLOR_MUTED }}>({Math.round(temp)}° actual)</span>
        </div>
        {/* R4: Stats row — Precip · Wind · Gust · Hum · UV */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 14px', marginBottom: allTriggers.length > 0 ? 6 : 0 }}>
          <span style={{ fontSize: 13, color: '#fff', display:'flex', alignItems:'center' }}><svg width="11" height="13" viewBox="0 0 14 18" fill="none" style={{display:'inline-block',verticalAlign:'middle',marginRight:3}}><path d="M7 1 C7 1 1 8 1 12 A6 6 0 0 0 13 12 C13 8 7 1 7 1Z" fill="#4a90d9"/></svg>{precip}%</span>
          <span style={{ fontSize: 13, color: '#fff', display:'flex', alignItems:'center' }}><svg width="14" height="12" viewBox="0 0 16 12" fill="none" style={{display:'inline-block',verticalAlign:'middle',marginRight:3}}><line x1="1" y1="3" x2="15" y2="3" stroke="#90a4ae" strokeWidth="1.8" strokeLinecap="round"/><line x1="1" y1="6.5" x2="12" y2="6.5" stroke="#90a4ae" strokeWidth="1.8" strokeLinecap="round"/><line x1="1" y1="10" x2="9" y2="10" stroke="#90a4ae" strokeWidth="1.8" strokeLinecap="round"/></svg>{wind} mph</span>
          {showGust && <span style={{ fontSize: 13, color: gustColor }}>Gust {gust} mph</span>}
          <span style={{ fontSize: 13, color: '#fff' }}>Hum {Math.round(hum)}%</span>
          {isDaytime && uv >= 3 && <span style={{ fontSize: 13, color: uv >= 11 ? '#EF4444' : uv >= 8 ? '#F97316' : uv >= 6 ? '#FDE047' : '#fff' }}>UV {uvLabel(Math.round(uv))}</span>}
        </div>
        {/* R5: Trigger lines — non-GO only, pins to bottom via margin-top:auto */}
        <div style={{
          borderTop: allTriggers.length > 0 ? '1px solid #333' : '1px solid transparent',
          paddingTop: allTriggers.length > 0 ? 6 : 0,
          marginTop: 'auto',
          display: 'flex', flexDirection: 'column', gap: 4,
          minHeight: 18,
        }}>
          {allTriggers.map((trig, i) => {
            const hasThreshold = /^(Precip|Wind|Gust|Feels)/.test(trig.text);
            const popup = hasThreshold ? buildTriggerPopup(trig.text, h, prof) : null;
            return (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <StatusIcon status={trig.level} size={14} />
                <span style={{ fontSize: 12, fontWeight: 700, color: trig.color, flex: 1, lineHeight: 1.3 }}>{trig.text}</span>
                {popup && (
                  <button
                    onClick={() => setTriggerPopup(popup)}
                    style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '1px 4px', flexShrink: 0, lineHeight: 1 }}
                  ><svg width="16" height="16" viewBox="0 0 16 16"><circle cx="8" cy="8" r="7" fill="none" stroke="#E5E7EB" strokeWidth="1"/><text x="8" y="8" textAnchor="middle" dominantBaseline="central" fontSize="9" fontWeight="700" fill="#E5E7EB" fontFamily="Arial,sans-serif">i</text></svg></button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // ── S115 DRILLDOWN HOUR CARD — 2-per-row grid cards for forecast drilldown ──
  // Matches the locked HTML mockup in ZAP_Forecast_DrillDown_Card_LookAndFeel.md
  const DrilldownHourCard = ({ h, profile: prof, isPast = false }) => {
    const isInWindow = false; // cream cards removed — always dark (matched Golf)
    const hTime = new Date(h.time || h.dt * 1000);
    const hHour = hTime.toLocaleTimeString('en-US', { hour: 'numeric', hour12: true });
    const hNextTime = new Date(hTime.getTime() + 60 * 60 * 1000);
    const hNextHour = hNextTime.toLocaleTimeString('en-US', { hour: 'numeric', hour12: true });
    const hHourRange = `${hHour} – ${hNextHour}`;
    const wind = h.windSpeedNum || 0;
    const gust = h.windGust || 0;
    const precip = h.precipChance || 0;
    const temp = h.temp || 0;
    const feels = h.feelsLike != null ? h.feelsLike : temp;
    const hum = h.humidity || 0;
    const uv = Math.round(h.uvIndex || 0);
    const isDaytime = h.isDaytime !== false;
    const hourResult = getHourStatus(h, prof);
    const sc = STATUS_COLORS[hourResult.status]?.color || '#9CA3AF';
    const labelShort = hourResult.status === 'STRONG CAUTION' ? 'STR CAU' : hourResult.status;
    const cond = h.conditions || h.shortForecast || 'Clear';
    // NWS owns both conditions and precip — use directly, no override needed
    const condDisplay = cond;

    // Build trigger display per spec rules:
    // NO-GO: 1 trigger only (worst)
    // STR CAU: max 2 (STR CAU level only, no CAUTION below)
    // CAUTION: max 3 (Precip/Wind/Feels)
    // Gust advisory if gust > wind+10 AND gust >= 20
    const triggers = hourResult.triggers || {};
    let displayTriggers = [];
    if (triggers['NO-GO']?.length) {
      displayTriggers = [{ text: triggers['NO-GO'][0], level: 'NO-GO', color: STATUS_COLORS['NO-GO'].color }];
    } else if (triggers['STRONG CAUTION']?.length) {
      displayTriggers = triggers['STRONG CAUTION'].slice(0, 2).map(t => ({ text: t, level: 'STRONG CAUTION', color: STATUS_COLORS['STRONG CAUTION'].color }));
    } else if (triggers['CAUTION']?.length) {
      displayTriggers = triggers['CAUTION'].slice(0, 3).map(t => ({ text: t, level: 'CAUTION', color: STATUS_COLORS['CAUTION'].color }));
    }
    // Gust advisory line
    if (gust > wind + 10 && gust >= 20 && !displayTriggers.some(t => t.text.startsWith('Gust'))) {
      const gustLevel = gust >= 40 ? 'NO-GO' : gust >= 30 ? 'STRONG CAUTION' : 'CAUTION';
      displayTriggers.push({ text: `Gust ${gust} mph`, level: gustLevel, color: STATUS_COLORS[gustLevel].color });
    }

    // Trigger text joined for R5 display
    const triggerText = displayTriggers.map(t => t.text).join(' · ');
    const worstLevel = displayTriggers.length > 0 ? displayTriggers[0].level : null;
    const triggerColor = displayTriggers.length > 0 ? displayTriggers[0].color : null;

    // ⓘ only on threshold triggers (Precip/Wind/Feels/Gust) — not condition triggers
    const hasThresholdTrigger = displayTriggers.some(t => /^(Precip|Wind|Gust|Feels)/.test(t.text));
    const popup = hasThresholdTrigger ? buildTriggerPopup(displayTriggers.find(t => /^(Precip|Wind|Gust|Feels)/.test(t.text)).text, h, prof) : null;

    return (
      <div style={{
        background: '#000',
        border: `5px solid ${sc}`,
        borderRadius: 12,
        padding: 7,
        display: 'flex',
        flexDirection: 'column',
        fontFamily: 'Georgia, serif',
        minWidth: 0,
        boxSizing: 'border-box',
        opacity: isPast ? 0.4 : 1,
      }}>
        {/* R1: hour (left) + status icon/label (right) — flex row per S113/S115 spec */}
        <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 4 }}>
          <span style={{ flex: 1, fontSize: 11, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap' }}>{hHourRange}</span>
          <div style={{ flexShrink: 0, marginLeft: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            <StatusIcon status={hourResult.status} size={16} />
            <span style={{ fontSize: 9, fontWeight: 700, color: sc, whiteSpace: 'nowrap' }}>{labelShort}</span>
          </div>
        </div>

        {/* R2: sky SVG 36px + condition 20px bold white — DOMINANT element */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 5 }}>
          {renderSkyIcon(condDisplay, isDaytime, 36)}
          <span style={{ fontSize: 20, fontWeight: 700, color: '#fff' }}>{skyLabel(condDisplay)}</span>
        </div>

        {/* R3: feels temp (left) | precip/wind/humidity (right, border-left) */}
        <div style={{ display: 'flex', alignItems: 'stretch', marginBottom: 4 }}>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 1 }}>
            <span style={{ fontSize: 18, fontWeight: 700, color: '#fff', lineHeight: 1.2 }}>{Math.round(feels)}°</span>
            <span style={{ fontSize: 10, color: '#fff' }}>({Math.round(temp)}° actual)</span>
          </div>
          <div style={{ borderLeft: '1px solid #333', paddingLeft: 7, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 3 }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', display:'flex', alignItems:'center' }}><svg width="11" height="13" viewBox="0 0 14 18" fill="none" style={{display:'inline-block',verticalAlign:'middle',marginRight:3}}><path d="M7 1 C7 1 1 8 1 12 A6 6 0 0 0 13 12 C13 8 7 1 7 1Z" fill="#4a90d9"/></svg>{precip}%</span>
            <span style={{ fontSize: 11, color: '#fff', whiteSpace: 'nowrap', display:'flex', alignItems:'center' }}><svg width="14" height="12" viewBox="0 0 16 12" fill="none" style={{display:'inline-block',verticalAlign:'middle',marginRight:3}}><line x1="1" y1="3" x2="15" y2="3" stroke='#90a4ae' strokeWidth="1.8" strokeLinecap="round"/><line x1="1" y1="6.5" x2="12" y2="6.5" stroke='#90a4ae' strokeWidth="1.8" strokeLinecap="round"/><line x1="1" y1="10" x2="9" y2="10" stroke='#90a4ae' strokeWidth="1.8" strokeLinecap="round"/></svg>{wind} mph</span>
            <span style={{ fontSize: 11, color: '#fff', whiteSpace: 'nowrap' }}>Hum {Math.round(hum)}%</span>
            {isDaytime && uv >= 3 && <span style={{ fontSize: 11, color: uv >= 11 ? '#EF4444' : uv >= 8 ? '#F97316' : uv >= 6 ? '#FDE047' : '#fff', whiteSpace: 'nowrap' }}>UV {uvLabel(Math.round(uv))}</span>}
          </div>
        </div>

        {/* R5: trigger line — non-GO only. Uniform height via minHeight. */}
        <div style={{
          borderTop: displayTriggers.length > 0 ? '1px solid #333' : '1px solid transparent',
          paddingTop: displayTriggers.length > 0 ? 3 : 0,
          marginTop: 'auto',
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          minHeight: 18,
        }}>
          {displayTriggers.length > 0 ? (<>
            <StatusIcon status={worstLevel} size={12} />
            <span style={{ fontSize: 11, fontWeight: 700, color: triggerColor, flex: 1, lineHeight: 1.3 }}>{triggerText}</span>
            {popup && (
              <button
                onClick={(e) => { e.stopPropagation(); setTriggerPopup(popup); }}
                style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', flexShrink: 0, lineHeight: 1 }}
              >
                <svg width="16" height="16" viewBox="0 0 16 16">
                  <circle cx="8" cy="8" r="7" fill="none" stroke='#E5E7EB' strokeWidth="1" />
                  <text x="8" y="8" textAnchor="middle" dominantBaseline="central" fontSize="9" fontWeight="700" fill='#E5E7EB' fontFamily="Arial,sans-serif">i</text>
                </svg>
              </button>
            )}
          </>) : null}
        </div>
      </div>
    );
  };

  // ── AUTH ──────────────────────────────────────────────────
  const [authed, setAuthed] = useState(true); // S50: password removed for testing

  // ── LAST ROUND OVERLAY — show once per calendar day ───────
  const [showRoundOverlay, setShowRoundOverlay] = useState(() => {
    try {
      const last = localStorage.getItem('zap_overlay_shown');
      const today = new Date().toDateString();
      return last !== today; // show if not shown today
    } catch { return true; }
  });
  const dismissOverlay = () => {
    try { localStorage.setItem('zap_overlay_shown', new Date().toDateString()); } catch {}
    setShowRoundOverlay(false);
  };
  const [isAdmin, setIsAdmin] = useState(() => {
    try { return localStorage.getItem('zap_admin') === 'true'; } catch { return false; }
  });
  const [authInput, setAuthInput] = useState('');
  const [authError, setAuthError] = useState('');

  // ── CONFIG ─────────────────────────────────────────────────
  const [zapConfig, setZapConfig] = useState({});
  useEffect(() => {
    fetch('/zap-config.json')
      .then(r => r.json())
      .then(cfg => setZapConfig(cfg))
      .catch(() => {});
  }, []);

  // ── S166 LOGGING: window-level error capture ──────────────
  // Catches errors that React ErrorBoundary can't:
  //   - errors thrown inside event handlers (Submit click, etc.)
  //   - unhandled promise rejections (failed fetches, async/await)
  //   - errors thrown inside setTimeout / setInterval callbacks
  // Logs to console with a 'ZAP' prefix so they're easy to grep in DevTools.
  // Mounted once at App startup; cleaned up on unmount (defensive — App never actually unmounts in practice).
  useEffect(() => {
    const onError = (e) => {
      console.error('ZAP window.error:', {
        message: e.message,
        filename: e.filename,
        line: e.lineno,
        col: e.colno,
        error: e.error,
        stack: e.error?.stack,
      });
    };
    const onRejection = (e) => {
      console.error('ZAP unhandledrejection:', {
        reason: e.reason,
        message: e.reason?.message,
        stack: e.reason?.stack,
      });
    };
    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection);
    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onRejection);
    };
  }, []);

  // ── CONFIG DERIVATIONS ─────────────────────────────────────
  const cfgCourses = zapConfig?.courses || []; // Activity Planner: no static course list
  const cfgPlayers = zapConfig?.players || PLAYERS;
  const cfgLore = zapConfig?.courseLore || COURSE_LORE;
  const cfgAssets = zapConfig?.courseAssets || COURSE_ASSETS;
  const cfgHolePars = zapConfig?.holePars || {};

  // ── MISSING STATE ──────────────────────────────────────────
  const [townFilter, setTownFilter] = useState(null);
  const [homeCourse, setHomeCourse] = useState(null);

  // ── AUTH HANDLER (S96: password gate removed — always authed) ──
  const handleAuth = (val) => {
    setAuthed(true);
    setIsAdmin(true);
    localStorage.setItem('zap_authed', '1');
    localStorage.setItem('zap_admin', '1');
  };

  // ── NAVIGATION ─────────────────────────────────────────────
  // mode: kept for backward compat with Planning/OnCourse sub-screens
  // screen: 'home' is now the default landing; null = legacy mode screens
  const [mode, setMode] = useState('planning');
  const [screen, setScreen] = useState('home');

  const [screenHistory, setScreenHistory] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('admin') === 'recover') return ['recover'];
      const saved = localStorage.getItem('zap_last_course');
      return saved ? ['home'] : [];
    } catch { return []; }
  });
  const scorecardBackGuard = useRef(null);
  const loadInFlight = useRef(false); // FETCHALL-DOUBLE-INVOCATION guard — prevents two useEffects both firing loadCourse simultaneously
  const [scorecardInReview, setScorecardInReview] = React.useState(false);
  // Reset review state when navigating away from scorecard screen
  React.useEffect(() => { if (screen !== 'scorecard') setScorecardInReview(false); }, [screen]);

  const [screenParams, setScreenParams] = React.useState({});

  const navigateTo = useCallback((newScreen, params) => {
    setScreenHistory(prev => [...prev, newScreen]);
    setScreen(newScreen);
    if (params) setScreenParams(prev => ({ ...prev, [newScreen]: params }));
    setTimeout(() => scrollToTop(), 50);
  }, []);

  const goBack = useCallback(() => {
    setScreenHistory(prev => {
      if (prev.length <= 1) { setScreen(null); return []; }
      const newHistory = prev.slice(0, -1);
      setScreen(newHistory[newHistory.length - 1]);
      return newHistory;
    });
    setTimeout(() => scrollToTop(), 50);
  }, []);

  // ── RECOVERY URL — ?admin=recover ──────────────────────────
  // JZ sends https://zap-golf.netlify.app?admin=recover to a golfer with a stuck round.
  // Must be opened in the same browser used to submit the round (localStorage is origin-locked).
  React.useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('admin') === 'recover') {
        setScreen('recover');
        setScreenHistory(['recover']);
        return;
      }
    } catch {}
    // S184: app_open logged inside loadCourse — removed duplicate log here that was causing
    // double app_open entries on every cold start for every course.
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const switchMode = useCallback((newMode) => {
    setMode(newMode);
    setScreen(null);
    setScreenHistory([]);
    setTimeout(() => scrollToTop(), 50);
  }, []);

  // ── COURSE SELECTION ───────────────────────────────────────
  const selectedCourseRef = useRef(null); // S183: stable ref for modal closure
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [showCourseSwitcher, setShowCourseSwitcher] = useState(false);
  const [showSourceLegend, setShowSourceLegend] = useState(false);
  const [modalRefreshTime, setModalRefreshTime] = useState(null);

  // ── PROFILE ────────────────────────────────────────────────
  const [profile, setProfile] = useState({    minTemp: 58, maxTemp: 95, maxWind: 25,
    bugSensitivity: 'medium',
    playWindow: 'all',
    playStart: '08:00', playEnd: '20:00',
    homeCourse: null,
    webhookUrl: '',
    adminUnlocked: false,
    audioAlertEnabled: true, // S275: storm audio alert — default ON (safety), matches Golf S274
  });
  const [profileSaved, setProfileSaved] = useState(false);
  const [profileLoadedLate, setProfileLoadedLate] = useState(false); // cold-start race: profile arrived after forecast

  // S227: AudioContext for storm audio alerts — unlocked on first user touch
  const audioCtxRef = React.useRef(null);
  const audioEnabledRef = React.useRef(profile.audioAlertEnabled);
  React.useEffect(() => {
    const unlock = () => {
      if (!audioCtxRef.current) {
        audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      if (audioCtxRef.current.state === 'suspended') audioCtxRef.current.resume();
      document.removeEventListener('touchstart', unlock);
    };
    document.addEventListener('touchstart', unlock, { once: true });
    return () => document.removeEventListener('touchstart', unlock);
  }, []);

  // Keep audioEnabledRef in sync at App level — avoids re-render inside OnCourseScreen
  const _prevAudioEnabled = React.useRef(profile.audioAlertEnabled);
  React.useEffect(() => {
    const was = _prevAudioEnabled.current;
    audioEnabledRef.current = profile.audioAlertEnabled;
    _prevAudioEnabled.current = profile.audioAlertEnabled;
    // If toggled ON — signal OnCourseScreen to treat current storm as new
    if (!was && profile.audioAlertEnabled) {
      audioEnabledRef._justEnabled = true;
    }
  }, [profile.audioAlertEnabled]);

  useEffect(() => {
    try {
      const saved = localStorage.getItem('zap_profile');
      if (saved) {
        const parsed = JSON.parse(saved);
        // Guard against old wrapped format { profile: {...}, homeCourse: '...' }
        const profileData = parsed?.profile ?? parsed;
        setProfile(prev => ({ ...prev, ...profileData }));
        // Cold-start race: if weatherData already loaded when profile arrives, flag it
        if (weatherData) setProfileLoadedLate(true);
      }
    } catch {}
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const saveProfileToStore = useCallback(() => {
    try { localStorage.setItem('zap_profile', JSON.stringify(profile)); } catch {}
  }, [profile]);
  useEffect(() => { saveProfileToStore(); }, [saveProfileToStore]);

  // ── WEATHER STATE ──────────────────────────────────────────
  const [weatherData, setWeatherData] = useState(null);
  const [lastRefreshTime, setLastRefreshTime] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [realtimeRefreshTick, setRealtimeRefreshTick] = useState(0);
  const [weatherError, setWeatherError] = useState(null);
  const [badResult, setBadResult] = useState(null);
  const [waterDensity, setWaterDensity] = useState(null);
  const [lightningData, setLightningData] = useState(null);
  const [pollenData, setPollenData] = useState(null);
  const [showRadarInfo, setShowRadarInfo] = useState(false);
  const [etData, setEtData] = useState(null);
  const [njwxData, setNjwxData] = useState(null);
  const [wapiCurrentData, setWapiCurrentData] = useState(null);
  const wapiLoadStartRef = useRef(null); // S216 F1: tracks when fire-and-forget fired for 3s timeout
  const njwxOverrideRef = useRef(null); // NJWxNet override: null=auto, true=forced on, false=forced off
  const [sharedRounds, setSharedRounds] = useState([]);

  const [activeWindow, setActiveWindow] = useState({
    dayIdx: -1,
    period: null
  });
  const [selectedForecastDay, setSelectedForecastDay] = useState(null); // date string 'YYYY-MM-DD'

  // ZAP Activity webhook — ZAP Activity Truth Chain sheet
  const ACTIVITY_WEBHOOK = 'https://script.google.com/macros/s/AKfycbxX95MQ8tYmQ_bVqnTeUHX7pbxEqryR5VwgZ8dFPbpxARbSCQ2uSFXLNJ9OdANlW146cw/exec';

  // DeviceID — persistent UUID, identifies user across sessions
  const deviceId = React.useMemo(() => {
    let id = localStorage.getItem('zap_device_id');
    if (!id) {
      id = 'zap_' + Math.random().toString(36).substr(2, 8);
      localStorage.setItem('zap_device_id', id);
    }
    return id;
  }, []);

  // SessionID — unique per app load, ties open → location set → report
  const sessionId = React.useMemo(() => 'sess_' + Math.random().toString(36).substr(2, 10), []);

  // activityLoggedThisSession — drives Change button guard
  const [activityLoggedThisSession, setActivityLoggedThisSession] = useState(false);

  // showChangeGuard — prompt shown when Change tapped and activity not yet logged
  const [showChangeGuard, setShowChangeGuard] = useState(false);

  // ── SHARED ROUNDS (Google Sheet via webhook) ───────────────
  // CACHE-1: Show cache instantly, fetch fresh in background.
  useEffect(() => {
    const ROUNDS_CACHE_KEY = 'zap_rounds_cache';
    const ROUNDS_TS_KEY    = 'zap_rounds_ts';

    // Show cache immediately — no waiting for network
    try {
      const cached = localStorage.getItem(ROUNDS_CACHE_KEY);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (parsed?.length > 0) setSharedRounds(parsed);
      }
    } catch {}

    // Fetch fresh in background
    const load = async () => {
      try {
        // Timestamp check — getTimestamp is wired in Apps Script (Code.gs line 341).
        // Auto-updates on every scorecard submission. Skip full fetch if timestamp unchanged.
        let sheetTs = '';
        let skipFetch = false;
        try {
          const tsResp = await fetch(`${ACTIVITY_WEBHOOK}?action=getTimestamp`);
          if (tsResp.ok) {
            const tsData = await tsResp.json();
            sheetTs = tsData.timestamp || '';
            const cachedTs = localStorage.getItem(ROUNDS_TS_KEY) || '';
            if (sheetTs && sheetTs === cachedTs) skipFetch = true;
          }
        } catch {} // network failure — fall through to full fetch

        if (skipFetch) return;

        const r = await fetch(`${ACTIVITY_WEBHOOK}?action=getRounds`);
        if (!r.ok) return;
        const d = await r.json();
        const normalized = (d.rounds || []).map(row => {
          if (Array.isArray(row.scores)) return row;
          const scores = Array.from({length: 18}, (_, i) => {
            const v = row[`H${i+1}`];
            return (v !== '' && v != null) ? parseInt(v) : null;
          });
          // Apps Script sometimes serializes numeric cells as ISO date strings (Excel serial bug).
          // e.g. Total=72 comes back as "1900-03-12T05:00:00.000Z" — day 71 from 1900-01-00.
          // Fix: if a field that should be a number is an ISO string, extract days-since-1900 as integer.
          const parseSheetInt = (v) => {
            if (v == null || v === '') return 0;
            if (typeof v === 'number') return Math.round(v);
            const s = String(v);
            // Plain integer string
            if (/^\d+$/.test(s)) return parseInt(s, 10);
            // Apps Script date-serialized number: "1900-MM-DDTHH:mm:ss.000Z"
            // The day-of-year from Jan 0 1900 = the original integer value
            const m = s.match(/^1900-(\d{2})-(\d{2})T/);
            if (m) {
              const month = parseInt(m[1], 10); // 1-based
              const day   = parseInt(m[2], 10);
              // Days since Jan 0 1900: Jan=31, Feb=28(non-leap), Mar=31...
              const daysInMonth = [0,31,28,31,30,31,30,31,31,30,31,30,31];
              let total = day;
              for (let mo = 1; mo < month; mo++) total += daysInMonth[mo];
              return total;
            }
            return parseInt(s, 10) || 0;
          };

          // Parse date — always produce clean YYYY-MM-DD string
          const parseSheetDate = (v) => {
            if (!v) return '';
            const s = String(v).trim();
            // Already ISO format
            if (s.match(/^\d{4}-\d{2}-\d{2}/)) return s.slice(0, 10);
            // Human-readable e.g. "Mon, Mar 23, 2026" — parse via Date, use en-CA for YYYY-MM-DD in local time
            try {
              const d = new Date(s);
              if (!isNaN(d.getTime())) return d.toLocaleDateString('en-CA');
            } catch {}
            return '';
          };

          // Parse time — if Apps Script serialized as "1899-12-30THH:mm:ss.000Z" extract HH:MM AM/PM
          const parseSheetTime = (v) => {
            if (!v) return '';
            const s = String(v).trim();
            if (/[AP]M$/i.test(s)) return s; // already formatted
            const m = s.match(/T(\d{2}):(\d{2})/);
            if (m) {
              let h = parseInt(m[1], 10), min = parseInt(m[2], 10);
              const ampm = h >= 12 ? 'PM' : 'AM';
              h = h % 12 || 12;
              return `${h}:${String(min).padStart(2,'0')} ${ampm}`;
            }
            return s;
          };

          return {
            id: row.ID || row.id,
            courseId: row.CourseID || row.courseId,
            courseName: row.CourseName || row.courseName,
            date: parseSheetDate(row.Date || row.date),
            teeTime: parseSheetTime(row.TeeTime || row.teeTime),
            finishTime: parseSheetTime(row.FinishTime || row.finishTime),
            comment: row.Comment || row.comment || '',
            players: typeof row.Players === 'string'
              ? row.Players.split(',').map(p => p.trim()).filter(Boolean)
              : (row.players || []),
            scores,
            pars: (row.Pars || row.pars)
              ? String(row.Pars || row.pars).split(',').map(p => parseInt(p,10)).filter(n => !isNaN(n))
              : Array(18).fill(4),
            total: parseSheetInt(row.Total || row.total),
            holesEntered: parseSheetInt(row.HolesEntered || row.holesEntered) || 18,
            photoUrl: row.PhotoURL || row.photoUrl || '',
            submittedAt: row.SubmittedAt || row.submittedAt || '',
            truthChain: (() => {
              const flags = row.TruthFlags || row.truthFlags || '';
              const other = row.TruthOther || row.truthOther || '';
              const arr = flags ? String(flags).split(',').map(s => s.trim()).filter(Boolean) : [];
              if (other) arr.push(String(other).trim());
              return arr;
            })(),
            weatherSnapshot: (() => {
              // S166: rehydrate full S154 schema. Reads new keys first, falls back to old keys
              // for rounds written before S166 (where Code.gs only persisted feelsLike, wind,
              // precipChance, condition — gust/humidity/dataSource columns didn't exist).
              const num = (v) => (v != null && v !== '' ? Number(v) : null);
              const str = (v) => (v != null && v !== '' ? String(v) : null);
              const feelsLike   = num(row.WeatherFeels);
              const wind        = num(row.WeatherWind);
              const gust        = num(row.WeatherGust);                       // S166 — null on old rows
              const humidity    = num(row.WeatherHumidity);                   // S166 — null on old rows
              const precipAccum = num(row.WeatherPrecip);                     // S166: column now stores inches (was %)
              const conditions  = str(row.WeatherCond) || str(row.weatherCond);
              const rawSource   = str(row.WeatherSource);                     // S166 — null on old rows
              // S246: parse stid out of WeatherSource ("njwxnet+forecast NJ09" → dataSource="njwxnet+forecast", stid="NJ09")
              const sourceParts = rawSource ? rawSource.split(' ') : [];
              const dataSource  = sourceParts[0] || null;
              const stid        = sourceParts[1] || null;
              if (feelsLike == null && wind == null && gust == null && humidity == null
                  && precipAccum == null && !conditions) return null;
              // Field names match in-memory snapshot from buildWeatherSnapshot — renderer reads same.
              return { feelsLike, wind, gust, humidity, precipAccum, conditions, dataSource, stid };
            })(),
            neshanicNines: row.NeshanicNines || row.neshanicNines || '',
          };
        }).filter(r => r.total > 0);
        try {
          // Only write cache if Sheet returned at least as many rounds as currently cached
          // Protects against race condition where Sheet hasn't finished writing newest round
          const existingCache = localStorage.getItem(ROUNDS_CACHE_KEY);
          const existingCount = existingCache ? JSON.parse(existingCache).length : 0;
          if (normalized.length >= existingCount) {
            localStorage.setItem(ROUNDS_CACHE_KEY, JSON.stringify(normalized));
            if (sheetTs) localStorage.setItem(ROUNDS_TS_KEY, sheetTs);
          }
        } catch {}
        setSharedRounds(normalized);
      } catch {}
    };
    load();
  }, []);

  // Cache invalidation fix: expose refreshRounds so 19th Hole + Statistics can re-fetch on mount
  const refreshRounds = useCallback(async () => {
    const ROUNDS_CACHE_KEY = 'zap_rounds_cache';
    const ROUNDS_TS_KEY    = 'zap_rounds_ts';
    try {
      let sheetTs = '';
      try {
        const tsResp = await fetch(`${ACTIVITY_WEBHOOK}?action=getTimestamp`);
        if (tsResp.ok) {
          const tsData = await tsResp.json();
          sheetTs = tsData.timestamp || '';
          const cachedTs = localStorage.getItem(ROUNDS_TS_KEY) || '';
          if (sheetTs && sheetTs === cachedTs) return; // still fresh
        }
      } catch {}
      const resp = await fetch(`${ACTIVITY_WEBHOOK}?action=getRounds`);
      if (!resp.ok) return;
      const d = await resp.json();
      const normalized = (d.rounds || []).map(row => {
        if (Array.isArray(row.scores)) return row;
        const scores = Array.from({length: 18}, (_, i) => {
          const v = row[`H${i+1}`];
          return (v !== '' && v != null) ? parseInt(v) : null;
        });
        const parseSheetInt = (v) => {
          if (v == null || v === '') return 0;
          if (typeof v === 'number') return Math.round(v);
          const s = String(v);
          if (/^\d+$/.test(s)) return parseInt(s, 10);
          const m = s.match(/^1900-(\d{2})-(\d{2})T/);
          if (m) {
            const daysInMonth = [0,31,28,31,30,31,30,31,31,30,31,30,31];
            let total = parseInt(m[2], 10);
            for (let mo = 1; mo < parseInt(m[1], 10); mo++) total += daysInMonth[mo];
            return total;
          }
          return parseInt(s, 10) || 0;
        };
        const parseSheetTime = (v) => {
          if (!v) return '';
          const s = String(v).trim();
          if (/[AP]M$/i.test(s)) return s;
          const m = s.match(/T(\d{2}):(\d{2})/);
          if (m) {
            let h = parseInt(m[1], 10), min = parseInt(m[2], 10);
            const ampm = h >= 12 ? 'PM' : 'AM';
            h = h % 12 || 12;
            return `${h}:${String(min).padStart(2,'0')} ${ampm}`;
          }
          return s;
        };
        return {
          id: row.ID || row.id,
          courseId: row.CourseID || row.courseId,
          courseName: row.CourseName || row.courseName,
          date: (() => { const v = String(row.Date || row.date || '').trim(); if (!v) return ''; if (v.match(/^\d{4}-\d{2}-\d{2}/)) return v.slice(0,10); try { const d = new Date(v); if (!isNaN(d.getTime())) return d.toLocaleDateString('en-CA'); } catch {} return ''; })(),
          teeTime: parseSheetTime(row.TeeTime || row.teeTime),
          finishTime: parseSheetTime(row.FinishTime || row.finishTime),
          comment: row.Comment || row.comment || '',
          players: typeof row.Players === 'string'
            ? row.Players.split(',').map(p => p.trim()).filter(Boolean)
            : (row.players || []),
          scores,
          pars: (row.Pars || row.pars)
            ? String(row.Pars || row.pars).split(',').map(p => parseInt(p,10)).filter(n => !isNaN(n))
            : Array(18).fill(4),
          total: parseSheetInt(row.Total || row.total),
          holesEntered: parseSheetInt(row.HolesEntered || row.holesEntered) || 18,
          photoUrl: row.PhotoURL || row.photoUrl || '',
          submittedAt: row.SubmittedAt || row.submittedAt || '',
          truthChain: (() => {
            const flags = row.TruthFlags || row.truthFlags || '';
            const other = row.TruthOther || row.truthOther || '';
            const arr = flags ? String(flags).split(',').map(s => s.trim()).filter(Boolean) : [];
            if (other) arr.push(String(other).trim());
            return arr;
          })(),
          weatherSnapshot: (() => {
            // S166: rehydrate full S154 schema. Reads new keys first, falls back to old keys
            // for rounds written before S166 (where Code.gs only persisted feelsLike, wind,
            // precipChance, condition — gust/humidity/dataSource columns didn't exist).
            const num = (v) => (v != null && v !== '' ? Number(v) : null);
            const str = (v) => (v != null && v !== '' ? String(v) : null);
            const feelsLike   = num(row.WeatherFeels);
            const wind        = num(row.WeatherWind);
            const gust        = num(row.WeatherGust);                       // S166 — null on old rows
            const humidity    = num(row.WeatherHumidity);                   // S166 — null on old rows
            const precipAccum = num(row.WeatherPrecip);                     // S166: column now stores inches (was %)
            const conditions  = str(row.WeatherCond) || str(row.weatherCond);
            const rawSource   = str(row.WeatherSource);                     // S166 — null on old rows
            // S246: parse stid out of WeatherSource ("njwxnet+forecast NJ09" → dataSource="njwxnet+forecast", stid="NJ09")
            const sourceParts = rawSource ? rawSource.split(' ') : [];
            const dataSource  = sourceParts[0] || null;
            const stid        = sourceParts[1] || null;
            if (feelsLike == null && wind == null && gust == null && humidity == null
                && precipAccum == null && !conditions) return null;
            // Field names match in-memory snapshot from buildWeatherSnapshot — renderer reads same.
            return { feelsLike, wind, gust, humidity, precipAccum, conditions, dataSource, stid };
          })(),
          neshanicNines: row.NeshanicNines || row.neshanicNines || '',
        };
      }).filter(r => r.total > 0);
      try {
        // Only write cache if Sheet returned at least as many rounds as currently cached
        const existingCache = localStorage.getItem(ROUNDS_CACHE_KEY);
        const existingCount = existingCache ? JSON.parse(existingCache).length : 0;
        if (normalized.length >= existingCount) {
          localStorage.setItem(ROUNDS_CACHE_KEY, JSON.stringify(normalized));
          if (sheetTs) localStorage.setItem(ROUNDS_TS_KEY, sheetTs);
        }
      } catch {}
      // 19TH-HOLE-STALE fix: merge any local rounds not yet in Sheet so a freshly
      // submitted round isn't clobbered when refreshRounds overwrites sharedRounds
      // before the Sheet has received it. Dedup by id (both sides are JS numbers).
      const localOnly = getLocalRounds().filter(
        lr => !normalized.some(sr => sr.id === lr.id)
      );
      const merged = localOnly.length > 0 ? [...normalized, ...localOnly] : normalized;
      setSharedRounds(merged);
    } catch {}
  }, [ACTIVITY_WEBHOOK]);

  // ── LOAD COURSE ───────────────────────────────────────────
  const loadCourse = (course, silent = false, profileOverride = null) => {
    if (loadInFlight.current) { return; } // FETCHALL-DOUBLE-INVOCATION: block concurrent loads
    loadInFlight.current = true;
    setSelectedCourse(course);
    selectedCourseRef.current = course; // S183: keep ref in sync
    setShowCourseSwitcher(false);
    if (silent) { loadInFlight.current = false; return; } // S183: guard stuck-open fix
    if (!silent) {
      // S252: App Open — post to Activity webhook with DeviceID + SessionID + location
      try {
        fetch(ACTIVITY_WEBHOOK, {
          method: 'POST', mode: 'no-cors',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: 'app_open',
            deviceId,
            sessionId,
            locationName: course.name || '',
            locationAddress: course.zip ? `${course.name}, ${course.zip}` : course.name || '',
            lat: course.lat,
            lng: course.lng,
            entryMode: course.entryMode || 'unknown',
          })
        }).catch(() => {});
      } catch (e) {}
      // S255: retryPendingRounds removed — Golf-only, always no-op on Activity domain
      // S183: navigation removed from loadCourse — loadCourse is data-only.
      // Navigation on course switch handled by caller (course switcher, cold-start useEffect).
      // S222: clearAllCaches() — complete cache clear on course switch
      clearAllCaches();
      setIsLoading(true);
      setWeatherError(null);
      setWeatherData(null);
      setBadResult(null);
      setWaterDensity(null);
      setLightningData(null);
      setPollenData(null);
      setEtData(null);
      setNjwxData(null);
      setWapiCurrentData(null);
      wapiLoadStartRef.current = null; // S216 F1: reset timeout on each load
    }

    // S174: fetchWeatherCascade replaced by per-variable fetchForecastCascade.
    // Old all-or-nothing GS→VC→OM cascade removed. New cascade lazy-fetches each
    // source on demand; PirateWeather only fires when something else broke.


    const fetchAll = async (crs) => {
      const effectiveProfile = profileOverride || profile;
      try {
        const [forecast, pollen, et, njwx, lightning] = await Promise.all([
          withTimeout(fetchForecastCascade(crs), 25000, 'Forecast cascade'),
          withTimeout(fetchPollen(crs.lat, crs.lng, crs.id), 8000, 'Pollen'),
          withTimeout(fetchET(crs.lat, crs.lng), 8000, 'ET'),
          withTimeout(fetchNJWxNet(crs, njwxOverrideRef.current), 8000, 'NJWxNet'),
          withTimeout(fetchLightningData(crs.lat, crs.lng), 8000, 'Lightning'),
        ]);
        // Open-Meteo data is exposed via cascade._omFull (no separate fetch needed)
        const om = forecast?._omFull || null;

        // Soil moisture: NJ76 (Duke Farms) covers Green Knoll + Neshanic only. All others use Open-Meteo.
        // Computed here so it's in scope for both the OSM background callback and the main BAD call below.
        const _njwxSoilStid = (crs.id === 'green-knoll' || crs.id === 'neshanic-valley' || crs.id === 'fox-hollow') ? 'NJ76' : null; // S243: fox-hollow added (NJ76 assigned)
        const _njwxSoilObs = _njwxSoilStid && njwx?.success ? njwx[_njwxSoilStid] : null;
        const _soilMoisturePct = _njwxSoilObs?.soilMoisture != null
          ? Math.round(_njwxSoilObs.soilMoisture * 10) / 10
          : (om?.soilMoisturePct ?? null);

        // OSM water density — non-blocking, loads in background after page renders
        // Does not affect Go/No-Go or forecast display — only Bug Index multiplier
        withTimeout(fetchOSMWaterDensity(crs.lat, crs.lng), 8000, 'OSM')
          .then(osm => {
            if (osm?.multiplier != null) {
              setWaterDensity(osm);
              setBadResult(prev => prev ? runBADEngine(weatherWithRealtime, crs, effectiveProfile, osm, et, _soilMoisturePct) : prev);
            }
            if (osm?.error) {
              logSystemHealth('overpass_fail', crs, { error: osm.error });
            }
          })
          .catch(err => {
            logSystemHealth('overpass_fail', crs, { error: err?.message || 'timeout' });
          });

        // ── S50: Extend forecast to 7 days using Open-Meteo beyond 48h ──
        // GribStream and Weatherbit cap at 48h. Meteoblue P1 for 0-48h (restored S206).
        // Open-Meteo: free, no key, no rate limit, returns 168h (7 days).
        // This fills calendar days 3-7 which would otherwise show UNKNOWN.
        // forecast_snap.py now captures all 7-day sources for future validation.
        if (om?.success && om?.hourly?.length > 0 && forecast?.hourly?.length > 0) {
          const cutoffMs = Date.now() + (48 * 3600 * 1000);
          const existingTimes = new Set(forecast.hourly.map(h => new Date(h.time).getTime()));
          const extension = om.hourly
            .filter(h => new Date(h.time).getTime() > cutoffMs)
            .filter(h => !existingTimes.has(new Date(h.time).getTime()))
            .map(h => ({ ...h, source: 'Open-Meteo (48h+ calendar)' }));
          if (extension.length > 0) {
            forecast.hourly = [...forecast.hourly, ...extension];
          }
        }

        // P1 = NJWxNet, P2 = WeatherAPI(temp/feels) + VC(wind/gust). Architects: WeatherAPI direct P1.
        // P2 wind: PirateWeather when NJWxNet wind = 0 (broken sensor)
        let weatherWithRealtime;
        if (njwx?.success) {
          // p1_recovered: NJWxNet is back after a P2 cycle
          if (_p2Active[crs.id]) {
            logSystemHealth('p1_recovered', crs, {
              source: 'NJWxNet',
              p2WasActive: _p2Active[crs.id].source,
              p2Since: _p2Active[crs.id].since,
            });
            delete _p2Active[crs.id];
          }
          weatherWithRealtime = applyNJWxNetOverride(forecast, njwx);
          // API-9: NJ09 wind sensor permanently unreliable (reads ~0.39mph when reality is 9-13mph)
          // Override wind+gust with PirateWeather for ANY location where NJ09 is the nearest station
          if (njwx?.stid === 'NJ09') {
            try {
              const pw = await withTimeout(fetchPirateWeather(crs), 6000, 'PirateWeather NJ09 override');
              incrementCallCount('pirateweather', crs);
              if (pw?.success && pw.current) {
                const pwWind = pw.current.windSpeedNum ?? 0;
                const pwGust = pw.current.windGust ?? 0;
                weatherWithRealtime = {
                  ...weatherWithRealtime,
                  current: {
                    ...weatherWithRealtime.current,
                    windSpeedNum: pwWind,
                    windSpeed: `${pwWind} mph`,
                    windGust: pwGust,
                    feelsLike: calcFeelsLike(
                      weatherWithRealtime.current.temp ?? 65,
                      weatherWithRealtime.current.humidity ?? 50,
                      pwWind
                    ),
                    _windSource: 'PirateWeather (NJ09 sensor excluded)',
                    _rtSources: {
                      ...(weatherWithRealtime.current._rtSources || {}),
                      wind: 'PirateWeather (NJ09 excluded)',
                      gust: 'PirateWeather (NJ09 excluded)',
                    },
                    _realtimeSources: {
                      ...(weatherWithRealtime.current._realtimeSources || {}),
                      wind: 'PirateWeather (NJ09 excluded)',
                      gust: 'PirateWeather (NJ09 excluded)',
                    },
                  },
                  _rtSources: {
                    ...weatherWithRealtime._rtSources,
                    wind: 'PirateWeather (NJ09 excluded)',
                    gust: 'PirateWeather (NJ09 excluded)',
                  },
                };
                console.log(`ZAP API-9: NJ09 wind excluded for ${crs.name}, using PirateWeather ${pwWind}mph`);
                incrementCallCount('nj09_excluded', crs);
              }
            } catch (e) {
              console.warn('ZAP API-9 PirateWeather fallback failed:', e.message);
            }
          }
          // Per-variable P2: cascade if NJWxNet wind is null.
          // B14 fix S191: mirrors fetchRealtimeOnly logic (B1 fix) — initial full load was
          // only checking for null, missing the stuck-at-zero sensor case. S262: removed
          // === 0 check — NJ09 already excluded separately; genuine calm wind (0 mph) should
          // not trigger P2 fallback.
          const curWindRaw = weatherWithRealtime?.current?.windSpeedRaw ?? weatherWithRealtime?.current?.windSpeedNum ?? null;
          if (curWindRaw === null) {
            try {
              const pw = await withTimeout(fetchPirateWeather(crs), 6000, 'PirateWeather P2 wind');
              incrementCallCount('pirateweather', crs);
              if (pw?.success && pw.current) {
                const pwWind = pw.current.windSpeedNum ?? 0;
                const pwGust = pw.current.windGust ?? 0;
                if (pwWind > 0 || pwGust > 0) {
                  logSystemHealth('p2_activated', crs, { variable: 'wind_gust', p1: 'NJWxNet', p2: 'PirateWeather', reason: 'njwxnet_wind_null', pwWind, p1Error: `NJWxNet wind=${curWindRaw}` });
                  weatherWithRealtime = {
                    ...weatherWithRealtime,
                    current: {
                      ...weatherWithRealtime.current,
                      windSpeedNum: pwWind,
                      windSpeed: `${pwWind} mph`,
                      windGust: pwGust,
                      feelsLike: calcFeelsLike(
                        weatherWithRealtime.current.temp ?? 65,
                        weatherWithRealtime.current.humidity ?? 50,
                        pwWind
                      ),
                      _windSource: 'PirateWeather (NJWxNet sensor fault)',
                    },
                    _rtSources: {
                      ...weatherWithRealtime._rtSources,
                      wind: 'PirateWeather (P2)',
                      gust: 'PirateWeather (P2)',
                    },
                  };
                  console.log(`ZAP P2 wind: NJWxNet null for ${crs.name}, using PirateWeather ${pwWind}mph`);
                }
              }
            } catch (e) {
              console.warn('ZAP P2 wind fallback failed:', e.message);
            }
          }
        } else {
          // NJWxNet not used — disabled by user, too far, or no station near this location.
          // P2: WeatherAPI current obs (temp/humidity/precip) + VC (wind/gust).
          const njwxError = njwx?.error || 'NJWxNet not used';
          console.log('ZAP: NJWxNet not used, fetching P2 sources (WeatherAPI + VC). Reason:', njwxError);
          logSystemHealth('p2_activated', crs, {
            variable: 'realtime_all', p1: 'NJWxNet', p2: 'WeatherAPI+VC',
            p1Error: njwxError,
          });
          _p2Active[crs.id] = { source: 'NJWxNet', since: new Date().toISOString() };
          const [wapiCurrent, vcCurrent] = await Promise.all([
            fetchWeatherAPICurrentObs(crs),
            fetchVC(crs),
          ]);
          incrementCallCount('weatherapi', crs);
          incrementCallCount('visual_crossing', crs);
          weatherWithRealtime = applyP2Override(forecast, wapiCurrent, vcCurrent);

          // Log to Google Sheet
          logSystemHealth('realtime_p2', crs, {
            p1Failed: 'NJWxNet',
            p1Error: njwxError,
            p2Temp: wapiCurrent?.success ? 'WeatherAPI' : 'FAILED',
            p2Wind: vcCurrent?.success ? 'VC' : 'FAILED',
            wapiTemp: wapiCurrent?.current?.temp,
            wapiFeels: wapiCurrent?.current?.feelsLike,
            vcWind: vcCurrent?.current?.windSpeedNum,
            vcGust: vcCurrent?.current?.windGust,
          });
        } // end NJWxNet not used else
        // UI-17: Patch sunset from Open-Meteo if Meteoblue (P1) didn't provide it
        if (om?.success) {
          if (!weatherWithRealtime.sunsetToday && om.sunsetToday) weatherWithRealtime = { ...weatherWithRealtime, sunsetToday: om.sunsetToday };
          if (!weatherWithRealtime.sunsetTomorrow && om.sunsetTomorrow) weatherWithRealtime = { ...weatherWithRealtime, sunsetTomorrow: om.sunsetTomorrow };
          if (!weatherWithRealtime.sunriseToday && om.sunriseToday) weatherWithRealtime = { ...weatherWithRealtime, sunriseToday: om.sunriseToday };
          // Patch vcDailyAll from om so days 3-7 have sunset data
          if ((!weatherWithRealtime.vcDailyAll || weatherWithRealtime.vcDailyAll.length === 0) && om.vcDailyAll?.length > 0) {
            weatherWithRealtime = { ...weatherWithRealtime, vcDailyAll: om.vcDailyAll };
          }
        }
        setWeatherData(weatherWithRealtime);
        setPollenData(pollen);
        setEtData(et);
        setNjwxData(njwx);
        setLightningData(lightning);
        // Fetch WeatherAPI current obs for R1 conditions icon — real-time conditions text, slot 28
        wapiLoadStartRef.current = Date.now(); // S216 F1: start 3s timeout clock
        fetchWeatherAPICurrentObs(crs).then(w => {
          setWapiCurrentData(w?.success ? w : null);
          if (!w?.success) logSystemHealth('wapi_current_fail', crs, { error: w?.error || 'unknown' }); // S216 F7
        }).catch(err => {
          logSystemHealth('wapi_current_fail', crs, { error: err?.message || 'unknown' }); // S216 F7
        });
        setLastRefreshTime(new Date());

        // Use existing waterDensity state (or fallback) — OSM loads in background and updates BAD when ready
        const currentOsm = waterDensity || { featureCount: 0, multiplier: 1, label: 'Loading...' };
        const bad = runBADEngine(weatherWithRealtime, crs, effectiveProfile, currentOsm, et, _soilMoisturePct);
        setBadResult(bad);
        setIsLoading(false);
        loadInFlight.current = false; // FETCHALL guard release — success path

        // S226: Silent P1 retry — if any forecast variable fell to P2 this load,
        // schedule a background reload after 20s to attempt P1 recovery.
        // By then, VC cold start will have resolved. One shot, no loop.
        // Uses crs (in scope) so it reloads the same course regardless of nav.
        if (_forecastP2[crs.id] && !_forecastP2[crs.id + '_retried']) {
          _forecastP2[crs.id + '_retried'] = true;
          delete _forecastP2[crs.id];
          logSystemHealth('silent_retry_scheduled', crs, { delayMs: 20000 });
          setTimeout(() => {
            loadCourse(crs, false); // S243: removed delete of _retried flag — must persist for session to prevent loop
          }, 20000);
        }
      } catch (err) {
        console.error('ZAP fetchAll error:', err);
        setWeatherError('Weather data unavailable. Check connection.');
        setIsLoading(false);
        loadInFlight.current = false; // FETCHALL guard release — error path
      }
    };

    if (!silent) fetchAll(course);;
  };

  // B-8: Cold-start fix — if course restored from localStorage but weatherData is null, fire loadCourse.
  // Without this, app opens to Home screen with course shown but no weather data loaded.
  // S193: guard against overwriting ?admin=recover screen on cold-start.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('admin') === 'recover') return;
    if (selectedCourse && !weatherData) {
        setScreen('home'); // S183: navigate home on cold-start
      setScreenHistory(['home']);
      loadCourse(selectedCourse, false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // S183: removed loadInFlight reset-on-course-change useEffect — was causing double-fire.
  // Guard releases correctly in silent path (5767), fetchAll success (6005), and error (6010).
  // Course switching still works — setSelectedCourse fires immediately inside loadCourse.

  // S101: Global 15-min auto-refresh KILLED — burns Meteoblue 500/day limit.
  // Realtime refresh: every 5 min when on Day of Play screen, handled here in parent
  // so setWeatherData doesn't remount OnCourseScreen (which would reset iemLoading).
  // Manual refresh via course header time tap. Full load on course switch/app open.
  useEffect(() => {
    if (screen !== 'dayofplay' || !selectedCourse) return;
    // DD-LOAD: if no weather data, fire silent full load
    if (!weatherData) { loadCourse(selectedCourse, true); return; }
    const refreshRealtime = async () => {
      try {
        const rt = await fetchRealtimeOnly(selectedCourse, njwxOverrideRef.current);
        setWeatherData(prev => {
          if (!prev) return prev;
          const patched = {
            ...prev,
            current: {
              ...prev.current,
              ...(rt.temp         != null && { temp: rt.temp }),
              ...(rt.humidity     != null && { humidity: rt.humidity }),
              ...(rt.windSpeedNum != null && { windSpeedNum: rt.windSpeedNum, windSpeed: rt.windSpeed }),
              ...(rt.windGust     != null && { windGust: rt.windGust }),
              ...(rt.feelsLike    != null && { feelsLike: rt.feelsLike }),
              ...(rt.precip1hr    != null && { precip1hr: rt.precip1hr }),
              _realtimeSources: rt._sources,
              _realtimeRefreshedAt: new Date(),
            },
          };
          return patched;
        });
      } catch (e) { console.warn('ZAP: Realtime refresh failed:', e.message); }
    };
    refreshRealtime();
    const rtInterval = setInterval(() => {
      // S181: Visibility guard — skip polling when app backgrounded (PWA closed,
      // phone locked, tab not active). Saves ~70% NJWxNet calls during a round
      // when phone is in pocket. User returning to app triggers immediate refresh
      // via the visibilitychange listener below.
      if (typeof document !== 'undefined' && document.hidden) return;
      refreshRealtime();
    }, 5 * 60 * 1000);
    // Immediate refresh on return-to-foreground so user sees fresh data on resume.
    const onVisibility = () => {
      if (typeof document !== 'undefined' && !document.hidden) refreshRealtime();
    };
    if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearInterval(rtInterval);
      if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisibility);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen, selectedCourse, realtimeRefreshTick]);


  // ── SPOND EVENTS ───────────────────────────────────────────
  const [spondEvents, setSpondEvents] = useState([]);
  useEffect(() => {
    const load = async () => {
      try {
        const r = await fetch(SPOND_EVENTS_URL, {
          headers: { 'Authorization': `token ${SPOND_GH_TOKEN}`, 'Accept': 'application/vnd.github.v3+json' }
        });
        if (r.ok) {
          const meta = await r.json();
          const d = JSON.parse(atob(meta.content));
          setSpondEvents(d.events || []);
        }
      } catch {}
    };
    load();
  }, []);

  // ── WINDOW STATUS — uses canonical getWindowStatus ─────────
  const windowStatus = (hrs) => {
    const result = getWindowStatus(hrs, profile);
    return result.status;
  };

  const statusStyle = (status) => {
    const s = STATUS_COLORS[status] || STATUS_COLORS['UNKNOWN'];
    return { bg: s.bg, color: s.color, border: s.border, text: s.text };
  };
  // S95: Trigger indicator symbols matching S84 max-case card reference
  // eslint-disable-next-line no-unused-vars
  const windowReason = (hrs) => {
    if (!hrs || !hrs.length) return null;
    const maxWind = Math.max(...hrs.map(h => h.windSpeedNum || 0));
    const minFeels = Math.min(...hrs.map(h => h.feelsLike != null ? h.feelsLike : (h.temp || 999)));
    const maxFeels = Math.max(...hrs.map(h => h.feelsLike != null ? h.feelsLike : (h.temp || 0)));
    const maxPrecip = Math.max(...hrs.map(h => h.precipChance || 0));
    const condStrings = hrs.map(h => (h.conditions || h.shortForecast || '').toLowerCase());
    const hasNoGoWx = condStrings.some(c => /thunder|lightning|heavy rain|freezing rain|sleet|snow|blizzard/.test(c) && !/blowing snow|drifting snow|dust storm/.test(c));
    const hasCautionWx = condStrings.some(c => /\brain\b|shower|drizzle|\bmist\b|\bfog\b|\bhaze\b/.test(c));
    const reasons = [];
    if (hasNoGoWx) reasons.push('Severe weather');
    else if (hasCautionWx) reasons.push('Rain expected');
    if (maxWind > WIND_NOGO) reasons.push(`Wind ${maxWind} mph — no-go`);
    else if (maxWind > WIND_STR_CAU) reasons.push(`Wind ${maxWind} mph — strong`);
    else if (maxWind > WIND_CAUTION) reasons.push(`Wind ${maxWind} mph — breezy`);
    if (maxPrecip > PRECIP_NOGO) reasons.push(`Rain ${maxPrecip}% — no-go`);
    else if (maxPrecip > PRECIP_STR_CAU) reasons.push(`Rain ${maxPrecip}% — strong caution`);
    else if (maxPrecip > PRECIP_CAUTION) reasons.push(`Rain ${maxPrecip}%`);
    if (minFeels < profile.minTemp) reasons.push(`Feels like ${Math.round(minFeels)}°F — below your ${profile.minTemp}°F limit`);
    if (maxFeels > profile.maxTemp) reasons.push(`Feels like ${Math.round(maxFeels)}°F — above your ${profile.maxTemp}°F limit`);
    return reasons.length ? reasons.join(' · ') : null;
  };

  // ── WINDOW SUMMARY ─────────────────────────────────────────
  const windowSummary = (hrs) => {
    if (!hrs || !hrs.length) return null;
    const temps = hrs.map(h => h.temp || 0);
    const feelsLikes = hrs.map(h => h.feelsLike != null ? h.feelsLike : (h.temp || 0));
    // Dominant condition: pick the "worst" (most impactful) condition across hours
    // Priority: thunder > snow > rain > drizzle > fog > cloudy > clear
    const condPriority = (c) => {
      const cl = (c || '').toLowerCase();
      if (/thunder|lightning|tstorm/.test(cl)) return 8;
      if (/freezing|sleet|ice/.test(cl)) return 7;
      if (/heavy rain|downpour/.test(cl)) return 6;
      if (/rain shower/.test(cl)) return 5;
      if (/snow/.test(cl)) return 5;
      if (/drizzle|light rain/.test(cl)) return 4;
      if (/rain/.test(cl)) return 5;
      if (/fog|mist|haze/.test(cl)) return 3;
      if (/overcast/.test(cl)) return 2;
      if (/cloudy|clouds/.test(cl)) return 1;
      return 0;
    };
    let dominantCondition = 'Clear';
    let maxPri = -1;
    for (const h of hrs) {
      const cond = h.conditions || h.shortForecast || 'Clear';
      const pri = condPriority(cond);
      if (pri > maxPri) { maxPri = pri; dominantCondition = cond; }
    }
    // isDaytime: true if any hour in window is daytime
    const isDaytime = hrs.some(h => h.isDaytime !== false);
    return {
      high: Math.max(...temps),
      low: Math.min(...temps),
      maxWind: Math.max(...hrs.map(h => h.windSpeedNum || 0)),
      maxGust: Math.max(...hrs.map(h => h.windGust || 0)),
      maxPrecip: Math.max(...hrs.map(h => h.precipChance || 0)),
      totalPrecip: hrs.reduce((s, h) => s + (h.precip || 0), 0),
      minFeelsLike: Math.min(...feelsLikes),
      maxFeelsLike: Math.max(...feelsLikes),
      maxUV: Math.max(...hrs.map(h => h.uvIndex || 0)),
      dominantCondition,
      isDaytime,
    };
  };

  // ── BUILD 7-DAY CALENDAR DATA ──────────────────────────────
  const buildCalendarDays = React.useCallback(() => {
    if (!weatherData?.hourly) return [];
    const now = new Date();
    const days = [];
    for (let d = 0; d < 7; d++) {
      const date = new Date(now);
      date.setDate(date.getDate() + d);
      const dayLabel = d === 0 ? 'Today' : d === 1 ? 'Tomorrow' :
        date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
      const dateStr = date.toLocaleDateString('en-CA');

      // AM: 6–12, PM: 12–20
      const mornHrs = weatherData.hourly.filter(h => {
        const hDate = new Date(h.time || h.dt * 1000);
        return hDate.toLocaleDateString('en-CA') === dateStr &&
          hDate.getHours() >= 6 && hDate.getHours() < 12;
      });
      const aftnHrs = weatherData.hourly.filter(h => {
        const hDate = new Date(h.time || h.dt * 1000);
        return hDate.toLocaleDateString('en-CA') === dateStr &&
          hDate.getHours() >= 12 && hDate.getHours() < 20;
      });

      // Mark past windows
      const mornPast = d === 0 && now.getHours() >= 12;
      const aftnPast = d === 0 && now.getHours() >= 20;

      days.push({
        d, dateStr, dayLabel,
        mornHrs, aftnHrs,
        mornStatus: mornPast ? 'PAST' : windowStatus(mornHrs),
        aftnStatus: aftnPast ? 'PAST' : windowStatus(aftnHrs),
        mornSummary: windowSummary(mornHrs),
        aftnSummary: windowSummary(aftnHrs),
        mornReason: windowReason(mornHrs),
        aftnReason: windowReason(aftnHrs),
      });
    }
    return days;
  }, [weatherData, profile]);

  // ── SORTED COURSES ─────────────────────────────────────────
  const sortedCourses = []; // Activity Planner: no static course list

  // ── AUTH GATE ──────────────────────────────────────────────
  if (!authed) {
    return (
      <div style={{ minHeight: '100vh', background: '#111', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div style={{ background: COLOR_CARD, borderRadius: 16, padding: 32, maxWidth: 320, width: '100%', textAlign: 'center', border: `1px solid ${COLOR_DARK_GREEN}` }}>
          <div style={{ fontSize: 24, fontWeight: 800, color: COLOR_GREEN, marginBottom: 8 }}>ZAP</div>
          <div style={{ fontSize: 24, fontWeight: 700, color: COLOR_WHITE, marginBottom: 4 }}>ZAP</div>
          <div style={{ fontSize: 13, color: COLOR_OFF_WHITE, marginBottom: 24 }}>Zur's Activity Planner</div>
          <input
            type="password"
            id="zap-password"
            name="zap-password"
            autoComplete="current-password"
            placeholder="Password"
            value={authInput}
            onChange={e => { setAuthInput(e.target.value); setAuthError(''); }}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                const val = authInput.trim().toLowerCase();
                if (val) {
                  const admin = true;
                  setAuthed(true);
                  setIsAdmin(admin);
                  try { localStorage.setItem('zap_authed', 'true'); localStorage.setItem('zap_admin', String(admin)); } catch {}
                } else {
                  setAuthError('Wrong password');
                  setAuthInput('');
                }
              }
            }}
            style={{ width: '100%', padding: '12px 16px', borderRadius: 10, border: `1px solid ${authError ? '#F87171' : COLOR_DARK_GREEN}`, background: COLOR_INPUT_BG, color: COLOR_WHITE, fontSize: 16, marginBottom: 8, boxSizing: 'border-box', outline: 'none' }}
          />
          {authError && <div style={{ color: '#F87171', fontSize: 13, marginBottom: 8 }}>{authError}</div>}
          <button
            onClick={() => {
              const val = authInput.trim().toLowerCase();
              if (val) {
                const admin = true;
                setAuthed(true);
                setIsAdmin(admin);
                try { localStorage.setItem('zap_authed', 'true'); localStorage.setItem('zap_admin', String(admin)); } catch {}
              } else {
                setAuthError('Wrong password');
                setAuthInput('');
              }
            }}
            style={{ ...BTN_LARGE, width: '100%', justifyContent: 'center', background: COLOR_CARD, border: `4px solid ${COLOR_DARK_GREEN}`, color: '#0a0a0a' }}
          >
            Enter
          </button>
        </div>
      </div>
    );
  }

  // ── COURSE SELECTOR (first launch + switcher) ──────────────
  // Activity Planner: no selectedCourse on first load — show HomeScreen
  // The HomeScreen handles location entry (GPS + address). Once a location
  // is selected, selectedCourse is set and we proceed to the dashboard.
  if (!selectedCourse && screen !== 'home' && screen !== 'profile') {
    // Redirect to home if no location selected and not already on home/profile
    setScreen('home');
    return null;
  }


  // ── INNER SCREEN COMPONENTS ────────────────────────────────
  const Header = ({ title, showBack, onBack }) => (
    <div style={{ background: COLOR_HEADER, padding: '8px 16px', display: 'flex', alignItems: 'center', gap: 10, borderBottom: `1px solid ${COLOR_DARK_GREEN}`, position: '-webkit-sticky', WebkitPosition: 'sticky', top: 0, zIndex: 100, transform: 'translateZ(0)', WebkitTransform: 'translateZ(0)' }}>
      {showBack && (
        <button onClick={onBack || goBack} style={{ ...BTN_BASE, padding: '8px 12px', background: 'transparent', color: COLOR_MUTED, fontSize: 20, minWidth: 44 }}>←</button>
      )}
      <div style={{ flex: 1, fontSize: 17, fontWeight: 700, color: COLOR_WHITE }}>{title}</div>
      {screenHistory.length > 2 && (
        <button onClick={() => { setScreenHistory(['home']); setScreen('home'); setTimeout(() => scrollToTop(), 50); }} style={{ ...BTN_BASE, padding: '6px 12px', background: COLOR_DARK_GREEN, color: '#0a0a0a', fontSize: 14, fontWeight: 700, borderRadius: 8 }}>Home</button>
      )}
    </div>
  );

// ── COURSE DIRECTORY (merged CourseDetail + 19th Hole per S98) ────────────

    // ---- Profile Screen ----
  const ProfileScreen = () => {
    const [localProfile, setLocalProfile] = useState({ ...profile });
    const [settingsTab, setSettingsTab] = useState('settings');
    const [iframeUrl, setIframeUrl] = useState(null);
    const [showQuickRef, setShowQuickRef] = useState(false);
  const [showAdminDocs, setShowAdminDocs] = useState(false);

    const [bpOpen, setBpOpen] = React.useState(false);
    const [expandedDocPhoto, setExpandedDocPhoto] = React.useState(null);

    return (
      <div style={{ padding: '16px', maxWidth: 480, margin: '0 auto' }}>

        {/* Tab bar */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          {[
            { id: 'settings', label: 'My Settings' },
            { id: 'help',     label: 'Help' },
          ].map(tab => (
            <button key={tab.id} onClick={() => setSettingsTab(tab.id)}
              style={{ flex: 1, padding: '10px 0', borderRadius: 10, border: `2px solid ${settingsTab === tab.id ? COLOR_GREEN : COLOR_DARK_GREEN}`,
                background: settingsTab === tab.id ? COLOR_DARK_GREEN : COLOR_INPUT_BG,
                color: settingsTab === tab.id ? '#0a0a0a' : COLOR_OFF_WHITE,
                fontWeight: settingsTab === tab.id ? 800 : 400, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit' }}>
              {tab.label}
            </button>
          ))}
        </div>

        {/* ── TAB 1: MY SETTINGS ── */}
        {settingsTab === 'settings' && (
        <div style={{ ...CARD_STYLE, padding: '20px', marginBottom: 12 }}>
          <div style={{ fontSize: '18px', fontWeight: 800, color: '#0a0a0a', marginBottom: 20, textTransform: 'uppercase', letterSpacing: '1px' }}>My Conditions</div>
          {/* C4: Two tappable numbers + live 5-zone color bar. Caution OUTSIDE limits only. */}
          {(() => {
            const TEMP_MIN = 30;
            const TEMP_MAX = 115;
            const cold = localProfile.minTemp;
            const hot  = localProfile.maxTemp;
            const range = TEMP_MAX - TEMP_MIN;
            // Zone boundaries — caution is OUTSIDE the limits only
            const noGoLow  = cold - 5;
            const noGoHi   = hot  + 5;
            // Pct helper
            const pct = (v) => Math.max(0, Math.min(100, (v - TEMP_MIN) / range * 100));
            // Gradient: red | yellow(5°) | green | yellow(5°) | red
            const grad = `linear-gradient(to right,
              #F87171 0%, #F87171 ${pct(noGoLow)}%,
              #FACC15 ${pct(noGoLow)}%, #FACC15 ${pct(cold)}%,
              #4ADE80 ${pct(cold)}%, #4ADE80 ${pct(hot)}%,
              #FACC15 ${pct(hot)}%, #FACC15 ${pct(noGoHi)}%,
              #F87171 ${pct(noGoHi)}%, #F87171 100%)`;
            // Plain English — updates as numbers change
            // eslint-disable-next-line no-unused-vars
            const plainText = `Below ${noGoLow}°F = No Go  ·  ${noGoLow}–${cold}°F = Caution  ·  ${cold}–${hot}°F = Go  ·  ${hot}–${noGoHi}°F = Caution  ·  Above ${noGoHi}°F = No Go`;
            return (
              <div style={{ marginBottom: 28 }}>
                <div style={{ fontSize: '14px', fontWeight: 700, color: '#0a0a0a', marginBottom: 14 }}>My Feels-Like Temperature Range</div>
                {/* Two tappable number controls — +/- buttons, no keyboard */}
                <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
                  {[
                    { key: 'minTemp', label: 'Feels Like Too Cold', color: '#5b9bd5', val: cold, min: TEMP_MIN, max: hot - 10 },
                    { key: 'maxTemp', label: 'Feels Like Too Hot',  color: '#e57373', val: hot,  min: cold + 10, max: TEMP_MAX },
                  ].map(({ key, label, color, val, min, max }) => (
                    <div key={key} style={{ flex: 1, textAlign: 'center' }}>
                      <div style={{ fontSize: '11px', color, fontWeight: 700, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', border: `2px solid ${color}`, borderRadius: 10, background: COLOR_DARKEST, overflow: 'hidden' }}>
                        <button
                          onClick={() => { const v = val - 1; if (v >= min) setLocalProfile(p => ({ ...p, [key]: v })); }}
                          style={{ width: 36, height: 56, background: 'transparent', border: 'none', color, fontSize: 22, cursor: 'pointer', flexShrink: 0 }}>−</button>
                        <div style={{ flex: 1, fontSize: 28, fontWeight: 800, color, textAlign: 'center' }}>{val}</div>
                        <button
                          onClick={() => { const v = val + 1; if (v <= max) setLocalProfile(p => ({ ...p, [key]: v })); }}
                          style={{ width: 36, height: 56, background: 'transparent', border: 'none', color, fontSize: 22, cursor: 'pointer', flexShrink: 0 }}>+</button>
                      </div>
                      <div style={{ fontSize: '11px', color: COLOR_MUTED, marginTop: 4 }}>°F</div>
                    </div>
                  ))}
                </div>
                {/* Live 5-zone color bar */}
                <div style={{ height: 20, borderRadius: 10, background: grad, marginBottom: 8, boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.4)' }} />
                {/* Plain English zones — stacked for readability */}
                <div style={{ fontSize: '12px', background: COLOR_DARKEST, borderRadius: 8, padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <div style={{display:'flex',alignItems:'center',gap:6}}><StatusIcon status="GO" size={14}/> <span style={{color:'#4ADE80', fontWeight:700}}>GO</span> <span style={{color:COLOR_OFF_WHITE}}>{cold}–{hot}°F</span></div>
                  <div style={{display:'flex',alignItems:'center',gap:6}}><StatusIcon status="CAUTION" size={14}/> <span style={{color:'#FACC15', fontWeight:700}}>CAUTION</span> <span style={{color:COLOR_OFF_WHITE}}>{noGoLow}–{cold}°F or {hot}–{noGoHi}°F</span></div>
                  <div style={{display:'flex',alignItems:'center',gap:6}}><StatusIcon status="NO-GO" size={14}/> <span style={{color:'#F87171', fontWeight:700}}>NO-GO</span> <span style={{color:COLOR_OFF_WHITE}}>below {noGoLow}°F or above {noGoHi}°F</span></div>
                </div>
              </div>
            );
          })()}
          {/* ── PLAYING WINDOW ── */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: '14px', fontWeight: 700, color: '#0a0a0a', marginBottom: 4 }}>My Activity Window</div>
            <div style={{ fontSize: '11px', color: '#3a3a3a', marginBottom: 10 }}>Go/No-Go uses worst conditions within this window only. Start Time can't be earlier than 6 AM. Set End Time to 12:00 AM for a window that runs through end of day.</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '10px', color: '#5b9bd5', fontWeight: 700, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Start Time</div>
                <input
                  type="time"
                  value={localProfile.playStart || '08:00'}
                  min="06:00"
                  onChange={e => setLocalProfile(p => ({ ...p, playStart: e.target.value < '06:00' ? '06:00' : e.target.value }))}
                  style={{ width: '100%', padding: '8px', background: COLOR_DARKEST, border: '2px solid #5b9bd5', borderRadius: 10, color: COLOR_WHITE, WebkitTextFillColor: '#ffffff', colorScheme: 'dark', fontSize: '18px', fontWeight: 800, textAlign: 'center' }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '10px', color: '#e57373', fontWeight: 700, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.5px' }}>End Time</div>
                <input
                  type="time"
                  value={localProfile.playEnd || '20:00'}
                  onChange={e => setLocalProfile(p => ({ ...p, playEnd: e.target.value }))}
                  style={{ width: '100%', padding: '8px', background: COLOR_DARKEST, border: '2px solid #e57373', borderRadius: 10, color: COLOR_WHITE, WebkitTextFillColor: '#ffffff', colorScheme: 'dark', fontSize: '18px', fontWeight: 800, textAlign: 'center' }}
                />
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', background: '#111', borderRadius: 10, border: '1px solid #1a1a1a', marginBottom: 12 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: COLOR_OFF_WHITE }}>🔊 Storm Audio Alert *</div>
            <button
              onClick={() => setLocalProfile(p => ({ ...p, audioAlertEnabled: !p.audioAlertEnabled }))}
              style={{
                width: 50, height: 28, borderRadius: 14, border: 'none', cursor: 'pointer', flexShrink: 0,
                background: localProfile.audioAlertEnabled ? '#4ADE80' : '#374151',
                position: 'relative', transition: 'background 0.2s'
              }}>
              <div style={{
                position: 'absolute', top: 3, width: 22, height: 22, borderRadius: 11,
                background: '#fff', transition: 'left 0.2s',
                left: localProfile.audioAlertEnabled ? 25 : 3
              }} />
            </button>
          </div>
          <button onClick={() => {
            const saved = { ...localProfile };
            setProfile(saved);
            try { localStorage.setItem('zap_profile', JSON.stringify(saved)); } catch {}
            loadCourse(selectedCourse, true, saved);
            setProfileSaved(true);
            setTimeout(() => { setProfileSaved(false); goBack(); }, 1000);
          }}
            style={{ ...BTN_LARGE, background: COLOR_CARD, border: `4px solid ${COLOR_DARK_GREEN}`, color: '#0a0a0a', justifyContent: 'center', fontSize: '18px', fontWeight: 700 }}>
            {profileSaved ? 'Saved!' : 'Save Settings'}
          </button>

          {/* S226: Storage caution note */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, marginTop: 10, padding: '8px 10px', background: 'rgba(250,204,21,0.08)', borderRadius: 6, border: '1px solid rgba(250,204,21,0.2)' }}>
            <span style={{ fontSize: 13, flexShrink: 0 }}>⚠️</span>
            <span style={{ fontSize: 11, color: '#3a3a3a', lineHeight: 1.5 }}>
              Your settings are saved on this device. If you ever clear Safari data or browser storage, you'll need to re-enter them here.
            </span>
          </div>

          {/* S227: Storm Audio Alert description */}
          <div style={{ fontSize: 11, color: COLOR_MUTED, lineHeight: 1.5, marginTop: 10, padding: '8px 10px' }}>
            🔊 When ON — an alarm fires when a storm is detected. Requires: screen unlocked and permanently on, app open on the Storm screen, and volume up. For weather days when you&#39;re monitoring conditions. Tap &quot;Got It&quot; to silence until next update, or &quot;Turn Off Audio&quot; to stop all alerts.
          </div>

          {/* Wind info — universal thresholds, not configurable */}

        </div>
        )} {/* end Tab 1 */}



        {settingsTab === 'help' && (
        <div style={{ marginBottom: 12 }}>

          {/* Quick Reference Guide */}
          <div style={{ ...CARD_STYLE, padding: '16px', marginBottom: 10 }}>
            <div style={{ fontSize: '14px', fontWeight: 700, color: '#0a0a0a', marginBottom: 6 }}>📖 How to Use ZAP</div>
            <div style={{ fontSize: '12px', color: '#0a0a0a', marginBottom: 10 }}>Quick reference — thresholds, workflow, every card explained.</div>
            <button onClick={() => setShowQuickRef(true)} style={{ display: 'block', width: '100%', background: COLOR_INPUT_BG, color: COLOR_OFF_WHITE, borderRadius: 8, padding: '12px 16px', fontSize: '14px', fontWeight: 700, textAlign: 'center', border: `1px solid ${COLOR_DARK_GREEN}`, cursor: 'pointer' }}>
              Open Guide →
            </button>
          </div>

          {/* Onboarding */}
          <div style={{ ...CARD_STYLE, padding: '16px', marginBottom: 10 }}>
            <div style={{ fontSize: '14px', fontWeight: 700, color: '#0a0a0a', marginBottom: 4 }}>🚀 Onboarding</div>
            <div style={{ fontSize: '12px', color: '#0a0a0a', marginBottom: 10 }}>Step-by-step setup guide for new users.</div>
            <a href="https://res.cloudinary.com/dovzgljte/image/upload/ZAP_PRO_ONBOARDING_INFOGRAPHIC_S265.jpg" target="_blank" rel="noopener noreferrer" style={{ display: 'block', borderRadius: 8, overflow: 'hidden', border: `1px solid ${COLOR_DARK_GREEN}`, marginBottom: 8 }}>
              <img src="https://res.cloudinary.com/dovzgljte/image/upload/w_400,h_90,c_fill,g_north/ZAP_PRO_ONBOARDING_INFOGRAPHIC_S265.jpg" alt="ZAP Pro Onboarding" style={{ width: '100%', height: 90, objectFit: 'cover', display: 'block' }} />
            </a>
            <div style={{ display: 'flex', gap: 6 }}>
              <button onClick={() => setIframeUrl('/ZAP_Onboarding_S241.html')}
                style={{ flex: 1, background: COLOR_INPUT_BG, color: COLOR_OFF_WHITE, borderRadius: 8, padding: '9px 4px', fontSize: 11, fontWeight: 700, textAlign: 'center', border: `1px solid ${COLOR_DARK_GREEN}`, cursor: 'pointer', fontFamily: 'inherit' }}>Read HTML →</button>
              <a href="https://www.dropbox.com/scl/fi/ps4ublwim6hmnb4cn4q6m/ZAP_PRO_ON_BOARDING-SLIDES.pdf?rlkey=yryuewpydhku8s2gwyzhkr8bi&st=66q2oixj&dl=0" target="_blank" rel="noopener noreferrer"
                style={{ flex: 1, background: '#1e2a10', color: '#fbbf24', borderRadius: 8, padding: '9px 4px', fontSize: 11, fontWeight: 700, textAlign: 'center', textDecoration: 'none', border: '1px solid #fbbf24' }}>Slides PDF →</a>
              <a href="https://youtu.be/KF5Y5R_Cxck" target="_blank" rel="noopener noreferrer"
                style={{ flex: 1, background: '#1a1010', color: '#f87171', borderRadius: 8, padding: '9px 4px', fontSize: 11, fontWeight: 700, textAlign: 'center', textDecoration: 'none', border: '1px solid #7f1d1d' }}>▶ Video</a>
            </div>
          </div>

          {/* Quick Reference Modal */}
          {showQuickRef && (
            <div style={{ position: 'fixed', inset: 0, background: '#000', zIndex: 9999, display: 'flex', flexDirection: 'column' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', background: '#0a0a0a', borderBottom: `2px solid ${COLOR_DARK_GREEN}`, flexShrink: 0 }}>
                <span style={{ fontSize: 16, fontWeight: 700, color: COLOR_WHITE }}>📋 Quick Reference Guide</span>
                <button onClick={() => setShowQuickRef(false)} style={{ background: 'transparent', border: 'none', color: COLOR_OFF_WHITE, fontSize: 28, cursor: 'pointer', padding: 4 }}>×</button>
              </div>
              <iframe src="/ZAP_Quick_Reference_S262.html" style={{ flex: 1, border: 'none', width: '100%' }} title="ZAP Quick Reference" />
            </div>
          )}

        </div>
        )} {/* end Help tab */}


      {/* ── IFRAME DOC MODAL ── */}
      {iframeUrl && (
        <div style={{ position: 'fixed', inset: 0, background: '#0a0a0a', zIndex: 99999, display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', background: '#0a0a0a', borderBottom: '2px solid #22C55E', flexShrink: 0 }}>
            <span style={{ color: '#FDE047', fontSize: 13, fontWeight: 700 }}>ZAP Docs</span>
            <button onClick={() => setIframeUrl(null)} style={{ background: '#22C55E', color: '#000', border: 'none', borderRadius: 8, padding: '8px 18px', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>✕ Close</button>
          </div>
          <iframe src={iframeUrl} style={{ flex: 1, border: 'none', width: '100%' }} title="ZAP Doc" />
        </div>
      )}
      </div>
    );
  };

  // ---- UV label helper ----
  const uvLabel = (uv) => {
    if (uv <= 0) return 'None';
    if (uv <= 2) return 'Low';
    if (uv <= 5) return 'Moderate';
    if (uv <= 7) return 'High';
    if (uv <= 10) return 'Very High';
    return 'Extreme';
  };

  // Truth Chain now lives inline on ScoreEntry screen (pre-set buttons + Other)

  // ---- Hourly Drilldown ----
  const Drilldown = () => {
    const [selectedDay, setSelectedDay] = useState(null); // null = today
    // eslint-disable-next-line no-unused-vars
    const windIcon = (mph) => {
      if (mph === 0) return 'Calm';
      if (mph <= 8) return 'Light';
      if (mph <= 18) return 'Breezy';
      return 'Strong';
    };

    if (!selectedCourse || !weatherData) return (
      <div style={{ padding: '40px 16px', textAlign: 'center', color: COLOR_MUTED }}>
        No weather data — go back and load a course first.
      </div>
    );

    const now = new Date();
    const hourly = weatherData.hourly || [];

    // Build list of available days from hourly data
    const dayMap = {};
    hourly.forEach(h => {
      const d = new Date(h.time);
      d.setHours(0,0,0,0);
      const key = d.toLocaleDateString('en-CA');
      if (!dayMap[key]) dayMap[key] = { date: d, hours: [] };
      dayMap[key].hours.push(h);
    });
    // Extend with vcDailyAll days beyond hourly range (days 3-6)
    // Use date string as key (YYYY-MM-DD) to avoid UTC/local timezone mismatch
    const vcDailyAll = weatherData?.vcDailyAll || [];
    // Rebuild dayMap with date string keys to avoid ISO UTC mismatch
    const dayMapByDate = {};
    Object.values(dayMap).forEach(entry => {
      const d = entry.date;
      const dateKey = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
      dayMapByDate[dateKey] = entry;
    });
    vcDailyAll.forEach(vcDay => {
      if (!dayMapByDate[vcDay.date]) {
        const base = new Date(vcDay.date + 'T00:00:00');
        // Synthesize 4 representative hours (9AM, 12PM, 2PM, 5PM) from daily data
        const makeHour = (hr, temp) => ({
          time: new Date(new Date(base).setHours(hr, 0, 0, 0)),
          temp, windSpeedNum: vcDay.windSpeed,
          windGust: Math.round(vcDay.windSpeed * 1.3), conditions: vcDay.conditions,
          precipChance: vcDay.precipChance, precip: vcDay.precip / 4, feelsLike: null,
        });
        dayMapByDate[vcDay.date] = {
          date: base,
          hours: [
            makeHour(9,  vcDay.tempMin),
            makeHour(12, Math.round((vcDay.tempMin + vcDay.tempMax) / 2)),
            makeHour(14, vcDay.tempMax),
            makeHour(17, Math.round((vcDay.tempMin + vcDay.tempMax) / 2)),
          ],
        };
      }
    });
    // Replace dayMap with date-keyed version for selector
    Object.keys(dayMap).forEach(k => delete dayMap[k]);
    Object.values(dayMapByDate).forEach(entry => {
      const d = new Date(entry.date);
      d.setHours(0,0,0,0);
      dayMap[d.toLocaleDateString('en-CA')] = { ...entry, date: d };
    });
    const days = Object.values(dayMap).sort((a,b) => a.date - b.date).slice(0, 7);

    // Selected day data
    const activeDay = selectedDay || (days[0] ? days[0].date.toLocaleDateString('en-CA') : null);
    const activeDayData = dayMap[activeDay] || (days[0] ? days[0] : null);
    const activeDayDate = activeDayData ? activeDayData.date : now;

    // Is this day beyond 48 hours?
    const hoursAhead = activeDayDate ? (activeDayDate - now) / 3600000 : 0;
    const isExtended = hoursAhead > 48;

    // Day label helper
    const dayLabel = (d) => {
      const today = new Date(); today.setHours(0,0,0,0);
      const tomorrow = new Date(today); tomorrow.setDate(today.getDate()+1);
      if (d.getTime() === today.getTime()) return 'Today';
      if (d.getTime() === tomorrow.getTime()) return 'Tomorrow';
      return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    };

    // Go/No-Go reason for a single hour slot — uses canonical getHourStatus
    // eslint-disable-next-line no-unused-vars
    const hourGoNoGo = (h) => {
      const result = getHourStatus(h, profile);
      return { status: result.status, color: result.color, reason: result.reason };
    };

    // eslint-disable-next-line no-unused-vars

    const isPast = (h) => new Date(h.time) < now;
    // Single closest slot only — prevents duplicate ▶ Now rows
    const nowMs = now.getTime();
    // Prefer the most recent past hour as "Now". Only advance to next future hour if < 30 min ahead.
    const closestHour = activeDayData
      ? (() => {
          const hours = activeDayData.hours;
          // Find last past hour
          const pastHours = hours.filter(h => new Date(h.time).getTime() <= nowMs);
          if (pastHours.length) return pastHours[pastHours.length - 1];
          // All future — pick nearest
          return hours.reduce((best, h) => {
            const diff = Math.abs(new Date(h.time).getTime() - nowMs);
            return diff < Math.abs(new Date(best.time).getTime() - nowMs) ? h : best;
          }, hours[0]);
        })()
      : null;
    // eslint-disable-next-line no-unused-vars
    const isNow = (h) => !!(closestHour && h.time === closestHour.time && Math.abs(new Date(h.time).getTime() - nowMs) < 7200000);

    return (
      <div style={{ maxWidth: 480, margin: '0 auto', width: '100%', overflowX: 'hidden', boxSizing: 'border-box', paddingBottom: 16 }}>

        {/* Extended forecast notice */}
        {isExtended && (
          <div style={{ padding: '10px 16px', background: '#2a2a00', borderBottom: `1px solid ${COLOR_GOLD}`, fontSize: '13px', color: COLOR_GOLD, textAlign: 'center' }}>
            Extended forecast — use for planning, conditions may change
          </div>
        )}

        {/* Day selector */}
        <div style={{ display: 'flex', overflowX: 'auto', padding: '12px 16px', gap: 8, borderBottom: `1px solid ${COLOR_DARK_GREEN}`, WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
          {days.map(({ date }) => {
            const key = date.toLocaleDateString('en-CA');
            const isActive = key === activeDay;
            return (
              <button key={key} onClick={() => setSelectedDay(key)}
                style={{ flexShrink: 0, padding: '10px 16px', borderRadius: 10, border: `2px solid ${isActive ? COLOR_GREEN : COLOR_DARK_GREEN}`,
                  background: isActive ? COLOR_GREEN_DARK : COLOR_INPUT_BG, color: isActive ? COLOR_GOLD : '#a0907a',
                  fontWeight: isActive ? 800 : 400, fontSize: '13px', cursor: 'pointer', fontFamily: 'inherit', minHeight: 44 }}>
                {dayLabel(date)}
              </button>
            );
          })}
        </div>

        {/* Hourly rows — S98 HourlyRow card design */}
        <div style={{ padding: '8px 12px' }}>
          {activeDayData ? activeDayData.hours.map((h, idx) => {
            // Pollen banner on first hour only
            const pollenBanner = (() => {
              const pd = pollenData;
              if (!pd || !pd.success || pd.offSeason || idx !== 0) return null;
              if (pd.level === 0) return null;
              const colors = [COLOR_GREEN, COLOR_GOLD, '#FB923C', COLOR_RED, COLOR_RED];
              return { label: `Pollen today: ${pd.label}`, color: colors[pd.level] };
            })();
            return (
              <div key={idx}>
                {pollenBanner && (
                  <div style={{ padding: '6px 12px', background: '#1a2a1a', fontSize: '12px', color: pollenBanner.color, fontWeight: 700, borderRadius: 8, marginBottom: 6 }}>
                    {pollenBanner.label}
                  </div>
                )}
                <HourlyRow h={h} profile={profile} />
              </div>
            );
          }) : (
            <div style={{ padding: '30px', textAlign: 'center', color: COLOR_MUTED }}>No data for this day</div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '10px 16px', background: COLOR_HEADER, borderTop: `1px solid ${COLOR_DARK_GREEN}`,
          fontSize: '12px', color: '#a0907a', display: 'flex', justifyContent: 'space-between' }}>
          <span>▶ = Current hour</span>
          <span style={{display:'flex',alignItems:'center',gap:4}}><StatusIcon status="CAUTION" size={12}/> Caution · <StatusIcon status="NO-GO" size={12}/> No-Go</span>
        </div>
      </div>
    );
  };

  // RadarScreen removed — Check Radar opens Windy directly from Game Day card


  // ---- Admin Panel ----
  const AdminPanel = () => {
    const [log, setLog] = useState(() => getWULog());
    const [showRaw, setShowRaw] = useState(false);

    const handleClear = () => {
      clearWULog();
      setLog([]);
    };

    const exportCSV = () => {
      if (!log.length) return;
      const headers = 'Timestamp,Course,VC Temp,VC Wind,VC Gust,VC Humidity,VC PrecipChance,VC Conditions,VC Source,WU Temp,WU Wind,WU PrecipChance';
      const rows = log.map(e => [
        e.ts, e.course,
        e.vc?.temp ?? '', e.vc?.windSpeed ?? '', e.vc?.windGust ?? '',
        e.vc?.humidity ?? '', e.vc?.precipChance ?? '',
        `"${(e.vc?.conditions || '').replace(/"/g, "'")}"`,
        e.vc?.source ?? '',
        e.wu?.temp ?? 'pending', e.wu?.windSpeed ?? 'pending', e.wu?.precipChance ?? 'pending'
      ].join(','));
      const csv = [headers, ...rows].join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `zap_wu_compare_${new Date().toISOString().slice(0,10)}.csv`;
      a.click(); URL.revokeObjectURL(url);
    };

    return (
      <div style={{ padding: '16px 16px 40px' }}>
        <div style={{ ...CARD_STYLE, padding: '14px 16px', marginBottom: 12, background: COLOR_RED_BG, border: `1px solid ${COLOR_RED}` }}>
          <div style={{ fontSize: 13, color: COLOR_RED, fontWeight: 700 }}>ADMIN MODE — Dr. Z Only</div>
          <div style={{ fontSize: 12, color: COLOR_MUTED, marginTop: 4 }}>WU comparison logging active. {log.length} entries stored.</div>
        </div>

        {/* WU vs VC Comparison Log */}
        <div style={{ fontSize: 14, fontWeight: 800, color: COLOR_GOLD, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 1 }}>VC vs WU Comparison Log</div>
        <div style={{ fontSize: 12, color: COLOR_MUTED, marginBottom: 12 }}>
          VC data logged on every course load. WU column shows "pending" until June trial is wired.
        </div>

        <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
          <button onClick={exportCSV} style={{ ...BTN_BASE, padding: '10px 16px', background: COLOR_INPUT_BG, color: COLOR_OFF_WHITE, fontSize: 13, fontWeight: 700, border: `1px solid ${COLOR_DARK_GREEN}` }}>
            Export CSV
          </button>
          <button onClick={() => setShowRaw(!showRaw)} style={{ ...BTN_BASE, padding: '10px 16px', background: COLOR_INPUT_BG, color: COLOR_GOLD, fontSize: 13, border: `1px solid ${COLOR_GOLD}` }}>
            {showRaw ? 'Hide Raw' : 'Show Raw JSON'}
          </button>
          <button onClick={handleClear} style={{ ...BTN_BASE, padding: '10px 16px', background: COLOR_RED_BG, color: COLOR_RED, fontSize: 13, border: `1px solid ${COLOR_RED}` }}>
            Clear Log
          </button>
        </div>

        {log.length === 0 ? (
          <div style={{ color: COLOR_MUTED, fontSize: 14 }}>No entries yet. Load a course to start logging.</div>
        ) : (
          <>
            {/* Last 10 entries table */}
            <div style={{ overflowX: 'auto', marginBottom: 12 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, color: COLOR_OFF_WHITE }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${COLOR_DARK_GREEN}` }}>
                    {['Time','Course','VC°F','VC Wind','VC Gust','VC Precip%','WU°F','WU Wind','WU Precip%','Δ Temp'].map(h => (
                      <th key={h} style={{ padding: '6px 8px', color: COLOR_GOLD, textAlign: 'left', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[...log].reverse().slice(0, 20).map((e, i) => {
                    const deltaTemp = (e.vc?.temp != null && e.wu?.temp != null) ? (e.vc.temp - e.wu.temp).toFixed(1) : '—';
                    return (
                      <tr key={i} style={{ borderBottom: `1px solid ${COLOR_DARKEST}`, background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.03)' }}>
                        <td style={{ padding: '5px 8px', color: COLOR_MUTED, whiteSpace: 'nowrap' }}>{new Date(e.ts).toLocaleString('en-US', { month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</td>
                        <td style={{ padding: '5px 8px', whiteSpace: 'nowrap' }}>{e.course}</td>
                        <td style={{ padding: '5px 8px' }}>{e.vc?.temp ?? '—'}</td>
                        <td style={{ padding: '5px 8px' }}>{e.vc?.windSpeed ?? '—'}</td>
                        <td style={{ padding: '5px 8px' }}>{e.vc?.windGust ?? '—'}</td>
                        <td style={{ padding: '5px 8px' }}>{e.vc?.precipChance ?? '—'}%</td>
                        <td style={{ padding: '5px 8px', color: COLOR_MUTED }}>{e.wu?.temp ?? 'pending'}</td>
                        <td style={{ padding: '5px 8px', color: COLOR_MUTED }}>{e.wu?.windSpeed ?? 'pending'}</td>
                        <td style={{ padding: '5px 8px', color: COLOR_MUTED }}>{e.wu?.precipChance != null ? `${e.wu.precipChance}%` : 'pending'}</td>
                        <td style={{ padding: '5px 8px', color: deltaTemp !== '—' ? (Math.abs(parseFloat(deltaTemp)) > 2 ? COLOR_RED : COLOR_GREEN) : COLOR_MUTED }}>{deltaTemp !== '—' ? `${deltaTemp > 0 ? '+' : ''}${deltaTemp}°` : '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {log.length > 20 && <div style={{ fontSize: 12, color: COLOR_MUTED, marginBottom: 12 }}>Showing 20 of {log.length} entries. Export CSV for full dataset.</div>}
          </>
        )}

        {showRaw && log.length > 0 && (
          <div style={{ ...CARD_STYLE, padding: 12, marginTop: 8 }}>
            <pre style={{ fontSize: 10, color: COLOR_MUTED, overflowX: 'auto', margin: 0 }}>
              {JSON.stringify(log.slice(-5), null, 2)}
            </pre>
          </div>
        )}

        {/* Sign out */}
        <button onClick={() => {
          try { localStorage.removeItem('zap_authed'); localStorage.removeItem('zap_admin'); } catch {}
          setAuthed(false); setIsAdmin(false);
        }} style={{ ...BTN_LARGE, background: COLOR_INPUT_BG, color: '#a0907a', marginTop: 24, justifyContent: 'center', fontSize: 14 }}>
          Sign Out
        </button>
      </div>
    );
  };

  // ---- Root render ----
  // Password gate
  if (!authed) {
    return (
      <div style={{ background: '#111', minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', fontFamily: "'Georgia', serif", padding: 24 }}>
        <div style={{ fontSize: 24, fontWeight: 800, color: COLOR_GREEN, marginBottom: 12 }}>ZAP</div>
        <div style={{ fontSize: 28, fontWeight: 800, color: COLOR_GOLD, marginBottom: 4 }}>ZAP</div>
        <div style={{ fontSize: 13, color: COLOR_MUTED, marginBottom: 32 }}>The Field-Level Truth</div>
        <div style={{ width: '100%', maxWidth: 320 }}>
          <input
            type="password"
            placeholder="Enter access code"
            value={authInput}
            onChange={e => setAuthInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleAuth()}
            style={{ width: '100%', padding: '14px 16px', fontSize: 16, borderRadius: 10, border: `2px solid ${authError ? COLOR_RED : COLOR_DARK_GREEN}`, background: COLOR_INPUT_BG, color: COLOR_WHITE, fontFamily: "'Georgia', serif", boxSizing: 'border-box', marginBottom: 10 }}
            autoFocus
          />
          {authError && <div style={{ color: COLOR_RED, fontSize: 13, marginBottom: 10 }}>{authError}</div>}
          <button onClick={handleAuth} style={{ ...BTN_LARGE, background: COLOR_CARD, border: `4px solid ${COLOR_DARK_GREEN}`, color: '#0a0a0a', justifyContent: 'center', fontSize: 16, fontWeight: 700 }}>
            Enter
          </button>
        </div>
        <div style={{ fontSize: 11, color: COLOR_OFF_WHITE, marginTop: 32 }}>Jersey Circuit Alpha — Summer 2026</div>
      </div>
    );
  }


  // ============================================================
  // SHARED STATS HELPERS
  // ============================================================
  // Canonical date formatter — handles ISO strings from Apps Script
  // Shared expanded round card — Tier 1 + Tier 2
  // worstHoleIndex: if set, highlights that hole cell in red

  // Collapsed round summary row — shared across All/By Course/By Player tabs
  // S161 Item 7: RoundSummaryRow — shared component per S160 ROUND-SUMMARY-ROW spec.
  // Layout (per JZ S161 confirmed design):
  //   {course name, white bold}              {score, gold big}
  //   {date · tee → finish, gold}            {Par 71, muted}
  //   {4h 0m, white}                          {-15 / E / +3, color-coded}
  //   {players, muted grey}                   {▼ chevron}
  //   {weather line · NJWxNet}  (full-width row below)
  //
  // Differential color rules (golf standard):
  //   negative (under par) → RED
  //   zero (even par)      → GREEN
  //   positive (over par)  → BLUE
  //
  // NOTE: This component handles DISPLAY only. Best/Worst/avg/sort logic still uses raw
  // round.total — that overhaul is deferred to next session per JZ direction.
  // ============================================================
  // ROUND HISTORY SCREEN (3-tab: All Rounds | By Course | By Player)
  // ============================================================

  // ============================================================
  // STATISTICS HUB (ScoreStatsScreen)
  // ============================================================

  // ============================================================
  // STATS OVERVIEW
  // ============================================================

  // ============================================================
  // PLAYERS LUCK CHARM
  // ============================================================

  // ============================================================
  // SCORECARD SCREEN
  // ============================================================


  // ── PLANNING MODE — CALENDAR TILE ─────────────────────────
  const CalendarTile = ({ day, period, status, summary, reason, past }) => {
    const isPast = past || status === 'PAST';
    const isUnknown = status === 'UNKNOWN';
    const isActive = activeWindow.dayIdx === day.d && activeWindow.period === period;
    const ss = (isPast || isUnknown) ? { bg: '#1a1a1a', color: '#6b6060', border: '#3a3030', text: '#6b6060' } : statusStyle(status);
    const periodLabel = period === 'morn' ? 'AM' : 'PM';
    const periodHours = period === 'morn' ? '6a–12p' : '12p–8p';
    const isToday = day.d === 0;

    // Build trigger hierarchy from hourly data (S84 max-case card format)
    const hrs = period === 'morn' ? day.mornHrs : day.aftnHrs;
    const tileTriggers = React.useMemo(() => {
      if (!hrs || !hrs.length || isPast || isUnknown || status === 'GO') return null;
      // Collect all triggers, then deduplicate by type — keep worst value per type per severity
      const rawTriggers = {};
      for (const h of hrs) {
        const hr = getHourStatus(h, profile);
        if (hr.triggers) {
          for (const [lv, trigs] of Object.entries(hr.triggers)) {
            if (!rawTriggers[lv]) rawTriggers[lv] = {};
            for (const t of trigs) {
              // Extract trigger type: "Wind 12 mph" → "Wind", "Gusts 31 mph" → "Gusts", "Precip 68%" → "Precip"
              // "Feels 22° (limit 45°)" → "Feels", "Thunderstorms" → "Thunderstorms"
              const typeMatch = t.match(/^([A-Za-z\s\/]+?)[\s]?\d/);
              const trigType = typeMatch ? typeMatch[1].trim() : t;
              // Extract numeric value for comparison
              const numMatch = t.match(/(\d+)/);
              const numVal = numMatch ? parseInt(numMatch[1]) : 0;
              // Keep the trigger string with the worst (highest) numeric value for this type
              // Exception: Feels-like triggers — lower is worse for cold, higher for hot
              if (!rawTriggers[lv][trigType] || numVal > rawTriggers[lv][trigType].val) {
                rawTriggers[lv][trigType] = { val: numVal, text: t };
              }
            }
          }
        }
      }
      // Convert to arrays, suppressing trigger types already shown in a higher severity tier
      const levels = ['NO-GO', 'STRONG CAUTION', 'CAUTION'];
      const seenTypes = new Set();
      const result = {};
      for (const lv of levels) {
        if (rawTriggers[lv]) {
          const filtered = {};
          for (const [type, data] of Object.entries(rawTriggers[lv])) {
            if (!seenTypes.has(type)) {
              filtered[type] = data;
              seenTypes.add(type);
            }
          }
          if (Object.keys(filtered).length > 0) {
            result[lv] = Object.values(filtered).map(v => v.text);
          }
        }
      }
      return Object.keys(result).length > 0 ? result : null;
    }, [hrs, status, isPast, isUnknown]);

    const handleTap = () => {
      if (isPast || isUnknown) return;
      if (isActive) {
        setActiveWindow(prev => ({ ...prev, dayIdx: -1 }));
      } else {
        setActiveWindow({ dayIdx: day.d, period });
      }
      setTimeout(() => scrollToTop(), 50);
    };

    return (
      <button
        onClick={handleTap}
        style={{
          flex: 1, minWidth: 0, border: `2px solid ${isActive ? ss.color : ss.border}`,
          borderRadius: 12, background: ss.bg,
          padding: '7px 6px', cursor: (isPast || isUnknown) ? 'default' : 'pointer',
          opacity: isPast ? 0.3 : isUnknown ? 0.5 : 1, textAlign: 'left',
          boxShadow: isActive ? `0 0 14px ${ss.color}55` : 'none',
          transition: 'all 0.15s',
        }}
      >
        {/* Period label + hours + StatusIcon */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <span style={{ fontSize: 15, fontWeight: 700, color: isPast ? '#556655' : ss.text }}>{periodLabel}</span>
            <span style={{ fontSize: 11, color: isPast ? '#445544' : ss.text, opacity: 0.7, marginLeft: 6 }}>{periodHours}</span>
          </div>
          {!isPast && !isUnknown && <StatusIcon status={status} size={22} />}
        </div>

        {isPast ? (
          <div style={{ fontSize: 14, color: '#445544', marginTop: 6 }}>— Past —</div>
        ) : isUnknown ? (
          <div style={{ fontSize: 12, color: '#556655', marginTop: 6 }}>No forecast</div>
        ) : (
          <>
            {/* Sky: emoji + condition text — prominent, same pattern as HourlyRow */}
            {summary && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4, marginBottom: 6 }}>
                {renderSkyIcon(summary.dominantCondition || 'Clear', summary.isDaytime !== false, 26)}
                <span style={{ fontSize: 13, fontWeight: 600, color: ss.text }}>{skyLabel(summary.dominantCondition || 'Clear')}</span>
              </div>
            )}

            {/* Feels-Like (primary) + actual in parens */}
            <div style={{ fontSize: 12, color: ss.text, opacity: 0.8 }}>Feels Like</div>
            {summary && (
              <div style={{ fontSize: 22, fontWeight: 700, color: ss.text, marginBottom: 6 }}>
                {Math.round(summary.maxFeelsLike)}°
                {summary.high != null && (
                  <span style={{ fontSize: 13, fontWeight: 400, opacity: 0.6 }}> ({summary.high}°)</span>
                )}
              </div>
            )}

            {/* TRIGGER HIERARCHY — S84 max-case card format */}
            {tileTriggers && status !== 'GO' && (() => {
              const levels = ['NO-GO', 'STRONG CAUTION', 'CAUTION'];
              const parts = [];
              for (const lv of levels) {
                if (tileTriggers[lv] && tileTriggers[lv].length > 0) {
                  const isWorst = lv === status;
                  const lvLabel = lv === 'STRONG CAUTION' ? 'STRONG CAUTION' : lv;
                  // eslint-disable-next-line no-unused-vars
                  const lvStyle = statusStyle(lv);
                  if (isWorst) {
                    // Worst level = section header + white bold triggers with indicator icons
                    parts.push(
                      <div key={lv} style={{ marginTop: 4 }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: '#fff', marginBottom: 2 }}>
                          {lvLabel} TRIGGERS:
                        </div>
                        {tileTriggers[lv].map((t, i) => (
                          <div key={i} style={{ fontSize: 11, fontWeight: 700, color: '#fff', paddingLeft: 2, display:'flex', alignItems:'center', gap:4 }}>
                            <StatusIcon status={lv} size={12} /> {t}
                          </div>
                        ))}
                      </div>
                    );
                  } else {
                    // Lower severity = "ALSO {LEVEL}:" + triggers in card dark text with indicator icons
                    parts.push(
                      <div key={lv} style={{ marginTop: 3 }}>
                        <div style={{ fontSize: 10, fontWeight: 600, color: ss.text, opacity: 0.8 }}>
                          ALSO {lv === 'STRONG CAUTION' ? 'STR CAU' : lv}:
                        </div>
                        {tileTriggers[lv].map((t, i) => (
                          <div key={i} style={{ fontSize: 11, fontWeight: 500, color: ss.text, opacity: 0.7, paddingLeft: 2, display:'flex', alignItems:'center', gap:4 }}>
                            <StatusIcon status={lv} size={12} /> {t}
                          </div>
                        ))}
                      </div>
                    );
                  }
                }
              }
              return parts.length > 0 ? <div style={{ marginTop: 2 }}>{parts}</div> : null;
            })()}

            {/* UV + Bugs footer */}
            {summary && (summary.maxUV > 3 || summary.maxBug > 1) && (
              <div style={{ fontSize: 11, color: ss.text, opacity: 0.6, marginTop: 4 }}>
                {summary.maxUV > 3 ? `UV ${Math.round(summary.maxUV)} ${uvLabel(Math.round(summary.maxUV))}` : ''}{summary.maxUV > 3 && summary.maxBug > 1 ? ' · ' : ''}{summary.maxBug > 1 ? `Bugs ${summary.maxBug}/10` : ''}
              </div>
            )}

            {/* Cart (today only) */}
            {isToday && badResult?.ground && (
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 3, paddingTop: 3, borderTop: `1px solid ${ss.text}33` }}>
                <span style={{ fontSize: 11, color: ss.text, opacity: 0.8 }}>Cart</span>
                <span style={{ fontSize: 11, fontWeight: 600, color: badResult.ground.sogginess === 'soggy' ? '#fff' : ss.text }}>
    {/* ACTIVITY PLANNER: cart/frost display suppressed -- not applicable outside golf */}
    {/* {badResult.ground.cartStatus || 'All Clear'} */}

                </span>
              </div>
            )}
          </>
        )}
      </button>
    );
  };

  // ── PLANNING MODE — INLINE DRILL-DOWN ─────────────────────
  const InlineDrillDown = ({ day, period }) => {
    const hrs = period === 'morn' ? day.mornHrs : day.aftnHrs;
    const status = period === 'morn' ? day.mornStatus : day.aftnStatus;
    const ss = statusStyle(status);
    const isToday = day.d === 0;

    if (!hrs || !hrs.length) {
      return (
        <div style={{ background: COLOR_CARD, borderRadius: 12, padding: 16, margin: '8px 0', border: `1px solid ${COLOR_DARK_GREEN}` }}>
          <div style={{ color: COLOR_MUTED, fontSize: 13 }}>No forecast data for this window.</div>
        </div>
      );
    }

    // Gust color helper (S85 #5: 20/30/40)
    const gustColor = (g) => g > 40 ? STATUS_COLORS['NO-GO'].color : g > 30 ? STATUS_COLORS['STRONG CAUTION'].color : g > 20 ? STATUS_COLORS['CAUTION'].color : '#e0e0e0';
    // Precip color
    // eslint-disable-next-line no-unused-vars
    const pColor = (v) => v > PRECIP_NOGO ? STATUS_COLORS['NO-GO'].color : v > PRECIP_STR_CAU ? STATUS_COLORS['STRONG CAUTION'].color : v > PRECIP_CAUTION ? STATUS_COLORS['CAUTION'].color : '#e0e0e0';
    // Wind color
    // eslint-disable-next-line no-unused-vars
    const wColor = (v) => v > WIND_NOGO ? STATUS_COLORS['NO-GO'].color : v > WIND_STR_CAU ? STATUS_COLORS['STRONG CAUTION'].color : v > WIND_CAUTION ? STATUS_COLORS['CAUTION'].color : '#e0e0e0';
    // Temp color
    // eslint-disable-next-line no-unused-vars
    const tColor = (f) => (f < profile.minTemp - 5 || f > profile.maxTemp + 5) ? STATUS_COLORS['NO-GO'].color : (f < profile.minTemp || f > profile.maxTemp) ? STATUS_COLORS['CAUTION'].color : '#e0e0e0';

    return (
      <div style={{ background: ss.bg, borderRadius: 12, padding: 14, margin: '8px 0', border: `2px solid ${ss.border}` }}>
        {/* Status header with close button */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <StatusIcon status={status} size={20} />
            <span style={{ fontSize: 18, fontWeight: 800, color: ss.text }}>{status}</span>
            <span style={{ fontSize: 12, color: ss.text, opacity: 0.7, marginLeft: 4 }}>
              {day.dayLabel} · {period === 'morn' ? '6 AM – 12 PM' : '12 PM – 8 PM'}
            </span>
          </div>
          <button
            onClick={() => setActiveWindow(prev => ({ ...prev, dayIdx: -1 }))}
            style={{ background: 'transparent', border: 'none', color: ss.text, fontSize: 24, cursor: 'pointer', padding: 4 }}
          >×</button>
        </div>

        {/* Cart status (today only) */}
        {isToday && badResult?.ground && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: 'rgba(0,0,0,0.2)', borderRadius: 6, marginBottom: 8, border: `1px solid ${badResult.ground.sogginess === 'soggy' ? STATUS_COLORS['CAUTION'].border : STATUS_COLORS['GO'].border}` }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: badResult.ground.sogginess === 'soggy' ? STATUS_COLORS['CAUTION'].color : STATUS_COLORS['GO'].color }}>
    {/* ACTIVITY PLANNER: cart/frost display suppressed -- not applicable outside golf */}
    {/* Cart: {badResult.ground.cartStatus || 'All Clear'} */}

            </span>
            {badResult.past24Rain > 0 && (
              <span style={{ fontSize: 12, color: '#e0e0e0' }}>{badResult.past24Rain}" rain (24h)</span>
            )}
          </div>
        )}



        {/* Pollen — 3 types compact */}
        <div style={{ display: 'flex', gap: 12, fontSize: 13, padding: '6px 12px', background: 'rgba(0,0,0,0.25)', borderRadius: 6, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
          <span style={{ color: '#e0e0e0', fontWeight: 600 }}>Pollen</span>
          {pollenData?.success && !pollenData.offSeason
            ? <>
                <span style={{ color: pollenData.tree.color }}>🌳 {pollenData.tree.label}</span>
                <span style={{ color: pollenData.grass.color }}>🌾 {pollenData.grass.label}</span>
                <span style={{ color: pollenData.weed.color }}>🌿 {pollenData.weed.label}</span>
              </>
            : <span style={{ color: '#668866' }}>No data</span>
          }
        </div>

        {/* Hourly rows — S164 fix: width:100% on wrapper so cards fill container */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {hrs.map((h, i) => (
            <div key={i} style={{ width: '100%' }}>
              <HourlyRow h={h} profile={profile} />
            </div>
          ))}
        </div>
      </div>
    );
  };

  // ── PLANNING MODE SCREEN ───────────────────────────────────
  const PlanningScreen = () => {
    const calDays = buildCalendarDays();
    const activeDayObj = activeWindow.dayIdx >= 0 ? calDays[activeWindow.dayIdx] : null;

    return (
      <div style={{ padding: '12px 12px 80px' }}>
        {/* Source legend removed — lives behind 📡 icon in header (S91) */}

        {/* Inline drill-down at top when active */}
        {activeDayObj && (
          <InlineDrillDown day={activeDayObj} period={activeWindow.period} />
        )}

        {/* Loading / error */}
        {isLoading && (
          <div style={{ textAlign: 'center', padding: 32, color: COLOR_MUTED }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: COLOR_MUTED, marginBottom: 8 }}>Loading...</div>
            <div>Loading weather for {selectedCourse.name}…</div>
          </div>
        )}
        {weatherError && !isLoading && (
          <div style={{ background: '#EF4444', border: '1px solid #DC2626', borderRadius: 10, padding: 16, marginBottom: 12, color: '#450A0A', fontSize: 13, fontWeight: 600 }}>
            {weatherError}
          </div>
        )}

        {/* 7-day calendar grid */}
        {!isLoading && calDays.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            {calDays.map(day => (
              <div key={day.d} style={{ marginBottom: 6 }}>
                {/* Day label — bold name + date */}
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 4, paddingLeft: 2 }}>
                  <span style={{ fontSize: 16, fontWeight: 800, color: COLOR_WHITE }}>{day.dayLabel.split(',')[0] || day.dayLabel}</span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: COLOR_WHITE }}>{day.dateStr ? new Date(day.dateStr + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}</span>
                  {(() => {
                    const sunsetRaw = weatherData?.daily?.find?.(d2 => d2.dateStr === day.dateStr)?.sunset
                      || weatherData?.daily?.sunset?.[day.d];
                    if (!sunsetRaw) return null;
                    const st = new Date(sunsetRaw);
                    const h = st.getHours(), m = st.getMinutes();
                    const label = `🌇 ${h > 12 ? h-12 : h}:${String(m).padStart(2,'0')} PM`;
                    return <span style={{ fontSize: 11, color: '#FCD34D', opacity: 0.8, marginLeft: 4 }}>{label}</span>;
                  })()}
                </div>
                {/* AM / PM tiles side by side */}
                <div style={{ display: 'flex', gap: 8 }}>
                  <CalendarTile
                    day={day} period="morn"
                    status={day.mornStatus} summary={day.mornSummary}
                    reason={day.mornReason}
                    past={day.d === 0 && new Date().getHours() >= 12}
                  />
                  <CalendarTile
                    day={day} period="aftn"
                    status={day.aftnStatus} summary={day.aftnSummary}
                    reason={day.aftnReason}
                    past={day.d === 0 && new Date().getHours() >= 20}
                  />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Spond events */}
        {spondEvents.length > 0 && (
          <div 
            onClick={() => setScreen('events')}
            style={{ background: COLOR_CARD, borderRadius: 12, padding: 14, border: `2px solid ${COLOR_DARK_GREEN}`, cursor: 'pointer' }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: COLOR_MUTED, marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Upcoming Events</span>
              <span style={{ fontSize: 12, color: COLOR_GREEN }}>View All →</span>
            </div>
            {spondEvents.slice(0, 3).map((ev, i) => {
              const teeTime = ev.startTime ? new Date(ev.startTime).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' }) : null;
              const arriveTime = ev.startTime ? (() => { const d = new Date(ev.startTime); d.setMinutes(d.getMinutes() - 30); return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' }); })() : null;
              const dateStr = ev.startTime ? new Date(ev.startTime).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'America/New_York' }) : '';
              return (
                <div key={i} style={{ padding: '8px 0', borderBottom: i < Math.min(spondEvents.length, 3) - 1 ? `1px solid ${COLOR_DARK_GREEN}` : 'none' }}>
                  <div style={{ fontSize: 15, color: COLOR_WHITE, fontWeight: 700 }}>{ev.course || ev.heading || 'TBD'}</div>
                  <div style={{ fontSize: 12, color: COLOR_MUTED, marginTop: 2 }}>
                    {dateStr}{teeTime ? ` · Start ${teeTime}` : ''}{arriveTime ? ` · Arrive ${arriveTime}` : ''}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // ── ON COURSE SCREEN ──────────────────────────────────────
  const OnCourseScreen = () => {
    const [iemData, setIemData] = React.useState(null);
    const [iemLoading, setIemLoading] = React.useState(true);
    const [iemRefreshTick, setIemRefreshTick] = React.useState(0);

    // S227: Storm audio alert
    const audioLoopRef = React.useRef(null);
    const audioSilencedRef = React.useRef(false);
    const prevAlertStateRef = React.useRef('CLEAR');
    const [showAudioPopup, setShowAudioPopup] = React.useState(false);

    const stopAudioLoop = React.useCallback(() => {
      if (audioLoopRef.current) { clearTimeout(audioLoopRef.current); audioLoopRef.current = null; }
    }, []);

    const playAudioPattern = React.useCallback((state) => {
      stopAudioLoop();
      const ctx = audioCtxRef.current;
      if (!ctx) return;
      if (ctx.state === 'suspended') ctx.resume();

      let beeps, cycleDur;
      if (state === 'GET IN NOW') {
        beeps = [
          {freq:1400,start:0,    dur:0.12,type:'square'},
          {freq:1400,start:0.2,  dur:0.12,type:'square'},
          {freq:1400,start:0.4,  dur:0.12,type:'square'},
          {freq:1600,start:0.6,  dur:0.4, type:'square'},
        ];
        cycleDur = 1200;
      } else if (state === 'HEAVY STORM') {
        beeps = [
          {freq:1200,start:0,    dur:0.2,type:'sawtooth'},
          {freq:1000,start:0.3,  dur:0.2,type:'sawtooth'},
          {freq:800, start:0.6,  dur:0.4,type:'sawtooth'},
        ];
        cycleDur = 1800;
      } else {
        beeps = [
          {freq:880,start:0,    dur:0.4,type:'sine'},
          {freq:660,start:0.6,  dur:0.6,type:'sine'},
        ];
        cycleDur = 2500;
      }

      const playOneCycle = () => {
        if (audioSilencedRef.current || !audioEnabledRef.current) return;
        for (const b of beeps) {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.type = b.type || 'square';
          osc.frequency.setValueAtTime(b.freq, ctx.currentTime + b.start);
          gain.gain.setValueAtTime(1.0, ctx.currentTime + b.start);
          gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + b.start + b.dur);
          osc.start(ctx.currentTime + b.start);
          osc.stop(ctx.currentTime + b.start + b.dur + 0.05);
        }
        audioLoopRef.current = setTimeout(playOneCycle, cycleDur);
      };
      playOneCycle();
    }, [stopAudioLoop]);

    // Watch alertState — fire audio on storm, stop on CLEAR
    React.useEffect(() => {
      const state = iemData?.alertState;
      if (!state) return;

      if (state === 'CLEAR' || state === 'UNKNOWN' || state === 'OFF_HOURS') {
        stopAudioLoop();
        setShowAudioPopup(false);
        audioSilencedRef.current = false;
        prevAlertStateRef.current = 'CLEAR';
        return;
      }

      if (!audioEnabledRef.current) {
        stopAudioLoop();
        setShowAudioPopup(false);
        return;
      }

      // If audio was just toggled ON — reset so current storm fires as new
      if (audioEnabledRef._justEnabled) {
        audioEnabledRef._justEnabled = false;
        prevAlertStateRef.current = 'CLEAR';
        audioSilencedRef.current = false;
      }

      const prev = prevAlertStateRef.current;
      const escalated = (
        (state === 'GET IN NOW' && prev !== 'GET IN NOW') ||
        (state === 'HEAVY STORM' && prev === 'RAIN APPROACHING')
      );
      const isNew = prev === 'CLEAR' || prev === 'UNKNOWN';

      if (isNew || escalated) {
        audioSilencedRef.current = false;
        playAudioPattern(state);
        setShowAudioPopup(true);
      } else if (!audioSilencedRef.current) {
        // Same state, not silenced — keep playing (loop manages itself)
        if (!audioLoopRef.current) {
          playAudioPattern(state);
          setShowAudioPopup(true);
        }
      }
      prevAlertStateRef.current = state;
    }, [iemData, profile.audioAlertEnabled, playAudioPattern, stopAudioLoop]);

    // Cleanup on unmount
    React.useEffect(() => {
      return () => { stopAudioLoop(); };
    }, [stopAudioLoop]);

    // Fetch IEM NEXRAD cells — locked spec S129 — runs 24/7, no off-hours gate
    React.useEffect(() => {
      const load = async () => {
        try {
          const url = 'https://mesonet.agron.iastate.edu/geojson/nexrad_attr.py';
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 15000);
          const r = await fetch(url, { signal: controller.signal });
          clearTimeout(timeoutId);
          if (r.status === 503) throw new Error('IEM_503');
          if (!r.ok) throw new Error('IEM fetch failed');
          const gj = await r.json();
          const features = gj.features || [];
          const lat = selectedCourse?.lat;
          const lng = selectedCourse?.lng;

          const R = 3958.8;
          const toRad = d => d * Math.PI / 180;
          const distMi = (la1, lo1, la2, lo2) => {
            const dLat = toRad(la2-la1), dLon = toRad(lo2-lo1);
            const a = Math.sin(dLat/2)**2 + Math.cos(toRad(la1))*Math.cos(toRad(la2))*Math.sin(dLon/2)**2;
            return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
          };
          const bearing = (la1, lo1, la2, lo2) => {
            const y = Math.sin(toRad(lo2-lo1))*Math.cos(toRad(la2));
            const x = Math.cos(toRad(la1))*Math.sin(toRad(la2)) - Math.sin(toRad(la1))*Math.cos(toRad(la2))*Math.cos(toRad(lo2-lo1));
            return (Math.atan2(y, x)*180/Math.PI + 360) % 360;
          };
          const bearingLabel = b => {
            const dirs = ['N','NE','E','SE','S','SW','W','NW'];
            return dirs[Math.round(b/45)%8];
          };

          // Process all cells DBZ>=35 within 50mi
          const allCells = features
            .filter(f => (f.properties?.max_dbz || 0) >= 35)
            .map(f => {
              const p = f.properties;
              const coords = f.geometry?.coordinates;
              const cLat = coords?.[1] ?? 0;
              const cLon = coords?.[0] ?? 0;
              const dist = distMi(lat, lng, cLat, cLon);
              const bear = bearing(lat, lng, cLat, cLon);
              // S177 BUGFIX: IEM `drct` is meteorological "direction storm is COMING FROM"
              // (per IEM docs: a "west" drct = storm moving from west to east).
              // Flip by 180° to get actual motion direction. Same pattern as NWS warning
              // text parser at line 2482. Without this flip, movingToward is fully inverted:
              // east cells moving away triggered false alerts; west cells approaching were ignored.
              const motionBear = ((p.drct || 0) + 180) % 360;
              const speedKt = p.sknt || 0;
              const speedMph = speedKt * 1.15078;
              // Valid speed required for ETA (except 10mi hard floor)
              const hasSpeed = speedMph > 2;
              const etaMin = hasSpeed ? Math.round(dist / speedMph * 60) : null;
              // ±45° bearing cone
              const bearFromCellToCourse = bearing(cLat, cLon, lat, lng);
              const angleDelta = Math.abs(((motionBear - bearFromCellToCourse) + 360) % 360);
              const movingToward = angleDelta < 45 || angleDelta > 315;
              return {
                dist: Math.round(dist*10)/10,
                bear, bearLabel: bearingLabel(bear),
                dbz: p.max_dbz,
                speedMph: Math.round(speedMph),
                etaMin, movingToward, hasSpeed,
                cLat, cLon
              };
            })
            .filter(c => c.dist <= 50)
            .sort((a, b) => (a.etaMin ?? 999) - (b.etaMin ?? 999));

          // Determine alert tier per locked spec:
          // GET IN NOW:      DBZ>=45, ETA<=20min OR dist<=10mi (moving toward)
          // HEAVY STORM:     DBZ>=45, ETA<=60min OR dist<=10mi (no-speed fallback)
          // RAIN APPROACHING:DBZ>=35, ETA<=90min, moving toward, has speed
          // CLEAR:           nothing qualifies

          let alertState = 'CLEAR';
          let alertColor = '#4ADE80';
          let alertMsg = 'No storms detected';
          let primaryCell = null;
          let additionalCount = 0;

          // Check GET IN NOW first (worst state)
          const getInNow = allCells.filter(c =>
            c.dbz >= 45 && c.movingToward &&
            ((c.hasSpeed && c.etaMin != null && c.etaMin <= 20) || c.dist <= 10)
          );
          // Check HEAVY STORM
          const heavyStorm = allCells.filter(c =>
            c.dbz >= 45 && c.movingToward &&
            ((c.hasSpeed && c.etaMin != null && c.etaMin <= 60) || c.dist <= 10)
          );
          // Check RAIN APPROACHING
          const rainApproach = allCells.filter(c =>
            c.dbz >= 35 && c.movingToward && c.hasSpeed &&
            c.etaMin != null && c.etaMin <= 90
          );

          if (getInNow.length > 0) {
            alertState = 'GET IN NOW';
            alertColor = '#EF4444';
            primaryCell = getInNow[0];
            additionalCount = getInNow.length - 1;
            const eta = primaryCell.etaMin;
            alertMsg = primaryCell.dist <= 10 && !primaryCell.hasSpeed
              ? `Storm from ${primaryCell.bearLabel}, ${primaryCell.dist}mi — speed unknown — monitor closely`
              : `Storm from ${primaryCell.bearLabel}, ${primaryCell.dist}mi — ~${eta} min`;
          } else if (heavyStorm.length > 0) {
            alertState = 'HEAVY STORM';
            alertColor = '#FB923C';
            primaryCell = heavyStorm[0];
            additionalCount = heavyStorm.length - 1;
            const etaLow = primaryCell.etaMin;
            const etaHigh = primaryCell.etaMin ? primaryCell.etaMin + Math.round(primaryCell.etaMin * 0.2) : null;
            alertMsg = primaryCell.dist <= 10 && !primaryCell.hasSpeed
              ? `Heavy storm from ${primaryCell.bearLabel}, ${primaryCell.dist}mi — speed unknown — monitor closely`
              : `Heavy storm from ${primaryCell.bearLabel}, ${primaryCell.dist}mi — ~${etaLow}–${etaHigh} min · potential lightning`;
          } else if (rainApproach.length > 0) {
            alertState = 'RAIN APPROACHING';
            alertColor = '#FACC15';
            primaryCell = rainApproach[0];
            additionalCount = rainApproach.length - 1;
            const etaLow = primaryCell.etaMin;
            const etaHigh = primaryCell.etaMin + Math.round(primaryCell.etaMin * 0.2);
            alertMsg = `Rain from ${primaryCell.bearLabel}, ${primaryCell.dist}mi — ~${etaLow}–${etaHigh} min`;
          }

          setIemData({
            cells: allCells, primaryCell, additionalCount,
            alertState, alertColor, alertMsg,
            fetchedAt: new Date()
          });
        } catch (err) {
          // On fetch failure during active alert: hold last known state
          setIemData(prev => {
            if (prev && prev.alertState && prev.alertState !== 'CLEAR' && prev.alertState !== 'UNKNOWN') {
              return { ...prev, alertMsg: `${prev.alertMsg} · tracking lost ${new Date().toLocaleTimeString('en-US',{hour:'numeric',minute:'2-digit'})} — check sky`, fetchedAt: new Date() };
            }
            return { error: err.message, alertState: 'UNKNOWN', alertColor: COLOR_MUTED, alertMsg: err.message === 'IEM_503' ? 'Storm tracking unavailable — check sky.' : 'Storm data unavailable — check radar', fetchedAt: new Date() };
          });
        }
        setIemLoading(false);
      };
      load();
      const interval = setInterval(() => {
        // S181: Visibility guard — skip polling when app backgrounded.
        // Storm tracking matters when user is actively checking sky → app foreground.
        // Resume immediately on return-to-foreground.
        if (typeof document !== 'undefined' && document.hidden) return;
        load();
      }, 5 * 60 * 1000);
      const onVisibility = () => {
        if (typeof document !== 'undefined' && !document.hidden) load();
      };
      if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVisibility);
      return () => {
        clearInterval(interval);
        if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisibility);
      };
    }, [selectedCourse, iemRefreshTick]);

    const cur = weatherData?.current;
    const now = new Date();
    // Activity Planner: extend drilldown to 2AM next day (covers evening/night activities)
    const midnight = new Date(now);
    if (now.getHours() >= 2) { midnight.setDate(midnight.getDate() + 1); }
    midnight.setHours(2, 0, 0, 0); // 2AM next day (or today if before 2AM)
    const nextHours = (weatherData?.hourly || []).filter(h => {
      const t = new Date(h.time || h.dt*1000);
      // Include current hour even if its timestamp is minutes past (e.g. 10:05 still shows 10 PM card)
      const tHourStart = new Date(t); tHourStart.setMinutes(0, 0, 0);
      const nowHourStart = new Date(now); nowHourStart.setMinutes(0, 0, 0);
      return tHourStart >= nowHourStart && t <= midnight;
    });

    return (
      <div style={{ padding: '12px 12px 80px', maxWidth: 480, margin: '0 auto' }}>

        {/* Realtime source legend removed — lives behind 📡 icon in header (S91) */}

        {/* ── S227: STORM AUDIO ALERT POP-UP ── */}
        {showAudioPopup && profile.audioAlertEnabled && iemData?.alertState && iemData.alertState !== 'CLEAR' && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.88)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
            <div style={{ background: '#0d0d0d', border: `2px solid ${iemData.alertColor || '#EF4444'}`, borderRadius: 14, padding: '24px 20px', width: '100%', maxWidth: 360 }}>
              <div style={{ fontSize: 22, fontWeight: 800, color: iemData.alertColor || '#EF4444', marginBottom: 8 }}>
                {iemData.alertState}
              </div>
              <div style={{ fontSize: 14, color: '#e5e7eb', marginBottom: 20, lineHeight: 1.5 }}>
                {iemData.alertMsg}
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  onClick={() => {
                    audioSilencedRef.current = true;
                    stopAudioLoop();
                    if (audioCtxRef.current) audioCtxRef.current.suspend();
                    setShowAudioPopup(false);
                  }}
                  style={{ flex: 1, padding: '14px 0', borderRadius: 10, border: `2px solid ${iemData.alertColor || '#4ADE80'}`, background: '#0d0d0d', color: iemData.alertColor || '#4ADE80', fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>
                  Got It
                </button>
                <button
                  onClick={() => {
                    stopAudioLoop();
                    setShowAudioPopup(false);
                    audioSilencedRef.current = false;
                    setProfile(p => {
                      const updated = { ...p, audioAlertEnabled: false };
                      try { localStorage.setItem('zap_profile', JSON.stringify(updated)); } catch {}
                      return updated;
                    });
                  }}
                  style={{ flex: 1, padding: '14px 0', borderRadius: 10, border: '2px solid #EF4444', background: '#0d0d0d', color: '#EF4444', fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>
                  Turn Off Audio
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── STORM ALERT ZONE ── */}
        <div style={{ background: '#050505', border: `2px solid ${iemData?.alertColor || '#4ADE80'}`, borderRadius: 14, padding: 16, marginBottom: 12 }}>
          {/* Header row: label + time + refresh */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: iemData?.alertColor || '#4ADE80' }}>
              STORM ALERT ZONE
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {iemData?.fetchedAt && (
                <div style={{ fontSize: 11, color: '#999' }}>
                  {iemData.fetchedAt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                </div>
              )}
              <button
                onClick={() => { clearAllCaches(); setIemLoading(true); setIemData(null); setIemRefreshTick(t => t + 1); loadCourse(selectedCourse); }}
                style={{ background: 'transparent', border: `1px solid #333`, borderRadius: 6, color: COLOR_OFF_WHITE, fontSize: 11, padding: '2px 8px', cursor: 'pointer' }}>
                ↻
              </button>
            </div>
          </div>

          {iemLoading ? (
            <div style={{ color: COLOR_MUTED, fontSize: 13 }}>Scanning for storm cells…</div>
          ) : (
            <>
              <div style={{ fontSize: 16, fontWeight: 800, color: iemData?.alertColor || '#4ADE80', marginBottom: 4 }}>
                {iemData?.alertState === 'OFF_HOURS' ? 'MONITORING OFF' : (iemData?.alertState || 'CLEAR')}
              </div>
              <div style={{ fontSize: 13, color: COLOR_OFF_WHITE, marginBottom: iemData?.additionalCount > 0 ? 8 : 0 }}>
                {iemData?.alertMsg}
              </div>
              {iemData?.additionalCount > 0 && (
                <div style={{ fontSize: 12, color: COLOR_MUTED, marginTop: 4 }}>
                  +{iemData.additionalCount} additional cell{iemData.additionalCount > 1 ? 's' : ''} behind it
                </div>
              )}

            </>
          )}
        </div>

        {/* ── DECISION FLOW STRIP ── */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0, marginBottom: 12, background: '#111', borderRadius: 10, padding: '10px 8px' }}>
          {[
            { icon: '🌩️', label: 'Storm Alert', sub: "What's coming" },
            { icon: '📡', label: 'Radar',       sub: 'Where it is' },
            { icon: '⚡', label: 'Right Now',   sub: "What's happening" },
            { icon: '📅', label: 'Forecast',    sub: "What's next" },
          ].map((step, i) => (
            <div key={step.label} style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
                <span style={{ fontSize: 18 }}>{step.icon}</span>
                <span style={{ fontSize: 10, fontWeight: 700, color: COLOR_OFF_WHITE, marginTop: 2, textAlign: 'center' }}>{step.label}</span>
                <span style={{ fontSize: 9, color: COLOR_MUTED, textAlign: 'center', marginTop: 1 }}>{step.sub}</span>
              </div>
              {i < 3 && <span style={{ fontSize: 14, color: COLOR_MUTED, flexShrink: 0, marginBottom: 14 }}>→</span>}
            </div>
          ))}
        </div>

        {/* ── CHECK RADAR — opens Windy directly; ℹ shows tips modal ── */}
        {(() => {
          const windyUrl = `https://www.windy.com/?radar,${selectedCourse.lat},${selectedCourse.lng},8`;
          return (
            <>
              <div style={{ position: 'relative', marginBottom: 12 }}>
                <button
                  onClick={() => {
                    window.open(windyUrl, '_blank', 'noopener,noreferrer');
                    try { navigator.clipboard.writeText(`${selectedCourse.lat},${selectedCourse.lng}`); } catch {}
                  }}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%', padding: '12px 0', background: COLOR_CARD, border: `4px solid ${COLOR_DARK_GREEN}`, borderRadius: 10, color: '#0a0a0a', fontSize: 15, fontWeight: 700, cursor: 'pointer', boxSizing: 'border-box' }}>
                  📡 Check Radar
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); setShowRadarInfo(true); }}
                  aria-label="Radar tips"
                  style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'transparent', border: 'none', cursor: 'pointer', padding: '4px 6px', lineHeight: 1 }}
                ><svg width="16" height="16" viewBox="0 0 16 16"><circle cx="8" cy="8" r="7" fill="none" stroke="#E5E7EB" strokeWidth="1"/><text x="8" y="8" textAnchor="middle" dominantBaseline="central" fontSize="9" fontWeight="700" fill="#E5E7EB" fontFamily="Arial,sans-serif">i</text></svg></button>
              </div>
              {showRadarInfo && (
                <div onClick={() => setShowRadarInfo(false)}
                  style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
                  <div onClick={(e) => e.stopPropagation()}
                    style={{ background: '#0d1f0d', border: `1px solid ${COLOR_DARK_GREEN}`, borderRadius: 14, padding: '20px 18px', maxWidth: 360, width: '100%' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: COLOR_OFF_WHITE }}>What to look for on Windy</div>
                      <button onClick={() => setShowRadarInfo(false)}
                        style={{ background: 'transparent', border: 'none', color: COLOR_OFF_WHITE, fontSize: 20, cursor: 'pointer', lineHeight: 1, padding: 0 }}>✕</button>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      <div style={{ fontSize: 13, color: COLOR_OFF_WHITE }}><span style={{ color: COLOR_GOLD, fontWeight: 700 }}>Radar</span><span style={{ color: COLOR_MUTED }}> — </span>Default view. Shows where rain is right now and which direction it's moving. Watch the animation.</div>
                      <div style={{ fontSize: 13, color: COLOR_OFF_WHITE }}><span style={{ color: COLOR_GOLD, fontWeight: 700 }}>Rain, Thunder</span><span style={{ color: COLOR_MUTED }}> — </span>Shows if the storm has lightning activity.</div>
                      <div style={{ fontSize: 13, color: COLOR_OFF_WHITE }}><span style={{ color: COLOR_GOLD, fontWeight: 700 }}>Wind</span><span style={{ color: COLOR_MUTED }}> — </span>See if strong winds are pushing ahead of the storm.</div>
                      <div style={{ fontSize: 13, color: COLOR_OFF_WHITE }}><span style={{ color: COLOR_GOLD, fontWeight: 700 }}>Clouds</span><span style={{ color: COLOR_MUTED }}> — </span>Shows how big the system is and how long it may last.</div>
                    </div>
                    <div style={{ fontSize: 11, color: COLOR_MUTED, marginTop: 12, borderTop: `1px solid #1a3a1a`, paddingTop: 10 }}>Pinch to zoom in for a closer look at the location.</div>
                  </div>
                </div>
              )}
            </>
          );
        })()}

        {/* ── COLD-START RACE BANNER — settings loaded after forecast ── */}
        {profileLoadedLate && (
          <div style={{ background: '#1a1a00', border: '1px solid #7c6f00', borderRadius: 8, padding: '8px 14px', marginBottom: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
            <span style={{ fontSize: 12, color: '#fde047' }}>⚠ Settings loaded after forecast — tap to refresh for accurate thresholds</span>
            <button
              onClick={() => { setProfileLoadedLate(false); if (selectedCourse) loadCourse(selectedCourse, false); }}
              style={{ background: '#7c6f00', color: '#fff', border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
              Refresh
            </button>
          </div>
        )}

        {/* ── RIGHT NOW — S98 card design ───── */}
        {cur && (() => {
          const rnStatus = (() => {
            // RIGHT NOW card status — real-time data only, no forecast. S212 locked.
            const SEVERITY = { 'GO': 0, 'CAUTION': 1, 'STRONG CAUTION': 2, 'NO-GO': 3 };
            const noGoTriggers = [], strCauTriggers = [], cauTriggers = [];

            // Precip — NJWxNet inches P1, WeatherAPI inches P2. No forecast % ever.
            const p1hr = njwxData?.success ? njwxData.current.precip1hr
                       : (weatherData?.current?.precip1hr ?? null);
            if (p1hr != null) {
              if      (p1hr >= 0.30) noGoTriggers.push('Heavy Rain');
              else if (p1hr >= 0.16) strCauTriggers.push('Raining');
              else if (p1hr >= 0.06) cauTriggers.push('Light Rain');
            }

            // Wind — same locked thresholds as rest of app
            const wind = cur.windSpeedNum || 0;
            const gust = cur.windGust || 0;
            if      (wind >= WIND_NOGO)    noGoTriggers.push(`Wind ${wind} mph`);
            else if (wind >= WIND_STR_CAU) strCauTriggers.push(`Wind ${wind} mph`);
            else if (wind >= WIND_CAUTION) cauTriggers.push(`Wind ${wind} mph`);
            // Gust ≥40 → NO-GO (matches forecast path getHourStatus)
            if (gust >= 40 && !noGoTriggers.some(t => t.startsWith('Wind'))) noGoTriggers.push(`Gust ${gust} mph`);

            // Feels Like — same locked thresholds as rest of app
            const fl = cur.feelsLike != null ? cur.feelsLike : (cur.temp || 0);
            if      (fl < profile.minTemp - 5) noGoTriggers.push(`Feels ${Math.round(fl)}° (limit ${profile.minTemp}°)`);
            else if (fl > profile.maxTemp + 5) noGoTriggers.push(`Feels ${Math.round(fl)}° (limit ${profile.maxTemp}°)`);
            else if (fl < profile.minTemp)     cauTriggers.push(`Feels ${Math.round(fl)}° (limit ${profile.minTemp}°)`);
            else if (fl > profile.maxTemp)     cauTriggers.push(`Feels ${Math.round(fl)}° (limit ${profile.maxTemp}°)`);

            // Determine status
            const triggers = {};
            if (noGoTriggers.length)   triggers['NO-GO']          = noGoTriggers;
            if (strCauTriggers.length) triggers['STRONG CAUTION'] = strCauTriggers;
            if (cauTriggers.length)    triggers['CAUTION']         = cauTriggers;
            let status = noGoTriggers.length ? 'NO-GO' : strCauTriggers.length ? 'STRONG CAUTION' : cauTriggers.length ? 'CAUTION' : 'GO';
            let color  = STATUS_COLORS[status].color;

            // IEM storm override — always wins or doesn't downgrade. Unchanged from prior logic.
            const STORM_TIER = {
              'GET IN NOW':       { status: 'NO-GO',          color: STATUS_COLORS['NO-GO'].color },
              'HEAVY STORM':      { status: 'STRONG CAUTION', color: STATUS_COLORS['STRONG CAUTION'].color },
              'RAIN APPROACHING': { status: 'CAUTION',        color: STATUS_COLORS['CAUTION'].color },
            };
            const tier = STORM_TIER[iemData?.alertState];
            if (tier) {
              const stormSev = SEVERITY[tier.status];
              if (stormSev > (SEVERITY[status] ?? 0)) {
                status = tier.status;
                color  = tier.color;
              }
              const stormTrigger = `${iemData.alertState} — ${iemData.alertMsg || 'Storm detected'}`;
              triggers[tier.status] = [...(triggers[tier.status] || []), stormTrigger];
            }

            return { status, color, triggers };
          })();
          const sc = STATUS_COLORS[rnStatus.status] || STATUS_COLORS['GO'];
          const showGust = (cur.windGust || 0) > (cur.windSpeedNum || 0) + 10;
          // R1 sky icon + label — amCharts icons, bucket drives when raining (slot 28)
          const p1hrR1 = njwxData?.success ? njwxData.current.precip1hr : (weatherData?.current?.precip1hr ?? null);
          // S216 F1: if wapiCurrentData hasn't arrived after 3s, show refresh prompt
          const wapiTimedOut = !wapiCurrentData && wapiLoadStartRef.current && (Date.now() - wapiLoadStartRef.current) > 3000;
          const r1Icon = p1hrR1 != null && p1hrR1 >= 0.30 ? 'rainy-7'
                       : p1hrR1 != null && p1hrR1 >= 0.16 ? 'rainy-5'
                       : p1hrR1 != null && p1hrR1 >= 0.06 ? 'rainy-3'
                       : p1hrR1 != null && p1hrR1 >= 0.01 ? 'rainy-1'
                       : (() => {
                           const c = (wapiCurrentData?.current?.conditions || '').toLowerCase();
                           if (/thunder|lightning/.test(c)) return 'thunder';
                           if (/snow|blizzard/.test(c)) return 'snowy-3';
                           if (/heavy rain|heavy drizzle/.test(c)) return 'rainy-7';
                           if (/rain|drizzle|shower/.test(c)) return 'rainy-3';
                           if (/overcast/.test(c)) return 'cloudy';
                           if (/mostly cloudy|cloudy/.test(c)) return 'cloudy-day-3';
                           if (/partly cloudy/.test(c)) return 'cloudy-day-1';
                           return wapiTimedOut ? null : 'day';
                         })();
          const r1Label = p1hrR1 != null && p1hrR1 >= 0.30 ? 'Heavy Rain'
                        : p1hrR1 != null && p1hrR1 >= 0.16 ? 'Raining'
                        : p1hrR1 != null && p1hrR1 >= 0.06 ? 'Light Rain'
                        : p1hrR1 != null && p1hrR1 >= 0.01 ? 'Trace'
                        : wapiTimedOut ? 'Hit refresh button for sky info'
                        : wapiCurrentData ? skyLabel(wapiCurrentData.current.conditions || 'Clear')
                        : '—';
          return (
            <div style={{ background: '#000', border: `8px solid ${sc.color}`, borderRadius: 12, padding: 14, marginBottom: 12 }}>
              {/* R1: label + status (left) + amCharts sky icon + condition (right) — S213 */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 14, fontWeight: 700, color: COLOR_WHITE }}>RIGHT NOW</span>
                  <StatusIcon status={rnStatus.status} size={18} />
                  <span style={{ fontSize: 13, fontWeight: 800, color: sc.color }}>{rnStatus.status}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {r1Icon && <img src={`/icons/amcharts/${r1Icon}.svg`} alt="" style={{ width: 44, height: 44 }} />}
                  <span style={{ fontSize: r1Icon ? 18 : 13, fontWeight: 600, color: r1Icon ? '#fff' : COLOR_MUTED }}>{r1Label}</span>
                </div>
              </div>
              {/* Source attribution + NJWxNet toggle */}
              {(() => {
                const { station: nearSt, distanceMi: nearDist } = getNearestNJWxNetStation(selectedCourse?.lat, selectedCourse?.lng);
                const autoOn = nearDist <= 8;
                const isOn = njwxOverrideRef.current === true || (njwxOverrideRef.current === null && autoOn);
                const rtSrc = weatherData?.current?._realtimeSources || weatherData?.current?._rtSources || weatherData?._rtSources || {};
                const tempSource = rtSrc.temp || '';
                const windSource = rtSrc.wind || '';
                const humSource  = rtSrc.humidity || '';
                const precipSource = rtSrc.precip || '';
                // Group variables by source — e.g. "WeatherAPI (Temp, Hum, Rain) · VC (Wind)"
                const varsBySource = {};
                if (tempSource)   (varsBySource[tempSource]   = varsBySource[tempSource]   || []).push('Temp');
                if (humSource)    (varsBySource[humSource]    = varsBySource[humSource]    || []).push('Hum');
                if (precipSource) (varsBySource[precipSource] = varsBySource[precipSource] || []).push('Rain');
                if (windSource)   (varsBySource[windSource]   = varsBySource[windSource]   || []).push('Wind');
                const offParts = Object.entries(varsBySource).map(([src, vars]) => `${src} (${vars.join(', ')})`);
                const offLabel = offParts.join(' · ');
                const label = isOn
                  ? `NJWxNet ON · ${nearSt?.stid} ${nearDist} mi`
                  : njwxOverrideRef.current === false
                    ? `${offLabel || 'Forecast'} (NJWxNet OFF ${nearDist} mi)`
                    : `${offLabel || 'Forecast'} (NJWxNet ${nearDist} mi — too far)`;
                const toggleColor = isOn ? '#4ADE80' : '#6B7280';
                return (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                    <div style={{ fontSize: 10, color: COLOR_MUTED }}>
                      <span style={{ color: '#7aafff' }}>📡</span> {label}
                    </div>
                    <button
                      onClick={() => {
                        const next = isOn ? false : true;
                        njwxOverrideRef.current = next;
                        loadCourse(selectedCourse);
                      }}
                      style={{ fontSize: 10, fontWeight: 700, color: toggleColor, background: 'transparent', border: `1px solid ${toggleColor}`, borderRadius: 6, padding: '2px 8px', cursor: 'pointer' }}
                    >
                      {isOn ? 'Turn Off' : 'Turn On'}
                    </button>
                  </div>
                );
              })()}
              {/* R3: FEELS + actual */}
              <div style={{ marginBottom: 8 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', textTransform: 'uppercase', letterSpacing: 1, marginRight: 6 }}>FEELS</span>
                <span style={{ fontSize: 26, fontWeight: 800, color: '#fff' }}>{cur.feelsLike != null ? `${Math.round(cur.feelsLike)}°` : '—'}</span>
                {cur.temp != null && <span style={{ fontSize: 14, color: COLOR_MUTED, marginLeft: 6 }}>({Math.round(cur.temp)}° actual)</span>}
              </div>
              {/* R4: stats row — Precip · Wind · Gust · Hum · UV · Sunset */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, marginBottom: rnStatus.status !== 'GO' ? 8 : 0 }}>
                {(() => {
                  const p = njwxData?.success ? njwxData.current.precip1hr : (weatherData?.current?.precip1hr ?? null);
                  const rainLabel = p == null ? 'Unavailable'
                                  : p >= 0.30 ? 'Heavy Rain'
                                  : p >= 0.16 ? 'Raining'
                                  : p >= 0.06 ? 'Light Rain'
                                  : p >= 0.01 ? 'Trace'
                                  : wapiCurrentData ? (() => {
                                      const c = (wapiCurrentData.current.conditions || '').toLowerCase();
                                      if (/thunder|lightning/.test(c)) return 'Raining';
                                      if (/heavy rain|heavy drizzle/.test(c)) return 'Heavy Rain';
                                      if (/rain|drizzle|shower/.test(c)) return 'Light Rain';
                                      return 'Dry';
                                    })()
                                  : 'Dry';
                  const rainColor = p == null ? COLOR_MUTED
                                  : p >= 0.30 ? STATUS_COLORS['NO-GO'].color
                                  : p >= 0.16 ? STATUS_COLORS['STRONG CAUTION'].color
                                  : p >= 0.06 ? STATUS_COLORS['CAUTION'].color
                                  : COLOR_WHITE;
                  return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <span style={{ fontSize: 11, color: COLOR_MUTED }}>Rain </span>
                      <span style={{ fontSize: 14, fontWeight: 700, color: rainColor }}>{rainLabel}</span>
                    </div>
                  );
                })()}
                <div><span style={{ fontSize: 11, color: COLOR_MUTED }}>Wind </span><span style={{ fontSize: 14, fontWeight: 700, color: cur.windSpeedNum >= WIND_NOGO ? STATUS_COLORS['NO-GO'].color : cur.windSpeedNum >= WIND_STR_CAU ? STATUS_COLORS['STRONG CAUTION'].color : cur.windSpeedNum >= WIND_CAUTION ? STATUS_COLORS['CAUTION'].color : COLOR_WHITE }}>{cur.windSpeedNum != null ? (cur.windSpeedNum === 0 ? `< 2 mph` : `${cur.windSpeedNum} mph`) : '—'}</span></div>
                {showGust && <div><span style={{ fontSize: 11, color: COLOR_MUTED }}>Gust </span><span style={{ fontSize: 14, fontWeight: 700, color: cur.windGust >= 40 ? STATUS_COLORS['NO-GO'].color : cur.windGust >= 30 ? STATUS_COLORS['STRONG CAUTION'].color : cur.windGust >= 20 ? STATUS_COLORS['CAUTION'].color : COLOR_WHITE }}>{cur.windGust} mph</span></div>}
                <div><span style={{ fontSize: 11, color: COLOR_MUTED }}>Hum </span><span style={{ fontSize: 14, fontWeight: 700, color: COLOR_WHITE }}>{cur.humidity != null ? `${Math.round(cur.humidity)}%` : '—'}</span></div>
                {cur.uvIndex >= 3 && cur.isDaytime !== false && <div><span style={{ fontSize: 11, color: COLOR_MUTED }}>UV </span><span style={{ fontSize: 14, fontWeight: 700, color: Math.round(cur.uvIndex) >= 11 ? '#EF4444' : Math.round(cur.uvIndex) >= 8 ? '#F97316' : Math.round(cur.uvIndex) >= 6 ? '#FDE047' : COLOR_WHITE }}>{Math.round(cur.uvIndex)} {uvLabel(Math.round(cur.uvIndex))}</span></div>}
                {(cur.sunset || weatherData?.sunsetToday) && <div><span style={{ fontSize: 11, color: COLOR_MUTED }}>Sunset </span><span style={{ fontSize: 14, fontWeight: 700, color: COLOR_WHITE }}>{cur.sunset ? formatTime12h(cur.sunset) : weatherData.sunsetToday}</span></div>}
              </div>
              {/* R5: trigger lines */}
              {rnStatus.status !== 'GO' && rnStatus.triggers && (
                <div style={{ borderTop: `1px solid ${sc.color}33`, paddingTop: 8 }}>
                  {Object.entries(rnStatus.triggers).map(([level, trigs]) => (
                    trigs.map((t, i) => {
                      const popup = buildTriggerPopup(t, cur, profile);
                      return (
                        <div key={`${level}-${i}`} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                          <StatusIcon status={level} size={12} style={{ marginRight: 4 }} />
                          <span style={{ fontSize: 13, color: STATUS_COLORS[level]?.color || sc.color, fontWeight: 600, flex: 1 }}>{t}</span>
                          {popup && (
                            <button
                              onClick={() => setTriggerPopup(popup)}
                              style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '1px 4px', flexShrink: 0, lineHeight: 1 }}
                            ><svg width="16" height="16" viewBox="0 0 16 16"><circle cx="8" cy="8" r="7" fill="none" stroke="#E5E7EB" strokeWidth="1"/><text x="8" y="8" textAnchor="middle" dominantBaseline="central" fontSize="9" fontWeight="700" fill="#E5E7EB" fontFamily="Arial,sans-serif">i</text></svg></button>
                          )}
                        </div>
                      );
                    })
                  ))}
                </div>
              )}
            </div>
          );
        })()}

        {/* ── CART + BUG + POLLEN — combined compact card ── */}
        {(badResult?.ground || badResult?.bug || pollenData?.success) && (
          <div style={{ background: '#000', border: `2px solid ${COLOR_DARK_GREEN}`, borderRadius: 10, padding: '12px 14px', marginBottom: 12 }}>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>

              {/* Ground */}
              {badResult?.ground && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 100 }}>
                  <span style={{ fontSize: 28, lineHeight: 1 }}>{badResult.ground.sogginess === 'soggy' ? '🌊' : badResult.ground.sogginess === 'soft' ? '💧' : '✅'}</span>
                  <div>
                    <div style={{ fontSize: 10, color: COLOR_MUTED, textTransform: 'uppercase', letterSpacing: 1 }}>Ground</div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: badResult.ground.sogginess === 'soggy' || badResult.ground.sogginess === 'soft' ? STATUS_COLORS['CAUTION'].color : STATUS_COLORS['GO'].color }}>
                      {badResult.ground.sogginess === 'soggy' ? 'Saturated' : badResult.ground.sogginess === 'soft' ? 'Soft' : 'Dry'}
                    </div>
                  </div>
                </div>
              )}

              {/* Bug */}
              {badResult?.bug && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 100 }}>
                  <img src="/icons/mosquito.png" alt="" style={{ width: 32, height: 32, objectFit: 'contain' }} />
                  <div>
                    <div style={{ fontSize: 10, color: COLOR_MUTED, textTransform: 'uppercase', letterSpacing: 1 }}>Bugs</div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: badResult.bug.index >= 8 ? STATUS_COLORS['NO-GO'].color : badResult.bug.index >= 5 ? STATUS_COLORS['CAUTION'].color : STATUS_COLORS['GO'].color }}>
                      {badResult.bug.index}/10
                    </div>
                    <div style={{ fontSize: 11, color: COLOR_MUTED }}>{badResult.bug.label || ''}</div>
                    {(() => { const h = new Date().getHours(); return (h < 6 || h >= 22); })() && <div style={{ fontSize: 10, color: COLOR_MUTED, fontStyle: 'italic' }}>night activity lower</div>}
                  </div>
                </div>
              )}
              {/* Pollen — compact single line, worst type first. S175 Fix #7: full-width row to prevent wrap */}
              {!(pollenData?.success && pollenData?.offSeason) && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: '1 1 100%', minWidth: 100 }}>
                  <span style={{ fontSize: 28, lineHeight: 1 }}>🌿</span>
                  <div>
                    <div style={{ fontSize: 10, color: COLOR_MUTED, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 2 }}>Pollen</div>
                    {pollenData === null ? (
                      <div style={{ fontSize: 13, color: COLOR_MUTED }}>…</div>
                    ) : pollenData?.success ? (
                      <div style={{ fontSize: 12, color: COLOR_MUTED }}>
                        <span style={{ color: COLOR_MUTED }}>Pollen: </span>
                        <span style={{ color: pollenData.tree.color }}>🌳 {pollenData.tree.label}</span>
                        <span style={{ color: COLOR_MUTED }}> · </span>
                        <span style={{ color: pollenData.grass.color }}>🌾 {pollenData.grass.label}</span>
                        <span style={{ color: COLOR_MUTED }}> · </span>
                        <span style={{ color: pollenData.weed.color }}>🌿 {pollenData.weed.label}</span>
                      </div>
                    ) : (
                      <div style={{ fontSize: 13, color: COLOR_MUTED }}>Unavailable</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}



        {/* ── Forecast Till Midnight ───────────────────── */}
        {(() => {
          // Parse sunset hour from weatherData.sunsetToday string e.g. "7:48 PM"
          let sunsetHour = 20; // fallback if no data
          const sunsetStr = weatherData?.sunsetToday;
          if (sunsetStr && sunsetStr !== 'N/A' && sunsetStr !== '') {
            try {
              const m = sunsetStr.match(/(\d+):(\d+)\s*(AM|PM)/i);
              if (m) {
                let h = parseInt(m[1]);
                const pm = m[3].toUpperCase() === 'PM';
                if (pm && h !== 12) h += 12;
                if (!pm && h === 12) h = 0;
                sunsetHour = h + parseInt(m[2]) / 60;
              }
            } catch {}
          }
          return (
            <div style={{ background: COLOR_INPUT_BG, border: `1px solid ${COLOR_DARK_GREEN}`, borderRadius: 14, padding: 16, marginBottom: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: COLOR_OFF_WHITE, marginBottom: 10 }}>
                Now through 2 AM
              </div>
              {nextHours.length === 0 ? (
                <div style={{ color: COLOR_MUTED, fontSize: 13 }}>No forecast data available.</div>
              ) : (
                // S161 Item 6: Column-major fill — morning/early hours down col 1, later hours down col 2.
                // Variable hour count (now → 2AM) → row count computed from hours array.
                // Odd hour counts produce one empty cell at bottom-right (acceptable trade-off for consistency
                // with forecast 7-day and drilldown which also use column-major).
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gridTemplateRows: `repeat(${Math.ceil(nextHours.length / 2)}, auto)`, gridAutoFlow: 'column', gap: 6, alignItems: 'stretch' }}>
                  {nextHours.map((h, i) => {
                    return (
                      <DrilldownHourCard key={i} h={h} profile={profile} isPast={false} isInWindow={false} />
                    );
                  })}
                </div>
              )}
            </div>
          );
        })()}

      </div>
    );
  };

  // ── 19TH HOLE SCREEN ─────────────────────────────────────
  // Keep alias for any legacy references

  // ── HOME SCREEN ────────────────────────────────────────────

  // ── STORM WATCH SCREEN ─────────────────────────────────────────
  const StormWatchScreen = () => {
    const [gpsState, setGpsState] = React.useState('idle');
    const [gpsCoords, setGpsCoords] = React.useState(null);
    const [gpsFallback, setGpsFallback] = React.useState(false);
    const [gpsAddress, setGpsAddress] = React.useState(null);
    const [gpsErrorMsg, setGpsErrorMsg] = React.useState(null);
    const [iemData, setIemData] = React.useState(null);
    const [iemLoading, setIemLoading] = React.useState(true);
    const [iemRefreshTick, setIemRefreshTick] = React.useState(0);

    // 5-minute auto-refresh once GPS is locked — same visibility guard as Game Day
    React.useEffect(() => {
      if (!gpsCoords) return;
      const interval = setInterval(() => {
        if (typeof document !== 'undefined' && document.hidden) return;
        loadIem(gpsCoords.lat, gpsCoords.lng);
      }, 5 * 60 * 1000);
      const onVisibility = () => {
        if (typeof document !== 'undefined' && !document.hidden) loadIem(gpsCoords.lat, gpsCoords.lng);
      };
      if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVisibility);
      return () => {
        clearInterval(interval);
        if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisibility);
      };
    }, [gpsCoords]);

    const loadIem = React.useCallback((lat, lng) => {
      setIemLoading(true);
      const run = async () => {
        try {
          const url = 'https://mesonet.agron.iastate.edu/geojson/nexrad_attr.py';
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 15000);
          const r = await fetch(url, { signal: controller.signal });
          clearTimeout(timeoutId);
          if (r.status === 503) throw new Error('IEM_503');
          if (!r.ok) throw new Error('IEM fetch failed');
          const gj = await r.json();
          const features = gj.features || [];

          const R = 3958.8;
          const toRad = d => d * Math.PI / 180;
          const distMi = (la1, lo1, la2, lo2) => {
            const dLat = toRad(la2-la1), dLon = toRad(lo2-lo1);
            const a = Math.sin(dLat/2)**2 + Math.cos(toRad(la1))*Math.cos(toRad(la2))*Math.sin(dLon/2)**2;
            return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
          };
          const bearing = (la1, lo1, la2, lo2) => {
            const y = Math.sin(toRad(lo2-lo1))*Math.cos(toRad(la2));
            const x = Math.cos(toRad(la1))*Math.sin(toRad(la2)) - Math.sin(toRad(la1))*Math.cos(toRad(la2))*Math.cos(toRad(lo2-lo1));
            return (Math.atan2(y, x)*180/Math.PI + 360) % 360;
          };
          const bearingLabel = b => {
            const dirs = ['N','NE','E','SE','S','SW','W','NW'];
            return dirs[Math.round(b/45)%8];
          };

          const allCells = features
            .filter(f => (f.properties?.max_dbz || 0) >= 35)
            .map(f => {
              const p = f.properties;
              const coords = f.geometry?.coordinates;
              const cLat = coords?.[1] ?? 0;
              const cLon = coords?.[0] ?? 0;
              const dist = distMi(lat, lng, cLat, cLon);
              const bear = bearing(lat, lng, cLat, cLon);
              const motionBear = ((p.drct || 0) + 180) % 360;
              const speedKt = p.sknt || 0;
              const speedMph = speedKt * 1.15078;
              const hasSpeed = speedMph > 2;
              const etaMin = hasSpeed ? Math.round(dist / speedMph * 60) : null;
              const bearFromCellToCourse = bearing(cLat, cLon, lat, lng);
              const angleDelta = Math.abs(((motionBear - bearFromCellToCourse) + 360) % 360);
              const movingToward = angleDelta < 45 || angleDelta > 315;
              return {
                dist: Math.round(dist*10)/10,
                bear, bearLabel: bearingLabel(bear),
                dbz: p.max_dbz,
                speedMph: Math.round(speedMph),
                etaMin, movingToward, hasSpeed,
                cLat, cLon
              };
            })
            .filter(c => c.dist <= 50)
            .sort((a, b) => (a.etaMin ?? 999) - (b.etaMin ?? 999));

          let alertState = 'CLEAR';
          let alertColor = '#4ADE80';
          let alertMsg = 'No storms detected';
          let primaryCell = null;
          let additionalCount = 0;

          const getInNow = allCells.filter(c =>
            c.dbz >= 45 && c.movingToward &&
            ((c.hasSpeed && c.etaMin != null && c.etaMin <= 20) || c.dist <= 10)
          );
          const heavyStorm = allCells.filter(c =>
            c.dbz >= 45 && c.movingToward &&
            ((c.hasSpeed && c.etaMin != null && c.etaMin <= 60) || c.dist <= 10)
          );
          const rainApproach = allCells.filter(c =>
            c.dbz >= 35 && c.movingToward && c.hasSpeed &&
            c.etaMin != null && c.etaMin <= 90
          );

          if (getInNow.length > 0) {
            alertState = 'GET IN NOW';
            alertColor = '#EF4444';
            primaryCell = getInNow[0];
            additionalCount = getInNow.length - 1;
            const eta = primaryCell.etaMin;
            alertMsg = primaryCell.dist <= 10 && !primaryCell.hasSpeed
              ? `Storm from ${primaryCell.bearLabel}, ${primaryCell.dist}mi — speed unknown — monitor closely`
              : `Storm from ${primaryCell.bearLabel}, ${primaryCell.dist}mi — ~${eta} min`;
          } else if (heavyStorm.length > 0) {
            alertState = 'HEAVY STORM';
            alertColor = '#FB923C';
            primaryCell = heavyStorm[0];
            additionalCount = heavyStorm.length - 1;
            const etaLow = primaryCell.etaMin;
            const etaHigh = primaryCell.etaMin ? primaryCell.etaMin + Math.round(primaryCell.etaMin * 0.2) : null;
            alertMsg = primaryCell.dist <= 10 && !primaryCell.hasSpeed
              ? `Heavy storm from ${primaryCell.bearLabel}, ${primaryCell.dist}mi — speed unknown — monitor closely`
              : `Heavy storm from ${primaryCell.bearLabel}, ${primaryCell.dist}mi — ~${etaLow}–${etaHigh} min · potential lightning`;
          } else if (rainApproach.length > 0) {
            alertState = 'RAIN APPROACHING';
            alertColor = '#FACC15';
            primaryCell = rainApproach[0];
            additionalCount = rainApproach.length - 1;
            const etaLow = primaryCell.etaMin;
            const etaHigh = primaryCell.etaMin + Math.round(primaryCell.etaMin * 0.2);
            alertMsg = `Rain from ${primaryCell.bearLabel}, ${primaryCell.dist}mi — ~${etaLow}–${etaHigh} min`;
          }

          setIemData({ cells: allCells, primaryCell, additionalCount, alertState, alertColor, alertMsg, fetchedAt: new Date() });
        } catch (err) {
          setIemData(prev => {
            if (prev && prev.alertState && prev.alertState !== 'CLEAR' && prev.alertState !== 'UNKNOWN') {
              return { ...prev, alertMsg: `${prev.alertMsg} · tracking lost ${new Date().toLocaleTimeString('en-US',{hour:'numeric',minute:'2-digit'})} — check sky`, fetchedAt: new Date() };
            }
            return { error: err.message, alertState: 'UNKNOWN', alertColor: COLOR_MUTED, alertMsg: err.message === 'IEM_503' ? 'Storm tracking unavailable — check sky.' : 'Storm data unavailable — check radar', fetchedAt: new Date() };
          });
        }
        setIemLoading(false);
      };
      run();
    }, []);

    const doGps = React.useCallback(() => {
      setGpsState('loading');
      setIemData(null);
      setIemLoading(true);
      setGpsErrorMsg(null);
      if (!navigator.geolocation) { setGpsErrorMsg('Device GPS timed out — tap Refresh to retry'); setGpsState('error'); return; }
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const lat = pos.coords.latitude;
          const lng = pos.coords.longitude;
          setGpsFallback(false);
          setGpsErrorMsg(null);
          setGpsCoords({ lat, lng });
          setGpsState('ready');
          loadIem(lat, lng);
          // Reverse geocode via Nominatim
          fetch(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`, {
            headers: { 'Accept-Language': 'en-US,en', 'User-Agent': 'ZAP-Golf-Weather/1.0' }
          }).then(r => r.json()).then(data => {
            const a = data.address || {};
            const parts = [
              a.house_number && a.road ? `${a.house_number} ${a.road}` : a.road,
              a.city || a.town || a.village || a.suburb,
              a.state
            ].filter(Boolean);
            setGpsAddress(parts.join(', '));
          }).catch(() => {});
        },
        (err) => {
          if (err.code === 1) {
            setGpsErrorMsg('Location access blocked. To enable: Settings → Privacy & Security → Location Services → Safari → set to "Ask Next Time", then tap Refresh.');
          } else {
            setGpsErrorMsg('Device GPS timed out — tap Refresh to retry.');
          }
          setGpsState('error');
        },
        { timeout: 8000, maximumAge: 0, enableHighAccuracy: false }
      );
    }, [loadIem]);

    const windyUrl = gpsCoords
      ? `https://www.windy.com/?radar,${gpsCoords.lat.toFixed(4)},${gpsCoords.lng.toFixed(4)},8`
      : 'https://www.windy.com/?radar';

    return (
      <div style={{ padding: '0 12px 24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0 8px' }}>
          <div style={{ fontSize: 20, fontWeight: 800, color: COLOR_WHITE }}>⚡ Storm Watch</div>
          <button onClick={() => navigateTo('home')} style={{ background: 'none', border: 'none', color: COLOR_GREEN, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>Home</button>
        </div>

        {gpsState === 'idle' && (
          <div style={{ textAlign: 'center', padding: 32 }}>
            <button
              onClick={doGps}
              style={{ background: COLOR_CARD, border: `4px solid ${COLOR_DARK_GREEN}`, borderRadius: 14, padding: '18px 32px', fontSize: 18, fontWeight: 800, color: '#0a0a0a', cursor: 'pointer', width: '100%' }}>
              📍 Get My Location
            </button>
            <div style={{ fontSize: 12, color: COLOR_OFF_WHITE, marginTop: 12 }}>Tap to check storm conditions at your location</div>
          </div>
        )}

        {gpsState === 'loading' && (
          <div style={{ textAlign: 'center', padding: 32, color: COLOR_MUTED, fontSize: 14 }}>Locating you…</div>
        )}

        {gpsState === 'error' && (
          <div style={{ background: COLOR_CARD, border: `1px solid ${COLOR_DARK_GREEN}`, borderRadius: 12, padding: 20, marginBottom: 12 }}>
            <div style={{ fontSize: 14, color: '#FFA500', fontWeight: 700, marginBottom: 8 }}>📍 GPS Unavailable</div>
            <div style={{ fontSize: 13, color: COLOR_OFF_WHITE, lineHeight: 1.5 }}>{gpsErrorMsg}</div>
          </div>
        )}

        {gpsState === 'ready' && (
          <>
            {/* Storm Alert Zone — verbatim match to Game Day */}
            <div style={{ background: '#050505', border: `2px solid ${iemData?.alertColor || '#4ADE80'}`, borderRadius: 14, padding: 16, marginBottom: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: iemData?.alertColor || '#4ADE80' }}>
                  STORM ALERT ZONE
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ fontSize: 11, color: COLOR_MUTED }}>
                    {gpsAddress || 'Your GPS location'}
                  </div>
                  {iemData?.fetchedAt && (
                    <div style={{ fontSize: 11, color: COLOR_MUTED }}>
                      {iemData.fetchedAt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                    </div>
                  )}
                </div>
              </div>

              {iemLoading ? (
                <div style={{ color: COLOR_MUTED, fontSize: 13 }}>Scanning for storm cells…</div>
              ) : (
                <>
                  <div style={{ fontSize: 16, fontWeight: 800, color: iemData?.alertColor || '#4ADE80', marginBottom: 4 }}>
                    {iemData?.alertState || 'CLEAR'}
                  </div>
                  <div style={{ fontSize: 13, color: COLOR_OFF_WHITE, marginBottom: iemData?.additionalCount > 0 ? 8 : 0 }}>
                    {iemData?.alertMsg}
                  </div>
                  {iemData?.additionalCount > 0 && (
                    <div style={{ fontSize: 12, color: COLOR_MUTED, marginTop: 4 }}>
                      +{iemData.additionalCount} additional cell{iemData.additionalCount > 1 ? 's' : ''} behind it
                    </div>
                  )}
                </>
              )}
            </div>

            <a href={windyUrl} target="_blank" rel="noopener noreferrer"
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%', padding: '12px 0', background: COLOR_CARD, border: `4px solid ${COLOR_DARK_GREEN}`, borderRadius: 10, color: '#0a0a0a', fontSize: 15, fontWeight: 700, textDecoration: 'none', boxSizing: 'border-box', marginBottom: 12 }}>
              📡 Check Radar on Windy
            </a>

            <button onClick={doGps} style={{ width: '100%', background: COLOR_INPUT_BG, border: `1px solid ${COLOR_DARK_GREEN}`, borderRadius: 12, padding: '12px 16px', fontSize: 14, fontWeight: 700, color: COLOR_WHITE, cursor: 'pointer' }}>
              ↻ Refresh
            </button>
          </>
        )}
      </div>
    );
  };

  // ── LOCATION MEMORY HELPERS ──────────────────────────────────
  // localStorage is primary. Sheet sync is Phase 2 (silent fail for now).
  // Each saved location stores: { id, name, formattedAddress, lat, lng, town, state, zip, placeType, phoneNumber, entryMode }

  const loadFavorites = () => {
    try { return JSON.parse(localStorage.getItem(ZAP_FAVORITES_KEY) || '[]'); } catch { return []; }
  };
  const saveFavorites = (list) => {
    try { localStorage.setItem(ZAP_FAVORITES_KEY, JSON.stringify(list.slice(0, 10))); } catch { /* silent */ }
  };
  const loadRecents = () => {
    try { return JSON.parse(localStorage.getItem(ZAP_RECENTS_KEY) || '[]'); } catch { return []; }
  };
  const saveRecents = (list) => {
    try { localStorage.setItem(ZAP_RECENTS_KEY, JSON.stringify(list.slice(0, 10))); } catch { /* silent */ }
  };
  const addRecent = (loc) => {
    // Deduplicate by lat+lng, move to top, keep max 10
    const existing = loadRecents().filter(r => !(Math.abs(r.lat - loc.lat) < 0.0001 && Math.abs(r.lng - loc.lng) < 0.0001));
    saveRecents([{ id: loc.id, name: loc.name, formattedAddress: loc.formattedAddress, lat: loc.lat, lng: loc.lng, town: loc.town, state: loc.state, zip: loc.zip, placeType: loc.placeType, phoneNumber: loc.phoneNumber, entryMode: loc.entryMode }, ...existing]);
  };
  const removeRecent = (id) => {
    saveRecents(loadRecents().filter(r => r.id !== id));
  };
  const isFavorite = (loc) => loadFavorites().some(f => Math.abs(f.lat - loc.lat) < 0.0001 && Math.abs(f.lng - loc.lng) < 0.0001);
  const toggleFavorite = (loc) => {
    const favs = loadFavorites();
    const idx = favs.findIndex(f => Math.abs(f.lat - loc.lat) < 0.0001 && Math.abs(f.lng - loc.lng) < 0.0001);
    if (idx >= 0) {
      saveFavorites(favs.filter((_, i) => i !== idx));
    } else {
      if (favs.length >= 10) return; // cap at 10
      saveFavorites([{ id: loc.id, name: loc.name, formattedAddress: loc.formattedAddress, lat: loc.lat, lng: loc.lng, town: loc.town, state: loc.state, zip: loc.zip, placeType: loc.placeType, phoneNumber: loc.phoneNumber, entryMode: loc.entryMode }, ...favs]);
    }
  };
  // ─────────────────────────────────────────────────────────────

  const LocationEntryScreen = React.memo(() => {
    const [gpsLoading, setGpsLoading] = React.useState(false);
    const [gpsError, setGpsError] = React.useState(null);
    const [addressInput, setAddressInput] = React.useState('');
    const [addressSuggestions, setAddressSuggestions] = React.useState([]);
    const [addressError, setAddressError] = React.useState(null);
    const addressDebounceRef = React.useRef(null);
    const [favorites, setFavorites] = React.useState(() => loadFavorites());
    const [recents, setRecents]     = React.useState(() => loadRecents());

    const refreshMemory = () => { setFavorites(loadFavorites()); setRecents(loadRecents()); };

    const handleToggleFavorite = (loc, e) => {
      e.stopPropagation();
      toggleFavorite(loc);
      refreshMemory();
    };

    const handleRemoveRecent = (id, e) => {
      e.stopPropagation();
      removeRecent(id);
      refreshMemory();
    };

    const handleSelectMemory = (loc) => {
      setActivityLoggedThisSession(false);
      addRecent(loc);
      setSelectedCourse(loc);
      loadCourse(loc);
      navigateTo('home');
    };

    const handleGPS = () => {
      if (!navigator.geolocation) {
        setGpsError('Geolocation not supported by your browser.');
        return;
      }
      setGpsLoading(true);
      setGpsError(null);
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          const { latitude: lat, longitude: lng } = pos.coords;
          // Reverse geocode via Nominatim
          let name = `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
          let town = '', state = '';
          try {
            const r = await fetch(
              `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`,
              { headers: { 'User-Agent': 'ZAP-Activity/1.0' } }
            );
            const geo = await r.json();
            const addr = geo?.address || {};
            const parts = [addr.suburb || addr.neighbourhood || addr.hamlet || addr.village || addr.town || addr.city, addr.state].filter(Boolean);
            if (parts.length) name = parts.join(', ');
            town = addr.town || addr.city || addr.village || '';
            state = addr.state || '';
          } catch (e) { /* use fallback name */ }
          const loc = makeLocation(lat, lng, name, { town, state, entryMode: 'gps' });
          setGpsLoading(false);
          setActivityLoggedThisSession(false);
          addRecent(loc);
          setSelectedCourse(loc);
          loadCourse(loc);
          navigateTo('home');
        },
        (err) => {
          setGpsLoading(false);
          setGpsError('Could not get your location. Check browser permissions.');
        },
        { timeout: 10000, enableHighAccuracy: true }
      );
    };

    const GPLACES_KEY = 'AIzaSyD7THXUzFUzKN1qBc2h0ObUf93zCrHfcYM';

    const handleAddressType = (val) => {
      setAddressInput(val);
      setAddressError(null);
      if (addressDebounceRef.current) clearTimeout(addressDebounceRef.current);
      if (!val.trim() || val.length < 3) { setAddressSuggestions([]); return; }
      addressDebounceRef.current = setTimeout(async () => {
        try {
          const r = await fetch('https://places.googleapis.com/v1/places:autocomplete', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-Goog-Api-Key': GPLACES_KEY },
            body: JSON.stringify({ input: val })
          });
          const d = await r.json();
          setAddressSuggestions(d.suggestions || []);
        } catch (e) {
          setAddressError('Search failed. Check your connection.');
        }
      }, 300);
    };

    // Types meaningful enough to display to the user — snake_case → Title Case handled below
    const DISPLAY_TYPES = {
      // Food & Drink
      restaurant: 'Restaurant', cafe: 'Café', bar: 'Bar', bakery: 'Bakery',
      fast_food_restaurant: 'Restaurant', coffee_shop: 'Coffee Shop',
      pizza_restaurant: 'Restaurant', chinese_restaurant: 'Restaurant',
      italian_restaurant: 'Restaurant', mexican_restaurant: 'Restaurant',
      japanese_restaurant: 'Restaurant', indian_restaurant: 'Restaurant',
      thai_restaurant: 'Restaurant', seafood_restaurant: 'Restaurant',
      american_restaurant: 'Restaurant', french_restaurant: 'Restaurant',
      korean_restaurant: 'Restaurant', vietnamese_restaurant: 'Restaurant',
      greek_restaurant: 'Restaurant', mediterranean_restaurant: 'Restaurant',
      barbecue_restaurant: 'Restaurant', deli: 'Deli', diner: 'Diner',
      food_court: 'Food Court', night_club: 'Night Club', brewery: 'Brewery',
      wine_bar: 'Wine Bar', cocktail_bar: 'Bar', brunch_restaurant: 'Restaurant',
      // Health
      doctor: 'Doctor', hospital: 'Hospital', pharmacy: 'Pharmacy',
      dentist: 'Dentist', veterinary_care: 'Vet', physiotherapist: 'Physical Therapy',
      // Fitness
      gym: 'Gym', spa: 'Spa', beauty_salon: 'Salon', hair_salon: 'Hair Salon',
      // Recreation & Outdoors
      park: 'Park', national_park: 'National Park', state_park: 'State Park',
      hiking_area: 'Hiking Area', golf_course: 'Golf Course', stadium: 'Stadium',
      bowling_alley: 'Bowling Alley', movie_theater: 'Movie Theater',
      amusement_park: 'Amusement Park', zoo: 'Zoo', aquarium: 'Aquarium',
      museum: 'Museum', art_gallery: 'Art Gallery', beach: 'Beach',
      campground: 'Campground', marina: 'Marina', ski_resort: 'Ski Resort',
      // Shopping
      shopping_mall: 'Shopping Mall', grocery_store: 'Grocery Store',
      supermarket: 'Supermarket', convenience_store: 'Convenience Store',
      // Places of worship
      church: 'Church', mosque: 'Mosque', synagogue: 'Synagogue',
      hindu_temple: 'Temple', place_of_worship: 'Place of Worship',
      // Education
      school: 'School', university: 'University', library: 'Library',
      // Travel
      hotel: 'Hotel', lodging: 'Lodging', motel: 'Motel',
      airport: 'Airport', train_station: 'Train Station', bus_station: 'Bus Station',
      // Services
      bank: 'Bank', atm: 'ATM', gas_station: 'Gas Station',
      car_repair: 'Auto Repair', car_dealer: 'Car Dealer',
      laundry: 'Laundry', post_office: 'Post Office',
      // Business
      real_estate_agency: 'Real Estate', insurance_agency: 'Insurance',
      lawyer: 'Law Office', accounting: 'Accounting',
    };

    const handleSelectSuggestion = async (suggestion) => {
      const placeId = suggestion.placePrediction?.placeId;
      const label = suggestion.placePrediction?.text?.text || '';
      setAddressInput(label);
      setAddressSuggestions([]);
      if (!placeId) return;
      try {
        const r = await fetch(
          `https://places.googleapis.com/v1/places/${placeId}?fields=location,displayName,formattedAddress,addressComponents,types,internationalPhoneNumber`,
          { headers: { 'X-Goog-Api-Key': GPLACES_KEY, 'X-Goog-FieldMask': 'location,displayName,formattedAddress,addressComponents,types,internationalPhoneNumber' } }
        );
        const d = await r.json();
        const lat = d.location?.latitude;
        const lng = d.location?.longitude;
        if (!lat || !lng) { setAddressError('Could not get coordinates. Try again.'); return; }

        // displayName
        const displayName = d.displayName?.text || '';

        // formattedAddress — strip trailing ", USA" or ", United States"
        const formattedAddress = (d.formattedAddress || '').replace(/,\s*(USA|United States)$/, '');

        // addressComponents — extract street, town, state, zip
        const comps = d.addressComponents || [];
        const getComp = (type) => comps.find(c => c.types?.includes(type));
        const streetNum = getComp('street_number')?.shortText || '';
        const route = getComp('route')?.shortText || '';
        const streetAddress = streetNum && route ? `${streetNum} ${route}` : route || streetNum || '';
        const town = getComp('locality')?.longText || getComp('neighborhood')?.longText || getComp('sublocality')?.longText || '';
        const state = getComp('administrative_area_level_1')?.shortText || '';
        const zip = getComp('postal_code')?.shortText || '';

        // place type — find first meaningful type
        const types = d.types || [];
        const isResidential = types.includes('street_address') || types.includes('premise') || types.includes('subpremise') || types.includes('route');
        const placeTypeLabel = !isResidential
          ? (types.map(t => DISPLAY_TYPES[t]).find(Boolean) || '')
          : '';

        // phone number
        const phoneNumber = d.internationalPhoneNumber || '';

        // name field:
        // residential → street address (displayName is just the street anyway)
        // named place → "Display Name, Street Address"
        const name = isResidential
          ? (displayName || streetAddress)
          : (streetAddress ? `${displayName}, ${streetAddress}` : displayName);

        const loc = makeLocation(lat, lng, name, {
          town, state, zip,
          placeType: placeTypeLabel,
          phoneNumber,
          formattedAddress,
          entryMode: 'address'
        });
        setAddressInput('');
        setActivityLoggedThisSession(false);
        addRecent(loc);
        setSelectedCourse(loc);
        loadCourse(loc);
        navigateTo('home');
      } catch (e) {
        setAddressError('Could not load location. Try again.');
      }
    };

    return (
      <div style={{ padding: '16px 12px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20, minHeight: '80vh', paddingTop: 60, position: 'relative' }}>

        {/* Top bar: Back (left) + Settings (right) */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', position: 'absolute', top: 12, left: 0, right: 0, padding: '0 12px', boxSizing: 'border-box' }}>
          <div>
            {selectedCourse && (
              <button onClick={() => navigateTo('home')} style={{ background: 'transparent', border: 'none', color: COLOR_MUTED, fontSize: 20, cursor: 'pointer', padding: '4px 0' }}>← Back</button>
            )}
          </div>

        </div>

        {/* Logo + PRO badge — matches HomeScreen pattern */}
        <div style={{ textAlign: 'center' }}>
          <div style={{ position: 'relative', display: 'inline-block', overflow: 'visible' }}>
            <img src="https://res.cloudinary.com/dovzgljte/image/upload/w_480,q_auto,f_auto/ZAP_Activity_Logo_S253_final.png" alt="ZAP — Zur's Activity Planner" loading="eager" style={{ width: 240, maxWidth: '80vw', height: 'auto', display: 'block' }} />
            <img src="https://res.cloudinary.com/dovzgljte/image/upload/w_200,q_auto,f_auto/ZAP_Pro_Badge_S259d.png" alt="PRO" style={{ position: 'absolute', bottom: 4, right: -65, width: 60, height: 'auto' }} />
          </div>
        </div>

        {/* GPS button */}
        <div style={{ width: '100%', maxWidth: 360 }}>
          <button
            onClick={handleGPS}
            disabled={gpsLoading}
            style={{ width: '100%', padding: '16px', background: COLOR_CARD, border: `4px solid ${COLOR_DARK_GREEN}`, borderRadius: 12,
              fontSize: 16, fontWeight: 800, color: '#0a0a0a', cursor: gpsLoading ? 'wait' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}
          >
            {gpsLoading ? '📡 Getting location…' : '📍 Use My Location'}
          </button>
          {gpsError && <div style={{ marginTop: 8, fontSize: 12, color: '#EF4444', textAlign: 'center' }}>{gpsError}</div>}
        </div>

        <div style={{ color: COLOR_MUTED, fontSize: 13 }}>-- or enter an address --</div>

        {/* Address typeahead */}
        <div style={{ width: '100%', maxWidth: 360, position: 'relative' }}>
          <input
            type="text"
            value={addressInput}
            onChange={e => handleAddressType(e.target.value)}
            placeholder="Start typing an address…"
            autoComplete="off"
            style={{ width: '100%', boxSizing: 'border-box', padding: '12px 14px', background: COLOR_INPUT_BG,
              border: `2px solid ${COLOR_DARK_GREEN}`, borderRadius: addressSuggestions.length > 0 ? '10px 10px 0 0' : 10,
              fontSize: 14, color: COLOR_WHITE, outline: 'none' }}
          />
          {addressSuggestions.length > 0 && (
            <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: COLOR_INPUT_BG,
              border: `2px solid ${COLOR_DARK_GREEN}`, borderTop: 'none', borderRadius: '0 0 10px 10px',
              zIndex: 100, overflow: 'hidden' }}>
              {addressSuggestions.map((s, i) => (
                <button
                  key={s.placePrediction?.placeId || i}
                  onClick={() => handleSelectSuggestion(s)}
                  style={{ width: '100%', padding: '12px 14px', background: 'transparent', border: 'none',
                    borderTop: i > 0 ? `1px solid ${COLOR_DARK_GREEN}` : 'none',
                    textAlign: 'left', cursor: 'pointer', color: COLOR_WHITE, fontSize: 13, lineHeight: 1.4 }}
                >
                  {s.placePrediction?.text?.text || ''}
                </button>
              ))}
            </div>
          )}
          {addressError && <div style={{ marginTop: 6, fontSize: 12, color: '#EF4444' }}>{addressError}</div>}
        </div>

        {/* Favorites + Recents — shown only when input is empty */}
        {!addressInput.trim() && (favorites.length > 0 || recents.length > 0) && (
          <div style={{ width: '100%', maxWidth: 360 }}>

            {/* Favorites */}
            {favorites.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: COLOR_MUTED, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6 }}>Favorites</div>
                {favorites.map(loc => (
                  <div key={loc.id || loc.name} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <button
                      onClick={() => handleSelectMemory(loc)}
                      style={{ flex: 1, textAlign: 'left', background: COLOR_INPUT_BG, border: `1px solid ${COLOR_DARK_GREEN}`,
                        borderRadius: 8, padding: '10px 12px', cursor: 'pointer', color: COLOR_WHITE }}
                    >
                      <div style={{ fontSize: 13, fontWeight: 700, lineHeight: 1.3 }}>{loc.name}</div>
                      {loc.formattedAddress && loc.formattedAddress !== loc.name && (
                        <div style={{ fontSize: 11, color: COLOR_MUTED, marginTop: 2 }}>{loc.formattedAddress}</div>
                      )}
                    </button>
                    {/* Star — filled gold to indicate favorited */}
                    <button onClick={(e) => handleToggleFavorite(loc, e)}
                      style={{ background: 'transparent', border: 'none', fontSize: 18, cursor: 'pointer', color: '#FACC15', padding: '4px' }}
                      title="Remove from favorites">★</button>
                  </div>
                ))}
              </div>
            )}

            {/* Recents */}
            {recents.length > 0 && (
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: COLOR_MUTED, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6 }}>Recent</div>
                {recents.map(loc => (
                  <div key={loc.id || loc.name} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <button
                      onClick={() => handleSelectMemory(loc)}
                      style={{ flex: 1, textAlign: 'left', background: COLOR_INPUT_BG, border: `1px solid ${COLOR_DARK_GREEN}`,
                        borderRadius: 8, padding: '10px 12px', cursor: 'pointer', color: COLOR_WHITE }}
                    >
                      <div style={{ fontSize: 13, fontWeight: 700, lineHeight: 1.3 }}>{loc.name}</div>
                      {loc.formattedAddress && loc.formattedAddress !== loc.name && (
                        <div style={{ fontSize: 11, color: COLOR_MUTED, marginTop: 2 }}>{loc.formattedAddress}</div>
                      )}
                    </button>
                    {/* Star — outline to indicate not yet favorited */}
                    <button onClick={(e) => handleToggleFavorite(loc, e)}
                      style={{ background: 'transparent', border: 'none', fontSize: 18, cursor: 'pointer', color: COLOR_MUTED, padding: '4px' }}
                      title="Add to favorites">☆</button>
                    {/* Delete recent */}
                    <button onClick={(e) => handleRemoveRecent(loc.id, e)}
                      style={{ background: 'transparent', border: 'none', fontSize: 16, cursor: 'pointer', color: COLOR_MUTED, padding: '4px' }}
                      title="Remove">✕</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Cache warning */}
        <div style={{ width: '100%', maxWidth: 360 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, padding: '8px 10px', background: 'rgba(250,204,21,0.08)', borderRadius: 6, border: '1px solid rgba(250,204,21,0.2)' }}>
            <span style={{ fontSize: 13, flexShrink: 0 }}>⚠️</span>
            <span style={{ fontSize: 11, color: COLOR_MUTED, lineHeight: 1.5 }}>
              Favorites and recents are saved on this device. If you clear your browser data, you'll need to re-enter them.
            </span>
          </div>
        </div>

      </div>
    );
  });

  // ── HOME SCREEN — nav hub (shown after location set) ─────
  const HomeScreen = () => {
    const isAddress = selectedCourse?.entryMode === 'address';

    const navCards = [
      { icon: '☀️', label: 'Forecast',              sub: '7-day outlook',                   action: () => navigateTo('forecast') },
      { icon: '🚴', label: 'Activity Day',           sub: 'Live conditions · Go/No-Go',      action: () => navigateTo('dayofplay') },
      ...(isAddress ? [{ icon: '🗺️', label: 'Directions & Contact', sub: `Google Maps${selectedCourse?.phoneNumber ? ' · Call' : ''}`, action: () => navigateTo('directions') }] : []),
      { icon: '📝', label: 'Post-Activity',          sub: 'Log & report conditions',         action: () => navigateTo('postactivity') },
      { icon: '🍺🍔', label: 'Nearby Spots',         sub: 'Restaurants & bars → Google Maps', action: () => navigateTo('nearbyspots') },
      { icon: '⚙️', label: 'Settings',               sub: 'Temp · activity window · audio',  action: () => navigateTo('profile') },
    ];

    return (
      <div style={{ padding: '4px 12px 0' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {navCards.map((card) => (
            <button
              key={card.label}
              onClick={card.action}
              style={{ width: '100%', background: '#f7f2ee', border: '4px solid #bf9d6f', borderRadius: 14,
                padding: '10px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 14, textAlign: 'left' }}
            >
              <div style={{ fontSize: 24, lineHeight: 1, width: 28, textAlign: 'center', flexShrink: 0 }}>{card.icon}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 17, fontWeight: 700, color: '#0a0a0a' }}>{card.label}</div>
                <div style={{ fontSize: 12, color: '#1a1010', marginTop: 2 }}>{card.sub}</div>
              </div>
              <div style={{ fontSize: 18, color: '#0a0a0a' }}>›</div>
            </button>
          ))}
        </div>
        <div style={{ textAlign: 'center', fontSize: 12, color: COLOR_MUTED, padding: '10px 0 12px' }}>© John Zur · 2026</div>
      </div>
    );
  };

    // ── POST-ACTIVITY SCREEN ──────────────────────────────────
  const PostActivityScreen = () => {
    const [activityType, setActivityType] = React.useState('');
    const [date, setDate] = React.useState(() => new Date().toISOString().slice(0,10));
    const [startTime, setStartTime] = React.useState('');
    const [endTime, setEndTime] = React.useState('');
    const [truthFlags, setTruthFlags] = React.useState([]);
    const [truthOther, setTruthOther] = React.useState('');
    const [submitting, setSubmitting] = React.useState(false);
    const [error, setError] = React.useState(null);

    const ACTIVITY_TYPES = ['Golf','Hiking','Biking','Dining','Beach','Camping','Running','Festival/Event','Other'];
    const TRUTH_PILLS = ['Rain wrong','Wind wrong','Temp wrong','Bug forecast wrong','Conditions wrong','Other'];

    const toggleFlag = (flag) => setTruthFlags(prev => prev.includes(flag) ? prev.filter(f => f !== flag) : [...prev, flag]);

    const handleSubmit = async () => {
      if (!date || !startTime || !endTime) { setError('Date, start time, and end time are required.'); return; }
      setError(null);
      setSubmitting(true);
      const id = Date.now();
      try {
        // P5 S262: build historical weather snapshot from actual observed data for the activity window.
        // Chain: NJWxNet timeseries (≤8mi) → WeatherAPI history → null. Never use weatherData.current.
        let weatherSnapshot = null;
        const actLat = selectedCourse?.lat;
        const actLng = selectedCourse?.lng;
        if (actLat && actLng) {
          const start = new Date(`${date}T${startTime}:00`);
          const end   = new Date(`${date}T${endTime}:00`);
          const { station, distanceMi } = getNearestNJWxNetStation(actLat, actLng);
          let njwxDone = false;
          if (station && distanceMi <= 8) {
            try {
              const njwxResult = await Promise.race([
                fetchNJWxNetTimeseries(station.stid, start, end, selectedCourse),
                new Promise(resolve => setTimeout(() => resolve({ success: false, error: 'timeout' }), 8000))
              ]);
              if (njwxResult.success && njwxResult.obsCount > 0) {
                weatherSnapshot = {
                  feelsLike:   njwxResult.avgTemp     != null ? Math.round(njwxResult.avgTemp)     : null,
                  wind:        njwxResult.avgWind     != null ? Math.round(njwxResult.avgWind)     : null,
                  gust:        njwxResult.avgGust     != null ? Math.round(njwxResult.avgGust)     : null,
                  humidity:    njwxResult.avgHumidity != null ? Math.round(njwxResult.avgHumidity) : null,
                  precipAccum: njwxResult.precipAccum ?? null,
                  conditions:  njwxResult.conditions  || null,
                  dataSource:  `njwxnet ${station.stid}`,
                  goNoGo:      badResult?.status || '',
                };
                njwxDone = true;
              }
            } catch { /* fall through to WeatherAPI */ }
          }
          if (!njwxDone) {
            // WeatherAPI history fallback — actual observed hourly data for the activity date
            try {
              const wapiHist = await Promise.race([
                fetch(`https://api.weatherapi.com/v1/history.json?key=${WAPI_KEY}&q=${actLat},${actLng}&dt=${date}&aqi=no`),
                new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 8000))
              ]);
              if (wapiHist.ok) {
                const wapiData = await wapiHist.json();
                const hours = wapiData?.forecast?.forecastday?.[0]?.hour || [];
                const startH = start.getHours();
                const endH   = end.getHours();
                const window = hours.filter(h => {
                  const hh = new Date(h.time).getHours();
                  return hh >= startH && hh <= endH;
                });
                if (window.length > 0) {
                  const avg = arr => arr.length ? Math.round(arr.reduce((s, v) => s + v, 0) / arr.length) : null;
                  weatherSnapshot = {
                    feelsLike:   avg(window.map(h => h.feelslike_f)),
                    wind:        avg(window.map(h => h.wind_mph)),
                    gust:        avg(window.map(h => h.gust_mph)),
                    humidity:    avg(window.map(h => h.humidity)),
                    precipAccum: Math.round(window.reduce((s, h) => s + (h.precip_in || 0), 0) * 10000) / 10000,
                    conditions:  window[Math.floor(window.length / 2)]?.condition?.text || null,
                    dataSource:  'weatherapi_history',
                    goNoGo:      badResult?.status || '',
                  };
                }
              }
            } catch { /* weatherSnapshot stays null */ }
          }
        }

        await fetch(ACTIVITY_WEBHOOK, {
          method: 'POST', mode: 'no-cors',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: 'activity_report',
            id,
            deviceId,
            sessionId,
            date,
            startTime,
            endTime,
            locationName: selectedCourse?.name || '',
            locationAddress: selectedCourse?.formattedAddress || selectedCourse?.name || '',
            lat: actLat,
            lng: actLng,
            activityType,
            truthFlags,
            truthOther: truthOther || '',
            weatherSnapshot,
          })
        });
        setActivityLoggedThisSession(true);
        navigateTo('home'); // P3 S262: navigate directly — inline component resets state before success screen renders
      } catch (e) {
        setError('Submission failed. Please try again.');
      } finally {
        setSubmitting(false);
      }
    };

    return (
      <div style={{ padding: '16px 14px 40px' }}>
        {/* Activity type */}
        <div style={{ marginBottom: 18 }}>
          <div style={{ fontSize: 13, color: COLOR_OFF_WHITE, fontWeight: 600, marginBottom: 8 }}>Activity type</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {ACTIVITY_TYPES.map(t => (
              <button key={t} onClick={() => setActivityType(t)}
                style={{ padding: '7px 14px', borderRadius: 20, border: `1px solid ${activityType === t ? COLOR_GREEN : COLOR_DARK_GREEN}`,
                  background: activityType === t ? COLOR_DARK_GREEN : 'transparent', color: activityType === t ? '#0a0a0a' : COLOR_OFF_WHITE,
                  fontSize: 13, cursor: 'pointer', fontWeight: activityType === t ? 700 : 400 }}>
                {t}
              </button>
            ))}
          </div>
        </div>
        {/* Date */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 13, color: COLOR_OFF_WHITE, fontWeight: 600, marginBottom: 6 }}>Date <span style={{ color: '#EF4444' }}>*</span></div>
          <input type="date" value={date} onChange={e => setDate(e.target.value)}
            style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', background: COLOR_INPUT_BG, border: `1px solid ${COLOR_DARK_GREEN}`, borderRadius: 8, color: COLOR_WHITE, fontSize: 14 }} />
        </div>
        {/* Start/End time */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, color: COLOR_OFF_WHITE, fontWeight: 600, marginBottom: 6 }}>Start time <span style={{ color: '#EF4444' }}>*</span></div>
            <input type="time" value={startTime} onChange={e => setStartTime(e.target.value)}
              style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', background: COLOR_INPUT_BG, border: `1px solid ${COLOR_DARK_GREEN}`, borderRadius: 8, color: COLOR_WHITE, fontSize: 14 }} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, color: COLOR_OFF_WHITE, fontWeight: 600, marginBottom: 6 }}>End time <span style={{ color: '#EF4444' }}>*</span></div>
            <input type="time" value={endTime} onChange={e => setEndTime(e.target.value)}
              style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', background: COLOR_INPUT_BG, border: `1px solid ${COLOR_DARK_GREEN}`, borderRadius: 8, color: COLOR_WHITE, fontSize: 14 }} />
          </div>
        </div>
        {/* Location (read-only) */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 13, color: COLOR_OFF_WHITE, fontWeight: 600, marginBottom: 6 }}>Location</div>
          <div style={{ padding: '10px 12px', background: COLOR_INPUT_BG, border: `1px solid ${COLOR_DARK_GREEN}`, borderRadius: 8, color: COLOR_OFF_WHITE, fontSize: 13 }}>
            {selectedCourse?.name || 'No location set'}
          </div>
        </div>
        {/* Truth Chain pills */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 13, color: COLOR_OFF_WHITE, fontWeight: 600, marginBottom: 8 }}>What was wrong? <span style={{ fontSize: 12, fontWeight: 400 }}>(optional)</span></div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {TRUTH_PILLS.map(flag => (
              <button key={flag} onClick={() => toggleFlag(flag)}
                style={{ padding: '7px 14px', borderRadius: 20, border: `1px solid ${truthFlags.includes(flag) ? COLOR_GREEN : COLOR_DARK_GREEN}`,
                  background: truthFlags.includes(flag) ? COLOR_DARK_GREEN : 'transparent', color: truthFlags.includes(flag) ? '#0a0a0a' : COLOR_OFF_WHITE,
                  fontSize: 13, cursor: 'pointer', fontWeight: truthFlags.includes(flag) ? 700 : 400 }}>
                {flag}
              </button>
            ))}
          </div>
          <textarea value={truthOther} onChange={e => setTruthOther(e.target.value.slice(0, 200))}
            placeholder={"Selected something above? Add details — e.g. 'Rain hit at 2pm, app showed 10%' · 'Wind was 25mph but app showed 12' · 'Temp felt 10° colder than shown' · 'Bugs were zero despite High rating' · 'Conditions wrong — sunny when app said cloudy'. Also use for general notes about the activity."}
            rows={4}
            style={{ marginTop: 10, width: '100%', boxSizing: 'border-box', padding: '10px 12px', background: COLOR_INPUT_BG, border: `1px solid ${COLOR_DARK_GREEN}`, borderRadius: 8, color: COLOR_WHITE, fontSize: 13, fontFamily: 'inherit', resize: 'vertical' }} />
          <div style={{ fontSize: 11, color: COLOR_MUTED, textAlign: 'right', marginTop: 4 }}>{truthOther.length}/200</div>
        </div>
        {/* Disclaimer */}
        <div style={{ fontSize: 12, color: COLOR_MUTED, marginBottom: 16, lineHeight: 1.5, fontStyle: 'italic' }}>
          By providing this information you are assisting with the accuracy of the application. No personal information will be recorded. (optional but helpful)
        </div>
        {error && <div style={{ fontSize: 13, color: '#EF4444', marginBottom: 12 }}>{error}</div>}
        <button onClick={handleSubmit} disabled={submitting}
          style={{ width: '100%', padding: '14px', background: COLOR_CARD, border: `4px solid ${COLOR_DARK_GREEN}`, borderRadius: 12,
            fontSize: 16, fontWeight: 800, color: '#0a0a0a', cursor: submitting ? 'wait' : 'pointer' }}>
          {submitting ? 'Submitting…' : 'Submit Report'}
        </button>
      </div>
    );
  };

  // ── NEARBY SPOTS SCREEN ───────────────────────────────────
  const NearbySpotsScreen = () => {
    const [searchTerm, setSearchTerm] = React.useState('restaurants');
    const [usePhoneGPS, setUsePhoneGPS] = React.useState(false);
    const [phoneCoords, setPhoneCoords] = React.useState(null);
    const [gpsGetting, setGpsGetting] = React.useState(false);

    const getPhoneGPS = () => {
      if (!navigator.geolocation) return;
      setGpsGetting(true);
      navigator.geolocation.getCurrentPosition(
        (pos) => { setPhoneCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude }); setGpsGetting(false); setUsePhoneGPS(true); },
        () => { setGpsGetting(false); },
        { timeout: 8000, enableHighAccuracy: true }
      );
    };

    const handleSearch = () => {
      const lat = (usePhoneGPS && phoneCoords) ? phoneCoords.lat : selectedCourse?.lat;
      const lng = (usePhoneGPS && phoneCoords) ? phoneCoords.lng : selectedCourse?.lng;
      if (!lat || !lng) return;
      const query = encodeURIComponent(searchTerm || 'restaurants');
      const url = `https://www.google.com/maps/search/${query}/@${lat},${lng},15z`;
      window.open(url, '_blank');
    };

    return (
      <div style={{ padding: '20px 14px' }}>
        <div style={{ fontSize: 14, color: COLOR_MUTED, marginBottom: 16 }}>
          Search near: <span style={{ color: COLOR_WHITE, fontWeight: 600 }}>
            {usePhoneGPS && phoneCoords ? 'Your current location' : (selectedCourse?.name || 'No location set')}
          </span>
        </div>
        {/* GPS toggle */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, padding: '12px 14px', background: COLOR_INPUT_BG, border: `1px solid ${COLOR_DARK_GREEN}`, borderRadius: 10 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, color: COLOR_WHITE, fontWeight: 600 }}>Use my current location</div>
            <div style={{ fontSize: 12, color: COLOR_OFF_WHITE, marginTop: 2 }}>Switch from {selectedCourse?.name?.split(',')[0] || 'activity location'} to where you are now</div>
          </div>
          <button onClick={() => { if (!usePhoneGPS) { if (phoneCoords) { setUsePhoneGPS(true); } else { getPhoneGPS(); } } else { setUsePhoneGPS(false); } }}
            style={{ padding: '8px 14px', borderRadius: 20, border: `1px solid ${usePhoneGPS ? COLOR_GREEN : COLOR_DARK_GREEN}`,
              background: usePhoneGPS ? COLOR_DARK_GREEN : 'transparent', color: usePhoneGPS ? COLOR_GREEN : COLOR_MUTED, fontSize: 13, cursor: 'pointer', fontWeight: 600 }}>
            {gpsGetting ? '📡…' : usePhoneGPS ? 'On' : 'Off'}
          </button>
        </div>
        {/* Search input */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 13, color: COLOR_OFF_WHITE, fontWeight: 600, marginBottom: 8 }}>What are you looking for?</div>
          <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            placeholder="restaurants, bars, ice cream, restrooms…"
            style={{ width: '100%', boxSizing: 'border-box', padding: '12px 14px', background: COLOR_INPUT_BG,
              border: `2px solid ${COLOR_DARK_GREEN}`, borderRadius: 10, color: COLOR_WHITE, fontSize: 14 }} />
        </div>
        <button onClick={handleSearch}
          style={{ width: '100%', padding: '14px', background: COLOR_CARD, border: `4px solid ${COLOR_DARK_GREEN}`, borderRadius: 12,
            fontSize: 16, fontWeight: 800, color: '#0a0a0a', cursor: 'pointer' }}>
          🗺️ Open in Google Maps
        </button>
        <div style={{ fontSize: 12, color: COLOR_OFF_WHITE, marginTop: 12, textAlign: 'center' }}>
          Google Maps opens — use its search bar to refine further
        </div>
      </div>
    );
  };

  // ── COURSE NAME TAPPABLE HEADER ────────────────────────────

  // ── FORECAST SCREEN — S98 spec: 7-day card list ───────────
  const ForecastScreen = () => {
    if (!weatherData) return (
      <div style={{ padding: '40px 16px', textAlign: 'center', color: COLOR_MUTED }}>
        Loading weather data…
      </div>
    );

    const vcDailyAll = weatherData.vcDailyAll || [];
    const hourly = weatherData.hourly || [];

    // Build 7 days. For each day get worst-status from hourly (if available) else from daily.
    const now = new Date();
    const days = [];
    for (let d = 0; d < 7; d++) {
      const date = new Date(now);
      date.setHours(0, 0, 0, 0);
      date.setDate(date.getDate() + d);
      const dateStr = `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}`;

      // Get hourly data for user's activity window (profile.playStart–playEnd)
      const [psH] = (profile?.playStart || '08:00').split(':').map(Number);
      const [peH, peM] = (profile?.playEnd || '20:00').split(':').map(Number);
      // S268: minute-accurate end boundary. '00:00' is unreachable as a Start Time (floored at 6AM),
      // so on End Time it unambiguously means "through end of day" rather than literal midnight-start.
      let peTotalMin = peH * 60 + (peM || 0);
      if (peTotalMin === 0) peTotalMin = 1440;
      const dayHours = hourly.filter(h => {
        const hd = new Date(h.time);
        hd.setHours(0,0,0,0);
        const hr = new Date(h.time).getHours();
        // S267: for today, drop hours that have already fully elapsed — card should reflect only remaining window
        // S268 fix: once the window itself has fully closed for today, stop dropping hours — revert to the
        // full window (unchanged end-of-window behavior) instead of falling through to the synthetic daily fallback
        const windowEndMs = date.getTime() + peTotalMin * 60000;
        const windowClosed = d === 0 && now.getTime() >= windowEndMs;
        const hourEndMs = new Date(h.time).getTime() + 3600000;
        const notElapsed = d !== 0 || windowClosed || hourEndMs > now.getTime();
        return hd.getTime() === date.getTime() && hr >= psH && (hr * 60) < peTotalMin && notElapsed;
      });

      // Daily summary from vcDailyAll
      const daily = vcDailyAll.find(v => v.date === dateStr) || null;

      // Feels hi/lo — computed first so status can use feelsHi.
      let feelsHi, feelsLo, tempHi, tempLo;
      if (dayHours.length > 0) {
        const feels = dayHours.map(h => h.feelsLike != null ? h.feelsLike : h.temp).filter(v => v != null);
        const temps = dayHours.map(h => h.temp).filter(v => v != null);
        feelsHi = feels.length ? Math.round(Math.max(...feels)) : null;
        feelsLo = feels.length ? Math.round(Math.min(...feels)) : null;
        tempHi  = temps.length ? Math.round(Math.max(...temps)) : null;
        tempLo  = temps.length ? Math.round(Math.min(...temps)) : null;
        if ((feelsHi == null || feelsLo == null) && daily) {
          feelsHi = feelsHi ?? daily.tempMax;
          feelsLo = feelsLo ?? daily.tempMin;
          tempHi  = tempHi  ?? daily.tempMax;
          tempLo  = tempLo  ?? daily.tempMin;
        }
      } else if (daily) {
        feelsHi = daily.tempMax; feelsLo = daily.tempMin;
        tempHi  = daily.tempMax; tempLo  = daily.tempMin;
      }

      // Status: worst hour across the activity window. Same logic as drilldown.
      let status = 'GO', worstResult = null, synth = null;
      if (dayHours.length > 0) {
        worstResult = getWindowStatus(dayHours, profile);
        status = worstResult.status;
      } else if (daily) {
        synth = {
          temp:         daily.tempMax,
          feelsLike:    calcFeelsLike(daily.tempMax, daily.humidity || 50, daily.windSpeed || 0),
          windSpeedNum: daily.windSpeed || 0,
          windGust:     Math.round((daily.windSpeed || 0) * 1.3),
          precipChance: daily.precipChance || 0,
          conditions:   daily.conditions || '',
        };
        worstResult = getHourStatus(synth, profile);
        status = worstResult.status;
      }

      // Dominant conditions + sky — use worst hour (highest precip) not midday
      const midday = dayHours.find(h => new Date(h.time).getHours() === 12) || dayHours[Math.floor(dayHours.length/2)] || null;
      const worstHour = dayHours.length > 0
        ? dayHours.reduce((worst, h) => (h.precipChance || 0) > (worst.precipChance || 0) ? h : worst, dayHours[0])
        : null;
      const conditions = (worstHour && (worstHour.precipChance || 0) >= 30 ? worstHour?.conditions : null) || midday?.conditions || daily?.conditions || 'Clear'; // S267: apply 30% threshold — match hourly drilldown rule

      // Max precip % for the window
      const maxPrecip = dayHours.length > 0
        ? Math.max(...dayHours.map(h => h.precipChance || 0))
        : (daily?.precipChance || 0);

      // Max wind
      const maxWind = dayHours.length > 0
        ? Math.max(...dayHours.map(h => h.windSpeedNum || 0))
        : (daily?.windSpeed || 0);

      // Avg humidity
      const avgHumidity = dayHours.length > 0
        ? Math.round(dayHours.reduce((s, h) => s + (h.humidity || 0), 0) / dayHours.length)
        : null;

      // Sunset — today/tomorrow from weatherData, days 2-6 from vcDailyAll daily data
      let sunset = null;
      if (d === 0) sunset = weatherData.sunsetToday;
      else if (d === 1) sunset = weatherData.sunsetTomorrow;
      else sunset = daily?.sunset || null;

      // Day label
      const dayLabel = d === 0 ? 'Today' : d === 1 ? 'Tomorrow'
        : date.toLocaleDateString('en-US', { weekday: 'long' });
      // eslint-disable-next-line no-unused-vars
      const dateLabel = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

      // Primary trigger (worst reason)
      const primaryTrigger = worstResult?.reason || null;

      const maxUV = dayHours.length > 0 ? Math.max(...dayHours.map(h => h.uvIndex || 0)) : 0;

      days.push({ dateStr, date, dayLabel, dateLabel, status, feelsHi, feelsLo, tempHi, tempLo, conditions, maxPrecip, maxWind, avgHumidity, sunset, primaryTrigger, hasHourly: dayHours.length > 0, midday, synth, maxUV });
    }

    const statusColor = (s) => STATUS_COLORS[s]?.color || '#9CA3AF';
    const DAY_ABBREVS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

    // ⓘ triggers — present for threshold-based triggers only
    const POPUP_TRIGGERS = ['wind', 'precip', 'feels', 'gust'];
    const hasTriggerPopup = (triggerText) => {
      if (!triggerText) return false;
      const t = triggerText.toLowerCase();
      return POPUP_TRIGGERS.some(k => t.includes(k));
    };

    return (
      <div style={{ padding: '0 12px 16px', boxSizing: 'border-box', overflowX: 'hidden', width: '100%' }}>
        {/* Back nav — no bar, no border, minimal height */}
        <div style={{ display: 'flex', alignItems: 'center', paddingBottom: 3 }}>
          <button onClick={goBack} style={{ ...BTN_BASE, padding: '4px 8px', background: 'transparent', color: COLOR_OFF_WHITE, fontSize: 20, minWidth: 44 }}>←</button>
          <div style={{ flex: 1, fontSize: 17, fontWeight: 700, color: COLOR_WHITE }}>Forecast</div>
          {screenHistory.length > 2 && (
            <button onClick={() => { setScreenHistory(['home']); setScreen('home'); setTimeout(() => scrollToTop(), 50); }} style={{ ...BTN_BASE, padding: '6px 12px', background: COLOR_DARK_GREEN, color: '#0a0a0a', fontSize: 14, fontWeight: 700, borderRadius: 8 }}>Home</button>
          )}
        </div>
        {/* Spec: 2-col grid, uniform card height via align-items:stretch */}
        {/* S161 Item 4: Column-major fill — Tue/Wed/Thu/Fri down col 1, Sat/Sun/Mon/Quote down col 2.
            Calendar-style scan vs row-major (Tue-Wed, Thu-Fri, Sat-Sun, Mon-Quote). */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gridTemplateRows: 'repeat(4, 1fr)', gridAutoFlow: 'column', gap: 6, alignItems: 'stretch', width: '100%', boxSizing: 'border-box' }}>
        {days.map((day) => {
          const sc = statusColor(day.status);
          // Spec: GO / CAUTION / STR CAU / NO-GO
          const labelShort = day.status === 'STRONG CAUTION' ? 'STR CAU' : day.status;
          const isSelected = selectedForecastDay === day.dateStr;
          // Spec: Mon/Tue/Wed/Thu/Fri/Sat/Sun
          const dayAbbrev = DAY_ABBREVS[day.date.getDay()];
          // Dim past days + today after the user's activity window ends (S268: was hardcoded to 8PM)
          const now = new Date();
          const todayStr = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;
          const [peHourDim, peMinDim] = (profile?.playEnd || '20:00').split(':').map(Number);
          let peTotalMinDim = peHourDim * 60 + (peMinDim || 0);
          if (peTotalMinDim === 0) peTotalMinDim = 1440; // '00:00' means through end of day, not midnight-start
          const nowTotalMin = now.getHours() * 60 + now.getMinutes();
          const isPast = day.dateStr < todayStr || (day.dateStr === todayStr && nowTotalMin >= peTotalMinDim);
          // Spec UI-J: use midday from days[] — NOT repHour lookup (fails days 2-7)
          const popupHour = day.midday;
          // Spec R5: ⓘ only for Wind/Precip/Feels/Gust — condition triggers (Fog, Thunder, Snow) get no ⓘ
          const isThresholdTrigger = day.primaryTrigger && /^(Precip|Wind|Gust|Feels)/.test(day.primaryTrigger);
          const popup = (day.status !== 'GO' && isThresholdTrigger && day.synth)
            ? buildTriggerPopup(day.primaryTrigger, day.synth, profile)
            : null;

          return (
            <button
              key={day.dateStr}
              onClick={() => { setSelectedForecastDay(day.dateStr); navigateTo('forecastdrilldown'); }}
              style={{
                /* Card shell — bg #000, border 5px status color, radius 12px, padding 8px, Georgia serif */
                background: isSelected ? '#111' : '#000',
                opacity: isPast ? 0.4 : 1,
                border: `5px solid ${sc}`,
                borderRadius: 12,
                padding: 6,
                outline: 'none',
                cursor: 'pointer',
                textAlign: 'left',
                fontFamily: 'Georgia, serif',
                boxSizing: 'border-box',
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                position: 'relative',
              }}
            >
              {/* R1: date · DayAbbrev · sunset (left, flex:1) | status icon + label (right, flex-shrink:0) */}
              <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 2 }}>
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', flexWrap: 'nowrap', gap: 3, whiteSpace: 'nowrap', fontSize: 12, fontWeight: 700, color: '#fff', minWidth: 0 }}>
                  <span>{day.dateLabel} · {dayAbbrev}</span>
                  {day.sunset && (<>
                    <svg width="13" height="13" viewBox="0 0 16 16" style={{ flexShrink: 0 }}>
                      <path fill="#fff" d="M7.646 4.854a.5.5 0 0 0 .708 0l1.5-1.5a.5.5 0 0 0-.708-.708l-.646.647V1.5a.5.5 0 0 0-1 0v1.793l-.646-.647a.5.5 0 1 0-.708.708zm-5.303-.51a.5.5 0 0 1 .707 0l1.414 1.413a.5.5 0 0 1-.707.707L2.343 5.05a.5.5 0 0 1 0-.707zm11.314 0a.5.5 0 0 1 0 .706l-1.414 1.414a.5.5 0 1 1-.707-.707l1.414-1.414a.5.5 0 0 1 .707 0zM8 7a3 3 0 0 1 2.599 4.5H5.4A3 3 0 0 1 8 7m3.71 4.5a4 4 0 1 0-7.418 0H.499a.5.5 0 0 0 0 1h15a.5.5 0 0 0 0-1h-3.79zM0 10a.5.5 0 0 1 .5-.5h2a.5.5 0 0 1 0 1h-2A.5.5 0 0 1 0 10m13 0a.5.5 0 0 1 .5-.5h2a.5.5 0 0 1 0 1h-2a.5.5 0 0 1-.5-.5" />
                    </svg>
                    <span style={{ fontSize: 10, color: '#E5E7EB', fontWeight: 700 }}>{day.sunset}</span>
                  </>)}
                </div>
                <div style={{ flexShrink: 0, marginLeft: 6, display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                  <StatusIcon status={day.status} size={16} />
                  <span style={{ fontSize: 9, fontWeight: 700, color: sc, whiteSpace: 'nowrap', lineHeight: 1, marginTop: 2 }}>{labelShort}</span>
                </div>
              </div>

              {/* R2: sky SVG 36px + condition 20px bold white — DOMINANT element (locked UI-DAY-1 S123) */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 2 }}>
                {renderSkyIcon(day.conditions, true, 36)}
                <span style={{ fontSize: 20, fontWeight: 700, color: '#fff', lineHeight: 1.2 }}>{skyLabel(day.conditions)}</span>
              </div>

              {/* R3: feels hi/lo (left) | precip 14px bold / wind 11px (right, border-left) */}
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  {day.feelsHi != null && day.feelsLo != null ? (<>
                    <div style={{ display: 'flex', alignItems: 'baseline', whiteSpace: 'nowrap', marginBottom: 0 }}>
                      <span style={{ fontSize: 22, fontWeight: 700, color: COLOR_WHITE, lineHeight: 1.2, minWidth: 48, display: 'inline-block' }}>{day.feelsHi}°</span>
                      <span style={{ fontSize: 10, fontWeight: 400, color: '#E5E7EB' }}>({day.tempHi}°)</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'baseline', whiteSpace: 'nowrap' }}>
                      <span style={{ fontSize: 13, fontWeight: 400, color: COLOR_WHITE, lineHeight: 1.1, minWidth: 48, display: 'inline-block' }}>{day.feelsLo}°</span>
                      <span style={{ fontSize: 10, fontWeight: 400, color: '#E5E7EB' }}>({day.tempLo}°)</span>
                    </div>
                  </>) : <span style={{ fontSize: 12, color: COLOR_MUTED }}>—</span>}
                </div>
                <div style={{ borderLeft: '1px solid #333', paddingLeft: 6, flexShrink: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 2 }}>
                  <span style={{ fontSize: 14, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', display:'flex', alignItems:'center' }}><svg width="11" height="13" viewBox="0 0 14 18" fill="none" style={{display:'inline-block',verticalAlign:'middle',marginRight:3}}><path d="M7 1 C7 1 1 8 1 12 A6 6 0 0 0 13 12 C13 8 7 1 7 1Z" fill="#4a90d9"/></svg>{day.maxPrecip}%</span>
                  <span style={{ fontSize: 11, color: '#fff', whiteSpace: 'nowrap', display:'flex', alignItems:'center' }}><svg width="14" height="12" viewBox="0 0 16 12" fill="none" style={{display:'inline-block',verticalAlign:'middle',marginRight:3}}><line x1="1" y1="3" x2="15" y2="3" stroke="#90a4ae" strokeWidth="1.8" strokeLinecap="round"/><line x1="1" y1="6.5" x2="12" y2="6.5" stroke="#90a4ae" strokeWidth="1.8" strokeLinecap="round"/><line x1="1" y1="10" x2="9" y2="10" stroke="#90a4ae" strokeWidth="1.8" strokeLinecap="round"/></svg>{Math.round(day.maxWind)} mph</span>
                </div>
              </div>

              {/* R4: ELIMINATED */}

              {/* R5: always rendered for uniform height. Visible only on non-GO cards with a trigger. */}
              <div style={{ borderTop: day.status !== 'GO' && day.primaryTrigger ? '1px solid #333' : '1px solid transparent', paddingTop: day.status !== 'GO' && day.primaryTrigger ? 3 : 0, marginTop: 'auto', display: 'flex', alignItems: 'center', gap: 4, minHeight: 14 }}>
                {day.status !== 'GO' && day.primaryTrigger ? (<>
                  <StatusIcon status={day.status} size={12} />
                  <span style={{ fontSize: 10, fontWeight: 700, color: sc, lineHeight: 1.3, flex: 1 }}>{day.primaryTrigger}</span>
                  {popup && (
                    <button
                      onClick={e => { e.stopPropagation(); setTriggerPopup(popup); }}
                      style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', flexShrink: 0, lineHeight: 1 }}
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16">
                        <circle cx="8" cy="8" r="7" fill="none" stroke="#E5E7EB" strokeWidth="1" />
                        <text x="8" y="8" textAnchor="middle" dominantBaseline="central" fontSize="9" fontWeight="700" fill="#E5E7EB" fontFamily="Arial,sans-serif">i</text>
                      </svg>
                    </button>
                  )}
                </>) : null}
              </div>
            </button>
          );
        })}
        {/* 8th slot — Daily Quote Card. Curated 125 quotes ≤100 chars. Grid 1fr rows = same height as day cards. */}
        {(() => {
          const QUOTES = [
            // SUN TZU
            { q: "Opportunities multiply as they are seized.", a: "Sun Tzu" },
            { q: "Know yourself and you will win all battles.", a: "Sun Tzu" },
            { q: "In the midst of chaos, there is also opportunity.", a: "Sun Tzu" },
            { q: "Speed is the essence of war.", a: "Sun Tzu" },
            { q: "The greatest victory is that which requires no battle.", a: "Sun Tzu" },
            // CONFUCIUS
            { q: "It does not matter how slowly you go as long as you do not stop.", a: "Confucius" },
            { q: "Everything has beauty, but not everyone sees it.", a: "Confucius" },
            { q: "Life is really simple, but we insist on making it complicated.", a: "Confucius" },
            { q: "He who learns but does not think is lost.", a: "Confucius" },
            { q: "Our greatest glory is not in never falling, but in rising every time we fall.", a: "Confucius" },
            { q: "Choose a job you love and you will never have to work a day in your life.", a: "Confucius" },
            // ARISTOTLE
            { q: "We are what we repeatedly do. Excellence, then, is not an act, but a habit.", a: "Aristotle" },
            { q: "Knowing yourself is the beginning of all wisdom.", a: "Aristotle" },
            { q: "Hope is a waking dream.", a: "Aristotle" },
            { q: "Happiness depends upon ourselves.", a: "Aristotle" },
            { q: "In all things of nature there is something of the marvelous.", a: "Aristotle" },
            // PLATO
            { q: "Be kind, for everyone you meet is fighting a harder battle.", a: "Plato" },
            { q: "Courage is knowing what not to fear.", a: "Plato" },
            { q: "The measure of a man is what he does with power.", a: "Plato" },
            // SOCRATES
            { q: "The unexamined life is not worth living.", a: "Socrates" },
            { q: "I know that I know nothing.", a: "Socrates" },
            { q: "To find yourself, think for yourself.", a: "Socrates" },
            { q: "Be as you wish to seem.", a: "Socrates" },
            // BERTRAND RUSSELL
            { q: "The good life is one inspired by love and guided by knowledge.", a: "Bertrand Russell" },
            { q: "The time you enjoy wasting is not wasted time.", a: "Bertrand Russell" },
            { q: "To conquer fear is the beginning of wisdom.", a: "Bertrand Russell" },
            { q: "Do not fear to be eccentric in opinion, for every opinion now accepted was once eccentric.", a: "Bertrand Russell" },
            // ALBERT CAMUS
            { q: "In the midst of winter, I found there was, within me, an invincible summer.", a: "Albert Camus" },
            { q: "Life is the sum of all your choices.", a: "Albert Camus" },
            { q: "Real generosity toward the future lies in giving all to the present.", a: "Albert Camus" },
            { q: "Blessed are the hearts that can bend; they shall never be broken.", a: "Albert Camus" },
            // CARL SAGAN
            { q: "Somewhere, something incredible is waiting to be known.", a: "Carl Sagan" },
            { q: "We are made of star stuff.", a: "Carl Sagan" },
            { q: "The cosmos is within us.", a: "Carl Sagan" },
            { q: "Extraordinary claims require extraordinary evidence.", a: "Carl Sagan" },
            { q: "We are a way for the cosmos to know itself.", a: "Carl Sagan" },
            { q: "For small creatures such as we the vastness is bearable only through love.", a: "Carl Sagan" },
            // NEIL DEGRASSE TYSON
            { q: "The universe is under no obligation to make sense to you.", a: "Neil deGrasse Tyson" },
            { q: "The good thing about science is that it's true whether or not you believe in it.", a: "Neil deGrasse Tyson" },
            { q: "The sky is not the limit. Your mind is.", a: "Neil deGrasse Tyson" },
            { q: "Kids are never the problem. They are born scientists.", a: "Neil deGrasse Tyson" },
            // STEPHEN HAWKING
            { q: "Intelligence is the ability to adapt to change.", a: "Stephen Hawking" },
            { q: "Remember to look up at the stars and not down at your feet.", a: "Stephen Hawking" },
            { q: "However difficult life may seem, there is always something you can do and succeed at.", a: "Stephen Hawking" },
            { q: "Quiet people have the loudest minds.", a: "Stephen Hawking" },
            { q: "Life would be tragic if it weren't funny.", a: "Stephen Hawking" },
            // ALBERT EINSTEIN
            { q: "Imagination is more important than knowledge.", a: "Albert Einstein" },
            { q: "A person who never made a mistake never tried anything new.", a: "Albert Einstein" },
            { q: "Life is like riding a bicycle. To keep your balance you must keep moving.", a: "Albert Einstein" },
            { q: "Logic will get you from A to B. Imagination will take you everywhere.", a: "Albert Einstein" },
            { q: "If you can't explain it simply, you don't understand it well enough.", a: "Albert Einstein" },
            { q: "Try not to become a man of success, but rather try to become a man of value.", a: "Albert Einstein" },
            // NIKOLA TESLA
            { q: "The present is theirs; the future, for which I really worked, is mine.", a: "Nikola Tesla" },
            { q: "Be alone, that is the secret of invention.", a: "Nikola Tesla" },
            { q: "I don't care that they stole my idea. I care that they don't have any of their own.", a: "Nikola Tesla" },
            { q: "Of all things, I liked books best.", a: "Nikola Tesla" },
            // WINSTON CHURCHILL
            { q: "Success is not final, failure is not fatal: it is the courage to continue that counts.", a: "Winston Churchill" },
            { q: "If you're going through hell, keep going.", a: "Winston Churchill" },
            { q: "We make a living by what we get, but we make a life by what we give.", a: "Winston Churchill" },
            { q: "Attitude is a little thing that makes a big difference.", a: "Winston Churchill" },
            { q: "You have enemies? Good. That means you've stood up for something.", a: "Winston Churchill" },
            // THEODORE ROOSEVELT
            { q: "Do what you can, with what you have, where you are.", a: "Theodore Roosevelt" },
            { q: "Believe you can and you're halfway there.", a: "Theodore Roosevelt" },
            { q: "Keep your eyes on the stars and your feet on the ground.", a: "Theodore Roosevelt" },
            { q: "The man who makes no mistakes does not usually make anything.", a: "Theodore Roosevelt" },
            { q: "In any moment of decision, the best thing you can do is the right thing.", a: "Theodore Roosevelt" },
            // LECH WALESA
            { q: "Deep faith eliminates fear.", a: "Lech Wałęsa" },
            { q: "Words are plentiful; deeds are precious.", a: "Lech Wałęsa" },
            { q: "We hold our heads high, despite the price we have paid, because freedom is priceless.", a: "Lech Wałęsa" },
            { q: "The thing that matters is not what you bear, but how you bear it.", a: "Lech Wałęsa" },
            { q: "You can't have freedom without responsibility.", a: "Lech Wałęsa" },
            // VACLAV HAVEL
            { q: "Work for something because it is good, not just because it stands a chance to succeed.", a: "Václav Havel" },
            { q: "Keep the company of those who seek the truth — run from those who have found it.", a: "Václav Havel" },
            { q: "Vision is not enough; it must be combined with venture.", a: "Václav Havel" },
            { q: "The salvation of this human world lies nowhere else than in the human heart.", a: "Václav Havel" },
            { q: "Anyone who takes himself too seriously always risks looking ridiculous.", a: "Václav Havel" },
            // FRANZ KAFKA
            { q: "There is infinite hope, but not for us.", a: "Franz Kafka" },
            { q: "In the fight between you and the world, back the world.", a: "Franz Kafka" },
            { q: "A book must be the axe for the frozen sea within us.", a: "Franz Kafka" },
            { q: "Start with what is right rather than what is acceptable.", a: "Franz Kafka" },
            { q: "Anyone who keeps the ability to see beauty never grows old.", a: "Franz Kafka" },
            // MIKE TYSON
            { q: "Everyone has a plan until they get punched in the face.", a: "Mike Tyson" },
            { q: "Discipline is doing what you hate to do, but do it like you love it.", a: "Mike Tyson" },
            { q: "Fear is your best friend or your worst enemy.", a: "Mike Tyson" },
            { q: "As long as we persevere and endure, we can get anything we want.", a: "Mike Tyson" },
            { q: "I'm a dreamer. I have to dream and reach for the stars.", a: "Mike Tyson" },
            // STEVE JOBS
            { q: "Stay hungry, stay foolish.", a: "Steve Jobs" },
            { q: "Innovation distinguishes between a leader and a follower.", a: "Steve Jobs" },
            { q: "Simple can be harder than complex.", a: "Steve Jobs" },
            { q: "Don't let the noise of others' opinions drown out your own inner voice.", a: "Steve Jobs" },
            { q: "Creativity is just connecting things.", a: "Steve Jobs" },
            { q: "The only way to do great work is to love what you do.", a: "Steve Jobs" },
            // WARREN BUFFETT
            { q: "Someone is sitting in the shade today because someone planted a tree a long time ago.", a: "Warren Buffett" },
            { q: "The best investment you can make is in yourself.", a: "Warren Buffett" },
            { q: "Price is what you pay. Value is what you get.", a: "Warren Buffett" },
            { q: "Risk comes from not knowing what you're doing.", a: "Warren Buffett" },
            { q: "The most important thing to do if you find yourself in a hole is to stop digging.", a: "Warren Buffett" },
            // NAVAL RAVIKANT
            { q: "Play long-term games with long-term people.", a: "Naval Ravikant" },
            { q: "Read what you love until you love to read.", a: "Naval Ravikant" },
            { q: "Happiness is a choice and a skill and you can dedicate yourself to learning that skill.", a: "Naval Ravikant" },
            { q: "A fit body, a calm mind, a house full of love. These things cannot be bought — they must be earned.", a: "Naval Ravikant" },
            // JAMES CLEAR
            { q: "You do not rise to the level of your goals. You fall to the level of your systems.", a: "James Clear" },
            { q: "Every action you take is a vote for the type of person you wish to become.", a: "James Clear" },
            { q: "Success is the product of daily habits — not once-in-a-lifetime transformations.", a: "James Clear" },
            { q: "Goals are good for setting a direction, but systems are best for making progress.", a: "James Clear" },
            { q: "Be the designer of your world and not merely the consumer of it.", a: "James Clear" },
            // RYAN HOLIDAY
            { q: "The obstacle is the way.", a: "Ryan Holiday" },
            { q: "Ego is the enemy.", a: "Ryan Holiday" },
            { q: "Stillness is the key.", a: "Ryan Holiday" },
            { q: "The best way to get what you want is to deserve what you want.", a: "Ryan Holiday" },
            // BRENE BROWN
            { q: "Vulnerability is not weakness; it's our greatest measure of courage.", a: "Brené Brown" },
            { q: "Talk to yourself like you would to someone you love.", a: "Brené Brown" },
            { q: "Owning our story can be hard but not nearly as difficult as spending our lives running from it.", a: "Brené Brown" },
            { q: "What makes you vulnerable makes you beautiful.", a: "Brené Brown" },
            { q: "Connection is why we're here. It is what gives purpose and meaning to our lives.", a: "Brené Brown" },
            // BARACK OBAMA
            { q: "Change will not come if we wait for some other person or some other time.", a: "Barack Obama" },
            { q: "The best way to not feel hopeless is to get up and do something.", a: "Barack Obama" },
            { q: "Our destiny is not written for us, but by us.", a: "Barack Obama" },
            { q: "We are the ones we've been waiting for.", a: "Barack Obama" },
            // DALAI LAMA
            { q: "My religion is very simple. My religion is kindness.", a: "Dalai Lama" },
            { q: "Remember that sometimes not getting what you want is a wonderful stroke of luck.", a: "Dalai Lama" },
            { q: "The purpose of our lives is to be happy.", a: "Dalai Lama" },
            { q: "Be kind whenever possible. It is always possible.", a: "Dalai Lama" },
            { q: "Sleep is the best meditation.", a: "Dalai Lama" },
            { q: "Judge your success by what you had to give up in order to get it.", a: "Dalai Lama" },
          ];
          const now = new Date();
          const start = new Date(now.getFullYear(), 0, 0);
          const dayOfYear = Math.floor((now - start) / 86400000);
          const quote = QUOTES[(dayOfYear * 17) % QUOTES.length];
          return (
            <div style={{
              background: '#0a0a0a',
              border: '2px solid #B8860B',
              borderRadius: 12,
              padding: '8px 10px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              textAlign: 'center',
              boxSizing: 'border-box',
              minWidth: 0,
              overflow: 'hidden',
              height: '100%',
            }}>
              <div style={{ fontSize: 13, color: '#FFFFFF', fontStyle: 'italic', lineHeight: 1.45, fontWeight: 500, marginBottom: 6 }}>{quote.q}</div>
              <div style={{ fontSize: 11, color: '#B8860B', fontWeight: 700, letterSpacing: '0.05em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}>— {quote.a}</div>
            </div>
          );
        })()}
        </div>
      </div>
    );
  };

  // ── FORECAST DRILLDOWN — S98 spec: separate page, hourly 8AM-8PM ──
  const ForecastDrilldown = () => {
    if (!weatherData || !selectedForecastDay) return (
      <div style={{ padding: '40px 16px', textAlign: 'center', color: COLOR_MUTED }}>No data</div>
    );

    const dateStr = selectedForecastDay;
    const date = new Date(dateStr + 'T00:00:00');
    const dayLabel = (() => {
      const now = new Date(); now.setHours(0,0,0,0);
      const tomorrow = new Date(now); tomorrow.setDate(now.getDate()+1);
      if (date.getTime() === now.getTime()) return 'Today';
      if (date.getTime() === tomorrow.getTime()) return 'Tomorrow';
      return date.toLocaleDateString('en-US', { weekday: 'long' });
    })();
    const isToday = dayLabel === 'Today';

    const [ddPsH] = (profile?.playStart || '08:00').split(':').map(Number);
    const [ddPeH, ddPeM] = (profile?.playEnd || '20:00').split(':').map(Number);
    let ddPeTotalMin = ddPeH * 60 + (ddPeM || 0);
    if (ddPeTotalMin === 0) ddPeTotalMin = 1440;
    // Show hours 8AM on selected day through 4AM next day (S268: extended from 2AM so people can check
    // conditions for the drive home even after their activity window has closed)
    const hourly = weatherData.hourly || [];
    const nextDay = new Date(date); nextDay.setDate(nextDay.getDate() + 1); nextDay.setHours(0,0,0,0);
    const dayHours = hourly.filter(h => {
      const ht = new Date(h.time);
      const hd = new Date(ht); hd.setHours(0,0,0,0);
      const hr = ht.getHours();
      // Same calendar day: 8AM onward (hours 8–23)
      if (hd.getTime() === date.getTime() && hr >= 8) return true;
      // Next calendar day: midnight through 3AM (hours 0–3)
      if (hd.getTime() === nextDay.getTime() && hr <= 3) return true;
      return false;
    });

    // If no hourly data, synthesize from vcDailyAll
    const vcDailyAll = weatherData.vcDailyAll || [];
    const daily = vcDailyAll.find(v => v.date === dateStr);
    const hours = dayHours.length > 0 ? dayHours : (daily ? [8,9,10,11,12,13,14,15,16,17,18,19,20,21,22,23,0,1,2,3].map(hr => ({
      time: new Date(new Date(hr <= 3 ? nextDay : date).setHours(hr, 0, 0, 0)),
      temp: Math.round(daily.tempMin + (daily.tempMax - daily.tempMin) * (Math.min(hr,19) - 8) / 12),
      feelsLike: null,
      windSpeedNum: daily.windSpeed || 0,
      windGust: Math.round((daily.windSpeed || 0) * 1.3),
      precipChance: daily.precipChance || 0,
      conditions: daily.conditions || 'Clear',
      humidity: daily.humidity || 60,
      uvIndex: hr >= 10 && hr <= 16 ? 4 : 1,
      isDaytime: hr >= 7 && hr < 20,
    })) : []);

    return (
      <div>
        {/* Today-only: Cart + Pollen advisory strip — single horizontal line */}
        {isToday && (badResult?.ground || (pollenData?.success && !pollenData.offSeason)) && (
          <div style={{ padding: '6px 12px', background: '#111', borderBottom: `1px solid ${COLOR_DARK_GREEN}`, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px 14px' }}>
            {badResult?.ground && (
              <span style={{ fontSize: 12, fontWeight: 700, color: badResult.ground.sogginess === 'soggy' || badResult.ground.sogginess === 'soft' ? STATUS_COLORS.CAUTION.color : STATUS_COLORS.GO.color }}>
                {badResult.ground.sogginess === 'soggy' ? '🌊 Ground: Saturated — standing water possible' :
                 badResult.ground.sogginess === 'soft'  ? '💧 Ground: Soft — surfaces may be wet' :
                 '✅ Ground: Dry'}
              </span>
            )}
            {pollenData?.success && !pollenData.offSeason && (
              <>
                <span style={{ fontSize: 12, color: pollenData.tree.color }}>🌳 {pollenData.tree.label}</span>
                <span style={{ fontSize: 12, color: pollenData.grass.color }}>🌾 {pollenData.grass.label}</span>
                <span style={{ fontSize: 12, color: pollenData.weed.color }}>🌿 {pollenData.weed.label}</span>
              </>
            )}
          </div>
        )}
        {/* Hour cards — 2-per-row grid per S115 spec */}
        <div style={{ padding: '8px 12px 24px', boxSizing: 'border-box', overflowX: 'hidden' }}>
          {hours.length > 0 ? (
            // Variable row count: up to 18 hours (8AM–1AM). Grid is 2 cols, rows computed dynamically.
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gridTemplateRows: `repeat(${Math.ceil(hours.length / 2)}, auto)`, gridAutoFlow: 'column', gap: 6, alignItems: 'stretch' }}>
              {hours.map((h, i) => {
                const ht = new Date(h.time);
                const hHour = ht.getHours();
                const isPastHour = isToday && (ht.getTime() + 3600000) <= new Date().getTime(); // S267: compare hour END, not start
                const isInWindow = hHour >= ddPsH && (hHour * 60) < ddPeTotalMin;
                const isDimmed = isPastHour || !isInWindow;
                return <DrilldownHourCard key={i} h={h} profile={profile} isPast={isDimmed} isInWindow={false} />;
              })}
            </div>
          ) : (
            <div style={{ padding: '40px 16px', textAlign: 'center', color: COLOR_MUTED }}>
              No hourly data available for this day
            </div>
          )}
        </div>
      </div>
    );
  };
  const CourseHeader = () => {
    const favd = selectedCourse ? isFavorite(selectedCourse) : false;
    const [, forceUpdate] = React.useReducer(x => x + 1, 0);
    const handleStar = () => {
      if (!selectedCourse) return;
      toggleFavorite(selectedCourse);
      forceUpdate();
    };
    return (
    <div style={{ background: COLOR_HEADER, borderBottom: `1px solid ${COLOR_DARK_GREEN}`, position: 'sticky', top: 0, zIndex: 100 }}>
      <div style={{ padding: '10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, flex: 1, minWidth: 0, marginRight: 8 }}>
          <button onClick={handleStar} style={{ background: 'transparent', border: 'none', fontSize: 20, cursor: 'pointer', color: favd ? '#FACC15' : COLOR_MUTED, padding: '0 2px', flexShrink: 0, lineHeight: 1, marginTop: 2 }} title={favd ? 'Remove from favorites' : 'Add to favorites'}>
            {favd ? '★' : '☆'}
          </button>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: COLOR_WHITE, lineHeight: 1.3 }}>
              {selectedCourse ? selectedCourse.name : 'No location selected'}
            </div>
            <div style={{ fontSize: 11, color: COLOR_MUTED, marginTop: 2 }}>
              {selectedCourse ? [
                selectedCourse.placeType,
                [selectedCourse.town, selectedCourse.state, selectedCourse.zip].filter(Boolean).join(', ')
              ].filter(Boolean).join(' · ') : ''}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button onClick={() => { setShowSourceLegend(true); }} style={{ background: 'transparent', border: 'none', padding: 4, cursor: 'pointer', fontSize: 16 }}>
            <span style={{ color: '#7aafff' }}>📡</span>
          </button>
          {isLoading && <div style={{ fontSize: 12, color: COLOR_MUTED }}>Loading…</div>}
          {lastRefreshTime && !isLoading && (
            <button
              onClick={() => { clearAllCaches(); loadCourse(selectedCourse); }}
              style={{ ...BTN_BASE, padding: '6px 12px', background: '#0a0a0a', border: `1px solid ${COLOR_GOLD}`, borderRadius: 8, fontSize: 12, color: COLOR_GOLD, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 4 }}
            >
              <span style={{ fontSize: 14 }}>↻</span> {lastRefreshTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </button>
          )}
          <button
            onClick={() => {
              if (activityLoggedThisSession) {
                // Already logged — silent pass-through to location entry
                setActivityLoggedThisSession(false);
                navigateTo('locationentry');
              } else {
                // Not logged — show guard prompt
                setShowChangeGuard(true);
              }
            }}
            style={{ ...BTN_BASE, padding: '6px 12px', background: COLOR_DARK_GREEN, border: `1px solid ${COLOR_DARK_GREEN}`, borderRadius: 8, fontSize: 12, color: '#0a0a0a', fontWeight: 600 }}
          >
            Change
          </button>
        </div>
      </div>
      {/* Change guard modal */}
      {showChangeGuard && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 24px' }}>
          <div style={{ background: '#111f11', border: `1px solid ${COLOR_DARK_GREEN}`, borderRadius: 14, padding: '24px 20px', maxWidth: 340, width: '100%' }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: COLOR_WHITE, marginBottom: 10 }}>Before changing locations</div>
            <div style={{ fontSize: 14, color: COLOR_MUTED, marginBottom: 20, lineHeight: 1.5 }}>Log your activity? <span style={{ color: COLOR_MUTED, fontSize: 12 }}>(optional)</span></div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <button
                onClick={() => { setShowChangeGuard(false); navigateTo('postactivity'); }}
                style={{ ...BTN_BASE, padding: '12px', background: COLOR_DARK_GREEN, border: `1px solid ${COLOR_DARK_GREEN}`, borderRadius: 10, fontSize: 14, color: '#0a0a0a', fontWeight: 700 }}
              >
                📝 Log Activity
              </button>
              <button
                onClick={() => { setShowChangeGuard(false); setActivityLoggedThisSession(false); navigateTo('locationentry'); }}
                style={{ ...BTN_BASE, padding: '12px', background: 'transparent', border: `1px solid ${COLOR_DARK_GREEN}`, borderRadius: 10, fontSize: 14, color: COLOR_MUTED }}
              >
                Skip
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    );
  };

  // ── SOURCE LEGEND OVERLAY ──────────────────────────────────
  // ── SOURCE LEGEND OVERLAY ──────────────────────────────────
  const SourceLegendOverlay = () => {
    if (!showSourceLegend) return null;
    const { station: _lgSt, distanceMi: _lgDist } = selectedCourse ? getNearestNJWxNetStation(selectedCourse.lat, selectedCourse.lng) : { station: null, distanceMi: null };
    const stationId = _lgSt ? _lgSt.stid : null;


    // Forecast cascade state — S174 per-variable cascade (GribStream/Meteoblue removed)
    const fcStatus = weatherData?._sourceStatus || {};
    const fcSources = weatherData?._sources || {};
    const fcAllFailed = !weatherData?.success;

    // Per-row dot logic: cascade table for one variable looks like
    //   [P1, P2, P3]  →  [green if won, yellow if fetched-and-standby, red if failed, muted if never tried]
    const cascadeMap = {
      // sourceCode: success flag for that source
      NWS:           fcStatus.nws,
      VC:            fcStatus.visualCrossing,
      WeatherAPI:    fcStatus.weatherapi,
      PirateWeather: fcStatus.pirateweather,
      OpenMeteo:     fcStatus.openMeteo,
      Meteoblue:     fcStatus.meteoblue,
    };
    const sourceLabel = {
      NWS: 'NWS Hourly', VC: 'Visual Crossing', WeatherAPI: 'WeatherAPI',
      PirateWeather: 'PirateWeather', OpenMeteo: 'Open-Meteo', Meteoblue: 'Meteoblue',
    };
    // For each row, the winner is the first source in cascade that succeeded.
    // Anything BEFORE the winner = failed (red). Anything AFTER = muted (never tried).
    const rowState = (cascade, winnerName) => {
      const winnerIdx = cascade.indexOf(winnerName);
      return cascade.map((src, i) => {
        if (winnerName == null) {
          // entire cascade failed — show all red
          return { name: sourceLabel[src], ok: false, standby: false };
        }
        if (i < winnerIdx) return { name: sourceLabel[src], ok: false, standby: false }; // failed
        if (i === winnerIdx) return { name: sourceLabel[src], ok: true, standby: false }; // active
        return { name: sourceLabel[src], ok: false, standby: true, muted: true };  // never tried
      });
    };

    const forecastRows = [
      { label: 'Temperature',    cascade: ['VC','PirateWeather','NWS'],         winner: fcSources.temp },
      { label: 'Wind',           cascade: ['VC','WeatherAPI','NWS'],            winner: fcSources.wind },
      { label: 'Gust',           cascade: ['VC','PirateWeather','WeatherAPI'],  winner: fcSources.gust },
      { label: 'Humidity',       cascade: ['VC','PirateWeather'],               winner: fcSources.humidity },
      { label: 'Precip 0–12h',   cascade: ['Meteoblue','VC','NWS'],             winner: fcSources.precip0_12 },  // S206: MB P1
      { label: 'Precip 12–48h',  cascade: ['Meteoblue','VC','OpenMeteo'],        winner: fcSources.precip12_48 },  // S206: MB P1
      { label: 'Precip 48h+',    cascade: ['OpenMeteo','WeatherAPI'],           winner: fcSources.precip48plus },
      { label: 'Conditions',     cascade: ['VC','OpenMeteo','WeatherAPI'],      winner: fcSources.conditions },
      { label: 'UV Index',       cascade: ['OpenMeteo','WeatherAPI'],           winner: fcSources.uv },
      { label: 'Feels Like',     cascade: [],                                   winner: 'calculated' },
    ];

    // Realtime P1/P2 — NJWxNet success drives everything (unchanged from S173)
    const njOk  = njwxData?.success;
    const p2DotOk      = !njOk;
    const p2DotStandby = njOk;

    const omOk  = fcStatus?.openMeteo;
    const iem   = lightningData !== null;
    const polOk = pollenData?.success;

    const Dot = ({ ok, standby }) => (
      <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', marginRight: 6, flexShrink: 0,
        background: standby ? '#FACC15' : ok ? '#22C55E' : '#EF4444', verticalAlign: 'middle' }} />
    );

    const sourceColor = (active, standby, failed) => {
      if (active) return '#22C55E';
      if (standby) return '#FACC15';
      return failed ? '#EF4444' : COLOR_MUTED;
    };

    return (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 9999, overflowY: 'auto' }}
        onClick={() => setShowSourceLegend(false)}>
        <div style={{ maxWidth: 440, margin: '40px auto', padding: 20, background: '#111', borderRadius: 14 }} onClick={e => e.stopPropagation()}>

          {/* Header + refresh */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
            <div>
              <div style={{ fontSize: 18, fontWeight: 700, color: COLOR_WHITE }}>📡 Data Sources</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#ffffff', marginTop: 2 }}>{selectedCourse?.name}</div>
            </div>
            <button onClick={() => setShowSourceLegend(false)} style={{ ...BTN_BASE, background: 'transparent', color: COLOR_OFF_WHITE, fontSize: 24, padding: 4 }}>×</button>
          </div>

          {/* Timestamp + refresh button — identical to header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
            {lastRefreshTime && (
              <button
                onClick={() => { clearAllCaches(); loadCourse(selectedCourse); setModalRefreshTime(new Date()); }}
                style={{ ...BTN_BASE, padding: '6px 12px', background: '#0a0a0a', border: `1px solid ${COLOR_GOLD}`, borderRadius: 8, fontSize: 12, color: COLOR_GOLD, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}
              >
                <span style={{ fontSize: 14 }}>↻</span> {(modalRefreshTime || lastRefreshTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </button>
            )}
            {isLoading && <span style={{ fontSize: 12, color: COLOR_MUTED }}>Loading…</span>}
          </div>

          {/* Forecast Sources */}
          <div style={{ fontSize: 13, fontWeight: 700, color: COLOR_GOLD, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 1 }}>Forecast Sources</div>
          <div style={{ background: COLOR_INPUT_BG, borderRadius: 10, padding: 12, marginBottom: 16, fontSize: 13, color: COLOR_WHITE, lineHeight: 1.8 }}>
            {forecastRows.map(({ label, cascade, winner }) => {
              if (cascade.length === 0) {
                // Feels Like — calculated locally
                return (
                  <div key={label} style={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: 2 }}>
                    <span style={{ color: COLOR_MUTED, marginRight: 4 }}>{label}:</span>
                    <Dot ok={true} standby={false} />
                    <span style={{ color: '#22C55E' }}>Calculated (NWS formula)</span>
                  </div>
                );
              }
              const cells = rowState(cascade, winner);
              return (
                <div key={label} style={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: 2 }}>
                  <span style={{ color: COLOR_MUTED, marginRight: 4 }}>{label}:</span>
                  {cells.map((s, i) => (
                    <span key={s.name}>
                      <Dot ok={s.ok} standby={s.standby} />
                      <span style={{ color: sourceColor(s.ok, s.standby, false) }}>{s.name}</span>
                      {i < cells.length - 1 && <span style={{ color: COLOR_MUTED }}> → </span>}
                    </span>
                  ))}
                </div>
              );
            })}
            {fcAllFailed && <div style={{ color: '#EF4444', fontSize: 12, marginTop: 4 }}>⚠ All forecast sources failed</div>}
          </div>

          {/* Realtime Sources — S216: reads from _sources/_rtSources, not hardcoded */}
          <div style={{ fontSize: 13, fontWeight: 700, color: COLOR_GOLD, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 1 }}>Realtime Sources</div>
          <div style={{ background: COLOR_INPUT_BG, borderRadius: 10, padding: 12, marginBottom: 16, fontSize: 13, color: COLOR_WHITE, lineHeight: 1.8 }}>
            {(() => {
              const rtSrc = weatherData?.current?._realtimeSources || weatherData?.current?._rtSources || weatherData?._rtSources || weatherData?._sources || {};
              const rtRow = (varName, p1Label, p2Name) => {
                const winner = rtSrc[varName] || '';
                const p1Won = !!winner && !winner.startsWith(p2Name);
                const p2Won = !!winner && winner.startsWith(p2Name);
                return [
                  { name: p1Label, ok: p1Won || (!winner && njOk), standby: !p1Won && njOk && !p2Won },
                  { name: p2Won ? winner : p2Name, ok: p2Won, standby: p1Won },
                ];
              };
              return [
                { label: 'Temperature', sources: rtRow('temp',   `NJWxNet ${stationId || '—'}`, 'WeatherAPI') },
                { label: 'Wind',        sources: rtRow('wind',   `NJWxNet ${stationId || '—'}`, 'PirateWeather') },
                { label: 'Gust',        sources: rtRow('gust',   `NJWxNet ${stationId || '—'}`, 'PirateWeather') },
                { label: 'Precip',      sources: rtRow('precip', `NJWxNet ${stationId || '—'} 2h sum`, 'WeatherAPI') },
                { label: 'Feels Like',  sources: [{ name: 'Calculated (NWS formula)', ok: true, standby: false }] },
                { label: 'Sky',         sources: [{ name: 'Open-Meteo WMO', ok: omOk, standby: false }] },
              ];
            })().map(({ label, sources }) => (
              <div key={label} style={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: 2 }}>
                <span style={{ color: COLOR_MUTED, marginRight: 4 }}>{label}:</span>
                {sources.map((s, i) => (
                  <span key={s.name}>
                    <Dot ok={s.ok} standby={s.standby} />
                    <span style={{ color: sourceColor(s.ok, s.standby, false) }}>{s.name}</span>
                    {i < sources.length - 1 && <span style={{ color: COLOR_MUTED }}> → </span>}
                  </span>
                ))}
              </div>
            ))}
          </div>

          {/* Other Sources — Meteoblue UV row REMOVED (UV now in forecast cascade) */}
          <div style={{ fontSize: 13, fontWeight: 700, color: COLOR_GOLD, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 1 }}>Other Sources</div>
          <div style={{ background: COLOR_INPUT_BG, borderRadius: 10, padding: 12, marginBottom: 16, fontSize: 13, color: COLOR_WHITE, lineHeight: 1.8 }}>
            <div><Dot ok={iem} standby={false}/><span style={{ color: COLOR_MUTED }}>Lightning:</span> <span style={{ color: iem ? '#22C55E' : '#EF4444' }}>IEM NEXRAD</span> (free, permanent)</div>
            <div><Dot ok={polOk} standby={false}/><span style={{ color: COLOR_MUTED }}>Pollen:</span> <span style={{ color: polOk ? '#22C55E' : '#EF4444' }}>Google Pollen API</span></div>
            <div><Dot ok={true} standby={false}/><span style={{ color: COLOR_MUTED }}>Events:</span> <span style={{ color: '#22C55E' }}>Spond</span> (cron 3 AM ET)</div>
          </div>

          {/* Legend */}
          <div style={{ fontSize: 11, color: COLOR_MUTED, textAlign: 'center' }}>
            <svg width="8" height="8" style={{display:'inline-block',verticalAlign:'middle',marginRight:4}}><circle cx="4" cy="4" r="4" fill="#22C55E"/></svg>active{'  '}
            <svg width="8" height="8" style={{display:'inline-block',verticalAlign:'middle',marginRight:4}}><circle cx="4" cy="4" r="4" fill="#FACC15"/></svg>standby{'  '}
            <svg width="8" height="8" style={{display:'inline-block',verticalAlign:'middle',marginRight:4}}><circle cx="4" cy="4" r="4" fill="#EF4444"/></svg>error
          </div>
          <div style={{ fontSize: 11, color: COLOR_MUTED, textAlign: 'center', marginTop: 8 }}>Tap outside to close</div>
        </div>
      </div>
    );
  };

  // ── MAIN RENDER ────────────────────────────────────────────
  // S166: outer ErrorBoundary catches render-time errors anywhere in the App tree.
  // Previously only the review screen inside ScorecardScreen was wrapped — a render
  // error elsewhere produced a white screen. This converts any such crash into a
  // visible red panel with the error message + Dismiss button.
  return (
    <ErrorBoundary>
    <div style={{ minHeight: '100svh', background: '#111', overflowX: 'hidden' }}>
      {/* ── OVERLAYS — rendered at root level, never clipped ── */}
      <TriggerPopupOverlay />
      <SourceLegendOverlay />
    <div style={{ background: '#111', color: COLOR_WHITE, fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', maxWidth: 480, margin: '0 auto', overflowX: 'hidden' }}>
      {/* ── LAST ROUND OVERLAY ─────────────────────────────── */}
      {showRoundOverlay && (() => {
        const allRounds = getLocalRounds();
        const lastRound = allRounds.length > 0 ? allRounds[allRounds.length - 1] : null;
        // Season best
        let bestRound = null;
        let bestRoundDiff = Infinity;
        allRounds.forEach(r => {
          const t = (r.players || []).reduce((s, p) => {
            const tot = (p.scores || []).reduce((a, v) => a + (parseInt(v) || 0), 0);
            return s + tot;
          }, 0);
          const par = r.par || 72;
          const d = t - par;
          if (d < bestRoundDiff) { bestRoundDiff = d; bestRound = { ...r, total: t }; }
        });
        if (!lastRound) return null;
        const lastTotal = (lastRound.players || []).reduce((s, p) => {
          return s + (p.scores || []).reduce((a, v) => a + (parseInt(v) || 0), 0);
        }, 0);
        const lastPar = lastRound.par || 72;
        if (lastTotal === 0) return null; // No valid scores entered
        const lastDiff = lastTotal - lastPar;
        // eslint-disable-next-line no-unused-vars
        const lastDiffStr = lastDiff === 0 ? 'E' : lastDiff > 0 ? `+${lastDiff}` : `${lastDiff}`;
        return (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.75)', zIndex: 9999,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '20px',
          }} onClick={dismissOverlay}>
            <div style={{
              background: '#1a1a1a', border: `2px solid ${COLOR_GREEN}`, borderRadius: 16,
              padding: 20, maxWidth: 380, width: '100%', position: 'relative',
            }} onClick={e => e.stopPropagation()}>
              {/* X button */}
              <button onClick={dismissOverlay} style={{
                position: 'absolute', top: 12, right: 12,
                background: 'transparent', border: 'none', color: COLOR_OFF_WHITE,
                fontSize: 22, cursor: 'pointer', lineHeight: 1, padding: 4,
              }}>×</button>

              {/* Last round */}
              <div style={{ fontSize: 12, fontWeight: 700, color: COLOR_MUTED, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>
                Last Round
              </div>
              <div style={{ fontSize: 15, fontWeight: 700, color: COLOR_WHITE, marginBottom: 2 }}>
                {lastRound.course || lastRound.courseName || 'Unknown course'}
              </div>
              <div style={{ fontSize: 12, color: COLOR_MUTED, marginBottom: 10 }}>
                {lastRound.date ? new Date(lastRound.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : ''}
              </div>
              {(lastRound.players || [])
                .filter(p => p.scores && p.scores.some(s => s !== null && s !== ''))
                .sort((a, b) => {
                  const aT = (a.scores||[]).reduce((s,v)=>s+(parseInt(v)||0),0);
                  const bT = (b.scores||[]).reduce((s,v)=>s+(parseInt(v)||0),0);
                  return aT - bT;
                })
                .map((p, i) => {
                  const tot = (p.scores||[]).reduce((s,v)=>s+(parseInt(v)||0),0);
                  const diff = tot - lastPar;
                  const diffStr = diff === 0 ? 'E' : diff > 0 ? `+${diff}` : `${diff}`;
                  const medal = i===0?'1st':i===1?'2nd':i===2?'3rd':'';
                  return (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 8px', background: i===0?'rgba(102,187,106,0.15)':'rgba(0,0,0,0.2)', borderRadius: 6, marginBottom: 4 }}>
                      <div style={{ fontSize: 14, color: COLOR_WHITE }}>{medal} {p.name}</div>
                      <div style={{ display: 'flex', gap: 10 }}>
                        <div style={{ fontSize: 14, fontWeight: 700, color: COLOR_WHITE }}>{tot}</div>
                        <div style={{ fontSize: 13, color: diff<0?'#4ADE80':diff>0?'#FB923C':COLOR_MUTED, fontWeight: 600, minWidth: 28, textAlign: 'right' }}>{diffStr}</div>
                      </div>
                    </div>
                  );
                })}

              {/* Season best — only show if different round */}
              {bestRound && bestRound.date !== lastRound.date && (
                <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px solid ${COLOR_DARK_GREEN}` }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: COLOR_GOLD, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>
                    Season Best
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontSize: 14, color: COLOR_WHITE, fontWeight: 600 }}>{bestRound.course || bestRound.courseName || 'Unknown'}</div>
                      <div style={{ fontSize: 12, color: COLOR_MUTED }}>
                        {bestRound.date ? new Date(bestRound.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : ''}
                      </div>
                    </div>
                    <div style={{ fontSize: 28, fontWeight: 800, color: COLOR_GOLD }}>{bestRound.total}</div>
                  </div>
                </div>
              )}

              {/* Footer */}
              <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px solid ${COLOR_DARK_GREEN}`, fontSize: 11, color: COLOR_MUTED, textAlign: 'center', lineHeight: 1.5 }}>
                To see this anytime, go to <span style={{ color: COLOR_GREEN, fontWeight: 700 }}>Statistics → Round History</span>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Sub-screen routing */}
      {screen === 'stormWatch' && <StormWatchScreen />}
      {screen === 'locationentry' && <LocationEntryScreen />}
      {screen === 'directions' && <>
        <Header title="Directions & Contact" showBack />
        {(() => {
          const addr = selectedCourse?.formattedAddress || selectedCourse?.name || '';
          const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(addr)}`;
          const phone = selectedCourse?.phoneNumber || '';
          return (
            <div style={{ padding: '24px 16px', display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 480, margin: '0 auto' }}>
              <div style={{ fontSize: 14, color: COLOR_MUTED, marginBottom: 4 }}>{addr}</div>
              <a href={mapsUrl} target="_blank" rel="noopener noreferrer"
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                  width: '100%', padding: '16px', background: COLOR_CARD, border: `4px solid ${COLOR_DARK_GREEN}`,
                  borderRadius: 12, fontSize: 16, fontWeight: 800, color: '#0a0a0a',
                  textDecoration: 'none', boxSizing: 'border-box' }}>
                🗺️ Get Directions in Google Maps
              </a>
              {phone && (
                <a href={`tel:${phone}`}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                    width: '100%', padding: '16px', background: COLOR_CARD, border: `4px solid ${COLOR_DARK_GREEN}`,
                    borderRadius: 12, fontSize: 16, fontWeight: 800, color: '#0a0a0a',
                    textDecoration: 'none', boxSizing: 'border-box' }}>
                  📞 Call {phone}
                </a>
              )}
            </div>
          );
        })()}
      </>}
      {!selectedCourse && screen === 'home' && <LocationEntryScreen />}
      {selectedCourse && screen === 'home' && <>
        <CourseHeader />
        <div style={{ textAlign: 'center', padding: '8px 0 10px', borderBottom: `1px solid ${COLOR_DARK_GREEN}`, marginBottom: 8, overflow: 'visible' }}>
          <div style={{ position: 'relative', display: 'inline-block', overflow: 'visible' }}>
            <img src="https://res.cloudinary.com/dovzgljte/image/upload/w_480,q_auto,f_auto/ZAP_Activity_Logo_S253_final.png" alt="ZAP" loading="eager" style={{ width: 240, maxWidth: '80vw', height: 'auto', display: 'block' }} />
            <img src="https://res.cloudinary.com/dovzgljte/image/upload/w_200,q_auto,f_auto/ZAP_Pro_Badge_S259d.png" alt="PRO" style={{ position: 'absolute', bottom: 4, right: -65, width: 60, height: 'auto' }} />
          </div>
        </div>
        {HomeScreen()}
      </>}
      {screen === 'forecast' && <><ForecastScreen /></>}
      {screen === 'forecastdrilldown' && <><Header title={(() => { if (!selectedForecastDay) return 'Forecast'; const d = new Date(selectedForecastDay + 'T00:00:00'); const now = new Date(); now.setHours(0,0,0,0); const tom = new Date(now); tom.setDate(now.getDate()+1); const day = d.getTime()===now.getTime()?'Today':d.getTime()===tom.getTime()?'Tomorrow':d.toLocaleDateString('en-US',{weekday:'long'}); const dt = d.toLocaleDateString('en-US',{month:'short',day:'numeric'}); return `${day} · ${dt}`; })()} showBack /><ForecastDrilldown /></>}
      {screen === 'dayofplay' && <><Header title="Activity Day" showBack /><OnCourseScreen /></>}
      {screen === 'profile' && <><Header title="Settings" showBack /><ProfileScreen /></>}
      {screen === 'drilldown' && <><Header title="Hourly Forecast" showBack /><Drilldown /></>}
      {screen === 'postactivity' && <><Header title="Post-Activity" showBack /><PostActivityScreen /></>}
      {screen === 'nearbyspots' && <><Header title="Nearby Spots" showBack /><NearbySpotsScreen /></>}
    {/* KILLED: {screen === 'coursedetail' ...} */}
    {/* KILLED: {screen === 'nineteenth' ...} */}
    {/* KILLED: {screen === 'scorecard' ...} */}
    {/* KILLED: {screen === 'events' ...} */}
    {/* KILLED: {screen === 'roundhistory' ...} */}
    {/* KILLED: {screen === 'scorestats' ...} */}
    {/* KILLED: {screen === 'statsoverview' ...} */}
    {/* KILLED: {screen === 'statsplayers' ...} */}
      {screen === 'admin' && isAdmin && <><Header title="Admin" showBack /><AdminPanel /></>}
      {/* Activity Planner: legacy mode block removed — screen-based routing handles all navigation */}
    </div>
    </div>
    </ErrorBoundary>
  );
}






































































